// types/types.ts

export type FactoryImageOperation =
  | { kind: 'persona-thumbnail' | 'archetype-thumbnail' | 'validate-persona' | 'validate-output' | 'persona-jpeg' | 'contact-sheet' | 'feedback' | 'coherence' | 'naming' | 'preview' }
  | { kind: 'fit-persona'; budget: number }
  | { kind: 'qwen' | 'topaz'; width: number; height: number }
  | { kind: 'object-panel'; name: string; dimensions: FactoryObject['dimensions'] };

export type FactoryImageRequest = {
  version: 1;
  operation: FactoryImageOperation;
  sizes: number[];
  expiresAt: number;
};

export type FactoryImageStatus =
  | { state: 'complete'; bytes: number }
  | { state: 'failed'; message: string };

export interface ListPromptEditorHandle {
  read: () => { text: string; start: number; end: number };
  replace: (start: number, end: number, text: string) => void;
  focus: () => void;
  select: (start: number, end: number) => void;
  contains: (target: Node) => boolean;
}
import { type RgbaColor } from 'react-colorful';
import type { Dispatch, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode, RefObject, SetStateAction, WheelEvent as ReactWheelEvent } from 'react';
import { toast } from 'sonner';
import {
  AGENT_MODEL_IDS,
  INSTRUCTION_MODEL_IDS as INSTRUCTION_MODEL_IDS_FROM_REGISTRY,
} from '#lib/model-registry';
import type { PortalUrlState } from '#lib/leasing/portal-url-state';
import type {
  WorkspaceTimelinePointerResolveArgs,
  WorkspaceTimelinePointerTrackLike,
  WorkspaceTimelineProjection,
  WorkspaceTimelineResolvedTrackView,
  WorkspaceTimelineSnapshot,
  WorkspaceTimelineSplitResolveArgs,
  WorkspaceTimelineSplitTrackLike,
  WorkspaceTimelineSummary,
  WorkspaceTimelineTrackView,
} from '#lib/workspace-timeline';
import type { TimelineCompositionStatus, TimelineTrackInput } from '#lib/timeline-video-types';
import type { RentRollEdit, ScenarioParams } from '@/components/asset-management/scenario-engine';
import type { CellStyle } from '@/components/asset-management/importSpreadsheet';
import type { GridBatchLaunch, GridGenerationPayload, GridSection } from '#lib/grid-generation-script';
export type Setter<T> = Dispatch<SetStateAction<T>>;


// ============================== Common Types ==============================

export type ModalName =
  | 'UserSettings'
  | 'ActionHistory'
  | 'ImageCrop'
  | 'ProjectTypeSelection'
  | 'AssistantDebug'
  | 'SaveStatus'
  | 'DetachedStateClarification'
  | 'LayerDeletionConfirmation'
  | 'ImageExport'
  | 'ModelGallery'
  | 'SupportChat'
  | 'NotificationCenter'
  | 'LeasingMutationConfirmation'
  | 'PromptLibrary'
  | 'OrganisationSelection'
  | 'MemberSelection'
  | 'DocumentPreview'
  | 'Confirmation'
  | null;
export const AGENT_LLMS: string[] = [...AGENT_MODEL_IDS];
export const INSTRUCTION_MODEL_IDS: string[] = [...INSTRUCTION_MODEL_IDS_FROM_REGISTRY];

// Tools that can carry their own default agent model (settable in the global
// parameters tab). Order is the display order in settings.
export const AGENT_TOOL_KEYS = ['STUDIO', 'ANALYST', 'SOURCE'] as const;
export type AgentToolKey = typeof AGENT_TOOL_KEYS[number];
export type ToolDefaultModels = Partial<Record<AgentToolKey, string>>;
export const AGENT_TOOL_LABELS: Record<AgentToolKey, string> = {
  STUDIO: 'Studio',
  ANALYST: 'Analyst',
  SOURCE: 'Source',
};


export type NormalizedPoint = { x: number; y: number };

export interface SpatialManifest {
  version: number;
  type: 'Polygon';
  coordinates: NormalizedPoint[];
  meta: {
    calculatedAreaSqm: number;
    calibrationSnapshot: number;
    centroid: NormalizedPoint;
    frontageSegment?: [NormalizedPoint, NormalizedPoint] | null;
    frontageSegments?: Array<[NormalizedPoint, NormalizedPoint]> | null;
    frontageMeters?: number | null;
  };
}

export interface LockableFile {
  id: string;
  file: File;
  locked: boolean;
}

export type BrandCategory = 
  | 'FOOD_AND_BEVERAGE'
  | 'HOME_AND_DECORATION'
  | 'FASHION_AND_APPAREL'
  | 'SPORT_AND_OUTDOOR'
  | 'BEAUTY_AND_COSMETICS'
  | 'HEALTH_AND_WELLNESS'
  | 'AUTOMOTIVE'
  | 'ELECTRONICS_AND_MULTIMEDIA'
  | 'SERVICES'
  | 'KIDS_AND_TOYS'
  | 'CULTURE_AND_LEISURE'
  | 'ACCESSORIES_AND_JEWELRY'
  | 'OTHER';

// 2. Define the Value-Object for runtime use (JSX, Object.values, etc.)
// satisfies Record<BrandCategory, BrandCategory> ensures this is perfectly in sync with the type above
export const BrandCategory = {
  FOOD_AND_BEVERAGE: 'FOOD_AND_BEVERAGE',
  HOME_AND_DECORATION: 'HOME_AND_DECORATION',
  FASHION_AND_APPAREL: 'FASHION_AND_APPAREL',
  SPORT_AND_OUTDOOR: 'SPORT_AND_OUTDOOR',
  BEAUTY_AND_COSMETICS: 'BEAUTY_AND_COSMETICS',
  HEALTH_AND_WELLNESS: 'HEALTH_AND_WELLNESS',
  AUTOMOTIVE: 'AUTOMOTIVE',
  ELECTRONICS_AND_MULTIMEDIA: 'ELECTRONICS_AND_MULTIMEDIA',
  SERVICES: 'SERVICES',
  KIDS_AND_TOYS: 'KIDS_AND_TOYS',
  CULTURE_AND_LEISURE: 'CULTURE_AND_LEISURE',
  ACCESSORIES_AND_JEWELRY: 'ACCESSORIES_AND_JEWELRY',
  OTHER: 'OTHER',
} as const satisfies Record<string, BrandCategory>;

export type PortfolioType =
  | 'LANDLORD'
  | 'DESIGN_AGENCY'
  | 'TENANT_REP'
  | 'IRRELEVANT'
  | 'OTHER';

export const PortfolioType = {
  LANDLORD: 'LANDLORD',
  DESIGN_AGENCY: 'DESIGN_AGENCY',
  TENANT_REP: 'TENANT_REP',
  IRRELEVANT: 'IRRELEVANT',
  OTHER: 'OTHER',
} as const satisfies Record<string, PortfolioType>;

type PortfolioCategory =
  | 'RETAIL_REIT'
  | 'RETAIL_PARK_OUTLET'
  | 'MALL_OPERATOR'
  | 'RETAIL_LANDLORD'
  | 'MIXED_USE_DEVELOPER'
  | 'BROKER_TENANT_REP'
  | 'DESIGN_AGENCY'
  | 'OTHER';

export const PortfolioCategory = {
  RETAIL_REIT: 'RETAIL_REIT',
  RETAIL_PARK_OUTLET: 'RETAIL_PARK_OUTLET',
  MALL_OPERATOR: 'MALL_OPERATOR',
  RETAIL_LANDLORD: 'RETAIL_LANDLORD',
  MIXED_USE_DEVELOPER: 'MIXED_USE_DEVELOPER',
  BROKER_TENANT_REP: 'BROKER_TENANT_REP',
  DESIGN_AGENCY: 'DESIGN_AGENCY',
  OTHER: 'OTHER',
} as const satisfies Record<string, PortfolioCategory>;

export type PortfolioStatus = 'COLD' | 'TARGETED' | 'CONTACTED' | 'NEGOTIATION' | 'SIGNED';

export const PortfolioStatus = {
  COLD: 'COLD',
  TARGETED: 'TARGETED',
  CONTACTED: 'CONTACTED',
  NEGOTIATION: 'NEGOTIATION',
  SIGNED: 'SIGNED',
} as const satisfies Record<string, PortfolioStatus>;

export const CNCC_SECTORS = {
  A: 'Alimentation',
  B: 'Équipement de la personne',
  C: 'Équipement de la maison',
  D: 'Culture, Cadeaux, Loisirs',
  E: 'Hygiène, Beauté, Santé',
  F: 'Services',
  G: 'Restauration',
} as const;

export type CNCCSector = keyof typeof CNCC_SECTORS;

export type SourcingTaxonomy = 'FATER_CATEGORIES' | 'CNCC_SECTORS';
export const SourcingTaxonomy = {
  FATER_CATEGORIES: 'FATER_CATEGORIES',
  CNCC_SECTORS: 'CNCC_SECTORS',
} as const satisfies Record<string, SourcingTaxonomy>;

export interface PropertySourcingCategoryFilters {
  portfolioFitMinimumScore?: number;
  /** CNCC sectors (A-G) the campaign focuses on; empty/absent = all sectors. */
  acceptedCnccSectors?: CNCCSector[];
  /** Precise CNCC secteurs/activities (e.g. "Chaussures de sport (sneakers)"). Narrower
   *  than the famille; brands lacking a secteur but in a targeted famille are still kept. */
  acceptedCnccActivities?: string[];
  /** Target unit surface range in m2; brands whose surface range definitely cannot fit are excluded. */
  surfaceMinSqm?: number;
  surfaceMaxSqm?: number;
  /** Store formats the unit takes (lib/leasing/formatFit.ts FORMATS); empty = any. A brand whose French
   *  network rarely uses them is capped on surface & format (owner, 2026-10-12: Krispy Kreme's kiosks). */
  acceptedFormats?: string[];
  /** When true, the campaign ignores the portfolio's reference shortlist and re-analyses the whole
   *  brand universe (subject to the other filters) instead of only shortlisted brands. */
  /** Minimum declared/estimated store count. A brand whose KNOWN network is smaller is dropped;
   *  one with no network data is kept, per the no-false-negative rule. */
  minimumNetworkSize?: number;
  /** Only keep brands that have a website on file. */
  requireKnownWebsite?: boolean;
  /** Unlist off-scope brands: a usual format outside the surface range is not scored, and a scored
   *  brand that clearly misses the brief is set aside. Absent = on. */
  excludeOffScope?: boolean;
  /** ISO country codes the run restricts candidates to, persisted so the form can read them back. */
  countryCodes?: string[];
}

/**
 * A saved set of Source launch parameters. Lives here, not in lib/leasing/sourcingPreset.ts, because
 * KernelState carries it and shared types belong in this file (AGENTS.md 4.1).
 *
 * Never carries a property, asset or portfolio id: a saved set is reusable across assets, and a
 * target id in it would silently retarget somebody's run.
 */
/** One saved, NAMED set of launch parameters. */
export interface SourcingPresetParamsSet {
  /** What the user called it: "ESQ params 1". Unique within a user's preset, trimmed, never empty. */
  name: string;
  params: SourcingPresetParams;
}

export interface SourcingPresetParams extends PropertySourcingCategoryFilters {
  /** ISO country codes the run restricts candidates to. */
  countryCodes?: string[];
  /** Minimum declared/estimated store count. */
  minimumNetworkSize?: number;
  /** Spending ceiling allocated to the run; actual brands analysed vary with analysis cost. */
  budgetCredits?: number;
  /** Only keep brands scoring at or above this fit score. */
  minimumFitScore?: number;
  /** Skip brands scored in the last six months. */
  excludeRecentlyScored?: boolean;
  /** Skip brands already present in the portfolio. */
  excludeOnsiteTenants?: boolean;
  /** Only keep brands with a verified website. */
  requireKnownWebsite?: boolean;
}

interface SourcingStrategyPayload {
  propertyId: string;
  targetUnitIds?: string[];
  targetBrandIds?: string[];
  freeTextContext?: string | null;
  filters: {
    categories?: string[];
    excludePropertyBrands?: boolean;
    excludeRecentLeads?: boolean;
  };
  scoringRubric?: ScoringCriterion[];
  autoSelect?: {
    topCount?: number;
    topPercent?: number;
    minScore?: number;
  };
}

export type BrandConceptFormat = 
  | 'STANDARD'
  | 'KIOSK'
  | 'CORNER'
  | 'FLAGSHIP'
  | 'DRIVE'
  | 'MOBILE';

type BrandLifecycleStatus =
  | 'ACTIVE'
  | 'INACTIVE'
  | 'LIQUIDATED';

export type BrandMarketPositioning =
  | 'DISCOUNT'
  | 'MASS_MARKET'
  | 'PREMIUM'
  | 'LUXURY';

export const BrandMarketPositioning = {
  DISCOUNT: 'DISCOUNT',
  MASS_MARKET: 'MASS_MARKET',
  PREMIUM: 'PREMIUM',
  LUXURY: 'LUXURY',
} as const satisfies Record<string, BrandMarketPositioning>;

export const BrandConceptFormat = {
  STANDARD: 'STANDARD',
  KIOSK: 'KIOSK',
  CORNER: 'CORNER',
  FLAGSHIP: 'FLAGSHIP',
  DRIVE: 'DRIVE',
  MOBILE: 'MOBILE',
} as const satisfies Record<string, BrandConceptFormat>;

export type GuideLanguage = 'en' | 'fr';

interface GuideChapterContent {
  title: string;
  content: React.ReactNode;
}

export interface GuideChapterModule {
  id: string;
  section: string;
  navTitle: Record<GuideLanguage, string>;
  content: Record<GuideLanguage, GuideChapterContent>;
}

export type AgentMode = 'director' | 'commander' | 'analyst' | 'master' | 'doctrine' | 'slides' | 'pulse';

export interface ImageAttachment {
  url: string;
  originalUrl?: string;
  polygons?: { x: number; y: number }[][];
}

export type ChatTimelineViewMode = 'session' | 'subject';

export interface ListPermissionRequest {
  id: string;
  /** The command asking; 'Always allow' remembers it per user. */
  action: 'DELETE_LIST_OUTPUTS' | 'PULSE_SET_ASSUMPTIONS';
  title: string;
  label: string;
  items: { id: string; name: string; thumbnailUrl?: string }[];
}

export interface SessionRuntimeState {
  statusMessage: string;
  chatState: ChatStateItem[];
  pendingMessages?: Message[];
  subjectNavigationHistory?: ChatSubjectFocusEntry[];
  subjectHistoryCursor?: number | null;
  runningScriptId: string | null;
  runningCommandId: string | null;
  runningCommandStartTime: number | null;
  idResolutionMap: Record<string, string>;
  isStopRequested: boolean;
  abortController: AbortController | null;
  timelineViewMode?: ChatTimelineViewMode;
  focusRuntimeByKey?: SessionFocusRuntimeMap;
  unpromptedTurnDepth?: number;
  chainStartedByUser?: boolean;
  slidesButtonIntent?: string | null;
  factoryPanelAttempts?: Record<string, FactoryPersonaPanelAttempt>;
  factoryObjectPanelAttempts?: Record<string, FactoryObjectPanelAttempt>;
  listRetryAllowance?: { projectId: string; userId: string; sourceTaskId: string | null; claim: () => number };
  latestListGenerationTaskIds?: string[];
  /** A List command waiting for the user's Allow / Deny (ListPermissionPrompt). */
  listPermission?: ListPermissionRequest | null;
  /** The run's closing conclusion has been asked for; set once per user turn so the agent is woken for it only once. */
  runConclusionAsked?: boolean;
  /** A message sent while the agent was working: already in the transcript, sent on its own once the session is idle
   * unless a later answer already took it in (a run's own continuation reads the whole history). */
  queuedUserMessage?: { timestamp: string; prompt: string; imageUrls: string[]; agentMode: AgentMode } | null;
  listGenerationAgreementScriptId?: string | null;
  listCreativeContextSaving?: string;
  listCreativeContextPersistence?: Promise<void>;
  messagePersistence?: Promise<void>;
  factoryReferenceFocus?: string;
  /** The jnn Designer's views of the turn running, by the id view_area or view_image gave each (kernel/designer/jnn/browserCalls.ts). */
  designerViews?: Record<string, DesignerView>;
  /** The reference tray files stored for the jnn turn running, tray entry id -> image path ('' when it could not be stored): each is shown to the model once per turn. */
  designerTray?: Record<string, string>;
  /** The undo steps the jnn turn running made, and those its own undo moved to the redo history: the only ones its undo and redo may take. */
  designerSteps?: { made: UndoableAction[]; undone: UndoableAction[] };
  /** The transcript keys (createChatMessageKeyer) of the user messages showing their List Auto technical prompt. */
  openTechnicalPromptKeys?: string[];
}

export interface ContextAnchor {
  entityType: EntityType;
  entityId: string;
  mass: number;
  provenance?: string;
  ephemeralData?: any;
  lastAccessedAt: number;
}

export interface ChatSessionContextAnchor {
  entityType: EntityType;
  entityId: string;
  name: string;
  mass: number;
}

export interface ActiveEntityFocus {
  entityType: EntityType;
  entityId: string;
  focusScope?: 'entity' | 'table';
  tableView?: SessionTableFocusView;
  focusActionType?: LeasingActionType;
  /**
   * The Source campaign this conversation belongs to. Stamped at launch and when a campaign is
   * opened from the dashboard; it is what makes a campaign and its chat inseparable: activating
   * the session opens this campaign, opening the campaign activates this session. Absent on
   * conversations that do not belong to a campaign (the Source dashboard, other modules).
   */
  campaignId?: string;
}

export interface SessionFocusRuntimeEntry {
  focusScope: 'entity' | 'table';
  entityType: EntityType;
  tableView?: SessionTableFocusView;
  presentationMode?: 'table' | 'grid';
  filters?: Record<string, unknown>;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  pageSize?: number;
  visibleIds?: string[];
  totalVisible?: number;
  updatedAt: number;
}

export type SessionFocusRuntimeMap = Record<string, SessionFocusRuntimeEntry>;

export interface EntityKernelStateEntry {
  entityType: KernelEntityType;
  entityId: string;
  /**
   * Local overlay payload merged over inventory registry for reads.
   * For Project entries, prefer `ProjectKernelState`.
   */
  state: Record<string, unknown>;
  updatedAt: number;
}

export type EntityKernelStateRegistry = Record<string, EntityKernelStateEntry>;

export interface ChatSubjectFocusEntry {
  entityType: EntityType;
  entityId: string;
  focusScope?: 'entity' | 'table';
  tableView?: SessionTableFocusView;
  focusActionType?: LeasingActionType;
  /** Carried so that back/forward subject navigation restores a campaign-BOUND subject; an entry
   *  recorded without it would overwrite the binding on replay (equivalence distinguishes it). */
  campaignId?: string;
  focusedAt: string;
}

export type EntityViewVariant = 'chip' | 'row' | 'card' | 'page';
export type DealEconomicsTab = 'RENT_FREE' | 'FITOUT' | 'STEPS' | 'CONTACT';

export interface BaseEntityProps<T = any> {
  entityId: string;
  variant?: EntityViewVariant;
  onClick?: () => void;
  selected?: boolean;
  className?: string;
  rowActions?: EntityRowAction<T>[];
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  onSelectionZoneMouseDown?: (event: ReactMouseEvent, id: string) => void;
}

export interface EntityRowAction<T = any> {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: (entity: T) => void | Promise<void>;
  visible?: (entity: T) => boolean;
  disabled?: (entity: T) => boolean;
  destructive?: boolean;
}

export interface EntityBulkAction<T = any> {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: (entities: T[], clearSelection: () => void) => void | Promise<void>;
  disabled?: (entities: T[]) => boolean;
  destructive?: boolean;
}

export interface WorkingMemory {
  anchors: ContextAnchor[];
  listCreativeContext?: ListCreativeContext;
  /** For a studio project session LAUNCHED BY MASTER: the originating master session id ,
   *  powers the "Open on Master" link and keeps the two sessions durably linked. */
  originMasterSessionId?: string | null;
}

/** The Designer brains that exist; the registry is lib/designer/brains.ts. */
export type DesignerBrainId = 'legacy' | 'jnn';

/** One Responses API conversation item of a tools-protocol Designer turn, opaque to the browser
 *  (reasoning carries encrypted_content). Shape and size are checked server-side: lib/designer/jnn/items.ts. */
export type DesignerItem = Record<string, unknown> & { type: string };

/** The generation the browser would run now, as a budgeted turn reports it for the server's quote: the selected model, its parameters, its output count. */
export type DesignerStudioState = { model: string; params: Record<string, unknown>; outputs: number };

/** POST /api/ai/designer. priorItems are the items of the conversation's earlier turns, in order. A turn
 *  starts with the user's prompt, continues with the outputs of the browser calls it was waiting on
 *  (turnItems are what its browserCalls event carried), is kept alive while the browser runs them
 *  (touch), or is stopped. Its steps, budget and spend are the server's (DesignerTurnState). */
export type DesignerTurnRequestBody = { brain: DesignerBrainId; sessionId: string; projectId: string } & (
  | { kind: 'start'; prompt: string; priorItems: DesignerItem[]; attachments?: DesignerAttachment[]; budgetCredits?: number; studio?: DesignerStudioState; restrictions?: DesignerRestrictions; timeZone?: string }
  | { kind: 'continue'; turnId: string; priorItems: DesignerItem[]; turnItems: DesignerItem[]; callOutputs: DesignerItem[]; studio?: DesignerStudioState; restrictions?: DesignerRestrictions; timeZone?: string }
  | { kind: 'touch'; turnId: string }
  | { kind: 'stop'; turnId: string }
);

/** A chat attachment of a jnn start: ref is the original's path, view the image the model is shown (ref itself when no
 *  smaller one was made), width and height the original's pixels after its orientation, absent when the browser could not decode it. */
export type DesignerAttachment = { ref: string; view: string; name: string; width?: number; height?: number };

/** The user's own locks on the jnn brain's action commands, read as the legacy Director reads them (lib/designer/agentCommandRestriction.ts). */
export type DesignerRestrictions = { disabled: string[]; modelLocked: string[] };

/** The composer's budget chip: a credit preset of the next jnn message, or none (lib/designer/jnn/budget.ts). */
export type DesignerBudgetChoice = 'none' | '2' | '3' | '4' | '5' | '8' | '10' | '15' | '25' | '50' | '100';

export type DesignerReasoningEffort = 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';

/** The jnn brain's per-user knobs (UserPreferences.designerJnn), bounded and defaulted by lib/designer/jnn/settings.ts. */
export type DesignerJnnSettings = {
  maxSteps: number;
  reasoningEffort: DesignerReasoningEffort;
  viewSide: number;
  compactThreshold: number;
  defaultBudget: DesignerBudgetChoice;
  webResults: number;
};

/** What read_scene reports of the open Canvas project (lib/designer/jnn/scene.ts). Every rect is in world units, a layer's being its world bounds (designerLayerWorldRect). */
export type DesignerScene = {
  projectName: string;
  activeWorkspaceId: string | null;
  model: string;
  /** The selected model's current parameters, its prompt included. */
  modelParameters: Record<string, unknown>;
  outputsPerGeneration: number | null;
  /** The images generate takes when it is passed no references; ref null when the file could not be stored. */
  referenceTray: Array<{ ref: string | null; name: string }>;
  /** The descriptions of the step undo would revert and of the one redo would reapply. */
  undoStep: string | null;
  redoStep: string | null;
  workspaces: Array<{
    id: string; name: string; frame: Rect; generationArea: Rect; activeVersionId: string | null; validatedVersionId: string | null;
    /** Oldest first. */
    versions?: Array<{ id: string; name: string; status: string }>;
    /** The active version's comment markers, at world positions; strokes says the user drew some. */
    comments?: Array<{ x: number; y: number; text: string }>;
    strokes?: boolean;
    /** Bottom to top. */
    layers: Array<{ id: string; name: string; kind: string; rect: Rect; visible: boolean; pixelWidth: number; pixelHeight: number; locked: boolean; alwaysAbove: boolean }>;
  }>;
};

/** One view_area image: the world rect it shows and its size in pixels. */
export type DesignerCanvasView = { worldRect: Rect; pxWidth: number; pxHeight: number };
/** One view_image image: the image path it shows, the rect of that image's pixels, and its own size in pixels. */
export type DesignerImageView = { image: string; srcRect: Rect; pxWidth: number; pxHeight: number };
export type DesignerView = DesignerCanvasView | DesignerImageView;

/** A function_call item of a Designer turn, as the provider returned it. */
export type DesignerCall = DesignerItem & { call_id: string; name: string; arguments: string };

/** A call the model made to a tool the browser runs; args is the model's JSON argument string. */
export type DesignerBrowserCall = { callId: string; name: string; args: string };

/** Measured tokens of one model call or one turn; reads and writes of the prompt cache are counted inside prompt. */
export type DesignerTokens = { prompt: number; cachedPrompt: number; cacheWrite: number; completion: number };

/**
 * A post-processing defect the Designer bench paints around a fresh generation (lib/designer/brainBenchDefects.ts), bbox in pixels of the
 * case's images; type none is the control, the same landed layer with no defect and no severity, its bbox the layer.
 */
export type DesignerBenchDefectType = 'seam' | 'halo' | 'color' | 'stretch' | 'islands' | 'none';
export type DesignerBenchDefect = { type: DesignerBenchDefectType; severity: 'visible' | 'subtle' | null; bbox: Rect };
/** An action the agent took, its geometry in world units even when it pointed in a view's pixels; a billed one keeps the area it ran on; error is a real case's refusal. */
export type DesignerBenchAction = { tool: string; args: Record<string, unknown>; generationArea?: Rect; error?: string };
/** The bench's own reading of a case's whole turn, indicative only: the owner's verdict, stored apart, wins. reported: its final text names the defect. */
export type DesignerBenchScore = { detected: boolean; located: boolean; reactionExpected: boolean; reported: boolean; why: string };
export type DesignerBenchDefectCase = {
  kind?: 'defect';
  id: string;
  model: string;
  capture: string;
  defect: DesignerBenchDefect;
  request: string;
  /** Storage paths in the user-files container: the composite as the canvas shows it, and the new layer alone on magenta. */
  images: { composite: string; layerAlone: string };
  /** The agent's final answer, and every action of the turn in order; capped when it asked for more canvas actions than the bench answers. */
  text: string;
  actions: DesignerBenchAction[];
  capped?: boolean;
  steps: number;
  credits: number;
  score: DesignerBenchScore;
  error?: string;
};
/**
 * A recent real request replayed with real generations and the real post-processing (--real-cases): storage paths of the frame
 * before, the frame after, and the turn's new layers alone on magenta; providerUsd is what its generations cost us, never billed.
 */
export type DesignerBenchRealCase = {
  kind: 'real';
  id: string;
  model: string;
  capture: string;
  family: string;
  note?: string;
  request: string;
  images: { before: string; after: string; layerAlone: string };
  text: string;
  actions: DesignerBenchAction[];
  capped?: boolean;
  steps: number;
  credits: number;
  generations: number;
  providerUsd: number;
  error?: string;
};
export type DesignerBenchCase = DesignerBenchDefectCase | DesignerBenchRealCase;
/** One run of the Designer bench, published under eval-captures/designer-bench/<id>/ for the owner to rate. */
export type DesignerBenchRun = { id: string; createdAt: string; models: string[]; cases: DesignerBenchCase[] };
/**
 * The owner's verdict on one case, in the run's verdicts.json beside its report; on a control, saw means a false alarm.
 * On a real case, saw means the finish is clean and reactionRight that it did what was asked.
 */
export type DesignerBenchVerdict = { saw: boolean; reactionRight: boolean; comment?: string; at: string; by: string };
/** A verdict as POSTed, before the server stamps it (lib/designer/benchRating.ts parseBenchVerdict). */
export type DesignerBenchVerdictInput = { run: string; caseId: string; saw: boolean; reactionRight: boolean; comment?: string };
/** Per model, over rated cases: saw counts defect cases only, falseAlarms the controls (type none) where a defect was "seen", clean the real cases rated clean. */
export type DesignerBenchModelSummary = {
  model: string; total: number; rated: number; defects: number; saw: number; right: number; controls: number; falseAlarms: number; real: number; clean: number;
};
export type DesignerBenchRunListing = { id: string; createdAt: string; cases: number };
/** /api/designer-bench: the published runs, then one run with its verdicts and a signed read URL for each case's images. */
export type DesignerBenchRunView = {
  run: DesignerBenchRun;
  verdicts: Record<string, DesignerBenchVerdict>;
  images: Record<string, Record<string, string>>;
};

/** A jnn turn as the server holds it, in its conversation's workingMemory.designerTurn (lib/designer/billing.server.ts):
 *  the request holding the lease is the turn's only writer of tokens and steps, and its settle. */
export type DesignerTurnState = {
  turnId: string;
  /** The project the turn works on, which the project lock finds it by; absent on a turn opened before. */
  projectId?: string;
  logId: string;
  /** The GenerationLog's replicatePredictionId. */
  logKey: string;
  model: string;
  startedAt: number;
  status: 'running' | 'stopping' | 'settled';
  lease: { requestId: string; until: number } | null;
  lastActivityAt: number;
  steps: number;
  tokens: DesignerTokens;
  /** What the turn's server tools paid other providers, in dollars (a web search); absent until one did. */
  toolUsd?: number;
  budgetCredits: number | null;
  /** The quotes of the generations already handed to the browser. */
  quotedCredits: number;
  /** While watch_history is on, when its last note read and the rows it told; absent or null when off, as every new turn starts. */
  historyWatch?: DesignerHistoryWatch | null;
};

/** The jnn turn's history watch (lib/designer/jnn/history.server.ts): the server time of its last read, and the ids of the rows it told. */
export type DesignerHistoryWatch = { since: number; told: string[] };

export type DesignerTurnUsage = {
  steps: number;
  promptTokens: number;
  cachedPromptTokens: number;
  cacheWriteTokens: number;
  completionTokens: number;
  credits: number;
};

/** The SSE events of /api/ai/designer, one JSON object per `data:` frame. */
export type DesignerTurnEvent =
  | { type: 'textDelta'; payload: string }
  | { type: 'textRetract'; payload: { chars: number } }
  | { type: 'turnComplete'; payload: { items: DesignerItem[]; usage: DesignerTurnUsage } }
  | { type: 'browserCalls'; payload: { turnId: string; calls: DesignerBrowserCall[]; items: DesignerItem[]; budgeted?: true } }
  | { type: 'error'; payload: { message: string } };

export type ListCreativeReference = {
  kind: 'folder' | 'factory-image' | 'persona' | 'object' | 'archetype' | 'output';
  id: string;
  introducedBy: string;
};

export interface ListCreativeContext {
  version: 1;
  projectId: string;
  revision: number;
  references: ListCreativeReference[];
  lastMessage?: { id: string; createdAt: string };
}

export interface ListCreativeContextChange {
  expectedRevision: number;
  rootUserMessageId?: string;
  action: 'add' | 'remove' | 'replace' | 'reset';
  references?: Pick<ListCreativeReference, 'kind' | 'id'>[];
}

export interface ListCreativeResource {
  kind: ListCreativeReference['kind'];
  id: string;
  name: string;
  description?: string;
  mention?: string;
  revision?: number;
  available: boolean;
}

// Creation scope, WHERE a session was born ("agents live in projects").
// Immutable after create; distinct from the mutable activeSubject (what the
// session is currently looking at). 'master' will join the union with the
// orchestrator brain (docs/specs/master-agent-orchestrator.md).
export type SessionCreationModule = 'studio' | 'source' | 'analyst' | 'global' | 'master' | 'doctrine' | 'slides' | 'daimon' | 'pulse';

export interface SessionCreationScope {
  module: SessionCreationModule;
  entityType?: string | null;
  entityId?: string | null;
}

export interface ChatSession {
  id: string;
  name: string;
  collaborationMode?: CollaborationMode;
  ownerId: string | null;
  messageCount: number;
  contextSummary?: ChatSessionContextAnchor[];
  activeSubject?: ActiveEntityFocus | null;
  subjectHistory?: ChatSubjectFocusEntry[];
  lastMessageSnippet?: string | null;
  strategicPlan: string | null;
  workingMemory?: WorkingMemory;
  creationModule?: SessionCreationModule;
  creationEntityType?: string | null;
  creationEntityId?: string | null;
  createdAt: string;
  updatedAt: string;
  isDirty?: boolean;
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
}

interface ChatSessionPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface TaskCompletionData<T = unknown> {
  id: string;
  status: 'SUCCESS' | 'FAILURE';
  data?: T;
  error?: any;
}

export interface PendingTaskPromise {
  resolve: (value: TaskCompletionData) => void;
  reject: (reason?: any) => void;
  chatSessionId?: string | null;
  projectId?: string | null;
}

export interface OrganisationStats {
  totalGenerationsAllTime: number;
  totalMembers: number;
  totalProjects: number;
  privateProjects: number;
  publicProjects: number;
}

export interface OrganisationPlanDTO {
  id: string;
  activationDate: string | null;
  expirationDate: string | null;
  includedCredits: number;
  usedCredits: number;
  reservedCredits?: number;
  teamRemainingCredits?: number;
  priceDisplayText?: string | null;
  name?: string | null;
  invoiceIds?: string[];
}

export interface InvoiceLineInput {
  description: string;
  quantity: number | null;
  amount: number | null;
  /** Credits this line pays for, when it is a credit line rather than a fee. */
  credits: number | null;
}

export interface InvoiceGrantDTO {
  id: string;
  name: string | null;
  includedCredits: number;
  usedCredits: number;
  activationDate: string;
  isActive: boolean;
}

export interface InvoiceDTO {
  id: string;
  organisationId: string;
  number: string;
  reference: string | null;
  purchaseOrder: string | null;
  issueDate: string;
  dueDate: string | null;
  totalAmount: number;
  currency: string;
  lines: InvoiceLineInput[];
  documentUrl: string | null;
  notes: string | null;
  createdAt: string;
  category: string | null;
  grants: InvoiceGrantDTO[];
}

export interface CreditGrantInvoiceRef {
  id: string;
  number: string;
  reference: string | null;
  documentUrl: string | null;
  totalAmount: number;
  currency: string;
  issueDate: string;
}

/** A Finance row: a credit grant, or ('service') an invoice that funds no grant. A service row's
 *  id is its invoice id, its name the invoice category and its price text the invoice lines. */
export interface CreditGrantDTO {
  id: string;
  kind: 'grant' | 'service';
  name: string | null;
  organisationId: string;
  organisationName: string;
  includedCredits: number;
  usedCredits: number;
  reservedCredits: number;
  activationDate: string;
  expirationDate: string | null;
  isActive: boolean;
  priceDisplayText: string | null;
  invoices: CreditGrantInvoiceRef[];
}

export interface CreditGrantFilters {
  organisationId?: string | string[];
  /** 'linked' = has an invoice, 'unlinked' = has none. Absent means both. */
  invoiceState?: 'linked' | 'unlinked' | Array<'linked' | 'unlinked'>;
  kind?: 'grant' | 'service' | Array<'grant' | 'service'>;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreditGrantEmail {
  subject: string;
  html: string;
  text: string;
}

interface OrganisationTeamPlanDTO {
  id: string;
  reservedCreditsFloat: number;
  usedCreditsFloat: number;
  allowOverageToOrg: boolean;
  expirationDate: string | null;
  sourceOrganisationPlanId: string;
}

export interface OrganisationTeamDTO {
  id: string;
  name: string;
  description?: string | null;
  organisationId?: string;
  memberIds: string[];
  createdAt: string;
  teamPlan?: OrganisationTeamPlanDTO | null;
  stats?: OrganisationTeamStatsDTO;
}

export interface OrganisationTeamStatsDTO {
  totalMembers: number;
  totalProjects: number;
  totalGenerations: number;
  totalCreditsSpentFloat: number;
  creditsLast30DaysFloat: number;
}

export interface OrganisationTeamPlanOptionDTO {
  id: string;
  includedCreditsFloat: number;
  usedCreditsFloat: number;
  reservedCreditsFloat: number;
  availableCreditsFloat: number;
  expirationDate: string | null;
  activationDate: string | null;
  isFreebie: boolean;
  accessScope: string | null;
  priceDisplayText: string | null;
}

export interface OrganisationTeamDetailsDTO {
  team: OrganisationTeamDTO & { organisationId: string };
  stats: OrganisationTeamStatsDTO;
}

/** A relative move of a team budget, against the organisation pool. */
export type TeamBudgetDeltaDirection = 'ALLOCATE_FROM_ORG' | 'RELEASE_TO_ORG';

/** Why a team budget move is refused, so the UI can name the cause instead of guessing it. */
export type TeamBudgetDeltaRefusal = 'INVALID_AMOUNT' | 'SOURCE_PLAN_EXHAUSTED' | 'RESERVATION_BELOW_USED';

/** Credit position of one team budget pool, the unit every organisation-team credit rule works on. */
export interface TeamCreditPool {
  reserved: number;
  used: number;
  /** `reserved - used`, floored at zero: what the team can still spend, allocate away or transfer. */
  remaining: number;
  sourcePlanId: string | null;
}

interface OrganisationTeamFilters extends BaseInventoryFilters {
  organisationId?: string;
  sourceOrganisationPlanId?: string;
}

interface MoveOrganisationTeamMembersInput {
  organisationId: string;
  memberIds: string[];
  targetTeamId?: string | null;
}

interface TransferOrganisationTeamCreditsInput {
  fromTeamId: string;
  toTeamId: string;
  creditsFloat: number;
  dryRun?: boolean;
}

interface CreateOrganisationTeamInput {
  organisationId: string;
  name: string;
  description?: string | null;
  memberIds: string[];
  sourceOrganisationPlanId: string;
  reservedCreditsFloat: number;
  allowOverageToOrg: boolean;
  autoTopUpSourcePlan: boolean;
}

interface UpdateOrganisationTeamPlanInput {
  sourceOrganisationPlanId: string;
  reservedCreditsFloat: number;
  allowOverageToOrg: boolean;
  autoTopUpSourcePlan: boolean;
}

interface SetOrganisationMemberSubordinatesInput {
  organisationId: string;
  managerUserId: string;
  subordinateUserIds: string[];
  /** Raw input for the optional weekly usage target: empty means no target, anything else must parse as a non-negative number. */
  targetWeeklyUsageCredits: string;
  /** Raw input for the optional reporting group name: empty means none. */
  targetGroupName: string;
}

/** What a user deletion carries along with the account itself. */
export interface UserDeletionOptions {
  deleteUserProjects: boolean;
  deleteGenerationLogs: boolean;
  deleteClerkUser: boolean;
}

// EDIT: OrganisationDetails removed. Use LeasingOrganisationDTO instead.

export interface CreditsData {
  /** Primary balance for the current viewer (team balance for non-admin team members, org/global balance for admins). */
  remainingCredits: number;
  /** Total organisation balance: active org pool remaining + active team remaining. */
  organisationRemainingCredits?: number;
  /** Active organisation plan pool remaining, excluding credits reserved to teams. */
  organisationPoolRemainingCredits?: number;
  /** Current viewer's active team balance, when the viewer belongs to a team. */
  teamRemainingCredits?: number;
  /** Sum of all active team remaining balances in the organisation. */
  totalTeamRemainingCredits?: number;
  /** Read-side spent projection from active org/team plan counters, not a GenerationLog historical SUM. */
  projectedTotalCreditsSpent?: number;
  allowOverage: boolean;
  overageDisplayText: string | null;
  isRestricted: boolean;
}

export type Message = {
  /** Stable client-side identity for optimistic message replacement before its timeline event exists. */
  clientMessageId?: string;
  content: string;
  /** List Auto mode: the request with the @ mentions the agent works from; `content` stays as typed. */
  technicalPrompt?: string;
  role: 'user' | 'system' | 'assistant';
  timestamp: string;
  timelineEventId?: string;
  targetEntityType?: string;
  targetEntityId?: string;
  audioUrl?: string;
  imageUrls?: string[];
  images?: ImageAttachment[];
  scriptsInstances?: ScriptInstance[];
  agentName?: string;
  thoughts?: string;
  assistantTurnId?: string;
  /** A tools-protocol Designer turn's Responses items (its user message first), sent back on the next turn. */
  designerItems?: DesignerItem[];
  /** Web-browsing telemetry (live "searching the web" status + sources) for this turn. */
};

export type TimelineEventCategory = 'COMMUNICATION' | 'AGENTIC' | 'MUTATION' | 'TELEMETRY';
export type TimelineEventType = 'USER_MESSAGE' | 'ASSISTANT_MESSAGE' | 'SYSTEM_MESSAGE' | string;

export interface TimelineEventDTO {
  id: string;
  organisationId: string;
  userId: string | null;
  chatSessionId: string | null;
  targetEntityType: string | null;
  targetEntityId: string | null;
  category: TimelineEventCategory;
  eventType: string;
  payload: Record<string, unknown>;
  createdAt: string;
  expiresAt: string | null;
}

interface TimelineFeedRef {
  ids: string[];
  hasMoreOlder: boolean;
  olderCursor: string | null;
}

interface TimelineSessionFeedRef extends TimelineFeedRef {
  sessionId: string;
}

interface TimelineSubjectFeedRef extends TimelineFeedRef {
  targetEntityType: string;
  targetEntityId: string;
}

interface TimelineRefsPayload {
  session?: TimelineSessionFeedRef;
  subject?: TimelineSubjectFeedRef;
}

export interface TimelineFeedIndex {
  bySessionId: Record<string, string[]>;
  bySubjectKey: Record<string, string[]>;
}

interface TimelineFeedStateEntry {
  hasMoreOlder: boolean;
  olderCursor: string | null;
  isLoading: boolean;
  updatedAt: number;
}

export type TimelineFeedStateRegistry = Record<string, TimelineFeedStateEntry>;

interface CreateTimelineEventPayload {
  chatSessionId?: string;
  targetEntityType?: string;
  targetEntityId?: string;
  ttlMsHint?: number;
  category: TimelineEventCategory;
  eventType: TimelineEventType;
  payload: Record<string, unknown>;
}

type PlanningOutcomeStatus = 'PENDING' | 'SUCCESS' | 'FAILURE' | 'PARTIAL';

export interface PlanningTracePayload {
  planText: string;
  agentPlan?: Record<string, unknown> | null;
  userPrompt?: string | null;
  chatSessionId?: string | null;
  sessionType?: string | null;
  contextSnapshot?: Record<string, unknown> | null;
  referencedGenerationTaskIds?: string[];
  outcomeStatus?: PlanningOutcomeStatus;
  source?: string;
}

export interface AssistantTurnTracePayload {
  turnId?: string | null;
  chatSessionId?: string | null;
  sessionType?: string | null;
  workspaceId?: string | null;
  agentMode?: AgentMode | null;
  userPrompt?: string | null;
  assistantResponse: string;
  responseThoughts?: string | null;
  assistantScript?: ScriptInstance | null;
  metadata?: Record<string, unknown> | null;
}

export interface ChatStateItem {
  text: string;
  startTime: number;
  /**
   * This line describes something FINISHED, so it must not animate.
   *
   * The strip animates its last entry to say "work is happening". Once a run's final count began
   * persisting instead of being wiped, that finished line inherited the animation and pulsed for
   * good: "Scored 10 of 10 brands" read as still scoring, on a pass that had completed. Reported
   * on 2026-08-16 as "why is he still scoring here".
   */
  settled?: boolean;
  /**
   * Live, but not the AGENT working: a sourcing run's progress line. It animates like any live
   * line, but agentIsWorking ignores it, so the composer stays sendable during a run.
   */
  ambient?: boolean;
  /** Drawn as a carousel of these brands' logos instead of the text (a run before its first brand lands). */
  brands?: ChatStatusBrand[];
}

export type ChatStatusBrand = { name: string; logoUrl?: string | null; active?: boolean };

export type CollaborationMode = 'ASSISTED' | 'COLLABORATIVE' | 'AUTONOMOUS';

/**
 * The three positions of the Source composer's mode picker. They are a reading of
 * `CollaborationMode`, not a parallel enum: the mapping and the rule each one changes live in
 * `lib/leasing/sourceAgentMode.ts`. Declared here so `UserPreferences` can carry one without
 * types/ having to import from lib/.
 */
export type SourceAgentMode = 'MANUAL' | 'ACCEPT_EDITS' | 'AUTO';

export type UserAccessScope = 'ALL_MODELS' | 'CHEAP_MODELS_ONLY';

/**
 * Controls whether the remaining-credits pill is shown at the top of the app for this user,
 * and which balance it reflects. Set per-user by an admin.
 * - 'HIDDEN' (or undefined): no pill.
 * - 'ORG': show the whole organisation's remaining credits.
 * - 'PERSONAL': show the balance available to this specific user (team balance for team members,
 *   org/global balance otherwise).
 */
export type CreditVisibilityMode = 'HIDDEN' | 'ORG' | 'PERSONAL';

export const BASE_BOOLEAN_PERMISSIONS = {
  canSeeDevOnlyDisplays: false,
  canAccessAssetManagement: false,
  canAccessFactory: false,
  // Studio Vector, the Illustrator-style editor, granted per account.
  canAccessVector: false,
  canAccessSubjectTimelineView: false,
  // Ouvre les outils de plan d'etage (Foundation, 3D, Mapper) sur la page Floor. Sans elle,
  // un etage reste la vue simplifiee « unites + occupants » decidee le 18/07/2026, qui est
  // le defaut pour tout le monde. Voir components/entities/pages/FloorPage.tsx.
  canAccessFloorPlanTools: false,
};

export type UserPermissions = Partial<typeof BASE_BOOLEAN_PERMISSIONS> & {
  // Existing pilot accounts carry this leasing grant in their stored permissions.
  canAccessLeasing?: boolean;
  accessScope?: UserAccessScope;
  creditVisibility?: CreditVisibilityMode;
  // Per-tool doctrine-edit grants. An org admin can delegate editing of a single
  // tool's doctrine (e.g. Source) to a member without making them a full org
  // admin. Empty/undefined = no delegated tool-doctrine rights. See DoctrineRights.
  doctrineToolAdmin?: DoctrineAgentKey[];
};

/**
 * Effective doctrine-edit rights for the current user, resolved server-side from
 * isFaterAdmin / isOrganisationAdmin / permissions.doctrineToolAdmin. Drives which
 * sections the Doctrine tab renders as editable. Every user always has their own
 * instruction + memory layers (not represented here, those are implicit).
 */
export interface DoctrineRights {
  // Org admins (and Fater admins) may edit the org-global doctrine, every tool
  // doctrine, and team doctrine within their organisation.
  org: boolean;
  // Tools this user may edit doctrine for. Contains every tool when `org` is true.
  tools: DoctrineAgentKey[];
}

export interface UserContext {
  dbUserId: string | null;
  organisationId: string | null;
  /** Server-owned membership, unlike the switchable admin organisation view. */
  authenticatedOrganisationId?: string | null;
  /** The effective user's team: the billing-suspension carve-out is keyed on it. */
  teamId?: string | null;
  /**
   * The suspension verdict as the SERVER resolved it, refreshed by the presence heartbeat.
   * Client gates must prefer it over evaluating their own tables: a tab carries the tables of
   * the bundle it loaded, which can predate the deploy that restored the account.
   */
  accessSuspension?: { suspended: boolean; withheldModelFamilies: readonly string[] } | null;
  isImpersonating: boolean;
  impersonatedUser: { name: string; email: string } | null; // The user being impersonated
  trueUser: { name: string; email: string } | null; // The admin doing the impersonating
  isFaterAdmin: boolean;
  isFaterOrgMember: boolean;
  /** May swipe brands in the phone's Review (lib/access/brandReviewAccess.ts). */
  canReviewBrands?: boolean;
  isOrganisationAdmin: boolean;
  permissions?: UserPermissions;
  doctrineRights?: DoctrineRights;
  /** Successful generations per Studio model id among the user's recent ones, read once per load. */
  studioModelUsage?: Record<string, number>;
}

// EDIT: MemberStatus is a UI derived state, kept for OrganisationMenu display logic
interface MemberStatus {
  type: 'live' | 'recent' | 'offline';
  text: string;
  color: string;
}

// EDIT: Deprecating MemberData in favor of LeasingUserDTO for data transport.
// Keeping this alias for now to minimize refactor noise in UI components,
// but pointing it to the DTO structure + UI specific extensions.
export type MemberData = LeasingUserDTO & {
  status?: MemberStatus | null; // UI only
};

export interface PendingInvite {
  id: string;
  emailAddress: string;
  status: string;
  createdAt: number;
}

interface DealerChatSessionContext {
  id: string;
  name: string;
  messageCount: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastUserMessageAt?: string | null;
  lastAssistantMessageAt?: string | null;
  activeSubject?: ActiveEntityFocus | null;
  subjectHistory?: ChatSubjectFocusEntry[];
  workingMemory?: WorkingMemory | null;
  focusRuntimeByKey?: SessionFocusRuntimeMap;
}

/**
 * Active Studio surface: "studio" is the deep technical "Canvas" design editor,
 * "marketing" is the flat batch "List" of outputs.
 */
export type StudioViewMode = 'studio' | 'marketing';
export type StudioProjectKind = 'canvas' | 'list' | 'vector';
export type StudioSection = StudioProjectKind | 'factory';

export type SourceActionName = 'navigation' | 'tab' | 'search' | 'filter' | 'chat' | 'shortlist' | 'contacts' | 'export';
export type SourceActionPhase = 'observed' | 'requested' | 'client_returned' | 'client_failed' | 'client_cancelled' | 'download_started';
export type SourceActionMetadata = {
  entityType?: string; entityId?: string; chatSessionId?: string; campaignId?: string; tab?: string;
  count?: number; acceptedCount?: number; hasQuery?: boolean; filterCount?: number;
  status?: 'INTERESTING' | 'DISMISSED'; queued?: boolean; format?: 'csv';
};
export type SourceActionInput = {
  action: SourceActionName;
  origin: 'human' | 'agent' | 'unknown' | 'observed';
  data?: SourceActionMetadata;
};
export type SourceUsageAction = SourceActionInput & {
  operationId: string; phase: SourceActionPhase; data: SourceActionMetadata;
};
export type SourceActionCompletion = (phase: Exclude<SourceActionPhase, 'observed' | 'requested'>, data?: Pick<SourceActionMetadata, 'count' | 'acceptedCount'>) => void;

export type SourceUsageInterval = {
  id: string;
  visitId: string;
  from: number;
  to: number;
  visible: [number, number][];
  active: [number, number][];
  gapMs: number;
  droppedRecords: number;
  action?: SourceUsageAction;
};

export type SourceUsageBatch = {
  sourceUsage: { version: 1; actorId: string; organisationId: string; intervals: SourceUsageInterval[] };
};

export type SourceCampaignRun = {
  version: 1; runId: string; organisationId: string; initiatingUserId: string;
  plannedAssets: number; plannedItems: number;
  kind: 'new' | 'extension' | 'reanalysis';
  environment: 'production' | 'preprod' | 'preview'; preparedAt: string;
};
export type SourceCampaignOutcome = 'completed' | 'partial' | 'stopped' | 'interrupted' | 'failed' | 'expired' | 'reconciled' | 'unknown';
export type SourceCampaignReason = 'completed' | 'items_failed' | 'work_unfinished' | 'user_stop' | 'worker_interrupted' | 'execution_failed' | 'no_targets' | 'invalid_targets' | 'queue_failed' | 'queue_expired' | 'stalled' | 'missing_outcome';
export type SourceCampaignCounts = { plannedAssets: number; observedAssets: number; total: number; processed: number; failed: number; unfinished: number };
export type SourceCampaignEventDetail =
  | { phase: 'accepted' | 'queue_accepted' | 'queue_failed' | 'claimed' }
  | { phase: 'stop_requested'; eventActorId: string }
  | { phase: 'ledger_link'; ledgerId: string; propertyId?: string }
  | { phase: 'terminal'; source: 'worker' | 'preflight' | 'queue_expiry' | 'reconciliation' | 'dispatch'; outcome: SourceCampaignOutcome; reason: SourceCampaignReason; executionEnded: boolean; counts?: SourceCampaignCounts };
export type SourceCampaignEvent = SourceCampaignEventDetail & { version: 1; origin: 'server'; context: SourceCampaignRun; campaignId: string; occurredAt: string };
export type SourcePassMeasurement = {
  outcome: 'completed' | 'partial' | 'stopped' | 'interrupted' | 'failed';
  reason: 'completed' | 'items_failed' | 'work_unfinished' | 'user_stop' | 'worker_interrupted' | 'execution_failed';
  executionEnded: boolean; total: number; processed: number; failed: number; unfinished: number;
};

// Raw kernel state definition used to auto-generate setter signatures.
export type PulseLayer = 'mix' | 'occupancy' | 'traffic' | 'reviews';
export interface PulseVenueLevel { id: string; name: string; ordinal: number }
/** Shortest walking routes from the venue's anchors that pass a shop's doors, and that count's percentile in the centre. */
export interface PulseExposure { routes: number; percentile: number }
/** A georeferenced indoor-map feature: a shop, an amenity or a building footprint; each ring is [lng, lat] pairs. */
export interface PulseVenueFeature {
  id: string; kind: 'unit' | 'amenity' | 'building'; level: string | null; name: string | null; areaSqm: number; rings: Array<Array<[number, number]>>; exposure?: PulseExposure | null;
  /** The Brand record behind a shop, linked by the server on every read: everything shown about the tenant is read live from it.
   * Stored on the venue only when its importer matched the name exactly (Westfield), which then wins over loose matching. */
  brandId?: string | null;
  /** Status of the centre's unit leased to that brand, when the brand is one of the centre's tenants. */
  unitStatus?: UnitStatus | null;
  /** The landlord's own unit code (e.g. B97), from the client's merchandising plan. */
  code?: string | null;
  /** A unit a scenario created by cutting or joining others: the units it replaces. */
  parentIds?: string[];
}
export interface PulseVenueAnchor { id: string; kind: 'entrance' | 'station' | 'bus' | 'parking'; level: string | null }
export interface PropertyVenue {
  source: string;
  updatedAt: string | null;
  /** Where occupancy comes from: the centre's own published shop-to-tenant list, and when it was read. */
  directory: { source: string; importedAt: string } | null;
  levels: PulseVenueLevel[];
  features: PulseVenueFeature[];
  anchors: PulseVenueAnchor[];
  /** Anchor kinds the venue does not mark, so exposure could not start from them. */
  missingAnchors: Array<PulseVenueAnchor['kind']>;
  /** Corridor segments with the exposure walks that use them, pointing the way most take it. */
  flows?: PulseFlow[];
}
export interface PulseFlow { level: string; path: [[number, number], [number, number]]; walks: number }
/** One staged change to a shop in a merchandising scenario. */
export interface PulseMove {
  shopId: string;
  type: 'relet' | 'vacate';
  brandId: string | null;
  brandName: string | null;
  brandCategory: BrandCategory | null;
  /** Target rent in €/m²/year; null until set. */
  rentPerSqm: number | null;
  /** YYYY-MM-DD. */
  start: string;
  rentFreeMonths: number;
  works: number;
}
/** A user's merchandising scenario for one asset, with the assumptions that price it. */
export interface PulseScenario {
  revision: string;
  savedAt: string;
  referenceRent: number | null;
  yieldPct: number | null;
  levelId: string | null;
  layer: PulseLayer;
  moves: PulseMove[];
  /** The units this scenario cuts or joins before its moves apply. */
  layout?: PulseLayoutChange[];
}
/** A named merchandising plan: one scenario on one centre. A centre can carry several. */
/** A scenario reshaping the centre: one unit cut into parts holding these shares of its area, or neighbours joined. Works in €. */
export type PulseLayoutChange =
  | { kind: 'split'; shopId: string; shares: number[]; works: number }
  | { kind: 'merge'; shopIds: string[]; works: number };
/**
 * One scenario of a plan: a named set of moves (on a layout it may reshape) and the reasoning behind it. The scenario on screen keeps its moves and layout in the
 * plan's own `moves` and `layout` (every edit acts there); the others keep theirs here, and switching swaps them.
 */
/** campaignId: the Source run this scenario places brands from, its hypothesis's basis (2026-10-14); the scenario on screen keeps it in the plan's own `campaignId`. */
export interface PulseVariant { id: string; name: string; thesis: string; moves: PulseMove[]; layout?: PulseLayoutChange[]; campaignId?: string | null }
export interface PulsePlan extends PulseScenario {
  /** Who made the plan; set by /api/plan/asset, which stores plans on their asset. */
  authorId?: string;
  authorName?: string;
  /** Pricing assumptions the Merch agent proposed and the user has not answered yet: kept with the plan so a reload never loses them. */
  pendingAssumptions?: { referenceRent: number | null; yieldPct: number | null; basis: string; at: string } | null;
  id: string;
  name: string;
  propertyId: string;
  createdAt: string;
  /** The Source run (campaign) whose shortlist this plan places brands from. Absent on plans made before plans were tied to one. */
  campaignId?: string | null;
  /** Absent on plans saved before scenarios existed: they read as one scenario. */
  scenarios?: PulseVariant[];
  activeScenarioId?: string;
}
export type PulseTool = 'select' | 'hand' | 'relet' | 'vacate';
/** A building from IGN's BD TOPO register: its footprint, measured height (m) and ground altitude (m above sea level). */
export interface PulseBuilding { id: string; rings: Array<Array<[number, number]>>; height: number | null; groundAltitude: number | null; storeys: number | null; usage: string | null }
/** Pulse's working state: the dashboard when planId is null, else the open plan's editor. */
export interface PulseRuntime {
  ownerKey: string | null;
  planId: string | null;
  propertyId: string | null;
  selectedUnitId: string | null;
  /** The right bar's section, as Studio's: the list of shops, or the selected shop. */
  section: 'shops' | 'shop' | 'scenarios';
  tool: PulseTool;
  /** The Merch chat beside the plan: closed when a plan opens, opened from the bubble at the bottom left. */
  chatOpen: boolean;
  /** The Merch plans of the assets the user sees (/api/plan/asset), plan id to plan; null until loaded. */
  plans: Record<string, PulsePlan> | null;
  /** The asset's scored Source shortlist, keyed by account and asset so a switch never shows another's list. */
  shortlist: { key: string; status: 'loading' | 'ready' | 'error'; ids: string[] } | null;
  /** The working copy of the open plan; null until it is loaded. */
  draft: PulsePlan | null;
  /** The map in 3D, and the IGN buildings that give it its heights, fetched for the open centre. */
  view3d: boolean;
  /** Ephemeral traffic controls; never a saved plan or financial assumption. */
  trafficView?: PulseTrafficView;
  /** Runtime-only browsing of captured places, scoped to the account and property. */
  mappingView?: { key: string; open: boolean; tab: 'places' | 'units'; query: string; selected: string | null; allPoints: boolean };
  /** The shop the agent last worked on, and every shop it touched this run: the map follows its cursor and pulses them. */
  agentEdit: { shopId: string; at: number } | null;
  agentTouched: string[];
  /** A camera request from the agent: frame these shops, or the whole level when empty. */
  zoom: { shopIds: string[]; at: number } | null;
  buildings: { propertyId: string; status: 'loading' | 'ready' | 'error'; list: PulseBuilding[] } | null;
  /** Revision of the saved scenario the draft started from; a save from a stale base is refused. */
  baseRevision: string | null;
  editVersion: number;
  savedVersion: number;
  saving: boolean;
  error: string | null;
  notice: string | null;
}

interface KernelData {
  pulse: PulseRuntime;
  portalStandaloneView: PortalView | null;
  globalScrollTop: number;
  diffusionModel: string;
  spectacleMode: boolean;
  cloudAssetRegistry: CloudAsset[];
  pendingAssetUrls: Set<string>;
  inflightLayerIds: Set<string>;
  isSubmittingAITask: boolean;
  isQueuingChatMessage: boolean;
  organisationCredits: CreditsData | null;
  isOrganisationCreditsLoading: boolean;
  organisationCreditsError: string | null;
  isOrganisationMember: boolean;
  activeModal: ModalName;
  modalPayload: any;
  menuData: QuickMenuData | null;
  chatPrompt: string;
  chatOutputMentionScope?: { projectId: string; userId: string };
  chatFactoryFolderMentions?: { projectId: string; userId: string; folders: { id: string; name: string; displayName?: string; alias: string }[] };
  selectedAssetId: string | null;
  activeChatSessionId: string | null;
  imagesInChatInput: LockableFile[];
  audioEnabled: boolean;
  /** Knowledge-source toggles in the chat composer: globe = live web search (WEB_SEARCH), database = internal DB knowledge (QUERY). Default both on. */
  webSearchEnabled: boolean;
  internalDbEnabled: boolean;
  /** Runtime errors observed this session, mounted to the agent every turn. */
  agentErrorLedger: AgentObservedError[];
  agentModeOverride: AgentMode | undefined;
  modelParams: { [model: string]: DiffusionParams };
  saveTelemetry: SaveTelemetry | null;
  userPreferences: UserPreferences;
  userPreferencesOwnerId: string | null;
  actionHistory: UserAction[];
  assistantTurnDebugLog: AssistantTurnDebugInfo[];
  isDrawing: boolean;
  isLoading: boolean;
  annotationBrushSize: number;
  annotationColor: string;
  annotationBrushHardness: number;
  annotationMode: AnnotationMode;
  // Inpaint mask painted by the Mask Pen tool, keyed by workspace id (separate from annotationMask).
  // Stored as resolution-independent vector shapes; rasterized to B/W only at generation time.
  inpaintMaskShapes: Record<string, MaskShape[]>;
  // When true, the inpaint mask overlay is hidden on the canvas (toggle in the right-click menu).
  masksHidden: boolean;
  maskPenMode: MaskPenMode;
  activeTool: InpaintTool;
  activeSoundTool: SoundTimelineTool;
  drawingToolKind: DrawingToolKind;
  drawingStrokeColor: string;
  drawingStrokeOpacity: number;
  drawingFillColor: string;
  drawingFillOpacity: number;
  drawingFillEnabled: boolean;
  drawingStrokeWidth: number;
  drawingCornerRadius: number;
  drawingFontSize: number;
  drawingFontColor: string;
  drawingFontOpacity: number;
  drawingTextBold: boolean;
  drawingTextItalic: boolean;
  drawingTextUnderline: boolean;
  drawingFontFamily: string;
  drawingFontWeight: number;
  drawingLineHeight: number;
  drawingLetterSpacingPercent: number;
  drawingTextAlign: DrawingTextAlign;
  layerToolColor: RgbaColor;
  layerToolWidth: number;
  isLayerSliderVisible: boolean;
  themeOptions: ThemeOptions;
  isGuestMode: boolean;
  userContext: UserContext;
  undoStack: UndoableAction[];
  redoStack: UndoableAction[];
  showResolutionHeatmap: boolean;
  nativeLayerCutSession: NativeLayerCutSession | null;
  workspaceColumnPick: WorkspaceColumnPick | null;
  recastZonePick: RecastZonePick | null;
  isAltKeyPressed: boolean;
  isShiftKeyPressed: boolean;
  scissorsHoverLayerId: string | null;
  /** PortfolioFit whose detail is open in the Source editor; drives the right-bar brand context. */
  sourceFocusedFitId: string | null;
  /** Show the rows the run dismissed. Off by default; see kernelStores for why. */
  sourceShowDismissed: boolean;
  /**
   * BrandLocation whose card is open on the Stores map. Published so the AGENT knows which store
   * the user means by "this one", saying "this store doesn't exist" while pointing at a pin was
   * unanswerable before, because the selection lived only in the map panel's local state.
   */
  sourceFocusedStoreId: string | null;
  /** In-flight count of per-brand enrichments; >0 drives the Egyptian-blue progress bar on the Source canvas. */
  sourceEnrichInFlight: number;
  /** Brand id -> the card fields being researched right now (lib/leasing/enrichingDims). */
  sourceEnrichingDims: Record<string, string[]>;
  /**
   * Which tab the shortlist brand detail shows. In the kernel, not React state, because the AGENT
   * has to be able to open one: "get me the contacts for X" must land the user ON the contacts tab
   * rather than announcing a result behind a tab they are not looking at.
   */
  sourceBrandDetailTab: 'summary' | 'verdict' | 'scoring' | 'prices' | 'expansion' | 'stores' | 'gallery' | 'news' | 'revenue' | 'contacts' | null;
  /** Brand whose contact discovery is running right now, so the contacts tab can show it working. */
  sourceContactDiscoveryBrandId: string | null;
  /** In-flight count of shortlist re-scores (rubric re-apply / doctrine re-judge); >0 sweeps the shortlist rows. */
  sourceRescoreInFlight: number;
  /** Raised by the composer's Stop button while a bulk enrichment runs; the bulk loop drains between brands. */
  sourceEnrichStopRequested: boolean;
  /** The sourcing mandate each chat session drives under, keyed by session id (lib/leasing/sourcingMandate.ts). */
  sourceMandates: Record<string, SourcingMandate>;
  sourceBudgetProposals: Record<string, { proposal?: SourceBudgetProposal; busy: boolean; error?: string }>;
  /** Brand ids left un-enriched after a paused bulk enrichment, the resumable remainder. Null when none. */
  sourceEnrichPausedBrandIds: string[] | null;
  /** Fit rows selected via checkboxes in the Source campaign table; drives the right-bar bulk-enrich panel. */
  sourceSelectedFitIds: string[];
  /** Focus-aware title the Source view publishes for the top-bar header (dashboard / campaign / leads / unit fits). Localized: never compare it to a literal. */
  sourceHeaderTitle: string | null;
  /** True while the Source view is showing its dashboard. Published beside the title because the title is translated, so `sourceHeaderTitle === 'Dashboard'` stopped being a reliable test. */
  sourceHeaderIsDashboard: boolean;
  sourceDashboardTab: 'shortlists' | 'assets';
  /** The Daimon conversation on screen; null starts a new one (the nav's Daimon entry). */
  daimonSessionId: string | null;
  sourceAssets: { ids: string[]; status: 'idle' | 'loading' | 'ready' | 'error'; error?: string };
  sourceOpeningRentRollId: string | null;
  /** True while the user is creating a new campaign from the Source dashboard (right bar shows the setup form instead of the dashboard context). */
  sourceCreatingCampaign: boolean;
  /** Bumped after a campaign is launched so the Source dashboard re-queries and shows the newly created campaign. */
  sourceCampaignsRefreshToken: number;
  /** A new campaign draft is being prepared; its chat session is still being created. */
  sourcePreparingCampaign: boolean;
  sourceOpeningCampaignId: string | null;
  studioOpeningProjectId: string | null;
  /**
   * The launch parameters currently in the Source setup form, mirrored out of the panel so a
   * command can READ what the user is looking at. The panel owns them; nothing else writes here.
   */
  sourceDraftParams: SourcingPresetParams | null;
  /**
   * A saved set a command wants APPLIED to the form. The panel consumes it and clears it, which is
   * what keeps this a one-shot instruction rather than a second source of truth for the form.
   */
  sourcePendingParams: SourcingPresetParams | null;
  /** What the user typed for the turn now running; '' when the agent started the turn itself. */
  lastUserTurnPrompt: string;
  /** The words that started the current chain, kept across its continuations (contacts guard only). */
  lastUserChainPrompt: string;
  /**
   * Bumped whenever a shortlist's doctrine INSTRUCTIONS are written (from the chat command or from
   * the doctrine workspace). A DoctrineDocument is not an inventory entity, so it has no socket
   * stream to subscribe to; this token is how the Source right bar reflects an agent edit live,
   * the same way `sourceCampaignsRefreshToken` does for the dashboard.
   */
  sourceDoctrineRefreshToken: number;
  /** Campaign whose fits are open in the Source center (null = dashboard). Lifted to the store so the right-bar form can open the just-launched campaign. */
  sourceSelectedCampaignId: string | null;
  /**
   * Dashboard list filter (right bar): show only the runs that covered THIS asset. There is one
   * sourcing operation, so the dashboard is never split by kind; it is filtered by real things.
   * null = every asset, which is also the whole-portfolio runs.
   */
  inventory: KernelInventoryState;
  timelineFeedIndex: TimelineFeedIndex;
  timelineFeedState: TimelineFeedStateRegistry;
  inventoryCounts: InventoryCountState;
  inventoryStats: InventoryStatsState;
  language: 'en' | 'fr';
  pendingProjectSuccessMissionIds: Set<string>;
  propertySearchQuery: string;
  dataConfidenceFilter: 'ALL' | 'TRUSTED' | 'FATER';
  socketStatus: SocketStatus;
  socketReconnectAttempts: number;
  socketLastEventTimestamp: string | null;
  activeRoleIds: LeasingAgentRoleId[];
  debugActionInFlight: string | null;
  debugActionError: string | null;
  entityKernelState: EntityKernelStateRegistry;
  marketSignalStats: MarketSignalStats | null;
  inventoryInitStatus: 'idle' | 'initializing' | 'initialized';
  leasingActions: LeasingActionItem[];
  isLeasingActionsLoading: boolean;
  studioViewMode: StudioViewMode;
  studioSection: StudioSection;
  factoryTour: { owner: string; context: string; step: number } | null;
  modelistBudget: ModelistBudgetChoice;
  designerBudget: DesignerBudgetChoice;
  fater3d: Fater3DState;
  video: VideoEditorState;
  share: ShareState;
}

type CapitalizeKey<S extends string> = S extends `${infer F}${infer R}` ? `${Uppercase<F>}${R}` : S;
type SetterKey<K extends string> = `set${CapitalizeKey<K & string>}`;

export type KernelSetters = {
  [K in keyof KernelData as SetterKey<K>]: Setter<KernelData[K]>;
};

type KernelGetters = {
  [K in keyof KernelData as `get${Capitalize<string & K>}`]: () => KernelData[K];
};

export type KernelState = KernelData & KernelSetters & {
  setInventorySlice: <K extends InventoryEntityType>(
    entity: K,
    updater: (prev: EntitySlice<K>) => EntitySlice<K>
  ) => void;
};

export type TelemetryStatus = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'CANCELLED';

interface TelemetryStep {
  name: string;
  status: TelemetryStatus;
  startTime: number | null;
  endTime: number | null;
  durationMs: number | null;
  error: string | null;
  metadata: Record<string, any>;
}

export interface SaveTelemetry {
  saveAttemptId: string;
  status: TelemetryStatus;
  startTime: number;
  endTime: number | null;
  steps: TelemetryStep[];
}

export interface OpenImageCropForLockableFilesOptions {
  instruction?: string | null;
  outputMode?: 'generateFile' | 'pathOnly';
  autoConfirmOnPreview?: boolean;
  alternateImageFile?: File | null;
}

export interface KernelCollapsiblePanelRef {
  toggleCollapse: (forceOpenToWidth?: number, userGesture?: boolean) => void;
  isCurrentlyCollapsed: () => boolean;
  getCurrentContentWidth: () => number;
}

export interface LazyWorkerRef {
  current: Worker | null;
  created: boolean;
}

/**
 * What an alignment answers. A refusal carries its reasons, worded for the user: the estimator can tell that no
 * shift-and-zoom explains the two images, and that has to survive the trip back from the worker.
 */
export interface LayerAlignmentResult {
  /** 2x3 row-major affine mapping an edited-layer pixel onto the underlying render, or null when refused. */
  matrix: number[] | null;
  /** Empty when `matrix` is set. */
  refusedFor: string[];
  /** Agreement between the regions that were matched, the evidence the refusal is built on. */
  consensus?: {
    inlierTiles: number;
    totalTiles: number;
    sides: string[];
    /** Null when nothing was fitted. */
    residualRms: number | null;
    /** False when the usable sides gave no lever arm and the scale was held at 1. */
    scaleEstimated: boolean;
    /**
     * What the tiles fitted, accepted or not. `matrix` is the same thing once the gate has agreed; a caller who
     * watches the result and can undo it applies this one instead, through matrixToApply. The benchmark reads it
     * to measure the estimator separately from the gate that judges it.
     */
    fittedMatrix: number[];
  };
}

/** Posted once, unsolicited, when the worker's OpenCV instance is usable. */
export interface OpenCvReadyMessage {
  type: 'ready';
  version: string;
  /** Since the worker's time origin, i.e. its creation: fetch, parse and WASM compile included. */
  initMs: number;
}

export interface KernelHandle extends
  useAudioManagerReturnType, 
  SocketManagerReturnType,
  useEntityKernelStateReturnType,
  useAgentsReturnType, 
  useWorkspacesReturnType,
  useLayersReturnType,
  useVersionsReturnType,
  useModelsReturnType,
  useGenerationTasksReturnType,
  UseWorkspaceTimelineReturnType,
  UseStudioDevelopReturnType,
  useZoomReturnType,
  useViewportsReturnType,
  useCanvasReturnType,
  useProjectsReturnType,
  useChatReturnType,
  useCommandsReturnType,
  useInventoryReturnType,
  useScoringCampaignsReturnType,
  useLeasingWorkflowsReturnType,
  useTeamsReturnType,
  Omit<KernelSetters, 'setActiveChatSessionId'>,
  KernelGetters {

  getState: () => KernelState;
  setState: (
    partial: KernelState | Partial<KernelState> | ((state: KernelState) => KernelState | Partial<KernelState>),
    replace?: boolean
  ) => void;
  subscribe: (listener: (state: KernelState, prevState: KernelState) => void) => () => void;

  subscribeToSocketEvent: (eventName: string, handler: SocketEventHandler) => () => void;
  dispatchSocketEvent: (eventName: string, payload: unknown) => void;
  handleSocketReconnect: () => Promise<void>;
  executeAction: (action: Omit<UndoableAction, 'id'>) => void;
  undoLastAction: () => void;
  redoLastAction: () => void;
  addFilesAsLayers: (
    files: FileList,
    dropTargetArea: string,
    worldPos?: { x: number, y: number },
    options?: {
      timelineDrop?: {
        workspaceId: string;
        startMs: number;
        insertBeforeLayerId?: string | null;
      };
      projectId?: string;
    }
  ) => Promise<void>;
  promptAndAddLayerFromFile: () => void;
  triggerGenerate: (source?: 'prompt_enter_key' | 'generate_button') => void;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  consecutiveRevertCounterRef: RefObject<number>;
  pendingLayerPromisesRef: RefObject<Map<string, { resolve: (layer: Layer) => void; reject: (reason?: any) => void; }>>;
  canvasContainerRef: RefObject<HTMLDivElement | null>;
  videoRefs: RefObject<Record<string, HTMLVideoElement | null>>;
  blendWorkerRef: RefObject<Worker | null>;
  generationPostprocessWorkerRef: RefObject<Worker | null>;
  buriedLayersWorkerRef: RefObject<Worker | null>;
  opencvWorkerRef: RefObject<LazyWorkerRef>;
  createOpenCvWorker: () => Worker;

  loadUserPreferences: () => Promise<void>;
  saveUserPreferences: (prefsToSave: UserPreferences) => Promise<void>;
  updateAndSaveUserPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => Promise<void>;
  saveDailyCreditUsageAlert: (
    alert: DailyCreditUsageNotificationPreference,
    fallbackTimezone: string
  ) => Promise<DailyCreditUsageAlertSaveResult>;
  handleNotEnoughCreditsError: () => void;
  logUserAction: (actionName: string, payload?: Record<string, any>) => void;
  beginSourceAction: (input: SourceActionInput) => SourceActionCompletion | undefined;
  openModal: (modalName: Exclude<ModalName, null>, payload?: any) => void;
  openImageCropForLockableFiles: (
    lockableFiles: LockableFile[],
    imageSetter: (files: LockableFile[] | ((prevFiles: LockableFile[]) => LockableFile[])) => void,
    index: number,
    sourceRect: DOMRect | null,
    options?: OpenImageCropForLockableFilesOptions
  ) => void;
  closeModal: () => void;
  handleError: (message: string, options?: Parameters<typeof toast.error>[1] & { logToConsole?: boolean }) => void;
  refreshOrganisationDetails: (orgId: string | null, mode: 'essential' | 'full' | 'members') => Promise<void>;
  fetchOrganisationPendingInvites: (organisationId: string) => Promise<PendingInvite[]>;
  saveOrganisationSettings: (
    organisationId: string,
    settings: { defaultCollaborationMode: CollaborationMode; favoriteLanguage: 'ENGLISH' | 'FRENCH' | null },
    refreshTargetOrgId: string | null
  ) => Promise<void>;
  inviteOrganisationMember: (organisationId: string, email: string) => Promise<{ message?: string }>;
  createOrganisationMemberDirectly: (
    organisationId: string,
    member: { email: string; firstName: string; lastName: string },
    refreshTargetOrgId: string | null
  ) => Promise<{ message?: string; password: string }>;
  resendOrganisationInvite: (organisationId: string, email: string) => Promise<{ message?: string }>;
  deleteOrganisationInvite: (inviteId: string) => Promise<{ message?: string }>;
  uploadOrganisationLogo: (organisationId: string, file: File) => Promise<void>;
  applyHomeOrganisationScope: (
    orgId: string,
    options?: { persistAdminPreference?: boolean }
  ) => Promise<void>;
  sendUserTeamReport: (
    targetUserId: string,
    options?: { overrideRecipientEmail?: string | null }
  ) => Promise<{
    success: boolean;
    managerId: string;
    recipientEmail: string;
    sent: number;
    failed: number;
    emailId: string | null;
  }>;
  toggleReferenceImageLock: (id: string) => void;
  registerCloudAssets: (urls: (string | null | undefined)[], source: CloudAssetSource) => void;
  clearAssetLoadingFailsafe: () => void;
  startAssetLoadingFailsafe: (timeoutMs?: number) => void;
  handleAssetLoadingTimeout: () => void;
  reportAssetLoaded: (url: string) => void;
  initializeVisualCriticalPath: (projectId: string, layers: Layer[]) => void;

  getLeasingBrandMap: () => Record<string, LeasingBrandDTO>;
  getLeasingBrandOrder: () => string[];
  getLeasingDealMap: () => Record<string, LeasingDealDTO>;
  getSocketLastEventTimestamp: () => string | null;
  getCurrentOrganisation: () => LeasingOrganisationDTO | null;
  setInventorySlice: <K extends InventoryEntityType>(
    entity: K,
    updater: (prev: EntitySlice<K>) => EntitySlice<K>
  ) => void;
  getWorkspace: (workspaceId: string | null | undefined) => Workspace | null;
  setLeftPanelRef: (panelRef: KernelCollapsiblePanelRef | null) => void;
  setRightPanelRef: (panelRef: KernelCollapsiblePanelRef | null) => void;
  toggleLeftPanel: (userGesture?: boolean) => void;
  openLeftPanel: () => void;
  toggleRightPanel: (userGesture?: boolean) => void;
  openRightPanel: (forceOpenToWidth?: number) => void;
  getLocalNumOutputs: (projectId: string) => number;
  setLocalNumOutputs: (projectId: string, updater: SetStateAction<number>) => void;
  getGenerationTasks: (projectId: string) => GenerationTask[];
  setGenerationTasks: (projectId: string, updater: SetStateAction<GenerationTask[]>) => void;
  getExtraFiles: (projectId: string) => LockableFile[];
  setExtraFiles: (projectId: string, updater: SetStateAction<LockableFile[]>) => void;
  getAudioReferenceFiles: (projectId: string) => LockableFile[];
  setAudioReferenceFiles: (projectId: string, updater: SetStateAction<LockableFile[]>) => void;
  getVideoLastImages: (projectId: string) => LockableFile[];
  setVideoLastImages: (projectId: string, updater: SetStateAction<LockableFile[]>) => void;
  setWorkspace: (
    workspaceId: string,
    updater: Workspace | null | ((previousWorkspace: Workspace) => Workspace | null),
    projectId: string
  ) => void;
  getLayerRegistry: () => Record<string, Layer>;
  setLayer: (
    layerId: string,
    updater: Layer | null | ((previousLayer: Layer) => Layer | null)
  ) => void;
  /** Queues these fields on the layer's own save, the one writer of its row, which uploads pixels the layer holds unstored first. */
  queueLayerSave: (layerId: string, changes: Partial<import('#lib/studio-runtime-dto').StudioLayerAutosavePayload>) => void;

  kernel: RefObject<KernelHandle | null>; // kernel.current changes EVERY RENDER. NEVER save locally kernel.current or any functions from it --> IMMEDIATE STALE ISSUES
  kernelReady: boolean;
}


// ============================== Design Module Types ==============================

// --- Core Data Structures ---
// Two PRODUCT KINDS of Studio project, fixed at creation (no switching, Canvas
// and List generations are not interoperable):
//  - Canvas projects: the original workspace system ('FREEFORM', and its guided
//    'STORE_SWAP' variant).
//  - List projects:   the flat batch/marketing view ('LIST').
//  - Vector projects: the Illustrator-style editor ('VECTOR'), objects not pixels.
// The one list every validator reads; a kind added here is added once.
export const STUDIO_PROJECT_TYPES = ['FREEFORM', 'STORE_SWAP', 'LIST', 'VECTOR'] as const;
export type ProjectType = (typeof STUDIO_PROJECT_TYPES)[number];

type AnnotationMode = 'annotation';

interface AnnotationCommentReply {
  id: string;
  text: string;
  author?: string;
  authorAvatarUrl?: string;
  timestamp?: number;
}

export interface AnnotationComment {
  id: string;
  x: number;
  y: number;
  text: string;
  color?: string;
  author?: string;
  authorAvatarUrl?: string;
  timestamp?: number;
  replies?: AnnotationCommentReply[];
  resolved?: boolean;
}
export type CloudAssetSource = 'generation' | 'upload' | 'snapshot' | 'mask' | 'version_view' | 'diff_view';

export interface CloudAsset {
  url: string;
  source: CloudAssetSource;
  createdAt: string;
  lastSeenAt: string;
}


export type ResizeHandleType = 'left' | 'right' | 'top' | 'bottom' | 'topRight' | 'bottomRight' | 'bottomLeft' | 'topLeft' | 'move' | null;

export type MergeStrategy = 'stretchOnArea' | 'repaintOnMask' | 'fitInArea' | 'maskArea' | 'combineWithLayer' | 'addInList' | 'alignOnArea';

export type SoundEffect = 'RESIZE' | 'VALUE_TICK' | 'TYPE_KEY' | 'SUCCESS' | 'TAB_SWITCH' | 'QUICK_SWITCH' | 'CUT' | 'GENERATE_START' | 'CONFIRM' | 'INTENSE_NOTIFICATION' | 'UNDO' | 'REDO' | 'HEATMAP' | 'VOICE_START' | 'VOICE_END' | 'WEB_SEARCH' | 'DAIMON_CLICK';

export type UserAction = {
  id: string;
  timestamp: string;
  name: string;
  payload: Record<string, any>;
};

export interface ThemeOptions {
  scanlineColor?: string;
  scanlineOpacity?: number;
  gridSpacing?: number;
  scanSpeed?: number;
  blueprintDashPattern?: number[];
  rainbowWaveSpeed?: number;
  rainbowWaveOpacity?: number;
  antsColor1?: string;
  antsColor2?: string;
  antsSpeed?: number;
  antsDashLength?: number;
  antsGapLength?: number;
}

export interface MenuItemState {
  isSelected: boolean;
  isHovered: boolean;
}

export interface QuickMenuAction {
  label: string;
  onClick: (event?: any) => void;
  onContextMenu?: (event?: any) => void;
  tooltip?: string;
  selected?: boolean;
  disabled?: boolean;
  icon?: React.ReactElement | ((state: MenuItemState) => React.ReactElement);
  customContent?: React.ReactNode | ((state: MenuItemState) => React.ReactNode);
  subActions?: QuickMenuAction[];
  separatorBefore?: boolean;
  /** Grey line after the label, shown by the 'composer' skin ("Upload from computer"). */
  hint?: string;
  /** A destructive row (Delete): ChatGPT's soft red label and icon. */
  danger?: boolean;
}

export interface QuickMenuData {
  position: { x: number; y: number };
  actions: QuickMenuAction[];
  /** 'composer': ChatGPT's + menu, as wide as the chat box it opens from (`width`). */
  skin?: 'composer';
  width?: number;
}

export interface CropPoint {
  x: number;
  y: number;
}

export interface NativeLayerCutSession {
  layerId: string;
  workspaceId: string;
  instruction: string | null;
  startedAt: number;
}

/**
 * Columns being picked on a workspace's frame, one pick at a time (useCanvas: openWorkspaceColumnPick): a single column
 * that commits on click, or cuts edited on the canvas until they are validated. Columns are fractions of the frame's width.
 */
export type WorkspaceColumnPick = { projectId: string; workspaceId: string } & (
  | { mode: 'single'; purpose: 'seam' }
  | { mode: 'cuts'; purpose: 'develop'; recipeId: string; cuts: StreetCuts }
);

/**
 * A square zone of Recast extras (lib/recastExtras.ts) in world units from its workspace frame's top-left corner,
 * remembered in the params of the workspace's bottom photo; `note` is appended to its prompt.
 */
export type RecastZone = { id: string; x: number; y: number; side: number; enabled: boolean; note: string };
/**
 * Recast extras' zones being corrected on a workspace's frame (useCanvas: openRecastZonePick); `selected` shows its note
 * field. `detecting` names the detection under way: only its result lands, and only while the pick still carries it.
 */
export type RecastZonePick = { projectId: string; workspaceId: string; zones: RecastZone[]; selected: number | null; detecting: string | null };

export interface NativeLayerCutOptions {
  blendRadius: number;
  invert: boolean;
}

// --- Documentation Guide Types ---
export type MaskInterpretation = 'fadeOutside' | 'drawBorder' | 'drawMask' | 'boundingBox' | 'drawMaskInvert' | 'drawAnnotationOverlay';

export type InpaintTool =
  'annotationLine' | 'annotationEraser' | 'comment' |
  'canvasControl' | 'scissors' | 'sound' | 'draw' | 'maskPen' | null;

// Mask Pen tool sub-mode: precise polygon (click anchor points), freehand brush, or drag rectangle.
export type MaskPenMode = 'pen' | 'brush' | 'rect';

// A vector mask shape (resolution-independent). Coordinates are in workspace frame-raster
// space (same space as the mask overlay canvas). Rasterized to B/W only at generation time.
export interface MaskShape {
  kind: 'polygon' | 'stroke';
  points: { x: number; y: number }[];
  width?: number; // stroke width (frame-raster px) for kind === 'stroke'
}

export type SoundTimelineTool = 'separation' | 'volume' | null;
export type DrawingToolKind =
  | 'rectangle'
  | 'ellipse'
  | 'triangle'
  | 'diamond'
  | 'star'
  | 'pentagon'
  | 'hexagon'
  | 'line'
  | 'arrow'
  | 'pencil'
  | 'text'
  | 'polygon';
export type DrawingTextAlign = 'left' | 'center' | 'right';

export interface DrawingToolStyle {
  strokeColor: string;
  strokeOpacity?: number;
  fillColor: string;
  fillOpacity?: number;
  fillEnabled: boolean;
  strokeWidth: number;
  cornerRadius: number;
  fontSize: number;
  fontColor: string;
  fontOpacity?: number;
  textBold: boolean;
  textItalic: boolean;
  textUnderline: boolean;
  fontFamily?: string;
  fontWeight?: number;
  lineHeight?: number;
  letterSpacingPercent?: number;
  textAlign?: DrawingTextAlign;
}

export interface DrawingLayerPayload {
  kind: DrawingToolKind;
  style: DrawingToolStyle;
  text?: string;
  points?: Array<{ x: number; y: number }>;
  createdAt: number;
  worldRect: Rect;
}

export const VALID_INPAINT_TOOL_NAMES_FOR_LLM = [
    "AnnotationLine",    // Maps to 'annotationLine'
    "AnnotationEraser",  // Maps to 'annotationEraser'
    "AnnotationComment", // Maps to 'comment'
    "CanvasControl",
    "Scissors",
    "Draw",
    "MaskPen",
    "None"               // Maps to null
];

// Mapping from LLM-friendly names to actual InpaintTool values
export const TOOL_NAME_MAP: Record<string, InpaintTool> = {
    "AnnotationLine": 'annotationLine',
    "AnnotationEraser": 'annotationEraser',
    "AnnotationComment": 'comment',
    "CanvasControl": 'canvasControl',
    "Scissors": 'scissors',
    "Draw": 'draw',
    "MaskPen": 'maskPen',
    "None": null,
};

export interface CanvasResizeState {
  isResizing: boolean;
  handleType: ResizeHandleType;
  startPos: { x: number, y: number } | null;
  delta: { dx: number, dy: number };
  initialRect: Rect | null;
  isShiftPressed: boolean;
  workspaceId: string | null;
}

export interface FrameResizeState {
  isResizing: boolean;
  handleType: ResizeHandleType;
  startPos: { x: number, y: number } | null;
  delta: { dx: number, dy: number };
  initialRect: Rect | null;
  workspaceId: string | null;
}

export interface BaseParams {
  prompt: string;
  listPersonaReferenceSelection?: { personaId: string; identityRevision: number; referenceIds: string[] }[];
  video_input_start_ms?: number | null;
  video_input_end_ms?: number | null;
  harmonize?: 'On' | 'Auto' | 'Off';
  harmonize_profile?: 'soft' | 'strict';
  alpha_radius?: number;
  preserve_high_res?: boolean;
  noParamsSave?: boolean;
  seed: number;
}

type GenerationLayerConsolidationMode = 'mergeIntoBatchAnchor';

interface GenerationLayerConsolidation {
  mode: GenerationLayerConsolidationMode;
  batchId: string;
  tileIndex: number;
  tileCount: number;
  tileEdgeFadeRadiusMode?: 'fixed' | 'tileOverlapMargin';
  tileOverlapScale?: number;
  grid?: GridBatchLaunch;
}

export interface ModelRunGenerationOptions {
  /** Runtime-only ownership registration, before image tasks are dispatched. */
  onTasksCreated?: (tasks: GenerationTask[]) => void;
  layerConsolidation?: GenerationLayerConsolidation | null;
  /**
   * Feed THIS asset to the model as the main image input instead of the
   * canvas render (or the pinned reference). Used by per-output actions in
   * List projects, e.g. "Upscale" on a tile must upscale that exact output,
   * never a generation-area render (which crops) and never the pinned
   * reference (which may be a different output).
   */
  mainImageUrlOverride?: string;
  /**
   * The existing stored asset path for `mainImageUrlOverride`. Reusing it avoids
   * uploading the same source image to permanent storage a second time.
   */
  mainImagePrivateUrlOverride?: string;
  /** Preserve a List variation source image geometry unless the command explicitly requests another output format. */
  preserveSourceAspectRatio?: boolean;
  /** This generation only: overrides UserPreferences.autoColorMatch, never writes it. */
  autoColorMatch?: boolean;
  /** This generation only: false skips the Integrate (Align, then Harmonize) its model runs after landing. */
  autoIntegrate?: boolean;
  /** Persisted run identity for List outputs launched together. */
  factoryRunId?: string;
  /**
   * Feed THIS clip to a video model as the source video instead of composing
   * one from the workspace timeline. List projects have no canvas and no
   * mounted video elements, so there is no timeline to compose from: their
   * clips are finished outputs addressed by URL.
   */
  mainVideoUrlOverride?: string;
  /**
   * The List output (workspace version) this run was launched from. Stamped
   * onto the created tasks so the tile can refuse a second launch while one
   * is already pending for the same output: only a fresh read of the pending
   * tasks covers the window after the launch call has resolved.
   */
  sourceListOutputId?: string;
  /** Project scope for output-only actions whose source has no workspace owner. */
  projectId?: string;
  /** Destination captured from the active persistent Canvas/List view at launch. */
  outputScope?: GenerationOutputScope;
  /** Image models only: this run's area in world units, instead of the workspace's Generation Area. */
  rect?: Rect;
  /** Canvas renders only: an opaque red band over the main image's full height, x and w in world units, painted on the image sent and nowhere else. */
  mainImageRedBand?: { x: number; w: number };
}

export type GenerationOutputScope =
  | { kind: 'WORKSPACE'; workspaceId: string }
  | { kind: 'PROJECT'; projectId: string };

export type ImageGenerationFailureDetails = {
  providerCode?: string;
  parameter?: string;
  stage: 'configuration' | 'reference' | 'provider' | 'response' | 'output-fetch' | 'output-validation' | 'output-save';
  reason: 'blocked' | 'no-image' | 'authentication' | 'rate-limit' | 'invalid-request' | 'unavailable' | 'timeout' | 'cancelled' | 'invalid-response' | 'unknown';
  httpStatus?: number;
  responseKind?: 'blocked' | 'textOnly' | 'empty' | 'noImage' | 'unknown';
  promptBlockReason?: string;
  finishReasons?: string;
  candidateCount?: number;
  textPartCount?: number;
  imageCount?: number;
};

export interface GenerationTask {
  id: string;
  /** Saved grouping for an observed List job; not an executable Factory run. */
  listPresentation?: { batchKey: string; snapshotCreatedAt?: number };
  /** One List card may contain several separately billed, auditable attempts. */
  listAttempt?: { rootTaskId: string; startedAt: number; number: number; limit: number; active?: boolean };
  /** Submission may still be running; check its status instead of purchasing a retry. */
  recoveryPending?: boolean;
  /** Client receipt time of a matching server status, not a provider heartbeat. */
  lastStatusConfirmedAt?: number;
  /** Stamped once in processTasks; List outputs retain it for stable card placement. */
  startedAt?: number;
  /** The List output this run was launched from, riding in from
   *  ModelRunGenerationOptions.sourceListOutputId. Runtime only, never persisted: it lets
   *  a second Upscale click on the same output be refused while this task is pending. */
  sourceListOutputId?: string;
  /** Shared by the outputs launched by one Factory command. */
  factoryRunId?: string;
  serverTaskId?: string | null;
  // Attribution timeline uniquement : la carte de generation apparait dans le fil
  // de cette session. Ne signifie PAS que l'agent a lance la tache : une generation
  // manuelle est taguee des qu'une session pointe sur le meme projet.
  chatSessionId?: string | null;
  // Propriete reelle : vrai seulement si un script agentique tournait sur la session
  // au moment de la creation. Seul champ que cancelGenerationTasksForSession doit lire
  // pour decider quoi tuer.
  isAgentSpawned?: boolean;
  type: 'diffuse' | 'film';
  workspaceId: string;
  /** Captured at launch; finalization must not resolve a destination from the current view. */
  outputScope?: GenerationOutputScope;
  params?: DiffusionParams;
  error?: string;
  failureCode?: 'IMAGE_GENERATION_BLOCKED' | 'IMAGE_GENERATION_NO_IMAGE';
  failureDetails?: ImageGenerationFailureDetails;
  urls?: string[] | string;
  mainImagePublicUrl?: string;
  maskUrl?: string;
  extraImageUrl?: string;
  extraImageUrl2?: string;
  mainVideoPublicUrl?: string;
  mainAudioPublicUrl?: string;
  mainAudioMimeType?: string;
  videoDurationSeconds?: number;
  model: string;
  mergeStrategy: MergeStrategy;
  rect: Rect;
  targetWidth: number;
  targetHeight: number;
  upscaleFactor: number;
  maskFile?: File | null;
  blurRadius: number;
  layer?: Layer;
  layerOverride?: Partial<Layer>;
  mainImageData?: ImageData | null;
  operation?: string;
  file_data?: string;
  projectId: string;
  sourceLayerId?: string | null;
  isOutcomeReported?: boolean;
  // video-loop plan Phase 2/3: live self-validation telemetry pushed over the
  // generation-task-status socket while a long render runs (see useGenerationTasks).
  loopTier?: 'direction' | 'motion' | 'render' | null;
  loopStepLabel?: string | null;
  loopEtaSeconds?: number | null;
  loopProgress?: number | null;
  segmentationPrompt?: string;
  existing?: boolean;
  gridSubdivisionsX?: number;
  gridSubdivisionsY?: number;
  layerContextSnapshot: { id: string; visible: boolean; }[] | null; // Snapshot of layers below when first injected
  parentVersionId?: string | null;
  mainImagePrivateUrl?: string | null;
  maskPrivateUrl?: string | null;
  extraImageUrlPrivate?: string | null;
  referenceImagePrivateUrls?: string[] | null;
  mainAudioPrivateUrl?: string | null;
  autoIntegrateWithUnderlying?: boolean;
  /** Frozen at launch; only gates the Harmonize step of auto-integrate. autoIntegrateWithUnderlying also drives output geometry, so it must not carry this. */
  autoColorMatch?: boolean;
  layerConsolidation?: GenerationLayerConsolidation | null;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface GetMaskImageOptions {
  maskedColor?: RgbaColor;
  unmaskedColor?: RgbaColor;
  maskWidth?: number;
  maskHeight?: number;
  inflationRadius?: number;
  requireMask?: boolean;
}

type ManualModificationType =
  | 'MANUAL_MOVE'
  | 'MANUAL_RESIZE'
  | 'MANUAL_ALIGN'
  | 'MANUAL_EXTRACT'
  | 'MANUAL_FEATHER'
  | 'MANUAL_HARMONIZE'
  | 'MANUAL_UNCHANGED_HARMONIZE'
  | 'MANUAL_VECTORIZE'
  | 'MANUAL_FILTER'
  | 'MANUAL_DEVELOP'
  | 'MERGED_INTO'
  | 'CREATED_FROM_MERGE';

export interface LayerModificationLogEntry {
  timestamp: string;
  type: ManualModificationType;
  description?: string;
}

export interface Layer {
  id: string;
  /** Runtime inputs may omit this before Kernel normalization; canonical StudioLayerDTO always requires it. */
  projectId?: string;
  renderOrder?: number;
  'top-layer'?: boolean;
  imageSrc?: string;

  /**
   * Defines the layer's position.
   * IMPORTANT: The (x, y) coordinates are relative to the `frameRect` of the workspace
   * that is currently providing the rendering context, which may not be the layer's owner workspace.
   * This allows layers from parent workspaces to be correctly positioned within a child's frame.
   */
  rect: Rect;

  imageData: ImageData | null;
  scale?: number;
  visible: boolean;
  name: string;
  locked: boolean;
  type?: 'video' | 'audio' | 'svg'; // No type means regular image
  opacity?: number;
  /** Runtime-only: excludes this layer from canvas rendering without removing it from the workspace. */
  skipRender?: boolean;
  mustRender?: boolean;
  /** Null denotes the official Project layer; non-empty values denote Workspace layers. */
  layerWorkspaceId: string | null;
  params?: DiffusionParams | Record<string, unknown>;
  source: string;
  timestamp?: number;
  contentVersion?: number;
  pulseToken?: number;
  isVectorizing?: boolean;
  isRasterizing?: boolean;
  isAligning?: boolean;
  isExtracting?: boolean;
  isHarmonizingUnchanged?: boolean;
  isIntegrating?: boolean;

  isPlaying?: boolean;
  videoSrc?: string;
  /**
   * Runtime-only: the raw video bytes fetched exactly once by the hydrator, mirroring how
   * `imageData` holds an image layer's decoded pixels. Layer2DVideo derives a `blob:`
   * objectURL from this (with lifecycle-scoped revocation) so the asset is downloaded a
   * single time and every seek is fully local. Never persisted (see StrippedLayer).
   */
  videoBlob?: Blob | null;
  videoFramesUrls?: string[];
  timelineStartMs?: string | number | null;
  timelineEndMs?: string | number | null;
  timelineSourceInMs?: string | number | null;
  loadError?: boolean;
  modificationLog?: LayerModificationLogEntry[];
  reconstructedInputUrl?: string;
}

export type StrippedLayer = Omit<Layer, 'imageData' | 'videoBlob'>;

/**
 * Optional runtime overrides used for background/headless command execution.
 */
export interface RuntimeState {
  parameters?: { [model: string]: DiffusionParams };
  diffusionModel?: string;
  referenceImages?: LockableFile[];
  videoEndImage?: LockableFile | null;
}

export interface WorkspaceVersion {
  id: string;
  name: string;
  timestamp: number;
  layerVisibility: { id: string; visible: boolean; }[];
  imageUrl?: string;
  isRenderCacheValid?: boolean;
  renderStateHash?: string;
  status: 'VALIDATED' | 'CANDIDATE' | 'REJECTED' | 'ARCHIVED';
  parentId: string | null;
  rejectionReason?: string | null;
  favorite?: boolean;
  /** LIST only: a hidden intermediate output produced during the self-validation
   *  loop. Never shown to the user; the winning draft is revealed (flag cleared)
   *  and the rest discarded when the loop ends. */
  isLoopDraft?: boolean;
  annotationComments?: AnnotationComment[];
  lastAnnotationMaskUrl?: string | null;
  annotationMask?: ImageData | null;
  annotationMaskIsDirty?: boolean;
}

export interface Workspace {
  id: string;
  projectId: string;
  name: string;
  layerIds: string[];
  activeVersionId: string | null;
  generationRect: Rect;
  aspectRatio: AspectRatio;
  frameRect: Rect;
  backgroundColor?: string;
  timelineDurationMs?: string | number | null;
  timelineFps?: number | null;

  lastMaskUrl?: string;
  annotationMask: ImageData | null;
  annotationMaskVersionId?: string | null;
  annotationsEnabled?: boolean;
  annotationOpaque?: boolean;
  isResizingCanvas?: boolean;
  canvasResizeState?: CanvasResizeState;
  frameResizeState?: FrameResizeState;

  expanded: boolean;
  locked: boolean;
  visible: boolean;
  
  cameraState?: Record<string, unknown>;
  versions?: WorkspaceVersion[];
  validatedVersionId: string | null;
  versionFilterMode?: 'PRIMARY' | 'ALL';
  animatedGenerationRect?: Rect | null;
  preferVersionPanelOverTimeline?: boolean;
  imageTimelineManuallyEnabled?: boolean;
  timelineCursorMs?: number | null;
  isTimelinePlaying?: boolean;
  timelinePlaybackRate?: number;
  isTimelineMuted?: boolean;
  isTimelineLoopEnabled?: boolean;
  timelineComposedPlayback?: StudioTimelineComposedPlaybackRuntime | null;
  isImportingAssets?: boolean;
  isMountingLayers?: boolean;
}

export type StudioWorkspaceVersionStatus =
  | 'VALIDATED'
  | 'CANDIDATE'
  | 'REJECTED'
  | 'ARCHIVED';

interface StudioWorkspaceVersionLayerVisibilityDTO {
  id: string;
  visible: boolean;
}

export interface StudioWorkspaceVersionDTO {
  id: string;
  projectId: string;
  workspaceId: string;
  parentVersionId: string | null;
  name: string;
  timestamp: string | number | null;
  status: StudioWorkspaceVersionStatus;
  layerVisibility: StudioWorkspaceVersionLayerVisibilityDTO[];
  imageUrl: string | null;
  isRenderCacheValid: boolean;
  renderStateHash: string | null;
  rejectionReason: string | null;
  favorite: boolean;
  annotationComments: AnnotationComment[] | null;
  lastAnnotationMaskUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StudioWorkspaceDTO {
  id: string;
  projectId: string;
  name: string;
  visible: boolean;
  generationRect: Rect;
  frameRect: Rect;
  backgroundColor?: string;
  aspectRatio: number | null;
  annotationsEnabled: boolean;
  annotationOpaque: boolean;
  lastMaskUrl: string | null;
  cameraState: Workspace['cameraState'] | null;
  validatedVersionId: string | null;
  timelineDurationMs: string | number | null;
  timelineFps: number | null;
  layerIds: string[];
  versionIds: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Transient per-workspace timeline composed-playback status. Lives in the
 * runtime overlay only (never persisted); drives the preview compositor and the
 * "buffering" overlay across every view of the workspace.
 */
export interface StudioTimelineComposedPlaybackRuntime {
  sourceUrl: string | null;
  isPreparing: boolean;
  preparePercent: number;
  prepareMessage: string | null;
}

/**
 * Runtime-only workspace overlay stored in entityKernelState.
 * Never persisted in StudioWorkspace DTO storage.
 */
export interface StudioWorkspaceRuntimeState {
  activeVersionId?: string | null;
  annotationMask?: ImageData | null;
  annotationMaskVersionId?: string | null;
  versionFilterMode?: 'PRIMARY' | 'ALL';
  isResizingCanvas?: boolean;
  canvasResizeState?: CanvasResizeState;
  frameResizeState?: FrameResizeState;
  expanded?: boolean;
  locked?: boolean;
  versions?: WorkspaceVersion[];
  animatedGenerationRect?: Rect | null;
  preferVersionPanelOverTimeline?: boolean;
  imageTimelineManuallyEnabled?: boolean;
  timelineCursorMs?: number | null;
  isTimelinePlaying?: boolean;
  timelinePlaybackRate?: number;
  isTimelineMuted?: boolean;
  isTimelineLoopEnabled?: boolean;
  timelineComposedPlayback?: StudioTimelineComposedPlaybackRuntime | null;
  isImportingAssets?: boolean;
  isMountingLayers?: boolean;
}

/**
 * Developer-facing aggregate workspace state:
 * persisted DTO + workspace-scoped runtime overlays.
 */
export interface StudioWorkspace extends StudioWorkspaceDTO, StudioWorkspaceRuntimeState {}

/**
 * Selector/view shape after DTO + draft + runtime merge.
 */
type StudioWorkspaceEntityState = StudioWorkspace;

/**
 * Draft shape for workspace entityKernelState.
 * - top-level: partial DTO overrides
 * - runtime: transient workspace-only runtime values
 */
interface StudioWorkspaceKernelState extends
  Partial<StudioWorkspaceDTO> {
  runtime?: Partial<StudioWorkspaceRuntimeState>;
}

type StudioWorkspaceKernelMergedState =
  Partial<StudioWorkspaceDTO> &
  Partial<StudioWorkspaceRuntimeState> &
  StudioWorkspaceKernelState;

export interface StudioLayerDTO {
  id: string;
  projectId: string;
  /** Null means the official Project layer; a non-empty value means Workspace layer. */
  workspaceId: string | null;
  renderOrder: number | null;
  topLayer: boolean;
  name: string;
  visible: boolean;
  type: Layer['type'] | string | null;
  rect: Rect;
  scale: number | null;
  imageSrc: string | null;
  videoSrc: string | null;
  videoFramesUrls: string[];
  timelineStartMs: string | number | null;
  timelineEndMs: string | number | null;
  timelineSourceInMs: string | number | null;
  source: string | null;
  params: DiffusionParams | Record<string, unknown> | null;
  timestamp: string | number | null;
  contentVersion: string | number | null;
  reconstructedInputUrl: string | null;
  modificationLog: LayerModificationLogEntry[] | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Runtime-only layer overlay stored in entityKernelState.
 * Never persisted in StudioLayer DTO storage.
 */
export interface StudioLayerRuntimeState {
  listPersonaSaving?: string;
  imageData?: ImageData | null;
  videoBlob?: Blob | null;
  skipRender?: boolean;
  locked?: boolean;
  opacity?: number;
  mustRender?: boolean;
  isPlaying?: boolean;
  loadError?: boolean;
  pulseToken?: number;
  isVectorizing?: boolean;
  isRasterizing?: boolean;
  isAligning?: boolean;
  isExtracting?: boolean;
  isHarmonizingUnchanged?: boolean;
  isIntegrating?: boolean;
  selectedSvgPaintTargetId?: string | null;
}

export type SvgPaintChannel = 'fill' | 'stroke' | 'stop-color';
export type SvgPaintSource = 'attribute' | 'style';

export interface SvgPaintTarget {
  id: string;
  elementIndex: number;
  elementName: string;
  elementLabel: string;
  channel: SvgPaintChannel;
  source: SvgPaintSource;
  color: string;
  opacity: number;
}

/**
 * Developer-facing aggregate layer state:
 * persisted DTO + layer-scoped runtime overlays.
 */
export interface StudioLayer extends StudioLayerDTO, StudioLayerRuntimeState {}

type LayerScopeLike = {
  projectId?: unknown;
  workspaceId?: unknown;
  layerWorkspaceId?: unknown;
};

/** Normalizes the nullable workspace boundary without turning whitespace into an ID. */
export const normalizeLayerWorkspaceId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const getLayerWorkspaceId = (layer: LayerScopeLike): string | null => {
  if (Object.prototype.hasOwnProperty.call(layer, 'layerWorkspaceId')) {
    return normalizeLayerWorkspaceId(layer.layerWorkspaceId);
  }
  return normalizeLayerWorkspaceId(layer.workspaceId);
};

export const isWorkspaceLayer = (layer: LayerScopeLike): boolean =>
  getLayerWorkspaceId(layer) !== null;

export const isProjectLayer = (layer: LayerScopeLike): boolean =>
  getLayerWorkspaceId(layer) === null;

export const listWorkspaceLayers = <T extends LayerScopeLike>(
  layers: readonly T[],
  projectId: string,
  workspaceId: string,
): T[] => {
  const normalizedProjectId = projectId.trim();
  const normalizedWorkspaceId = workspaceId.trim();
  if (!normalizedProjectId || !normalizedWorkspaceId) return [];
  return layers.filter(
    (layer) =>
      typeof layer.projectId === 'string' &&
      layer.projectId.trim() === normalizedProjectId &&
      getLayerWorkspaceId(layer) === normalizedWorkspaceId,
  );
};

export const listProjectLayers = <T extends LayerScopeLike>(
  layers: readonly T[],
  projectId: string,
): T[] => {
  const normalizedProjectId = projectId.trim();
  if (!normalizedProjectId) return [];
  return layers.filter(
    (layer) =>
      typeof layer.projectId === 'string' &&
      layer.projectId.trim() === normalizedProjectId &&
      isProjectLayer(layer),
  );
};

/**
 * Selector/view shape after DTO + draft + runtime merge.
 */
type StudioLayerEntityState = StudioLayer;

/**
 * Draft shape for layer entityKernelState.
 * - top-level: partial DTO overrides
 * - runtime: transient layer-only runtime values
 */
interface StudioLayerKernelState extends
  Partial<StudioLayerDTO> {
  runtime?: Partial<StudioLayerRuntimeState>;
}

type StudioLayerKernelMergedState =
  Partial<StudioLayerDTO> &
  Partial<StudioLayerRuntimeState> &
  StudioLayerKernelState;

export type OutputImageFormat = 'webp' | 'png' | 'jpeg';
export type ImageExportFileFormat = OutputImageFormat | 'svg' | 'pdf';
export type DraftBound = 'min' | 'max' | null;
export type PrintExportFileFormat = 'tiff' | 'jpeg';
export type PrintIccProfile = 'adobe-rgb-1998' | 'srgb' | 'iso-coated-v2-300-eci';
export type PrintIccColorSpace = 'rgb' | 'cmyk';
export type PrintBleedMm = 0 | 30 | 50;
export type PrintPresetId = 'standard-120x180' | 'standard-300x200' | 'standard-600x250' | 'custom';

export interface PrintExportOptions {
  preset: PrintPresetId;
  widthCm: number;
  heightCm: number;
  dpi: 72 | 100 | 150 | 300;
  format: PrintExportFileFormat;
  iccProfile: PrintIccProfile;
  bleedMm: PrintBleedMm;
  target: 'area' | 'frame';
}

export interface PrintSnapshotResource {
  kind: 'url' | 'arrayBuffer' | 'blob';
  value: string | ArrayBuffer | Blob;
  encoding?: 'rgba8';
  width?: number;
  height?: number;
}

export interface PrintSnapshotLayer {
  id: string;
  source: PrintSnapshotResource;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  rotationDeg: number;
  flipX: boolean;
  flipY: boolean;
  renderOrder: number;
}

export interface PrintSnapshot {
  version: 1;
  width: number;
  height: number;
  dpi: number;
  format: PrintExportFileFormat;
  bleedPx: number;
  icc: ArrayBuffer;
  iccProfile?: PrintIccProfile;
  layers: PrintSnapshotLayer[];
}

export interface PrintExportPreferences {
  preset?: PrintPresetId;
  widthCm?: number;
  heightCm?: number;
  dpi?: 72 | 100 | 150 | 300;
  format?: PrintExportFileFormat;
  iccProfile?: PrintIccProfile;
  bleedMm?: PrintBleedMm;
  target?: 'area' | 'frame';
}

interface CalculatedFitRect extends Rect {
  shiftDx: number;
  shiftDy: number;
}

export interface GridOverlayOptions {
  subdivisionsX: number;
  subdivisionsY: number;
  lineWidth: number;
  lineColor: string;
}

export interface OriginalImage {
  file?: File | null,
  upscaleFactor: number,
  width: number;
  height: number;
  targetWidth: number;
  targetHeight: number;
  imageData?: ImageData | null;
}

export type AspectRatio = number | null;

/**
 * Canonical persisted Studio project payload (DB / file skeleton).
 * This shape must stay transport-oriented and free of transient UI runtime.
 */
interface ProjectDTO {
  images?: StrippedLayer[]; // Legacy: use cloudAssetRegistry going forward
  cloudAssetRegistry?: CloudAsset[];
  layers?: StrippedLayer[];
  workspaces?: Omit<Workspace, 'annotationMask' | 'annotationMaskVersionId'>[];
  activeLayerWorkspaceId?: string;
  actionHistory?: UserAction[];
  workspaceCounter?: number;
  projectType?: ProjectType;
}

/**
 * Project-scoped runtime state only (draft/runtime overlay stored under entityKernelState).
 * Split by runtime zones to keep migrations incremental and explicit.
 */
interface ProjectPersistenceRuntimeState {
  isProjectLoading?: boolean;
  essentialAssetsReady?: boolean;
  pendingAssetUrls?: Set<string>;
  inflightLayerIds?: Set<string>;
  isSaving?: boolean;
  saveTelemetry?: SaveTelemetry | null;
}

interface ProjectGenerationRuntimeState {
  isSubmittingAITask?: boolean;
  diffusionModel?: string;
  spectacleMode?: boolean;
  modelParams?: { [model: string]: DiffusionParams };
  localNumOutputs?: number;
  generationTasks?: GenerationTask[];
  extraFiles?: LockableFile[];
  audioReferenceFiles?: LockableFile[];
  videoLastImages?: LockableFile[];
}

export interface StudioDevelopSettings {
  exposure: number;
  contrast: number;
  highlights: number;
  shadows: number;
  whites: number;
  blacks: number;
  temperature: number;
  tint: number;
  vibrance: number;
  saturation: number;
  clarity: number;
  sharpen: number;
  noiseReduction: number;
  vignette: number;
}

export interface StudioDevelopSession {
  workspaceId: string;
  settings: StudioDevelopSettings;
  openedAt: number;
  isApplying?: boolean;
  isPreviewRendering?: boolean;
  previewUrl?: string | null;
  applyPreviewUrl?: string | null;
  applyProgress?: number;
  previewError?: string | null;
}

export interface GridGenerationPanelState {
  workspaceId: string;
  payload: GridGenerationPayload;
  openedAt: number;
}

/** A press on an already-selected canvas layer, at its screen position. */
export type LayerReclickPress = { layerId: string; x: number; y: number };

interface ProjectCanvasRuntimeState {
  isDrawing?: boolean;
  annotationBrushSize?: number;
  annotationColor?: string;
  annotationBrushHardness?: number;
  annotationMode?: AnnotationMode;
  activeTool?: InpaintTool;
  drawingToolKind?: DrawingToolKind;
  drawingStrokeColor?: string;
  drawingStrokeOpacity?: number;
  drawingFillColor?: string;
  drawingFillOpacity?: number;
  drawingFillEnabled?: boolean;
  drawingStrokeWidth?: number;
  drawingCornerRadius?: number;
  drawingFontSize?: number;
  drawingFontColor?: string;
  drawingFontOpacity?: number;
  drawingTextBold?: boolean;
  drawingTextItalic?: boolean;
  drawingTextUnderline?: boolean;
  drawingFontFamily?: string;
  drawingFontWeight?: number;
  drawingLineHeight?: number;
  drawingLetterSpacingPercent?: number;
  drawingTextAlign?: DrawingTextAlign;
  layerToolColor?: RgbaColor;
  layerToolWidth?: number;

  selectedLayersId?: string[];
  draggedLayerId?: string | null;
  isResizingLayer?: boolean;
  isPanning?: boolean;
  panOffset?: { x: number; y: number };
  zoomLevel?: number;
  pointBeingDragged?: { layerId: string; pointName: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom' | 'left' | 'right' } | null;
  UIWorkspaceId?: string;
  isLayerSliderVisible?: boolean;
  rightSidebarTab?: 'models' | 'layers' | 'settings';
  aspectRatioEnforcementPulse?: { workspaceId: string; ratio: number; token: number };
  layerListRevealRequest?: { layerId: string; token: number };
  developSession?: StudioDevelopSession | null;
  gridGenerationPanelState?: GridGenerationPanelState | null;
  showResolutionHeatmap?: boolean;
  nativeLayerCutSession?: NativeLayerCutSession | null;
  scissorsHoverLayerId?: string | null;
}

/** The List surface is a flat grid of outputs and the user opens ONE of them in the expanded
 *  viewer. That focus is what "this image" / "the active image" / "the image on the canvas" means
 *  in a List view, so it is runtime state the AGENT reads, not private component state. */
interface ProjectListRuntimeState {
  focusedListOutputId?: string | null;
  /** Append-only for the current account/browser lifetime; removed output numbers are not reused. */
  listOutputMentionIds?: { userId: string; ids: string[] };
  /** Local projection state for detached output actions; never persisted as Project DTO data. */
  projectOutputFavorites?: string[];
  projectOutputFeedback?: Record<string, 'good' | 'bad'>;
}

interface ProjectViewRuntimeState {
  /** The project-level surface selection. Project kind still governs generation semantics. */
  projectViewMode?: StudioViewMode;
  /** A Canvas project's 3D scene shown in place of its canvas (lib/modelist/projectScene.ts). */
  projectSurface?: 'canvas' | 'scene';
}


interface ProjectHistoryRuntimeState {
  undoStack?: UndoableAction[];
  redoStack?: UndoableAction[];
  assistantTurnDebugLog?: AssistantTurnDebugInfo[];
  pendingProjectSuccessMissionIds?: Set<string>;
}

export interface ProjectRuntimeState extends
  ProjectPersistenceRuntimeState,
  ProjectGenerationRuntimeState,
  ProjectCanvasRuntimeState,
  ProjectListRuntimeState,
  ProjectViewRuntimeState,
  ProjectHistoryRuntimeState {}

/**
 * Developer-facing aggregate project state:
 * persisted DTO + project-scoped runtime overlays.
 */
export interface Project extends ProjectDTO, ProjectRuntimeState {}

/**
 * Shape expected in entityKernelState for `Project:<id>`.
 * - Top-level keys: draft overlay on persisted `ProjectDTO`
 * - `runtime`: transient project-scoped runtime payload
 */
export interface ProjectKernelState extends Partial<ProjectDTO> {
  runtime?: Partial<ProjectRuntimeState>;
}

/**
 * Read shape returned by entityKernelState merge for Project.
 * Transitional: merged from Studio skeleton (`ProjectDTO`) and inventory project rows (`LeasingProjectDTO`).
 */
export type ProjectKernelMergedState =
  Partial<ProjectDTO> &
  Partial<LeasingProjectDTO> &
  ProjectKernelState;

export type ProjectMetadata = {
  id: string;
  name: string;
  imageUrl?: string | null;
  lastModified?: string;
  totalCreditsSpent: number;
  favorite?: boolean;
  toDelete?: boolean;
  userId?: string;
  ownerName?: string;
};

interface OrganisationProject {
  id: string;
  name: string;
  lastModified: string;
  imageUrl: string | null;
  authorName: string;
  authorAvatarUrl: string | null;
  userId: string;
}

// --- Project Success Analysis Types ---

export type ProjectSuccessLayerChangeType = 'INTRODUCED' | 'UPDATED' | 'PERSISTED';

export interface ProjectSuccessLayerGenerationMetadata {
  model: string | null;
  prompt: string | null;
  rect: Rect | null;
  rawParams: DiffusionParams | null;
  trace?: {
    snapshotId?: string | null;
    taskId?: string | null;
    taskStatus?: string | null;
    generationLogId?: string | null;
    providerJobId?: string | null;
    chatSessionId?: string | null;
    workspaceId?: string | null;
    projectId?: string | null;
    acceptedVersionId?: string | null;
    candidateVersionId?: string | null;
  } | null;
  inputAssets?: {
    main?: string | null;
    mask?: string | null;
    control?: string | null;
    references?: (string | null)[] | null;
  } | null;
}

export interface ProjectSuccessLayerInfo {
  id: string;
  name: string;
  timestamp?: number;
  source: string;
  imageSrc?: string;
  reconstructedInputUrl?: string;
  generation: ProjectSuccessLayerGenerationMetadata;
  modificationLog?: LayerModificationLogEntry[];
}

export interface ProjectSuccessVersionInfo {
  id: string;
  name: string;
  timestamp: number;
  parentId: string | null;
  generativeContext?: unknown;
  imageUrl?: string | null;
}

export interface ProjectSuccessLayerTimelineStep {
  version: ProjectSuccessVersionInfo;
  layerState: ProjectSuccessLayerInfo;
  changeType: ProjectSuccessLayerChangeType;
  contributingTaskIds: string[];
  isFinal: boolean;
}

export interface ProjectSuccessLayerGenealogy {
  layer: ProjectSuccessLayerInfo;
  timeline: ProjectSuccessLayerTimelineStep[];
}

export interface ProjectSuccessWorkspaceAnalysis {
  workspaceId: string;
  workspaceName: string;
  validatedVersion: ProjectSuccessVersionInfo | null;
  initialVersion: ProjectSuccessVersionInfo | null;
  layerGenealogies: ProjectSuccessLayerGenealogy[];
}

export interface ProjectSuccessAnalysis {
  projectName: string;
  analyzedWorkspaces: ProjectSuccessWorkspaceAnalysis[];
}

type PromptAnnotationValue = 'good' | 'neutral' | 'bad';

interface PromptAnnotation {
  value: PromptAnnotationValue;
  evaluatedAt: string;
  userId: string;
}

export interface GenerationTaskSnapshot {
  id: string;
  clientTaskId?: string | null;
  userId?: string | null;
  organisationId?: string | null;
  projectId?: string | null;
  chatSessionId?: string | null;
  workspaceId?: string | null;
  /** Derived from the persisted workspaceId/projectId for legacy snapshots. */
  outputScope?: GenerationOutputScope | null;
  generationLogId?: string | null;
  providerJobId?: string | null;
  layerId?: string | null;
  taskStatus?: string | null;
  failureMessage?: string | null;
  type: string;
  modelName: string;
  mergeStrategy?: string | null;
  rect?: Rect | null;
  params?: DiffusionParams | null;
  targetWidth?: number | null;
  targetHeight?: number | null;
  upscaleFactor?: number | null;
  blurRadius?: number | null;
  operation?: string | null;
  fileData?: string | null;
  segmentationPrompt?: string | null;
  gridSubdivisionsX?: number | null;
  gridSubdivisionsY?: number | null;
  mainImagePrivateUrl?: string | null;
  maskPrivateUrl?: string | null;
  extraImageUrlPrivate?: string | null;
  referenceImagePrivateUrls?: string[] | null;
  isOutcomeReported?: boolean | null;
  existing?: boolean | null;
  candidateVersionId?: string | null;
  finalAssetUrls?: string[] | null;
  finalMetadata?: Record<string, unknown> | null;
  finalizedAt?: string | null;
  mergedAt?: string | null;
  isAccepted?: boolean | null;
  acceptedAt?: string | null;
  acceptedVersionId?: string | null;
  annotations?: PromptAnnotation[] | null;
  createdAt: string;
}

export const COMMAND_GROUPS = {
  GA_CHANGE: [
    'FRAME_LAYERS',
    'FRAME_VISIBLE_CONTENT',
    'FOCUS_GENERATION_AREA',
    'SET_GENERATION_AREA_RECT'
  ],
  MODEL_LOCK_RESTRICTED: [
    'SELECT_MODEL',
  ],
};


export const DIRECTOR_OUTPUT_SCHEMA = {
    type: "object",
    description: "A single JSON object representing the script to be executed for the assistant's turn. This object follows the natural language portion of the response.",
    properties: {
        label: { type: "string", description: "A short, user-friendly label for the script (e.g., 'Change to Video Mode and open files')." },
        autoRecall: {
            type: "boolean",
            description: "Optional. Controls automatic follow-up after script completion. Runtime mode policy applies: in ASSISTED, this is always treated as false (hard pause). In COLLABORATIVE/AUTONOMOUS, default is true unless explicitly set to false."
        },
        listGenerationProposal: {
            type: "object",
            description: "List only: when asking for confirmation of ONE new image, provide its exact scene brief with explicit Factory @handles. No alternatives, edits, panel builds or management. commands must be empty and autoRecall false. Omit for ordinary requests that should execute immediately.",
            properties: { brief: { type: "string" } },
            required: ["brief"]
        },
        listReferenceContinuation: {
            type: "object",
            description: "List only: explicitly continue editing one output previously selected by the user. Choose userMessageId from the server's continuation catalog; omit for unrelated/new images. Keep this choice through read-only folder lookups.",
            properties: { userMessageId: { type: "string" } },
            required: ["userMessageId"]
        },
        listCreativeReferences: {
            type: "array",
            description: "List only: choose the available creative-context resources needed for THIS generation. Available references are not all active. Use canonical kind/id from the server context. Keep the user's chosen persona/object/archetype across follow-ups to that creative request; omit unrelated references. Outputs here are examples, never implicit edit sources.",
            items: { type: "object", properties: { kind: { type: "string", enum: ["folder", "factory-image", "persona", "object", "archetype", "output"] }, id: { type: "string" } }, required: ["kind", "id"] }
        },
        listSceneSource: {
            type: "string", enum: ["current-message", "conversation"],
            description: "List generation: current-message when this message supplies a complete new scene; conversation when resolving an offered option, continuing an existing brief, or replacing a subject in that scene. In conversation mode the generation prompt must contain the complete resolved scene, with the latest human changes applied. Never carry old facial anatomy or panel-authoring instructions."
        },
        commands: {
            type: "array",
            description: "A list of UI command objects to be executed sequentially. Each command must conform to the definitions provided in 'AVAILABLE UI COMMANDS'. Can be an empty array if the script is just a placeholder or proposal.",
            items: {
                type: "object",
                properties: {
                    action_type: { type: "string", description: "The 'action_type' string of the command to execute (must match one from 'AVAILABLE UI COMMANDS')." },
                    parameters: { type: "object", description: "An object containing the parameters required for the specified 'action_type'. Keys and value types must match the command's parameter definition." },
                    description: { type: "string", description: "A very brief human-readable description of what this specific command step achieves." }
                },
                required: ["action_type", "parameters", "description"]
            }
        }
    },
    required: ["label", "commands"]
};


export const ModelVisibilityState = {
  Never: 0,
  Always: 1,
  Default: 2,
} as const;

export type ModelVisibilityState = (typeof ModelVisibilityState)[keyof typeof ModelVisibilityState];

export type UserActivity = 'DESIGN' | 'SOURCE';

interface ImageExportPreferences {
  format?: ImageExportFileFormat;
  target?: 'area' | 'frame';
  megapixels?: number;
  qualityPercent?: number;
  print?: PrintExportPreferences;
}

export interface DailyCreditUsageNotificationPreference {
  enabled?: boolean;
  thresholdCredits?: number | null;
  repeatStepCredits?: number | null;
  timezone?: string | null;
}

interface NotificationPreferences {
  dailyCreditUsageAlert?: DailyCreditUsageNotificationPreference;
}

/** Outcome of the pure daily-credit-usage-alert build (normalize + validate + merge). */
export type DailyCreditUsageAlertBuildResult =
  | { ok: true; notificationPreferences: NonNullable<UserPreferences['notificationPreferences']> }
  | { ok: false; error: string };

/** Outcome the notification panel renders after the Kernel persisted the alert. */
export type DailyCreditUsageAlertSaveResult =
  | { ok: true }
  | { ok: false; error: string };

export type DaimonModel = 'gpt-realtime-2.1' | 'gpt-realtime-1.5';

export type DaimonConversationMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  cards?: Array<{ kind: 'image' | 'document' | 'link'; url: string; title: string; subtitle: string }
    | { kind: 'visual'; visual: 'stores' | 'openings' | 'revenue' | 'brand'; brandId: string; url: string; title: string; subtitle: string }>;
};
/** A film of the Video tool (lib/video/video.server.ts): a conversation whose HyperFrames project lives on the video runner. */
export type VideoFilm = { id: string; name: string; updatedAt: string; readOnly: boolean; cover: string; activity?: 'working' | 'rendering' };
/** One step of a film's agent or render, as the runner records it (scripts/video/video-host.mjs); a later step of the same key replaces it. */
export type VideoStep = {
  seq: number; at: string; key?: string; kind: 'user' | 'start' | 'say' | 'think' | 'plan' | 'tool' | 'end' | 'render' | 'file';
  label: string; status: 'running' | 'success' | 'failed' | 'stopped'; tool?: string; cmd?: string; output?: string; file?: string;
  plan?: Array<{ text: string; done: boolean }>; usd?: number; ms?: number; refs?: string[];
  /** What went with the person's words: the in and out points and the selected clip, shown above their message. */
  context?: { range: { from: number; to: number } | null; selected: string | null };
  /** A call to another module through the agent toolset ("Used Studio · Consumed 12 credits"), and what it charged. */
  module?: string; credits?: number;
};
export type VideoFile = { path: string; size: number; mtime: number };
/**
 * The agent toolset (lib/toolset, docs/specs/agent-toolset.md): what one module offers every other module's agent.
 * A call's result: what it did in words, the credits it settled, and its files (base64) for the caller to keep.
 */
export type AgentToolResult = { module: string; summary: string; credits: number; label?: string; files?: Array<{ name: string; data: string }>; filesDir?: 'assets' | 'refs'; links?: Array<{ kind: 'project'; id: string }> };
/** The conversation a tool call comes from, and the modules already in the chain (a module is never called twice). */
export type AgentToolOrigin = { module: string; conversationId: string; chain: string[] };
/** An image sent with a message to the Video agent, base64 (the browser shrinks it to 2560 px first). */
export type VideoImage = { name: string; data: string };
/** The Video editor's view of a film's root composition (lib/video/composition.ts reads and edits it). */
export type VideoClipKind = 'video' | 'audio' | 'image' | 'scene' | 'text' | 'element' | 'camera';
export type VideoClip = {
  id: string; tag: string; kind: VideoClipKind; start: number; duration: number; track: number;
  /** data-start as written when it is an expression ("intro + 0.5"), resolved above into `start`. */
  startExpr?: string; src?: string; mediaStart: number; volume?: number; text?: string; hidden: boolean;
  /** The lane's name (data-lane), a camera shot's zoom (data-zoom), and whether the film's code animates it from its clip. */
  lane?: string; zoom?: number; scripted?: boolean;
  /** A sound's edge fades in seconds (data-fade-in, data-fade-out), applied by the player and the render. */
  fadeIn?: number; fadeOut?: number;
  /** A name to show for a clip that has no words (data-label, else its title): a camera shot's target. */
  label?: string;
  /** A group such as a chapter (data-group), whose members name it (data-member-of) and move with it. */
  group?: boolean; memberOf?: string;
};
export type VideoComposition = { width: number; height: number; duration: number; clips: VideoClip[] };
export type VideoClipChange = { zoom?: number; start?: number; duration?: number; track?: number; mediaStart?: number; volume?: number; text?: string; hidden?: boolean; fadeIn?: number; fadeOut?: number };
/** Where an edit of the agent's landed: a clip and its place, to go to from the step that made it. */
export type VideoEditLink = { id: string; start: number; track: number; gone?: boolean };
/** One change of the film by the agent, as the timeline replays it: clips born, changed and gone, in visiting order. */
export type VideoAgentEdit = { key: number; born: string[]; changed: string[]; gone: VideoClip[]; targets: VideoEditLink[] };
/** A render's settings (components/video/VideoRenderModal.tsx), validated again by the runner. */
export type VideoRenderOptions = { format: 'mp4' | 'webm' | 'mov' | 'gif'; resolution: 'film' | 'landscape-4k' | 'portrait-4k' | 'square-4k'; quality: 'delivery' | 'standard' | 'draft'; fps: 24 | 25 | 30 | 60 };
/** One thing the person changed by hand on a clip (lib/video/composition.ts clipEdits), told to the agent's next turn. */
export type VideoClipEdit = { id: string; field: string; from: string | null; to: string | null };
export type VideoNewClip = { kind: 'video' | 'audio' | 'image' | 'text'; src?: string; text?: string; start: number; duration: number; track: number; id?: string };
/** The Video tool in the kernel: the films, and the open one with its root composition as last read or saved. */
export type VideoEditorState = {
  films: VideoFilm[]; filmsLoaded: boolean; filmId: string | null; film: VideoFilmState | null;
  html: string | null; htmlMtime: number | null; selectedClipId: string | null; error: string | null;
  /** The in and out points (I and O, as Premiere): the stretch of the film the person points the agent at. */
  range: { from: number; to: number } | null;
};
/** Files (lib/share/share.server.ts): documents shared by link on our own domain, their links and who opened them. */
/** The owner side of Files acts for one user inside one organisation. */
export type ShareCaller = { dbUserId: string; userOrgId: string };
export type ShareDocumentDTO = {
  id: string; name: string; contentType: string; size: number; createdAt: string; ownerName: string;
  /** The owner's own preview of the file (/api/assets), never the link a visitor gets. */
  fileUrl: string; links: number; views: number; lastViewedAt: string | null;
  /** A picture of the file exists for link previews. */
  hasPreview: boolean;
};
export type ShareLinkDTO = {
  id: string; url: string; address: string; name: string; expiresAt: string | null; hasPassword: boolean; requireEmail: boolean; verifyEmail: boolean;
  allowList: string[]; allowDownload: boolean; watermark: boolean; notify: boolean; revoked: boolean; createdAt: string; views: number; lastViewedAt: string | null;
};
export type ShareVisitDTO = {
  id: string; linkId: string; linkName: string; email: string | null; openedAt: string; durationMs: number;
  /** Milliseconds spent on each page, by page number. */
  pages: Record<string, number>; downloaded: boolean;
};
/** A link's settings as the owner sets them; a password left undefined is kept, null removes it. */
export type ShareLinkSettings = {
  name?: string; expiresAt?: string | null; password?: string | null; requireEmail?: boolean; verifyEmail?: boolean;
  /** A custom address (share.fater.ai/deck); absent keeps the current one, empty gives a long random one. */
  address?: string;
  allowList?: string[]; allowDownload?: boolean; watermark?: boolean; notify?: boolean;
};
export type ShareState = {
  documents: ShareDocumentDTO[]; loaded: boolean; documentId: string | null;
  detail: { links: ShareLinkDTO[]; visits: ShareVisitDTO[]; linkBase: string } | null; error: string | null;
};
export type ShareRequest = { action: 'documents' } | { action: 'document'; document: string }
  | { action: 'add'; assetPath: string; name: string; contentType: string; size: number }
  | { action: 'rename'; document: string; name: string } | { action: 'delete'; document: string }
  | { action: 'preview'; document: string; assetPath: string }
  | { action: 'createLink'; document: string; settings: ShareLinkSettings } | { action: 'updateLink'; link: string; settings: ShareLinkSettings }
  | { action: 'revokeLink' | 'deleteLink'; link: string };
export type VideoRequest = { action: 'films' } | { action: 'film'; film: string; since?: number } | { action: 'upload'; film: string; file: File }
  | { action: 'create'; name: string } | { action: 'message'; film: string; text: string; at?: number; selected?: string | null; range?: { from: number; to: number } | null; images?: VideoImage[] }
  | { action: 'stop' | 'delete'; film: string } | { action: 'render'; film: string; options?: VideoRenderOptions }
  | { action: 'read' | 'remove'; film: string; path: string } | { action: 'write'; film: string; path: string; content: string; baseMtime: number | null; edits?: VideoClipEdit[] }
  | { action: 'rename'; film: string; name: string };
/** A film as the editor reads it: its runner state, and the signed base its monitor loads files from (/api/video/files/<token>/). */
export type VideoFilmState = {
  id: string; name: string; readOnly: boolean; fileBase: string; running: boolean; rendering: string | null; pending: number;
  steps: VideoStep[]; files: VideoFile[]; version: number; spentUsd: number; credits: number; lastRender: string | null;
};

/** A Fater 3D session (lib/modelist/modelist.server.ts), kept in its site conversation's working memory. */
export type ModelistRunState = {
  id: string; site: string; brief: string; status: 'running' | 'waiting' | 'stopped' | 'failed';
  startedAt: string; updatedAt: string; steps: number; costUsd: number; credits: number;
  sessionId?: string; model?: string; lastTurn?: string; lastImage?: string;
};
/** One step of a Modelist run, as the conversation's live view shows it. */
export type ModelistStep = {
  runId: string; key: string; kind: 'start' | 'say' | 'think' | 'plan' | 'tool' | 'turn' | 'user' | 'file' | 'error' | 'end' | 'studio'; seq: number; at: string;
  label: string; status: 'running' | 'success' | 'failed'; tool?: string; detail?: string; parent?: string; ms?: number;
  /** A command: what ran, its output's tail and its length in lines. */
  cmd?: string; output?: string; lines?: number;
  /** The Modelist's checklist, as Codex shows its plan. */
  plan?: Array<{ text: string; done: boolean }>;
  /** The element the camera follows (a view framed on it), and the pass a GLB belongs to. */
  focus?: string; pass?: number;
  /** The model's version when the step left (site3d/realise.py, model.json): what its pictures and files show. */
  model?: { version: number; md5: string };
  /** What each view picture of the step was captured of: the model's version and the view as defined then (scripts/modelist/views.mjs, viewPrint). */
  captures?: Record<string, { version: number; print: string; lit?: string }>;
  /** A live construction step (site3d/live.py): the elements of each part (model, interieur, exterieur) that changed or went. */
  live?: { seq: number; upserts: Record<string, string[]>; deletes: Record<string, string[]> };
  images?: Array<{ url: string; name: string }>; files?: Array<{ url: string; name: string; bytes: number }>; u: number;
  /** A user's message: the files it attached, by the names they had when picked, whatever their kind. */
  attached?: string[];
  /** A view handed to Studio: what the Modelist asked for, and the project the app made of it. */
  studio?: { to: 'list' | 'canvas'; name: string; view: string; width: number; height: number };
  project?: { id: string; kind: 'list' | 'canvas'; name: string; listDenied?: boolean };
  /** The credits this step's bill charged (a turn's model use, a generation). */
  credits?: number;
};
/** The 3D generators the user can pick in the 3D chat (lib/modelist/generators.ts lists them). */
export type ModelistGenerator = 'auto' | 'cad' | 'tripo' | 'meshy' | 'hunyuan' | 'trellis' | 'marble';
/** The budget the user sets in the 3D chat for each request: a time in minutes, or credits. */
export type ModelistBudget = { minutes: number } | { credits: number };
/** The 3D chat's budget chip: both presets are kept so switching the unit back finds the last one. */
export type ModelistBudgetChoice = { unit: 'time' | 'credits'; minutes: string; credits: string };
/** readOnly: another user's project, looked at without changing it. */
export type Fater3DSite = { id: string; name: string; updatedAt: string; status: ModelistRunState['status'] | null; cover: string | null; steps: number; credits: number; readOnly?: boolean };
export type Fater3DSiteFilters = { organisationId?: string; userId?: string };
/** A capture or a hand-off to Studio the user asked for, and the last step seen when they asked. `toCanvas`: the capture then lands on the scene's own canvas. */
export type Fater3DViewJob = { kind: 'capture' | 'send'; afterSeq: number; toCanvas?: boolean };
/**
 * Where a site stands on Earth, where north is in its model (degrees clockwise from the model's north axis, Blender
 * +Y and the viewer's -Z, as site3d's calib.json geo.north_deg), and the time zone its dates and hours are read in, an
 * IANA name ("Europe/Paris", "Etc/GMT-7"): any reader finds the same hour from it, wherever it runs.
 */
export type Fater3DPlace = { lat: number; lon: number; north: number; zone: string };
/**
 * How a model is lit, for any reader of it (lib/modelist/light.ts holds the defaults and what follows from the sun's
 * elevation; scripts/modelist/light.mjs its bounds). Two independent choices: what stands behind and around the model,
 * the neutral studio or the real sky, which is drawn from where the sun stands and is no setting of its own; and where
 * the sun stands, by its two angles or by a date and a time over a place. Both pairs are kept whichever is in force,
 * so changing the choice and back loses nothing. The azimuth turns clockwise from the model's north and the elevation
 * rises from the horizon, in degrees, under it too; the date is YYYY-MM-DD and the time the hour of the day, both on
 * the place's clock.
 */
export type Fater3DLight = {
  backdrop: 'studio' | 'sky';
  sun: 'angles' | 'time';
  azimuth: number;
  elevation: number;
  date: string;
  time: number;
};
/**
 * A light as it is saved, on a project or with a view: by date and time it carries the place it stands over, so it is
 * the same sun for whoever reads it; by its angles it carries none.
 * `sunAt` is where its sun stands, resolved, for the readers that have no astronomy (site3d's Blender captures): in
 * the MODEL's frame, in degrees, the azimuth clockwise from the model's north axis (Blender +Y, the viewer's -Z; the
 * place's north is already added) and the elevation above the horizon, negative under it. By its angles it is those
 * two angles. By date and time it is lib/modelist/sun.ts's position, to a thousandth of a degree, the date's hours
 * read on the zone's clock as it stands at local noon of that date: on a day the clock changes, the whole day keeps
 * noon's offset. The app writes it with every save; the server works it out again from the light it parsed and
 * overwrites what the browser sent (modelist.server.ts). A light saved before it existed has none.
 */
export type Fater3DSavedLight = Fater3DLight & { place?: Fater3DPlace; sunAt?: { azimuth: number; elevation: number } };
/**
 * The finish of the 3D space's picture, the viewer's own and never a saved view's (lib/modelist/render.ts holds the
 * defaults and ranges). The sun's intensity and the environment's are shares of the display mode's own; exposure is in
 * stops; haze runs from 0, none, to 1. The effects are all off at their defaults: the occlusion's radius is a share of
 * the model's size, a bloom of 0 is none, and contrast, saturation, vignette and grain are neutral at 0. A shadow
 * softness of 0 is the crisp shadow; reflections come from the sky alone, or from a capture of the scene around the
 * model.
 */
export type Fater3DRender = {
  sunIntensity: number;
  environment: number;
  exposure: number;
  toneMapping: 'neutral' | 'aces' | 'agx';
  shadows: 'off' | 'standard' | 'high';
  shadowSoftness: number;
  ground: boolean;
  haze: number;
  glass: boolean;
  reflections: 'sky' | 'scene';
  ao: boolean;
  aoIntensity: number;
  aoRadius: number;
  bloom: number;
  bloomThreshold: number;
  dof: boolean;
  dofAperture: number;
  edges: boolean;
  sharp: boolean;
  contrast: number;
  saturation: number;
  vignette: number;
  grain: number;
};
/** A reference attached in the Modelist's message box: a photo (preview) or a design file (document, its name). */
export type Fater3DAttachment = { id: string; preview: string; url: string | null; document?: string; name: string; bytes: number };
/**
 * Fater 3D's state (KernelData.fater3d): the user's sites, the open one, its Modelist run and every step of it, as
 * read from app/api/modelist/route.ts. The left chat and the 3D space read the same steps.
 */
export type Fater3DState = {
  /** The module's home (its sites) or one site open in the editor, as every module opens on its dashboard. */
  view: 'dashboard' | 'site';
  sitesLoaded: boolean;
  sites: Fater3DSite[];
  /** The dashboard's organisation and user pills, as the Studio's projects dashboard; unset is no filter. */
  filters: Fater3DSiteFilters;
  /** What is open: a site of the module (sceneOf null) or a Studio project's scene. */
  sceneOf: string | null;
  sessionId: string | null;
  run: ModelistRunState | null;
  steps: Record<string, ModelistStep>;
  /** The server's clock at the last read, ticking a second at a time while the Modelist works. */
  now: number;
  error: string | null;
  /** The 3D generator the Modelist uses for objects it generates rather than models in code. */
  generator: ModelistGenerator;
  /** The site's saved views (site3d/views.json), and those being captured or sent to Studio, since when. */
  views: Record<string, ModelistView>;
  viewJobs: Record<string, Fater3DViewJob>;
  /** The project's place: the one saved on it, else its calibration's with the zone the viewer gave it; null when it has none, or none could be read. */
  place: Fater3DPlace | null;
  /** The place the user moved the sun's to, or that the light of a view carries: null is the project's own. Dropped when another site opens. */
  movedPlace: Fater3DPlace | null;
  /**
   * The light saved as the project's default: a site opens on it, and the Default preset returns to it. Null when the
   * project has none; undefined until the project was read, when nobody knows.
   */
  projectLight: Fater3DSavedLight | null | undefined;
  /** Whether the user named the moved place's zone in this session: it then stays wherever the place is moved. Read only while a place is moved. */
  zoneNamed: boolean;
  /**
   * A save of the project's light or place the project has not shown yet: sent, and awaited from a read. In queue
   * mode the runner writes it later; a runner from before the save existed never does.
   */
  unconfirmed: { light?: Fater3DSavedLight; place?: Fater3DPlace };
  /** Whether the light is settled for the open site: its project's default was applied, or the user set a light or a place first. The default is never put over either. */
  landed: boolean;
  /** In the store, not the composer, so the Daimon's read_project sees what is attached before she sends. */
  attachments: Fater3DAttachment[];
  /**
   * The message a canvas workspace prepared for its project's scene (Powers > Rebuild in 3D): the start of the request
   * and its picture, null while it is made. That scene's chat delivers both, once the scene is on screen (sceneOpen).
   */
  prepared: { id: string; projectId: string; text: string; attachment: Fater3DAttachment | null; sceneOpen: boolean } | null;
  /** Where a project's scene draws its right panel: the Portal's right column, while it holds the scene, shown or kept under Studio's panel. */
  panelHost: HTMLElement | null;
  /** The light in force: a draft over the project's default or a view's, as the camera is over a view. */
  light: Fater3DLight;
  /** Kept for the session, across sites and scenes. */
  render: Fater3DRender;
};
/** A group made or exploded by hand in the 3D space, which the runner keeps (site3d/manual.py). */
export type ModelistStructureChange = { group: string; members: string[] } | { explode: string };
/** Fater 3D's requests (app/api/modelist/route.ts). */
export type ModelistRequest =
  | { action: 'sites'; organisationId?: string; userId?: string }
  | { action: 'read'; sessionId: string; since?: number }
  | { action: 'scene'; projectId: string }
  | { action: 'start'; sessionId?: string | null; projectId?: string; brief: string; model?: string; images?: string[]; names?: string[]; generator?: string; budget?: ModelistBudget }
  | { action: 'message'; sessionId: string; text: string; model?: string; images?: string[]; names?: string[]; generator?: string; budget?: ModelistBudget }
  | { action: 'stop'; sessionId: string }
  | { action: 'rename'; sessionId: string; name: string }
  | { action: 'saveEdit'; sessionId: string; name: string; data: string }
  | { action: 'saveStructure'; sessionId: string; structure: ModelistStructureChange }
  | { action: 'views'; sessionId: string }
  | { action: 'saveView'; sessionId: string; name: string; view: ModelistView; rename?: string }
  | ({ action: 'street'; sessionId: string } & StreetRequest)
  | { action: 'saveLight'; sessionId: string; light?: Fater3DSavedLight; place?: Fater3DPlace }
  | { action: 'removeView' | 'exportModel'; sessionId: string; name: string }
  // `place`: the project's place as the 3D space read it, over which the server works out the default light of a view in a project that has none.
  | { action: 'captureView'; sessionId: string; name: string; place?: Fater3DPlace }
  | { action: 'validate' | 'restore'; sessionId: string }
  | { action: 'sendView'; sessionId: string; name: string; to: 'list' | 'canvas'; projectName?: string; place?: Fater3DPlace };
/** A saved camera of a 3D site (site3d/views.py): metres, Blender axes (Z up, north +Y). */
/** A Street View point of a Fater 3D site (lib/modelist/streetview.ts): where the person stands, the way they first
 * face, and the 360° photograph (equirectangular) made there from the model. */
/** pano is the photograph; while `state` is 'making' it is still the render the server is working from. A point with
 * no pano is drawn from `workspaceId`, a workspace of the scene's own project's canvas, as that workspace is now. */
export type StreetPoint = { id: string; name: string; feet: [number, number, number]; yaw: number; pano?: string; workspaceId?: string; flat?: string; depth?: string; at?: string; state?: 'making' | 'failed'; stage?: string; message?: string; world?: StreetWorld };
/** A walkable World Labs world made from a point's photograph: its splats and collider, set upright by scale and ground. */
export type StreetWorld = { state?: 'making' | 'failed'; stage?: string; message?: string; at?: string; spz?: string; collider?: string; scale?: number; ground?: number;
  /** While making: World Labs' job and our billing entry, so a server restart can pick the world up again. */
  operationId?: string; logId?: string; startedAt?: string };
/** A latitude band of a canvas workspace point's 360° (lib/modelist/streetview.ts STREET_BANDS), latitudes in degrees,
 * the lower first: its pixels as the scene captured them, overlap included, or its picture as the workspace exports it. */
export type StreetBandPixels = { name: string; lat: [number, number]; pixels: ImageData };
/** `seam`: degrees of longitude the band's left edge was moved east of -180 (lib/modelist/projectScene.ts setStreetSeam). */
export type StreetBandPicture = { lat: [number, number]; blob: Blob; seam: number };
/** A Street View workspace's band layer, kept in its params: its band, its tile left to right, the workspace's seam and its Develop 360 cuts. */
export type StreetBandMark = { band: 'sky' | 'ground' | 'horizon'; tile: number; seam: number; cuts?: StreetCuts };
/**
 * Develop 360's cuts of a Street View panorama, remembered on its band layers (lib/modelist/streetDevelop.ts): `at` the
 * cuts as fractions of the frame's width, sorted inside (0, 1); `notes` one per slice left to right.
 */
export type StreetCuts = { at: number[]; notes: string[] };
/** Street View of a site: its points, a new one's 360° render made a photograph, a point kept or dropped. */
export type StreetRequest = { op: 'list' } | { op: 'place'; point: StreetPoint; note?: string } | { op: 'world'; id: string } | { op: 'save'; point: StreetPoint } | { op: 'remove'; id: string };
export type StreetAnswer = { ok: boolean; points?: StreetPoint[]; pano?: string; message?: string };
export type ModelistView = {
  eye: [number, number, number]; target: [number, number, number]; projection: 'perspective' | 'parallel';
  lens?: number; width?: number; style: 'realistic' | 'clay' | 'white' | 'lines'; size: string; samples?: number;
  /** A cut shown with the view (Blender metres, Z up): a plan `plan` metres above the ground, a vertical section
   * through `point` keeping the side `normal` faces. A plan or a section is a view, as in Revit. */
  cut?: { plan?: number; section?: { point: [number, number, number]; normal: [number, number, number] } };
  /** The workspace the view last became on its Studio project's canvas. */
  workspaceId?: string;
  /** The light the view was saved under; a view from before lights has none, and leaves the light alone. */
  light?: Fater3DSavedLight;
  /** The conversation's last step when the view was created or renamed: a picture from before is another view's of that name. */
  since?: number;
  /** The conversation's last step when the user last changed what the view shows: a picture from before may be of the view as it was. */
  edited?: number;
};
export type DaimonConversationRequest = {
  action: 'current' | 'history' | 'subject' | 'select' | 'lookup' | 'create' | 'adopt' | 'append' | 'usage';
  sessionId?: string; name?: string; activeId?: string | null; explicitNew?: boolean;
  subject?: { entityType: string; entityId: string; name?: string } | null;
  role?: 'user' | 'assistant' | 'tool'; text?: string; cards?: DaimonConversationMessage['cards'];
  eventKey?: string; responseId?: string; usage?: unknown; billingToken?: string;
};

export interface UserPreferences {
  /** Personal merchandising plans, keyed by plan id. */
  pulsePlans?: Record<string, PulsePlan>;
  projectOutputSignals?: Record<string, Record<string, { favorite?: boolean; feedback?: 'good' | 'bad' }>>;
  slidesWelcomeVersion?: string | null;
  acknowledgedNoticeIds?: string[];
  assistantGlobalPrompt?: string;
  spectacleModeDefault?: boolean;
  performanceHudEnabled?: boolean;
  // Fater 3D: a moving camera draws the space at half resolution, sharp again once it rests. Unset means off.
  fater3dLighterWhileMoving?: boolean;
  disabledCommands?: string[];
  lockSelectedModelForAgent?: boolean;
  /** The model the lock pins, restored on load (the selected model itself is not persisted). */
  lockedStudioModel?: string;
  shortcuts?: { [key: string]: string };
  imageVariationCount?: number;
  workspaceVersionPreviewFocusTopVisibleLayerRect?: boolean;
  // Studio canvas toolbar: 'ALL' reveals mask, shapes and comment. Unset means
  // 'ESSENTIAL', so no default has to be seeded in the Kernel store for existing accounts.
  studioToolbarTools?: 'ESSENTIAL' | 'ALL';
  assistantModel?: string;
  // Default collaboration mode for new chat sessions (overrides the org default).
  // Unset resolves to AUTONOMOUS so the agent keeps momentum instead of pausing.
  defaultCollaborationMode?: CollaborationMode;
  // Source composer mode picker (Manual / Accept edits / Auto), remembered per user so the choice
  // survives closing a shortlist. It is NOT a second source of truth: the session still stores the
  // decision in its `collaborationMode`, and this only says which mode a Source session opens on.
  // Deliberately separate from `defaultCollaborationMode`, which would drag Studio along with it.
  sourceAgentMode?: SourceAgentMode;
  /**
   * The emoji chosen for a Source page, keyed by campaign id (or 'dashboard').
   *
   * A user preference rather than a column on the campaign: it needs no migration on a production
   * database, and it persists - unlike Doctrine's picker, whose choice lives in memory and is gone
   * on reload. The trade is that the icon is yours, not the team's; making it shared means a write
   * path to the campaign record.
   */
  sourcePageEmojis?: Record<string, string>;
  // Optional per-tool default agent model. When set for a tool, that tool uses
  // this model instead of the global assistantModel. See AGENT_TOOL_KEYS.
  toolDefaultModels?: ToolDefaultModels;
  // Composer model selector: most-recently-used unified model ids, most recent first.
  recentAssistantModelIds?: string[];
  modelVisibility?: { [modelId: string]: typeof ModelVisibilityState.Never | typeof ModelVisibilityState.Always };
  autoPromoteOnGenerate?: boolean;
  language?: 'ENGLISH' | 'FRENCH' | null;
  appearanceTheme?: 'DARK' | 'LIGHT' | 'SYSTEM' | null;
  commanderOrganisationId?: string | null;
  actSilently?: boolean;
  lastImageExportSettings?: ImageExportPreferences;
  toastMode?: 'error-only' | 'debug' | 'silent';
  // List composer: AUTO lets a short agent step add the @ mentions a plain request means; MANUAL sends as typed.
  listAgentMode?: 'AUTO' | 'MANUAL';
  /** Fater 3D: how much the trackpad's swipe, pinch and twist glide, 0 (instant) to 1. */
  fater3dFluidity?: number;
  // The Designer brain the next Studio turn of any conversation runs on; unset means legacy.
  designerBrain?: DesignerBrainId;
  // The jnn brain's knobs, each read through resolveDesignerJnnSettings (lib/designer/jnn/settings.ts).
  designerJnn?: Partial<DesignerJnnSettings>;
  // Canvas auto-integrate: Harmonize after Align. Unset means off.
  autoColorMatch?: boolean;
  /** How free the Daimon is to act: MANUAL asks before any change, with the price of a paid one;
   *  ACCEPT_EDITS (default) and AUTO spend without asking; deleting always asks.
   *  Enforced in components/daimon/DaimonVoice.tsx. */
  daimonAgentMode?: 'MANUAL' | 'ACCEPT_EDITS' | 'AUTO';
  /** Voice model for new web and phone calls; unset defaults to GPT-Realtime-2.1. */
  daimonVoiceModel?: DaimonModel;
  /** The language she speaks and hears; unset or 'auto' follows the user's last words. */
  daimonLanguage?: 'auto' | 'fr' | 'en';
  /** Her character, in the user's words (lib/daimon/personality.ts); unset keeps the default. */
  daimonPersonality?: string;
  /** Server-owned selection shared by Daimon web and phone connections. */
  daimonCurrentConversationId?: string;
  // List commands the user chose 'Always allow' for in the permission prompt.
  listAlwaysAllow?: ListPermissionRequest['action'][];
  activities?: UserActivity[];
  notificationPreferences?: NotificationPreferences;
  // Source › Portfolios: the portfolio ids this user chooses to see. Admins (incl. Fater admins)
  // set this to stop mixing assets from different portfolio owners. Empty/unset → the active-client
  // default. Only constrains Fater members; portfolio-scoped client users are already isolated.
  visiblePortfolioIds?: string[] | null;
  // Master sessions already put through the one-time forced re-titling pass. Per user, not per
  // browser: a forced re-title overwrites a manual rename, so replaying it on another machine
  // would destroy the user's own titles. Grows without bound on purpose, see Portal.tsx.
  retitledMasterSessionIds?: string[];
  // Source › asset picker: the property ids this user worked on last, most recent first, capped
  // at 5. Per user, not per browser, so their centres follow them across machines. Undefined
  // until preferences hydrate; the picker then falls back to alphabetical only.
  sourceRecentAssetIds?: string[];
  // Master sessions this user pinned to the top of the left bar. Per user, not per browser:
  // pinning is a decision about a conversation, and the conversation itself follows the user
  // to any machine. Undefined until preferences hydrate; the Pinned section is empty until then.
  pinnedMasterSessionIds?: string[];
}

export type AgentVersionContext = {
  id: string;
  status: 'VALIDATED' | 'CANDIDATE' | 'REJECTED';
  parentId: string | null;
  rejectionReason?: string | null;
  generativeContext?: {
    model: string;
    prompt: string;
    params: DiffusionParams | null;
  } | null;
};

export type AgentWorkspaceContext = {
  id: string;
  name: string;
  activeVersionId: string | null;
  validatedVersionId: string | null;
  versions: AgentVersionContext[];
};

export type AgentProjectContext = {
  generationRect: Rect;
  activeWorkspaceId: string;
  projectId: string;
  projectName: string;
  projectType?: ProjectType;
  /** Persistent Canvas/List surface captured for prompt and command presentation. */
  studioViewMode?: StudioViewMode;
  listOutputs?: {
    loadedCount: number;
    omittedCount: number;
    focusedOutputId: string | null;
    latestRequest: Array<{ taskId: string; outputIds: string[]; state: 'Succeeded' | 'Unconfirmed' | 'Failed' | 'Pending' | 'Unknown'; failureCode: string | null; failureDetails?: ImageGenerationFailureDetails | null; attempt: number | null; attemptLimit: number | null }>;
    outputs: Array<{
      id: string; name: string; kind: 'image' | 'video'; timestamp: number;
      prompt: string | null; model: string | null; feedback: 'good' | 'bad' | null;
      clientTaskId: string | null; generationTaskId: string | null;
      inspectionRequested: boolean;
      personas?: ListOutputPersonaAssociation[];
      objects?: ListOutputObjectAssociation[];
      archetypes?: ListOutputArchetypeAssociation[];
    }>;
    tasks: Array<{ id: string; chatSessionId: string | null; model: string; prompt: string | null; state: string; startedAt: number | null }>;
    omittedTaskCount: number;
  };
  collaborationMode?: CollaborationMode;
  workspaces: AgentWorkspaceContext[];
  disabledModels?: string[];
  selectableModels?: string[];
  // Number of Mask-tool regions currently painted on the ACTIVE workspace. Lets the agent know
  // whether masked inpainting (ideogram-inpaint) can run, or whether to ask the user to paint a mask.
  activeWorkspaceInpaintMaskRegions?: number;
};


export interface LlmTurnImage {
  url: string;
  label: string;
}

/**
 * Agent error pipeline (docs/specs/agent-error-pipeline.md): a runtime error
 * observed in the user's session, mounted to the agent every turn until
 * resolved or escalated. Lives in kernelStores.agentErrorLedger (session ring
 * buffer, capped).
 */
export interface AgentObservedError {
  id: string;
  /** Epoch ms of first occurrence. */
  ts: number;
  source: 'kernel' | 'window' | 'promise';
  message: string;
  detail?: string;
  status: 'NEW' | 'REPORTED' | 'RESOLVED' | 'ESCALATED';
  /** Occurrences folded into this entry (same message within the dedup window). */
  count: number;
}

/** Compact per-turn report of observed errors sent to the agent. */
export interface AgentRuntimeErrorReport {
  id: string;
  source: AgentObservedError['source'];
  message: string;
  count: number;
  ageSeconds: number;
  status: AgentObservedError['status'];
}

/** An OPEN agent-created support ticket, the agent's unresolved-issue memory. */
export interface AgentKnownIssue {
  displayNumber: string;
  title: string;
  description: string | null;
  createdAt: string;
}

export interface LlmTurnVideo {
  url: string;
  label: string;
  mimeType?: string;
  layerId?: string;
  durationMs?: number | null;
}

export interface ScriptDefinition {
  id?: string;
  label: string;
  commands: Array<Omit<UiCommand, 'id' | 'status' | 'error'>>;
  autorun?: boolean;
  autoRecall?: boolean;
  listGenerationProposal?: { brief: string };
  listGenerationAgreement?: ListGenerationAgreement;
  listReferenceContinuation?: Pick<ListReferenceContinuation, 'userMessageId'> | ListReferenceContinuation;
  listCreativeReferences?: Pick<ListCreativeReference, 'kind' | 'id'>[];
  listSceneSource?: 'current-message' | 'conversation';
  listCreativePlan?: ListCreativeGenerationPlan;
  /** True for scripts authored by an AI agent; false for deterministic app-authored scripts. */
  isAgentGenerated?: boolean;
}

export interface ListCreativeGenerationPlan {
  sessionId: string;
  userId: string;
  projectId: string;
  revision: number;
  rootUserMessageId: string;
  references: Pick<ListCreativeReference, 'kind' | 'id'>[];
  resourceRevisions: Pick<ListCreativeResource, 'kind' | 'id' | 'revision'>[];
  commands: ScriptDefinition['commands'];
}

export interface ListReferenceContinuation {
  userMessageId: string;
  rootUserMessageId: string;
  rootTimestamp: string;
  projectId: string;
  sessionId: string;
  userId: string;
  currentPrompt: string;
  previousPrompt: string;
  outputId: string;
}

export interface ListGenerationAgreement {
  brief: string;
  projectId: string;
  sessionId: string;
  userId: string;
  proposal: boolean;
  consumed?: boolean;
  creativeContext?: { revision: number; resources: Pick<ListCreativeResource, 'kind' | 'id' | 'revision'>[] };
}


export interface ErrorPayload {
  failedCommandAction: string;
  failedCommandParams: Record<string, any>;
  errorMessage: string;
  scriptId: string;
  commandId: string;
  sessionId?: string;
  workspaceDrift?: {
    startedIn: { id: string; name: string };
    failedIn: { id: string; name: string };
  };
}

export interface ScriptInstance {
  id: string;
  label: string;
  commands: UiCommand[];
  autorun: boolean;
  autoRecall?: boolean;
  listGenerationAgreement?: ListGenerationAgreement;
  listReferenceContinuation?: ListReferenceContinuation;
  listCreativeReferences?: Pick<ListCreativeReference, 'kind' | 'id'>[];
  listSceneSource?: 'current-message' | 'conversation';
  listCreativePlan?: ListCreativeGenerationPlan;
  /** Captured at the Studio turn boundary, never inferred from the current tab during execution. */
  studioViewMode?: StudioViewMode;
  executeCount?: number;
  /** True for scripts authored by an AI agent; false for deterministic app-authored scripts. */
  isAgentGenerated?: boolean;
  isManuallyInvoked?: boolean; // for avoiding assistant loop error attempts
}

export interface UiCommand {
  id: string;
  action_type: string;
  parameters: Record<string, any>;
  description?: string;
  status?: 'pending' | 'running' | 'success' | 'failed';
  error?: string;
  /** Short result summary shown below the command description (e.g. "Queried 12 leads") */
  resultSummary?: string;
}

interface UiCommandParameterInfo {
  name: string;
  type: string;
  description?: string;
  required: boolean;
  example?: any;
  options?: string[];
}

type CommandPrerequisiteEntityType = EntityType | 'DocumentTemplate';
export type CommandTargetEntityType = CommandPrerequisiteEntityType | 'Project' | 'ABSOLUTE';

interface CommandPrerequisite {
  entityType: CommandPrerequisiteEntityType;
  instruction: string;
}

export interface UiCommandInfo { // This structure is sent from frontend as `available_commands`
  action_type: string;
  description: string;
  parameters: UiCommandParameterInfo[];
  availableInModes?: AgentMode[];
  /**
   * Primary target for this command. Must be a single entity type.
   * Use `ABSOLUTE` for cross-cutting commands (e.g. QUERY/MUTATE/WEB_SEARCH).
   */
  targetEntityType: CommandTargetEntityType;
  prerequisites?: CommandPrerequisite[];
  isLocked?: boolean;
  unlockHint?: string;
}



export interface UndoableAction {
  id: string;
  description: string;
  /** The chat session the action edits: deleting the session purges it from both stacks. */
  chatSessionId?: string;
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
}

export type FluxTwoProResolution = 'match_input_image' | '0.5 MP' | '1 MP' | '2 MP' | '4 MP';
export type FluxTwoProAspectRatio = 'match_input_image' | '1:1' | '16:9' | '3:2' | '2:3' | '4:5' | '5:4' | '9:16' | '3:4' | '4:3';

export interface FluxTwoProParams extends BaseParams {
  resolution: FluxTwoProResolution;
  aspect_ratio?: FluxTwoProAspectRatio;
  add_annotations_as_ref?: boolean;
}

export interface Kling3Params extends BaseParams {
  negative_prompt: string;
  duration: number;
  resolution: '720p' | '1080p';
  generate_audio: boolean;
}

export interface Kling3OmniParams extends BaseParams {
  negative_prompt: string;
  duration: number;
  resolution: '720p' | '1080p';
  generate_audio: boolean;
  keep_original_sound: boolean;
  video_reference_type: 'feature' | 'base';
  reference_input_mode?: VideoReferenceInputMode;
}

export type Seedance2Resolution = '480p' | '720p' | '1080p' | '4k';
export type VideoReferenceInputMode = 'frame' | 'video';
export type Seedance2ReferenceInputMode = VideoReferenceInputMode;
export type Seedance2AspectRatio = 'auto' | '21:9' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16';
export type GeminiOmniFlashReferenceInputMode = VideoReferenceInputMode;
export type GeminiOmniFlashAspectRatio = 'auto' | '16:9' | '9:16';

export interface Seedance2Params extends BaseParams {
  duration: number;
  resolution: Seedance2Resolution;
  generate_audio: boolean;
  reference_input_mode?: Seedance2ReferenceInputMode;
  aspect_ratio?: Seedance2AspectRatio;
}

export interface GeminiOmniFlashParams extends BaseParams {
  duration: number;
  reference_input_mode?: GeminiOmniFlashReferenceInputMode;
  aspect_ratio?: GeminiOmniFlashAspectRatio;
  use_audio_input?: boolean;
}

// --- Cheap/fast validation-tier video models (all fal) ---------------------
// Deployed below the expensive Seedance/Kling tier as the loop's cheap motion
// validators. Prompting chapters live in lib/prompt/knowledgeBase.ts; provider
// dispatch in app/api/film/route.ts. `aspect_ratio: 'auto'` is resolved from the
// source frame app-side (same precedent as Seedance), never trust the provider.
type WanVideoResolution = '480p' | '580p' | '720p';
type WanVideoAspectRatio = 'auto' | '16:9' | '9:16' | '1:1' | '4:3' | '3:4';

// fal-ai/wan/v2.2-5b/text-to-video/fast-wan, cheapest text-to-video sketch.
export interface WanFastT2VParams extends BaseParams {
  duration: number; // seconds; app maps to num_frames at 24fps (clamped 17-161)
  resolution: WanVideoResolution;
  negative_prompt?: string;
  aspect_ratio?: WanVideoAspectRatio;
}

// fal-ai/wan/v2.2-a14b/image-to-video, 720p i2v with optional last-frame keyframe.
export interface WanI2VParams extends BaseParams {
  duration: number; // seconds; app maps to num_frames at 24fps
  resolution: WanVideoResolution;
  negative_prompt?: string;
  aspect_ratio?: WanVideoAspectRatio; // 'auto' resolved from the source frame app-side
}

// fal-ai/minimax/hailuo-02-fast/image-to-video, cheapest 512p motion validator.
// Fixed 512p (no resolution param); duration is the string enum "6" | "10".
export interface HailuoFastParams extends BaseParams {
  duration: number; // 6 or 10 seconds (sent to fal as a string enum)
}

// The 2026-08/09 fal video wave (lib/latest-video-models.ts): text- or image-to-video, one shape.
export interface LatestVideoParams extends BaseParams {
  duration: number;
  resolution: string;
  aspect_ratio?: string;
  generate_audio?: boolean;
  /** Seamless loop, on models that make one (Luma Ray 3.2). */
  loop?: boolean;
}

// --- SOTA fast tier (all fal, all image-to-video) --------------------------
// Durations are stored numeric here and converted to each endpoint's string enum
// in app/api/film/route.ts. i2v models animate the current workspace frame.

// fal-ai/veo3.1/fast/image-to-video, Google Veo 3.1 Fast (up to 4K, native audio).
type VeoResolution = '720p' | '1080p' | '4k';
type VeoAspectRatio = 'auto' | '16:9' | '9:16';
export interface Veo31FastParams extends BaseParams {
  duration: number; // 4 | 6 | 8 seconds (sent as "4s"/"6s"/"8s")
  resolution: VeoResolution;
  aspect_ratio?: VeoAspectRatio;
  negative_prompt?: string;
  generate_audio?: boolean;
}

// fal-ai/kling-video/v2.5-turbo/pro/image-to-video, Kling 2.5 Turbo Pro (tail keyframe).
type Kling25AspectRatio = '16:9' | '9:16' | '1:1';
export interface Kling25TurboParams extends BaseParams {
  duration: number; // 5 | 10 seconds (sent as "5"/"10")
  aspect_ratio?: Kling25AspectRatio;
  negative_prompt?: string;
  cfg_scale?: number;
}

// blackforestlabs/flux-3/*, Black Forest Labs FLUX.3 video (audio, end-frame, draft tier).
// The route picks image-to-video or first-last-frame-to-video from the attached end frame,
// and the /draft suffix from the model id.
export type Flux3Resolution = '720p' | '1080p';
export type Flux3AspectRatio = 'auto' | '21:9' | '2:1' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16';
export interface Flux3Params extends BaseParams {
  duration: number; // 5..20 seconds
  resolution: Flux3Resolution;
  aspect_ratio?: Flux3AspectRatio;
  generate_audio?: boolean;
}

// fal-ai/ltx-2.3/image-to-video/fast, Lightricks LTX-2.3 Fast (audio, end-frame).
type LtxResolution = '1080p' | '1440p' | '2160p';
type LtxAspectRatio = 'auto' | '16:9' | '9:16';
export interface Ltx23FastParams extends BaseParams {
  duration: number; // 6..20 seconds
  resolution: LtxResolution;
  aspect_ratio?: LtxAspectRatio;
  fps?: number; // 24 | 25 | 48 | 50
  generate_audio?: boolean;
}

// fal-ai/luma-dream-machine/ray-2-flash/image-to-video, Luma Ray 2 Flash (loop, end-frame).
type LumaResolution = '540p' | '720p' | '1080p';
type LumaAspectRatio = '16:9' | '9:16' | '4:3' | '3:4' | '21:9' | '9:21';
export interface LumaRayFlashParams extends BaseParams {
  duration: number; // 5 | 9 seconds (sent as "5s"/"9s")
  resolution: LumaResolution;
  aspect_ratio?: LumaAspectRatio;
  loop?: boolean;
}

export type TopazVideoUpscaleModel =
  | 'Proteus'
  | 'Artemis HQ'
  | 'Artemis MQ'
  | 'Artemis LQ'
  | 'Nyx'
  | 'Nyx Fast'
  | 'Nyx XL'
  | 'Nyx HF'
  | 'Gaia HQ'
  | 'Gaia CG'
  | 'Gaia 2'
  | 'Starlight Precise 1'
  | 'Starlight Precise 2'
  | 'Starlight Precise 2.5'
  | 'Starlight HQ'
  | 'Starlight Mini'
  | 'Starlight Sharp'
  | 'Starlight Fast 1'
  | 'Starlight Fast 2';

export interface TopazVideoUpscaleParams extends BaseParams {
  model: TopazVideoUpscaleModel;
  upscale_factor: number;
  target_fps?: number;
  compression?: number;
  noise?: number;
  halo?: number;
  grain?: number;
  recover_detail?: number;
  H264_output?: boolean;
}

export interface MireloSfxParams extends BaseParams {
  prompt: string;
}

export type MagnificVideoUpscaleResolution = '720p' | '1k' | '2k' | '4k';
export type MagnificVideoUpscaleFlavor = 'vivid' | 'natural';

export interface MagnificVideoUpscaleParams extends BaseParams {
  resolution: MagnificVideoUpscaleResolution;
  creativity: number;
  fps_boost: boolean;
  sharpen: number;
  smart_grain: number;
  flavor: MagnificVideoUpscaleFlavor;
}

export type MagnificImageUpscaleFlavor = 'photo' | 'photo_denoiser' | 'sublime';

export interface MagnificImageUpscaleParams extends BaseParams {
  scale_factor: number;
  sharpen: number;
  smart_grain: number;
  ultra_detail: number;
  flavor: MagnificImageUpscaleFlavor;
  filter_nsfw: boolean;
}

export type SeedVR2OutputMegaPixels = 4 | 8 | 12 | 16 | 20 | 24 | 28 | 32 | 36 | 40;
export type SeedVR2InputMegaPixels = 'auto' | 0.5 | 1 | 2 | 3 | 4 | 5 | 10 | 16;

export interface SeedVR2Params extends BaseParams {
  outputMegaPixels: SeedVR2OutputMegaPixels;
  inputMegaPixels: SeedVR2InputMegaPixels;
  /** Effective model-only factor sent to ComfyUI; GenerationTask.upscaleFactor remains the Studio merge factor. */
  upscale_factor?: number;
}

export type QwenImage21Resolution = '0.5 MP' | '1 MP' | '2 MP' | '3 MP' | '4 MP';

export type QwenImage21BorderLock = { left: number; top: number; right: number; bottom: number };

export interface QwenImage21Params extends BaseParams {
  resolution: QwenImage21Resolution;
  steps: number;
  cfg?: number;
  negativePrompt?: string;
  /** First sigma of the schedule, 0.9-1: below 1 the run starts from the noised source instead of pure noise. */
  noiseLevel?: number;
  cfgOneLastSteps?: number;
  /** Derived by the kernel from the Generation Area, never set by a user or an agent. */
  borderLock?: QwenImage21BorderLock;
  /** Derived by the kernel from the layers' pixel density, never set by a user or an agent. */
  referenceScale?: number;
}

export type TopazImageEnhanceModel =
  | 'Low Resolution V2'
  | 'Standard V2'
  | 'CGI'
  | 'High Fidelity V2'
  | 'Text Refine'
  | 'Recovery'
  | 'Redefine'
  | 'Recovery V2'
  | 'Standard MAX'
  | 'Wonder'
  | 'Wonder 3'
  // Legacy aliases kept for backward compatibility with saved params.
  | 'High Fidelity V3'
  | 'Text and Shapes'
  | 'Art and CG';

type TopazImageEnhanceOutputFormat = 'jpeg' | 'png' | 'tiff';
type TopazImageSubjectDetection = 'All' | 'Foreground' | 'Background';
type TopazImageEnhancementStrength = 'low' | 'medium' | 'high';

export type TopazImageInputMegaPixels = SeedVR2InputMegaPixels;
export type TopazImageOutputMegaPixels = SeedVR2OutputMegaPixels | 30;

export interface TopazImageEnhanceParams extends BaseParams {
  topazModel: TopazImageEnhanceModel;
  inputMegaPixels: TopazImageInputMegaPixels;
  outputMegaPixels: TopazImageOutputMegaPixels;
  topazOutputMegaPixels?: number;
  topazOutputFormat: TopazImageEnhanceOutputFormat;
  // fal-ai/topaz/upscale/image-native fields (optional, with legacy compatibility)
  model?: TopazImageEnhanceModel;
  /** Legacy/provider field; the kernel derives this from inputMegaPixels/outputMegaPixels. */
  upscale_factor?: number;
  /** Actual rendered input dimensions used to keep route normalization aligned with the kernel. */
  topazInputWidth?: number;
  topazInputHeight?: number;
  crop_to_fill?: boolean;
  image_url?: string;
  output_format?: 'jpeg' | 'png';
  subject_detection?: TopazImageSubjectDetection;
  face_enhancement?: boolean;
  face_enhancement_creativity?: number;
  face_enhancement_strength?: number;
  sharpen?: number;
  denoise?: number;
  fix_compression?: number;
  strength?: number;
  creativity?: number;
  texture?: number;
  autoprompt?: boolean;
  detail?: number;
  enhancement_strength?: TopazImageEnhancementStrength;
}

export type NanoBananaAspectRatio = 'auto' | '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9';

type NanoBanana2AspectRatio = NanoBananaAspectRatio | '1:4' | '4:1' | '1:8' | '8:1';

export interface NanoBananaParams extends BaseParams {
  aspect_ratio?: NanoBananaAspectRatio;
  add_annotations_as_ref?: boolean;
}

export type NanoBananaImageSize = '1K' | '2K' | '4K';

export interface NanoBananaProParams extends NanoBananaParams {
  imageSize: NanoBananaImageSize;
  enable_google_search: boolean;
  add_annotations_as_ref?: boolean;
}

export interface NanoBanana2Params extends Omit<NanoBananaProParams, 'aspect_ratio'> {
  aspect_ratio?: NanoBanana2AspectRatio;
}

export interface QwenImage2ProParams extends BaseParams {
  negative_prompt: string;
  add_annotations_as_ref?: boolean;
}

/** fal prices Qwen Image 3 on two resolution tiers; the app derives the shape from the Generation Area. */
export type QwenImage3Resolution = '1K' | '2K';

export interface QwenImage3Params extends BaseParams {
  negative_prompt: string;
  resolution: QwenImage3Resolution;
  add_annotations_as_ref?: boolean;
}

export type GptImage2Params = BaseParams;
export type GptImage25Mode = 'sunburst' | 'flare';
export type GptImage25Quality = 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export interface GptImage25Params {
  prompt: string;
  mode: GptImage25Mode;
  quality: GptImage25Quality;
}
export type Mai25FlashParams = BaseParams;

type QwenMultiangleImageSize =
  | 'square_hd'
  | 'square'
  | 'portrait_4_3'
  | 'portrait_16_9'
  | 'landscape_4_3'
  | 'landscape_16_9'
  | { width: number; height: number };

export interface QwenEditMultiangleParams extends BaseParams {
  lora_scale: number;
  guidance_scale: number;
  zoom: number;
  horizontal_angle: number;
  vertical_angle: number;
  num_inference_steps: number;
  negative_prompt?: string;
  image_size?: QwenMultiangleImageSize;
  acceleration?: 'none' | 'regular';
  enable_safety_checker?: boolean;
  output_format?: 'png' | 'jpeg' | 'webp';
  num_images?: number;
  sync_mode?: boolean;
  add_annotations_as_ref?: boolean;
}

export interface Seedream4_5Params extends BaseParams {
  outputMegaPixels: number;
  mask_alpha_radius: number;
  add_annotations_as_ref?: boolean;
}

type GrokImagineAspectRatio =
  | '2:1'
  | '20:9'
  | '19.5:9'
  | '16:9'
  | '4:3'
  | '3:2'
  | '1:1'
  | '2:3'
  | '3:4'
  | '9:16'
  | '9:19.5'
  | '9:20'
  | '1:2';

type GrokImagineOutputFormat = 'jpeg' | 'png' | 'webp';

export interface GrokImagineParams extends BaseParams {
  num_images: number;
  aspect_ratio: GrokImagineAspectRatio;
  output_format: GrokImagineOutputFormat;
  sync_mode: boolean;
}

export type GrokImagine2Resolution = '1K' | '2K';
/** 'auto' is accepted by the edit endpoint only, where it preserves the first input image shape. */
export type GrokImagine2AspectRatio = GrokImagineAspectRatio | 'auto';

export interface GrokImagine2Params extends BaseParams {
  resolution: GrokImagine2Resolution;
  aspect_ratio: GrokImagine2AspectRatio;
}

export interface MuseImageParams extends BaseParams {
  /** "width:height", derived by the kernel at generation; never 'auto'. */
  aspect_ratio?: string;
}

/** Equal-pixel-area tiers per BFL: the dimensions follow the aspect ratio, none is forced square. */
export type Flux3ImageResolution = '512sq' | '768sq' | '1k' | '2k' | '4k';
export type Flux3ImageAspectRatio =
  | 'auto' | '21:9' | '2:1' | '16:9' | '3:2' | '7:5' | '4:3' | '5:4'
  | '1:1' | '4:5' | '3:4' | '5:7' | '2:3' | '9:16' | '1:2';

export interface Flux3ImageParams extends BaseParams {
  resolution: Flux3ImageResolution;
  /** Derived by the kernel at generation; an explicit preset survives only in List. */
  aspect_ratio?: Flux3ImageAspectRatio;
}

export type Flux3KleinResolution = '1k' | '2k' | '4k';
export interface Flux3KleinParams extends BaseParams {
  resolution: Flux3KleinResolution;
  /** Written by the generation route from the frame and the tier, so the snapshot records the size sent. */
  image_size?: { width: number; height: number };
}

// Ideogram 4.0 text-to-image or maskless full-area reimagine.
// With a source image, use Remix v4; without one, use Generate v4.
export type IdeogramMagicPrompt = 'AUTO' | 'ON' | 'OFF';
export type IdeogramRenderingSpeed = 'FLASH' | 'TURBO' | 'DEFAULT' | 'QUALITY';
export type IdeogramStyleType = 'AUTO' | 'GENERAL' | 'REALISTIC' | 'DESIGN' | 'FICTION';
export type IdeogramV4Resolution =
  | 'AUTO'
  | '2048x2048'
  | '1440x2880'
  | '2880x1440'
  | '1664x2496'
  | '2496x1664'
  | '1792x2240'
  | '2240x1792'
  | '1440x2560'
  | '2560x1440'
  | '1600x2560'
  | '2560x1600'
  | '1728x2304'
  | '2304x1728'
  | '1296x3168'
  | '3168x1296'
  | '1152x2944'
  | '2944x1152'
  | '1248x3328'
  | '3328x1248'
  | '1024x3072'
  | '3072x1024'
  | '1280x3072'
  | '3072x1280';

export interface IdeogramEditParams extends BaseParams {
  image_weight: number; // 1-100, default 50: how strongly the source image is preserved
  resolution: IdeogramV4Resolution;
  rendering_speed: IdeogramRenderingSpeed;
  enable_copyright_detection?: boolean;
  // Legacy v3 fields kept for saved params/backward compatibility. They are not sent to v4 endpoints.
  magic_prompt?: IdeogramMagicPrompt;
  style_type?: IdeogramStyleType;
  negative_prompt?: string;
  num_images?: number;
}

// Ideogram masked inpainting via Inpaint v3 (POST https://api.ideogram.ai/v1/ideogram-v3/inpaint).
// Requires a source image + a B/W mask (black = region to regenerate). No image_weight / negative_prompt.
export interface IdeogramInpaintParams extends BaseParams {
  magic_prompt: IdeogramMagicPrompt;
  rendering_speed: IdeogramRenderingSpeed;
  style_type: IdeogramStyleType;
  num_images?: number;
}

// Ideogram 3 text-to-image via Generate v3 (POST https://api.ideogram.ai/v1/ideogram-v3/generate).
// Pure prompt-driven generation (no source image). aspect_ratio uses Ideogram's WxH format.
export type IdeogramAspectRatio =
  | '1x1'
  | '16x9'
  | '9x16'
  | '4x3'
  | '3x4'
  | '3x2'
  | '2x3';

export interface IdeogramGenerateParams extends BaseParams {
  magic_prompt: IdeogramMagicPrompt;
  rendering_speed: IdeogramRenderingSpeed;
  style_type: IdeogramStyleType;
  negative_prompt?: string;
  num_images?: number;
  aspect_ratio: IdeogramAspectRatio;
}

// Studio Vector's text-to-vector models (lib/vector-models.ts). Colours are #rrggbb; the artboard
// sets the size, so there is no aspect ratio here.
export interface RecraftVectorParams extends BaseParams {
  colors?: string[];
  background_color?: string | null;
}
export type ArrowReasoningEffort = 'low' | 'medium' | 'high' | 'xhigh';
export interface ArrowVectorParams extends BaseParams {
  instructions?: string;
  colors?: string[];
  reasoning_effort?: ArrowReasoningEffort;
}
export type IdeogramVectorQuality = 'low' | 'medium' | 'high';
export interface IdeogramVectorParams extends BaseParams {
  colors?: string[];
  quality?: IdeogramVectorQuality;
}

// Union type for DiffusionParams
export type DiffusionParams =
  | FluxTwoProParams
  | Kling3Params
  | Kling3OmniParams
  | Seedance2Params
  | GeminiOmniFlashParams
  | TopazVideoUpscaleParams
  | MireloSfxParams
  | MagnificVideoUpscaleParams
  | MagnificImageUpscaleParams
  | SeedVR2Params
  | QwenImage21Params
  | TopazImageEnhanceParams
  | NanoBananaParams
  | NanoBananaProParams
  | NanoBanana2Params
  | Seedream4_5Params
  | QwenImage2ProParams
  | QwenImage3Params
  | RecraftVectorParams
  | ArrowVectorParams
  | IdeogramVectorParams
  | GptImage2Params
  | GptImage25Params
  | Mai25FlashParams
  | QwenEditMultiangleParams
  | GrokImagineParams
  | GrokImagine2Params
  | MuseImageParams
  | Flux3ImageParams
  | Flux3KleinParams
  | IdeogramEditParams
  | IdeogramInpaintParams
  | IdeogramGenerateParams
  | WanFastT2VParams
  | WanI2VParams
  | HailuoFastParams
  | LatestVideoParams
  | Veo31FastParams
  | Kling25TurboParams
  | Ltx23FastParams
  | Flux3Params
  | LumaRayFlashParams;

// --- API Request Body Types for /api/projects ---

// We create a base type for common properties
interface ProjectApiRequestBodyBase {
  action: string;
  projectId?: string;
}

export interface LoadMetadataRequestBody extends ProjectApiRequestBodyBase {
  action: 'loadMetadata';
  projectId: string;
  ownerId?: string;
}

export interface AssistRequestBody {
  prompt: string;
  /** Explicit false allows Director continuation context; absent flags never inherit selections. */
  isUserTurn?: boolean;
  situation: string;
  lastScriptError?: ErrorPayload;
  /** Runtime errors observed in the user's session (agent error pipeline). */
  runtimeErrors?: AgentRuntimeErrorReport[];
  progressiveStepRetryCount?: number;
  systemContextImages?: LlmTurnImage[];
  systemContextVideos?: LlmTurnVideo[];
  userAttachedImages?: LlmTurnImage[];
  available_commands: UiCommandInfo[];
  chatHistory?: Message[];
  projectContext: AgentProjectContext;
  userPreferences: UserPreferences;
  actionHistory?: UserAction[];
  mode?: AgentMode;
  strategicPlan?: string | null;
  chatSessionContext?: DealerChatSessionContext | null;
  /** Director mode: the browser's id of the legacy Designer turn, which holds the project until the browser releases it (lib/designer/billing.server.ts). */
  designerTurnId?: string;
  /** Slides mode: active guideline of the focused deck (drives the guideline chapter). */
  slidesGuidelineKey?: string;
  slidesResearchIntent?: boolean;
  /** Binds a review turn/continuation to its original run, including before dispatch. */
  slidesReviewRunId?: string;
  /**
   * This turn is a REACTION, not an answer: the user clicked, created or finished something and
   * the agent is responding to it. Nobody typed a question, so nobody is waiting to be reasoned
   * at - they are waiting to see the screen acknowledge what they just did.
   *
   * The server pins the instant model for these.
   */
  reactionTurn?: boolean;
}

export interface GenerateOriginalConceptsRequestBody {
  prompt: string;
  ideasCount: number;
  generateMultiplier?: number;
  sessionId?: string;
  bypassJudge?: boolean;
}

export interface GenerateOriginalConceptsResponse {
  ideas: string[];
  results?: Array<{
    combinationId: string;
    score: number | null;
    concept: string;
  }>;
  costSummary?: {
    totalCostUsd: number;
    totalCallCount: number;
    perModel: Array<{
      model: string;
      calls: number;
      costUsd: number;
      inputTokens: number;
      outputTokens: number;
      cachedInputTokens: number;
      uncachedInputTokens: number;
    }>;
  };
  pipelineDetails?: {
    steps: Array<{
      step: string;
      durationMs: number;
      info?: Record<string, unknown>;
    }>;
    agents: Array<{
      agent: string;
      status: 'success' | 'fallback' | 'bypassed';
      model?: string;
      attempts?: number;
      triedModels?: string[];
      categoryId?: string;
      batchIndex?: number;
      outputCount?: number;
      error?: string;
      structuredOutput?: unknown;
      rawOutput?: string;
    }>;
  };
}

export interface WebSearchRequestBody {
  query: string;
  freshness?: 'oneDay' | 'oneWeek' | 'oneMonth' | 'oneYear';
  summary?: boolean;
}

export interface AssistantTurnDebugInfo {
  turnId: string;
  requestBody: AssistRequestBody;
  response?: any;
}

export type CommandHandler = (
  params: Record<string, any>,
  kernel: RefObject<KernelHandle | null>,
  options: {
    workspaceId: string;
    projectId?: string;
    studioViewMode?: StudioViewMode;
    isHeadless: boolean;
    sessionId: string;
    nextCommandId?: string | null;
    abortSignal?: AbortSignal;
    isAgentGenerated?: boolean;
    claimListRetryAllowance?: () => number;
    listRetrySourceTaskId?: string | null;
    allowListNoImageRecovery?: boolean;
    listGenerationCohort?: Set<string>;
    listReferenceCache?: Map<string, Promise<LockableFile>>;
    numOutputsOverride?: number;
  }
) => Promise<string | void>; // Can return the new workspace ID if context switches

/**
 * Per-command labels surfaced by the CommandTrace timeline block when it
 * summarises a run ("queried 3 entities, wrote 1 entity, launched 1
 * campaign"). Commands that omit this fall back to the generic "ran N
 * commands" counter.
 *
 * Keeping this on the command definition (instead of a hard-coded map
 * inside `Chat.tsx`) means whoever adds a new command finds the label
 * next to the `action_type` they're declaring, not buried in a rendering
 * component.
 */
interface CommandTraceLabels {
  /** Verb when the command is currently running, e.g. `querying`. */
  active: string;
  /** Verb when the command has finished, e.g. `queried`. */
  done: string;
  /** Singular unit label, e.g. `entity`, `contact`, `document`. */
  unit: string;
  /**
   * Optional explicit plural. When absent, the renderer appends `s`
   * (`entity` → `entitys` is wrong, so pass `entities` explicitly).
   */
  pluralUnit?: string;
  /**
   * The unit of the work this ONE command sets off, when that work outlives the command.
   *
   * The trace counts commands, which is right for a query and wrong for a command that hands a job
   * to the worker: EXTEND_SHORTLIST returns in a second and reads "Extended 1 shortlist" for the
   * two minutes the run actually takes. Naming the unit `brand` instead would only move the lie -
   * one command would read "Added 1 brand" having queued ten - so the count has to come from the
   * run, not from the command. Declaring this asks the trace to find the campaign this command
   * started and read its live progress: "extending 3 of 5 brands", counting up.
   */
  progressUnit?: string;
}

export interface UiCommandDefinition extends UiCommandInfo {
  handler: CommandHandler;
  /** Spends no credit and destroys nothing: the Daimon runs it without asking, even in Manual. */
  costsNothing?: boolean;
  onDisabled?: CommandHandler; // Optional: Runs if the command is disabled.
  headlessCompatible?: boolean; // False means it uses the global UI and isn't compatible with background agentic work
  evaluateLockState?: (context: {
    kernel: RefObject<KernelHandle | null>;
    activeSubject: ActiveEntityFocus | null;
    anchors: ContextAnchor[];
    activeProjectId?: string | null;
  }) => { isLocked: boolean; unlockHint: string } | null;
  /** See `CommandTraceLabels`. Optional; absent commands roll into a
   *  generic "ran N commands" counter. */
  traceLabels?: CommandTraceLabels;
}

export interface useAudioManagerReturnType {
  addAssistantMessage: ( content: string, scriptsInstances?: ScriptInstance[], agentName?: string, imageUrls?: string[], thoughts?: string ) => Promise<void>;
  playUISound: (effect: SoundEffect, options?: { volume?: number; durationMs?: number; loop?: boolean }) => void;
  stopUISound: (effect: SoundEffect) => void;
  playSpecificAudioUrl: (audioUrl: string) => Promise<void>;
  generateAudioForContent: (content: string, messageTimestamp: string, playImmediately?: boolean) => Promise<void>;
  playAdhocAlert: (alertText: string) => Promise<void>;
}

export type SocketEventHandler = (payload: unknown) => void;

export interface SocketManagerReturnType {
  connectSocket: () => void;
  disconnectSocket: () => void;
  getSocketConnectionId: () => string | null;
  socketRef?: RefObject<any>;
}

export type DealerExecutionOptions = {
  slidesProposalRunId?: string;
  slidesReviewRunId?: string;
  /** Explicit action feedback; does not grant user-typed command authorization. */
  showThinking?: boolean;
  skipUserMessage?: boolean;
  attachedImageUrls?: string[];
  ignoreScripts?: boolean;
  /** This turn is the agent waking itself after a turn the APP started, so it inherits that turn's
   *  depth instead of counting as a fresh user request. Set only by triggerReEvaluation. */
  continuesUnpromptedTurn?: boolean;
  /** A person typed `prompt` into the composer for this turn, so a billed command may check it and
   *  find they asked. Set only by askAssistant, from its own `skipUserMessage`. Absent everywhere
   *  else, which is what makes a turn the app started unable to spend the user's credits. */
  userTypedThePrompt?: boolean;
  /** This turn continues the one before it, whoever started that one: the agent woke itself to read
   *  what a command returned. Distinct from `continuesUnpromptedTurn`, which is only true inside an
   *  APP-started chain; a continuation of something a person typed sets this and not that. Set only
   *  by triggerReEvaluation, the single choke point every continuation goes through. */
  continuesTurn?: boolean;
  /** How many times THIS turn already failed and re-ran itself (provider error, dead stream).
   *  Set only by the retry path in _executeDealerTurn; capped there. The task at hand must not
   *  die with one failed LLM call (SRC-034). */
  retryAttempt?: number;
};

/** Payload carried by the 'DetachedStateClarification' modal: the turn to resume once resolved. */
export type DetachedStateClarificationPayload = {
  prompt: string;
  imageUrls?: string[];
  onScriptComplete?: (finalWorkspaceId: string) => Promise<void>;
  workspaceId: string;
  historicalVersionId: string;
};

/** Payload carried by the 'Confirmation' modal: Cancel closes it, the action runs only on confirm. */
export type ConfirmationModalPayload = {
  title: string;
  message?: string;
  confirmLabel: string;
  /** The typed value when the dialog asks for one (input). */
  onConfirm: (value: string) => void;
  /** A field to fill (a name): its starting text and an optional label; Enter confirms, an empty value cannot. */
  input?: { defaultValue: string; label?: string };
  /** 'primary' for an ordinary action (the white pill); destructive by default (soft red). */
  tone?: 'primary' | 'danger';
};

export interface useAgentsReturnType {
  askAssistant: (
    sessionId: string,
    prompt: string,
    imageUrls?: string[],
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>,
    options?: {
      skipDetachedCheck?: boolean;
      skipUserMessage?: boolean;
      /** The message is already in the transcript (queued) but a person typed it. */
      queuedByUser?: boolean;
      showThinking?: boolean;
      slidesReview?: 'start' | 'restart';
      slidesProposal?: boolean;
      ignoreScripts?: boolean;
      targetWorkspaceId?: string;
      agentMode: AgentMode;
    }
  ) => Promise<void>;

  /** Resolves the detached-version question raised by askAssistant, then resumes the pending turn. */
  resolveDetachedState: (
    mode: 'latest' | 'promote',
    payload: DetachedStateClarificationPayload
  ) => void;

  askDealer: (
    sessionId: string,
    prompt: string, 
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>
  ) => Promise<void>;

  executeSystemDealerTurn: (
    sessionId: string,
    prompt: string,
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>,
    options?: DealerExecutionOptions
  ) => Promise<void>;

  generateOriginalConcepts: (
    prompt: string,
    ideasCount: number,
    options?: {
      sessionId?: string;
      generateMultiplier?: number;
      bypassJudge?: boolean;
      abortSignal?: AbortSignal;
    }
  ) => Promise<GenerateOriginalConceptsResponse>;
  
  triggerReEvaluation: (workspaceId: string, errorPayload?: ErrorPayload, targetSessionId?: string) => Promise<void>;
}

export interface useWorkspacesReturnType {
  getProjectUIWorkspaceId: (projectId: string) => string;
  setProjectUIWorkspaceId: (projectId: string, workspaceId: string) => void;
  setWorkspaceAnimatedGenerationRect: (
    workspaceId: string,
    updater: Rect | null | ((previous: Rect | null) => Rect | null)
  ) => void;
  loadWorkspace: (
    workspaceInput: unknown
  ) => {
    workspace: Workspace;
  };
  persistWorkspaceCreate: (workspace: Workspace, context: string) => Promise<void>;
  handleAddWorkspace: (projectId: string, options?: {
    frameSize?: { width: number; height: number };
    name?: string;
    focus?: boolean;
    zoomToFit?: boolean;
    logSource?: string;
    /** Given the workspace's CREATE, which settles once its row exists on the server. */
    onPersisted?: (written: Promise<void>) => void;
  }) => string | undefined;
  renameWorkspace: (workspaceId: string, newName: string) => void;
  deleteWorkspace: (projectId: string, workspaceId: string) => void;
  toggleWorkspaceExpanded: (workspaceId: string) => void;
  toggleWorkspaceLock: (workspaceId: string) => void;
  toggleWorkspaceVisibility: (workspaceId: string) => void;
  switchAndEnsureWorkspace: (projectId: string, workspaceName: string) => Promise<string | undefined>;
  moveLayersToWorkspace: (projectId: string, layerNamesOrIds: string[], targetWorkspaceId: string) => Promise<void>;
  duplicateWorkspace: (projectId: string, workspaceIdToDuplicate: string, newWorkspaceNames: string[], options?: { merge?: boolean, switchToNew?: boolean}) => Promise<string | null>;
  fitWorkspaceFrameToContent: (projectId: string, workspaceId: string, handleType: ResizeHandleType) => Promise<void>;
  fitWorkspaceFrameToGenerationArea: (projectId: string, workspaceId: string) => void;
  forceFrameRatioOnContent: (projectId: string, workspaceId: string, ratio: number) => Promise<void>;
  calculateNewRectForResize: (currentState: CanvasResizeState, currentPosition: Rect, currentAspectRatio: number | null, frameRect: Rect) => Rect;
  expandWorkspaceFrame: (projectId: string, workspaceId: string, direction: 'top' | 'right' | 'bottom' | 'left', amount: number) => void;
  setWorkspaceAspectRatio: (projectId: string, workspaceId: string, ratio: AspectRatio) => void;
  setWorkspaceFrameRect: (workspaceId: string, newRect: Rect, oldRectOverride?: Rect) => void;
  clearWorkspaceAnnotations: (workspaceId: string) => void;
}

export interface useLayersReturnType {
  loadLayer: (
    layerInput: unknown
  ) => {
    layer: Layer;
  };
  updateLayerVisibility: (layerId: string) => void;
  /** Hides, in one undo step, the visible layers whose hiding changes neither the render nor what any layer still shown reads beneath itself; returns their ids. */
  hideBuriedLayers: (workspaceId: string) => Promise<string[]>;
  updateLayerLock: (layerId: string) => void;
  removeLayer: (layerId: string) => void;
  updateLayerName: (layerId: string, newName: string) => void;
  selectLayer: (projectId: string, layerId: string, isShiftClick: boolean) => void;
  reorderLayer: (projectId: string, draggedId: string, targetId: string, dropOnUpperHalf: boolean) => void;
  copySelectedLayersToClipboard: (projectId: string) => void;
  restoreLayerConfig: (layerId: string) => void;
  downloadLayer: (layerId: string) => void;
  duplicateLayer: (layerId: string) => void;
  addLayerFromFile: (workspaceId: string, file: File, partialLayer?: Partial<Layer>) => Promise<Layer>;
  addLayerFromUrl: (workspaceId: string, imageUrl: string, partialLayer?: Partial<Layer>) => Promise<Layer>;
  addLayerFromImageData: (workspaceId: string | null | undefined, imageData: ImageData, partialLayer?: Partial<Layer>) => Layer;
  addSvgLayer: (
    workspaceId: string,
    intrinsicSize: { width: number; height: number },
    partialLayer?: Partial<Layer>
  ) => Layer;
  mergeLayerDown: (layerId: string) => Promise<void>;
  mergeLayersCanvas: (layerA: Layer, layerB: Layer) => Promise<Layer>;
  mergeGeneratedLayerIntoAnchorLayer: (
    generatedLayer: Layer,
    anchorLayerId: string,
    options?: {
      description?: string;
      isUndoable?: boolean;
      params?: Layer['params'];
    }
  ) => Promise<Layer | null>;
  /** Draws a grid tile into a fixed-size section anchor, creating the anchor on first use. */
  paintLayerIntoGridSection: (
    tileLayer: Layer,
    target: { workspaceId: string; section: GridSection; layerId: string | null; params: Layer['params']; name: string }
  ) => Promise<Layer | null>;
  getCombinedVisibleContentBoundingBox: (workspaceId: string) => Promise<Rect | null>;
  calculateUnionRectOfLayers: (layersToConsider: Layer[]) => Promise<{ unionRect: Rect; contributingLayers: Layer[] } | null>;
  duplicateLayers: (workspaceId: string, layerNamesOrIds: string[]) => Promise<void>;
  updateSelectedLayers: (workspaceId: string, layerNamesOrIds: string[], mode: 'REPLACE' | 'ADD' | 'REMOVE') => Promise<void>;
  getLayersInWorkspaceContext: (workspaceId: string | null | undefined) => Layer[];
  listLayersByProjectId: (projectId: string) => Layer[];
  attachLayerToWorkspace: (workspaceId: string, layerId: string) => void;
  detachLayerFromWorkspace: (workspaceId: string, layerId: string) => void;
  bumpLayerPulseToken: (layerId: string) => void;
  alignLayerToUnderlying: (layerToAlign: Layer) => Promise<void>;
  extractChangesFromUnderlying: (layerToDenoise: Layer, isUndoable?: boolean, underlyingLayersOverride?: Layer[]) => Promise<Layer | void>;
  harmonizeLayerColor: (layerToCorrect: Layer, isUndoable?: boolean, harmonizeProfile?: 'soft' | 'strict') => Promise<Layer | void>;
  harmonizeLayerUnchanged: (layerToCorrect: Layer) => Promise<void>;
  integrateLayer: (layer: Layer) => Promise<void>;
  integrateGeneratedLayer: (layer: Layer, underlyingLayersOverride: Layer[], harmonize: boolean) => Promise<Layer | null>;
  blendLayer: (layerToBlend: Layer, fadeRadius?: number, isUndoable?: boolean) => Promise<Layer | void>;
  cutLayer: (layer: Layer, options?: { zoomToLayer?: boolean }) => Promise<void>;
  startNativeLayerCut: (layer: Layer, options?: { zoomToLayer?: boolean }) => Promise<void>;
  applyNativeLayerCut: (
    layerId: string,
    polygons: CropPoint[][],
    options: NativeLayerCutOptions,
  ) => Promise<boolean>;
  cancelNativeLayerCut: () => void;
  reloadLayerAsset: (layerId: string) => void;
  hydrateProjectLayerPreviews: (
    projectId: string,
    layerIds?: string[],
    options?: { retry?: boolean },
  ) => void;
  hydrateVisibleLayers: (projectId: string) => void;
}

export interface useVersionsReturnType {
  ensureCurrentStateAsVersion: (
    workspaceId: string,
    reason?: string,
    options?: { lightweight?: boolean; outputScope?: GenerationOutputScope; skipBuriedLayerCleanup?: boolean }
  ) => Promise<string | null>;
  ensureWorkspaceVersionAfterCompositionCommit: (
    workspaceId: string,
    reason?: string,
    options?: { outputScope?: GenerationOutputScope }
  ) => Promise<string | null>;
  ensureWritableAnnotationVersion: (workspaceId: string) => Promise<string | null>;
  commitWorkspaceAnnotationMask: (workspaceId: string, mask: ImageData) => Promise<string | null>;
  syncActiveWorkspaceVersionFromLayerVisibility: (workspaceId: string | null | undefined) => string | null;
  getActiveWorkspaceVersionId: (workspaceId: string) => string | null;
  saveWorkspaceVersionAnnotation: (
    workspaceId: string,
    versionId?: string | null,
    options?: { signal?: AbortSignal }
  ) => Promise<{
    annotationUploadAttempted: boolean;
    annotationUploadSucceeded: boolean;
  }>;
  applyWorkspaceVersion: (workspaceId: string, versionId: string) => void;
  deleteWorkspaceVersion: (workspaceId: string, versionId: string) => void;
  deleteWorkspaceVersions: (
    workspaceId: string,
    versionIds: string[],
  ) => { deletedIds: string[]; skippedIds: string[] };
  navigateWorkspaceVersion: (direction: 'up' | 'down' | 'left' | 'right', workspaceId: string) => void;
  promoteVersion: (workspaceId: string, versionIdToPromote: string) => void;
  getVersionsForDisplay: (workspaceId: string, filterMode: 'PRIMARY' | 'ALL') => WorkspaceVersion[];
  rejectWorkspaceVersions: (workspaceId: string, versionIds: string[], reason?: string | null) => void;
  invalidateWorkspaceVersionCache: (workspaceId: string | null | undefined) => void;
  updateVersionCache: (workspaceId: string, versionId: string, newImageUrl: string) => void;
  regenerateVersionVisuals: (workspaceId: string, versionId: string) => Promise<{ imageUrl: string }>;
  
  addCompletedGenerationAsVersion: (
    workspaceId: string,
    versionData: Pick<WorkspaceVersion, 'name' | 'layerVisibility' | 'parentId'>,
    options?: {
      newLayerForRendering?: Layer;
      targetPixels?: number;
      assetSource?: CloudAssetSource;
      fallbackImageUrl?: string | null;
      abortSignal?: AbortSignal;
      outputScope?: GenerationOutputScope;
    }
  ) => Promise<WorkspaceVersion | null>;

  getOrRenderVersionImage: (
    workspaceId: string,
    versionId: string,
    options?: { targetPixels?: number; assetSource?: CloudAssetSource; rect?: Rect }
  ) => Promise<string | null>;

  toggleVersionFavorite: (workspaceId: string, versionId: string) => void;
}

interface useCommandsReturnType {
  runGeneratedScriptLogic: (
    workspaceId: string,
    scriptToRun: ScriptInstance,
    onComplete?: (finalWorkspaceId: string) => Promise<void>,
    options?: {
      startingCommandId?: string;
      singleExecution?: boolean;
      isHeadless?: boolean;
      sessionId?: string;
      claimListRetryAllowance?: () => number;
      listRetrySourceTaskId?: string | null;
      contextOverrides?: {
        idResolutionMap?: Record<string, string>;
      };
    }
  ) => Promise<void>;
  parseSlashCommand: (input: string) => { commandType: string; params: Record<string, any>; description: string } | null;
  execute: (promptToExecute: string, imageUrls: string[] | undefined, options: { agentMode: AgentMode; projectId: string }) => Promise<void>;
  executeSlashCommandDirectly: (commandString: string, projectId: string) => Promise<void>;
  refreshFactoryDirection: (workspaceId: string, replaceMasterPrompt?: boolean) => Promise<Workspace['cameraState']>;
}

export interface useGenerationTasksReturnType {
  nameListOutputs: (projectId: string, layerIds: string[], signal?: AbortSignal) => Promise<void>;
  pendingTaskPromisesRef: RefObject<Map<string, PendingTaskPromise>>;
  waitForTask: (taskId: string) => Promise<TaskCompletionData>;
  reportTaskSuccess: (taskId: string, resultData?: unknown) => void;
  reportTaskFailure: (taskId: string, error: any) => void;
  onTaskOutcome: (taskId: string, status: 'SUCCESS' | 'FAILURE', data?: unknown, error?: any) => void;
  getGenerationTasks: (projectId: string) => GenerationTask[];
  setGenerationTasks: (projectId: string, updater: SetStateAction<GenerationTask[]>) => void;
  cancelGenerationTasksForSession: (sessionId: string) => void;
  handleGenerationTaskStatusSocketEvent: (payload: unknown) => void;
  /**
   * Recover long-running generation results (mainly video) that were finalized
   * server-side but whose terminal Socket.IO event was lost while the socket was
   * absent. Queries the server for the given tasks (defaults to all tasks the
   * client is still waiting on) and resolves any terminal ones. Called on socket
   * reconnect and when a new socket waiter is registered.
   */
  reconcilePendingGenerationTasks: (taskIds?: string[], restoreList?: { projectId: string; chatSessionId: string; signal?: AbortSignal }) => Promise<void>;
  dismissFailedGenerationTask: (projectId: string, taskId: string) => Promise<void>;
  processTasks: (tasks: GenerationTask[], options?: { abortSignal?: AbortSignal }) => Promise<void>;
  /** Runs a grid script, then releases its anchor's skipRender whatever the outcome. */
  runGridBatch: (workspaceId: string, script: ScriptInstance, batchId: string) => Promise<void>;
  /** Relaunches only the tiles an interrupted grid batch is missing, into the same anchor layer. */
  resumeGridBatch: (layerId: string) => Promise<void>;
}

/** Studio "Filter" engine: workspace-composite filtering, preview, commit and undo/redo. */
export interface UseStudioDevelopReturnType {
  /** Records the value a slider starts from, so a commit knows whether anything moved. */
  beginStudioDevelopSettingEdit: (projectId: string, workspaceId: string, key: keyof StudioDevelopSettings) => void;
  /** Live slider drag: updates the session settings without touching the composite. */
  changeStudioDevelopSetting: (projectId: string, workspaceId: string, key: keyof StudioDevelopSettings, value: number) => void;
  /** Slider release: commits the setting as an undoable filter application. */
  commitStudioDevelopSetting: (projectId: string, workspaceId: string, key: keyof StudioDevelopSettings, value: number) => void;
  /** Renders the workspace composite at full resolution and commits the filtered layer. */
  commitStudioDevelopSettings: (
    projectId: string,
    workspaceId: string,
    settings: StudioDevelopSettings,
    sourceLabel: string
  ) => Promise<void>;
  /** Debounced preview render. Returns the canceller for the pending render. */
  scheduleStudioDevelopPreview: (
    projectId: string,
    workspaceId: string,
    settings: StudioDevelopSettings,
    previewInputKey: string
  ) => () => void;
  /** Undoable restore of the workspace to its pre-filter baseline. */
  revertStudioDevelopFilters: (projectId: string, workspaceId: string) => void;
  closeStudioDevelopSession: (projectId: string, workspaceId: string) => void;
  /**
   * Releases the full-resolution worker, scheduled snapshots, preview object URLs and, above all,
   * the baseline retained for that workspace: the engine state must not outlive the panel session
   * that created it, or the next one inherits a stale composite.
   */
  teardownStudioDevelop: (projectId: string, workspaceId: string) => void;
}

export interface UseWorkspaceTimelineReturnType {
  getWorkspaceTimelineSummary: (workspaceId: string) => WorkspaceTimelineSummary | null;
  getWorkspaceTimelineSnapshot: (workspaceId: string) => WorkspaceTimelineSnapshot | null;
  /** DOM-free, agent-compatible canonical timeline projection. */
  getWorkspaceTimelineProjection: (projectId: string, workspaceId: string) => WorkspaceTimelineProjection | null;
  /** DOM-free, agent-compatible resolved track list. */
  getWorkspaceTimelineResolvedTracks: (projectId: string, workspaceId: string) => WorkspaceTimelineResolvedTrackView[];
  getWorkspaceTimelineTrack: (layerId: string) => WorkspaceTimelineTrackView | null;
  /** DOM-free, agent-compatible track lookup. */
  getWorkspaceTimelineTrackById: (layerId: string, workspaceId?: string, projectId?: string) => WorkspaceTimelineTrackView | null;
  /** DOM-free, agent-compatible selected track lookup. */
  getWorkspaceTimelineSelectedTracks: (projectId: string, workspaceId: string) => WorkspaceTimelineResolvedTrackView[];
  /** UI-specific pointer resolver: consumes layout/clientX data, not agent-safe. */
  resolveWorkspaceTimelineTrackAtClientX: <TTrack extends WorkspaceTimelinePointerTrackLike>(args: WorkspaceTimelinePointerResolveArgs<TTrack>) => TTrack | null;
  /** UI-specific pointer resolver: consumes layout/clientX data, not agent-safe. */
  resolveWorkspaceTimelineSplitMsAtClientX: <TTrack extends WorkspaceTimelineSplitTrackLike>(args: WorkspaceTimelineSplitResolveArgs<TTrack>) => number | null;
  listWorkspaceTimelineTrackIds: (workspaceId: string) => string[];
  setWorkspaceTimelineCursor: (
    projectId: string,
    workspaceId: string,
    cursorMs: number,
    options?: { minDeltaMs?: number }
  ) => void;
  toggleWorkspaceTimelineView: (projectId: string, workspaceId: string) => void;
  ensureWorkspaceImageTimelineInitialized: (projectId: string, workspaceId: string) => void;
  updateTimelineTrackTiming: (
    layerId: string,
    patch: { startMs?: number; endMs?: number | null; sourceInMs?: number },
    options?: { undoable?: boolean; description?: string }
  ) => void;
  updateTimelineTrackAudio: (
    layerId: string,
    patch: {
      mutedInTimeline?: boolean;
      audioOnly?: boolean;
      audioGain?: number;
      separatedFromLayerId?: string | null;
      fadeInRatio?: number | null;
      fadeOutRatio?: number | null;
    },
    options?: { undoable?: boolean; description?: string }
  ) => void;
  /** Toggles a fade on a visual track: off when already set, default ratio otherwise. Audio tracks are ignored. */
  toggleTimelineTrackFade: (layerId: string, effect: 'fade-in' | 'fade-out') => void;
  updateTimelineTrackLane: (
    layerId: string,
    patch: { laneId?: string | null; laneOrder?: number | null },
    options?: { undoable?: boolean; description?: string }
  ) => void;
  reorderWorkspaceTimelineTracks: (
    projectId: string,
    workspaceId: string,
    orderedTrackLayerIds: string[],
    options?: { undoable?: boolean; description?: string }
  ) => void;
  splitWorkspaceTimelineTrack: (projectId: string, workspaceId: string, layerId: string, splitMs: number) => void;
  duplicateWorkspaceTimelineTrack: (projectId: string, workspaceId: string, layerId: string) => void;
  packWorkspaceTimelineTracks: (projectId: string, workspaceId: string, layerIds: string[]) => void;
  uncompactWorkspaceTimelineTrack: (layerId: string) => void;
  removePendingTimelineGenerationTask: (projectId: string, taskId: string) => void;
  /** Composes the timeline tracks of a workspace into a single video file (prepare -> wait -> fetch). */
  composeWorkspaceTimelineVideo: (
    params: WorkspaceTimelineVideoCompositionParams
  ) => Promise<{ blob: Blob; cacheKey: string }>;
}

export interface WorkspaceTimelineVideoCompositionParams {
  workspaceId: string;
  startMs?: number;
  endMs?: number;
  outputWidth: number;
  outputHeight: number;
  fps: number;
  outputFormat?: 'mp4' | 'mov';
  qualityPreset?: 'draft' | 'balanced' | 'high';
  includeAudio?: boolean;
  signal?: AbortSignal;
  tracks: TimelineTrackInput[];
  /** Progress feed of the backend composition, for callers that display it. */
  onStatus?: (status: TimelineCompositionStatus) => void;
}

export interface useModelsReturnType {
  getLocalNumOutputs: (projectId: string) => number;
  setLocalNumOutputs: (projectId: string, updater: SetStateAction<number>) => void;
  getExtraFiles: (projectId: string) => LockableFile[];
  setExtraFiles: (projectId: string, updater: SetStateAction<LockableFile[]>) => void;
  getAudioReferenceFiles: (projectId: string) => LockableFile[];
  setAudioReferenceFiles: (projectId: string, updater: SetStateAction<LockableFile[]>) => void;
  getVideoLastImages: (projectId: string) => LockableFile[];
  setVideoLastImages: (projectId: string, updater: SetStateAction<LockableFile[]>) => void;
  getModelParameterValue: (modelId: string, parameterName: string) => any | undefined;
  setModelParameter: (modelId: string, parameterName: string, value: any, skipEventLogging? : boolean) => Promise<void>;
  quickEnhance: (workspaceId: string, options?: { preserveHighRes?: boolean; abortSignal?: AbortSignal }) => Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }>;
  quickUpscale: (workspaceId: string, options?: { preserveHighRes?: boolean; abortSignal?: AbortSignal }) => Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }>;
  /** Runs the fixed seedvr2 grid sweep (2x2 = 8K, 3x3 = 16K) on the whole workspace frame, without opening the grid panel. */
  sweepUpscale: (workspaceId: string, gridSize: 2 | 3) => Promise<void>;
  /** Runs a Develop 360 recipe (lib/modelist/streetDevelop.ts) on a Street View workspace cut at `cuts`: its slices all at once, each an ordinary generation. */
  developStreet: (workspaceId: string, recipeId: string, cuts: StreetCuts) => Promise<void>;
  /** Texturise (lib/modelist/streetDevelop.ts STREET_TEXTURE_RECIPES): one generation over a Street View workspace's whole frame. */
  texturiseStreet: (workspaceId: string, recipeId: string) => Promise<void>;
  /** Repair cuts (lib/modelist/streetDevelop.ts): one generation per cut Develop 360 remembers on the workspace, all at once, each output cut to a strip over its cut. */
  repairStreetCuts: (workspaceId: string) => Promise<void>;
  /** The people of the workspace's frame found by SAM and grouped into square zones (lib/recastExtras.ts), nothing billed. */
  detectRecastZones: (workspaceId: string) => Promise<RecastZone[]>;
  /** Recast extras: each enabled zone in reading order, one after the other, an ordinary qwen-image-2.1 generation; a final version of what landed. */
  recastExtras: (workspaceId: string, zones: RecastZone[]) => Promise<void>;
  /** One generation of the Recast extras recipe on the workspace's generation area, then a version of it. */
  recastGenerationArea: (workspaceId: string, options?: { abortSignal?: AbortSignal }) => Promise<void>;
  isRecastExtrasRunning: (workspaceId: string) => boolean;
  /** Stops a Recast extras run before its next zone; the zone in flight still lands. */
  stopRecastExtras: (workspaceId: string) => void;
  vectorizeLayer: (layerId: string) => Promise<Layer | null>;
  vectorizeVisibleLayers: (workspaceId: string) => Promise<Layer[]>;
  resetModelParameters: (modelId: string) => Promise<void>;
  /** Switches the active diffusion model, carrying a user-authored prompt over to it. */
  selectDiffusionModel: (modelId: string) => void;
  /** A project kind that the active model does not serve switches to the first model that does. */
  ensureDiffusionModelForProject: (projectId: string) => void;
  setReferenceImages: (files: File[], mode: 'replace' | 'append') => Promise<void>;
  prepareWorkspaceForGeneration: (workspaceId: string) => void;
  runModel: (
    workspaceId: string,
    runtimeStateOverride?: RuntimeState,
    numOutputsOverride?: number,
    abortSignal?: AbortSignal,
    generationOptions?: ModelRunGenerationOptions
  ) => Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }>;
  /** Runs the ideogram-inpaint model on the workspace's painted mask, from the canvas right-click shortcut. */
  runIdeogramInpaintOnMask: (workspaceId: string, prompt: string, maskPoint: { x: number; y: number }) => Promise<void>;
  autoCut: (workspaceId: string, layerOverride?: Partial<Layer>, abortSignal?: AbortSignal) => Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }>;
  generateInParallel: (overrides: Record<string, any>[], sourceWorkspaceId: string, options?: { allowedSourceImageUrls?: string[]; abortSignal?: AbortSignal }) => Promise<void>;
  /**
   * Force every generation card produced during a Master-driven Studio run to be
   * tagged to the master session's timeline (pass the master session id), or clear
   * the override (pass null). Lets a master command tag its generations to its own
   * session directly, with no active-session ownership or navigation required.
   */
  setMasterRunChatSessionId: (id: string | null) => void;
}

export interface useZoomReturnType {
  animateZoomAndPan: (projectId: string, targetZoom: number, targetPan: { x: number; y: number }, animationDuration: number, easing?: (progress: number) => number) => void;
  handleZoom: (projectId: string, event: React.WheelEvent<HTMLDivElement>, canvasContainer: HTMLDivElement | null) => void;
  handleWheelPan: (projectId: string, deltaX: number, deltaY: number) => void;
  shiftViewportPan: (projectId: string, dx: number, dy: number) => void;
  zoomToFitRectInViewport: (projectId: string, rectToFit: Rect, marginFactorMultiplier?: number, spectacleMode?: boolean) => void;
  handleZoomInFromButton: (projectId: string) => void;
  handleZoomOutFromButton: (projectId: string) => void;
  applyViewportTransform: (pan: { x: number; y: number }, zoom: number, syncCssVars?: boolean) => void;
  /** A Vector project's artboard never leaves the window; other kinds return the pan unchanged. */
  clampViewportPanForProject: (projectId: string, pan: { x: number; y: number }, zoom: number) => { x: number; y: number };
}

export interface KernelViewportRuntimeState {
  panOffset: { x: number; y: number };
  zoomLevel: number;
}

export interface KernelViewportBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface KernelViewportOwner {
  entityType: KernelEntityType;
  entityId: string;
  /** Nested path under the entity runtime overlay where the viewport is stored. */
  runtimePath: readonly string[];
}

export interface KernelViewportRegistration {
  key: string;
  owner: KernelViewportOwner;
  containerElement: HTMLDivElement | null;
  transformElement: HTMLDivElement | null;
  contentBounds?: KernelViewportBounds | null;
  defaultViewport?: KernelViewportRuntimeState;
  minZoom?: number;
  maxZoom?: number;
  fitMargin?: number;
  dragPanMode?: 'background' | 'anywhere';
}

export interface useViewportsReturnType {
  registerKernelViewport: (registration: KernelViewportRegistration) => void;
  unregisterKernelViewport: (viewportKey: string) => void;
  getKernelViewportState: (viewportKey: string) => KernelViewportRuntimeState;
  setKernelViewportState: (viewportKey: string, viewport: KernelViewportRuntimeState) => void;
  applyKernelViewportTransform: (viewportKey: string, viewport?: KernelViewportRuntimeState, syncCssVars?: boolean) => void;
  handleKernelViewportWheel: (viewportKey: string, event: ReactWheelEvent<HTMLDivElement>) => void;
  handleKernelViewportPointerDown: (viewportKey: string, event: ReactPointerEvent<HTMLDivElement>) => void;
  handleKernelViewportPointerMove: (viewportKey: string, event: ReactPointerEvent<HTMLDivElement>) => void;
  handleKernelViewportPointerUp: (viewportKey: string, event: ReactPointerEvent<HTMLDivElement>) => void;
  fitKernelViewportToRect: (viewportKey: string, rectToFit: KernelViewportBounds, animationDuration?: number) => void;
  fitKernelViewportToRegisteredBounds: (viewportKey: string, animationDuration?: number) => void;
  zoomKernelViewportIn: (viewportKey: string) => void;
  zoomKernelViewportOut: (viewportKey: string) => void;
  resetKernelViewport: (viewportKey: string) => void;
}

export interface useCanvasReturnType {
  /** Opens the one column pick on a workspace, Set the seam's single column or Develop 360's cuts pre-filled from the workspace; false when refused. */
  openWorkspaceColumnPick: (workspaceId: string, request: { purpose: 'seam' } | { purpose: 'develop'; recipeId: string }) => boolean;
  /** Replaces the open cuts pick's cuts; ignored for invalid ones. */
  updateWorkspaceColumnPick: (cuts: StreetCuts) => void;
  cancelWorkspaceColumnPick: () => void;
  /** Closes the pick and applies it: the seam at `fraction` of the frame's width, or the cuts remembered then developed. */
  commitWorkspaceColumnPick: (fraction?: number) => Promise<void>;
  /** Opens Recast extras' zones on a workspace, those it remembers unless `detect`, else detected; false when refused. */
  openRecastZonePick: (workspaceId: string, detect?: boolean) => Promise<boolean>;
  updateRecastZonePick: (zones: RecastZone[], selected: number | null) => void;
  cancelRecastZonePick: () => void;
  /** Closes the pick, remembers its zones and recasts the enabled ones. */
  commitRecastZonePick: () => Promise<void>;
  handleCanvasKeyDown: (event: KeyboardEvent) => void;
  downloadGenerationAreaAsImage: (workspaceId: string, megapixels?: number, format?: OutputImageFormat) => Promise<void>;
  downloadWorkspaceView: (workspaceId: string, megapixels?: number, format?: OutputImageFormat) => Promise<void>;
  extractLayersBoundingBox: (workspaceId: string, layersToUse?: Layer[]) => Promise<{ minX: number, minY: number, maxX: number, maxY: number } | null>;
  zoomToFitAllLayers: (workspaceId: string, layersToFit?: Layer[]) => Promise<boolean>;
  getIdealLongestCanvasSize: (workspaceId: string, layerFilter?: (layer: Layer) => boolean) => Promise<number | undefined>;
  renderWorkspaceAreaToNewLayer: (projectId: string, workspaceId: string) => Promise<Layer | null>;
  renderWorkspaceArea: ( workspaceId: string, layerIdsToGrab?: string[], targetPixels?: number, destroyFillAreaBehindMask?: boolean, alphaChannel?: boolean, multipleOf?: number, rect?: Rect, outputFormat?: OutputImageFormat, gridOverlay?: GridOverlayOptions, maskInterpretation?: MaskInterpretation, extraLayers?: Layer[], outputQuality?: number, background?: string ) => Promise<OriginalImage>;
  /** Produces the exported image file (native SVG, raster, PDF wrapper or source asset) for the given export settings. */
  renderImageExport: (options: ImageExportRenderOptions) => Promise<OriginalImage>;
  zoomOnCanvas: (workspaceId: string) => boolean;
  zoomOnAllFrames: (projectId: string) => boolean;
  fitCanvasToVisibleContentLayers: (workspaceId: string, marginFactor?: number) => Promise<void>;
  calculateFitToVisibleContentLayersRect: (workspaceId: string, marginFactor?: number) => Promise<CalculatedFitRect | null>;
  setGenerationArea: (workspaceId: string, newX: number, newY: number, newWidth: number, newHeight: number, suppressSaveState?: boolean) => Promise<void>;
  renderWorkspaceView: ( workspaceId: string, targetPixels?: number, outputFormat?: OutputImageFormat ) => Promise<OriginalImage | null>;
  calculateAdjustGenerationAreaRelativeRect: ( workspaceId: string, referenceAreaType: 'currentGA' | 'visibleContent', reductionPercentage: number, anchorPoint: string ) => Promise<CalculatedFitRect | null>;
  zoomOnLayer: (layerOrId: Layer | string) => Promise<void>;
  resizeGenerationAreaToLayerBBox: (layerOrId: Layer | string, ignoreTransparentBorder?: boolean) => Promise<void>;
  resizeGenerationAreaToLayersUnion: (workspaceId: string, layerNamesOrIds: string[], marginFactor?: number) => Promise<void>;
  /** Applies the generation area at once, then glides its drawn frame there from where it was (600 ms, RESIZE sound). */
  animateGenerationArea: (workspaceId: string, target: Rect) => Promise<void>;
  resizeGenerationAreaToWorkspace: (projectId: string, workspaceId: string) => Promise<void>;
  focusGenerationAreaForGridPanel: (
    projectId: string,
    workspaceId: string,
    options?: {
      panelWidthPx?: number;
      panelGapPx?: number;
      viewportPaddingPx?: number;
      bottomOverlayPx?: number;
      marginFactor?: number;
    }
  ) => boolean;
}

export interface ImageExportRenderOptions {
  /** Workspace holding the pixels. Optional only in source-image mode. */
  workspaceId?: string | null;
  target: 'area' | 'frame';
  format: ImageExportFileFormat;
  /** Pixel budget of the output. */
  targetPixels: number;
  /** Output pixel size written in the native SVG document header. */
  exportWidth: number;
  exportHeight: number;
  qualityPercent: number;
  alpha: boolean;
  includeAnnotations: boolean;
  /** SOURCE-IMAGE mode: export this asset instead of a canvas render. */
  sourceImageUrl?: string | null;
  /** Localized "no visible layers" message; defaults to English. */
  noVisibleLayersMessage?: string;
  /** Print mode opts into the dedicated worker rasterizer and encoder. */
  print?: PrintExportOptions;
  onPrintProgress?: (percent: number) => void;
  printSignal?: AbortSignal;
}

export interface ProjectAssetReport {
  reportGeneratedAt: string;
  projectId: string;
  userEmail: string;
  summary: {
    projectStartTime: string;
    projectEndTime: string;
    totalCreditsUsed: number;
    totalCloudAssets: number;
    potentiallyOrphanedAssets: number;
    assetsBySource: {
      generation: number;
      upload: number;
      snapshot: number;
      mask: number;
    };
    totalDownloads: number;
  };
  /** Raw history, kept verbatim so the event timeline stays authoritative. */
  timeline: UserAction[];
}

export interface useProjectsReturnType {
  deleteProject: (projectId: string) => void;
  copyProject: (projectId: string) => Promise<ProjectMetadata | null>;
  adminCopyProjectToSelf: (projectId: string) => Promise<void>;
  updateProjectDto: (
    projectId: string,
    patch: Partial<Pick<ProjectDTO, 'projectType'>>
  ) => void;
  loadProject: (projectId: string, ownerId?: string) => Promise<void>;
  resyncOpenStudioScene: () => Promise<void>;
  renameProject: (oldProjectID: string, newProjectName: string) => Promise<void>;
  createProjectAndQuickstart: (userId: string, prompt: string, imageUrls?: string[], options?: { projectType?: ProjectType; artboard?: { width: number; height: number } }) => Promise<void>;
  startDealProject: (params: { dealId: string; prompt: string; unitThumbnail?: string | null; brandLogo?: string | null }) => Promise<void>;
  openProject: (projectId: string, userId: string, quickstartPrompt?: string, imageUrls?: string[]) => void;
  sendImageToNewProject: (file: File, name: string) => Promise<void>;
  downloadProjectStory: (projectId: string) => Promise<void>;
  buildProjectAssetReport: (
    projectId: string,
    timeline: UserAction[],
    userEmail: string
  ) => Promise<ProjectAssetReport | null>;
}

export interface useChatReturnType {
  createAndActivateChatSession: (
    initialPrompt: string,
    options?: {
      initialSubject?: ActiveEntityFocus | null;
      creationScope?: SessionCreationScope | null;
    }
  ) => Promise<void>;
  renameChatSession: (sessionId: string, newName: string) => void;
  deleteChatSession: (sessionId: string) => void;
  updateActiveSessionMessages: (updater: (prevMessages: Message[]) => Message[], targetSessionId?: string | null) => void;
  flushChatSession: (sessionId: string) => void;
  addUserMessage: (content: string, images?: string[] | ImageAttachment[], options?: { technicalPrompt?: Promise<string> }) => void;
  persistVisibleMessage: (message: Message, targetSessionId?: string | null, options?: { requirePersistence?: boolean; signal?: AbortSignal }) => Promise<void>;
  recordAgentTelemetry: (opts: { title: string; detail?: string; entityType?: string; entityId?: string; sessionId?: string | null }) => void;
  addPersistedAssistantMessage: (content: string, agentName?: string, targetSessionId?: string | null) => Promise<void>;
  maybeAutoTitleSession: (sessionId: string, options?: { force?: boolean }) => Promise<void>;
  deleteMessage: (sessionId: string, messageIdentifier: string | number) => void;
  /** The right-click menu of one transcript message, opened at `position`. */
  openChatMessageMenu: (input: {
    position: { x: number; y: number };
    message: Message;
    messageKey: string;
    userLike: boolean;
    imageSrc: string;
  }) => void;
  downloadChatImage: (url: string) => Promise<void>;
  deleteChatSessionSilently: (sessionId: string) => Promise<void>;
  createAndActivateSession: (
    options?: {
      isStillWanted?: () => boolean;
      nameHint?: string;
      initialSubject?: ActiveEntityFocus | null;
      creationScope?: SessionCreationScope | null;
      collaborationMode?: CollaborationMode;
    }
  ) => Promise<string | null>;
  ensureSession: (
    options?: {
      isStillWanted?: () => boolean;
      nameHint?: string;
      initialSubject?: ActiveEntityFocus | null;
      creationScope?: SessionCreationScope | null;
    }
  ) => Promise<string | null>;
  /**
   * `openingCampaignId` marks an activation that is PART of opening that campaign, so the
   * page-follows-the-conversation reconciliation does not read a not-yet-bound session row as
   * "this conversation has no campaign" and close the campaign being opened.
   */
  activateChatSession: (sessionId: string, options?: { openingCampaignId?: string; isStillWanted?: () => boolean }) => Promise<void>;
  persistChatSession: (sessionId: string) => Promise<void>;
  markChatSessionDirty: (sessionId: string) => void;
  setSessionCollaborationMode: (sessionId: string, mode: CollaborationMode) => void;
  anchorEntity: (sessionId: string, entityType: EntityType, entityId: string, mass?: number) => void;
  unanchorEntity: (sessionId: string, entityType: EntityType, entityId: string) => void;
  /** Unanchors every anchor of the session, clears its subject, and resets the selected asset. */
  clearSessionFocus: (sessionId: string | null) => void;
  setSessionTableFocusPresentationMode: (input: {
    sessionId: string | null;
    tableView: SessionTableFocusView;
    entityType: EntityType;
    presentationMode: 'table' | 'grid';
  }) => void;
  setActiveSubject: (
    sessionId: string,
    subject: ActiveEntityFocus | null,
    options?: { recordInHistory?: boolean }
  ) => void;
  goToPreviousSubject: (sessionId: string) => ActiveEntityFocus | null;
  canGoToPreviousSubject: (sessionId: string) => boolean;
  goToNextSubject: (sessionId: string) => ActiveEntityFocus | null;
  canGoToNextSubject: (sessionId: string) => boolean;
  refreshGlobalActionFeed: (orgId?: string) => Promise<void>;
  refreshEntityActionGroup: (
    entityType: EntityType,
    entityId: string,
    options?: { orgId?: string }
  ) => Promise<LeasingActionItem[]>;
  getChatSessionMessages: (sessionId?: string | null) => Message[];
  syncChatSessionMessagesFromTimeline: (sessionId: string) => void;
  resolveNextSubjectAction: (
    sessionId: string,
    resolvedSubjectOverride?: ActiveEntityFocus | null,
    options?: { applyToSession?: boolean }
  ) => Promise<{ recommendation: string; nextAction: LeasingActionItem | null } | null>;
  handleSessionExit: (sessionId: string) => Promise<void>;
  clearActiveChatSession: () => void;
  startThinkingSequence: (sessionId: string) => void;
  /**
   * Write the live status strip under a conversation.
   *
   * The agent's steps ACCUMULATE, because each is a distinct thing it did. A run's progress is ONE
   * line that changes, so it passes `replacesPrevious` and swaps the last entry instead of adding
   * one. Without that, "Starting the sourcing pass" and every "Scored N of M" stacked into a pile
   * of stale counts. An empty status clears the whole strip, agent steps included.
   */
  setSessionStatus: (
    sessionId: string,
    status: string,
    options?: { replacesPrevious?: boolean; settled?: boolean; ambient?: boolean; brands?: ChatStatusBrand[] },
  ) => void;
  getLastProjectId: (sessionId: string) => string | null;
  resolveActiveSessionProjectId: () => string;
  requestScriptStop: (sessionId?: string) => void;
  submitChatPrompt: (options?: ChatComposerSubmitOptions) => Promise<void>;
  queueChatUserMessage: (options?: ChatComposerQueueOptions) => Promise<void>;
  /** Sends the session's queued message if the agent is idle and nothing has answered it yet. */
  flushQueuedChatMessage: (sessionId: string) => Promise<void>;
  openChatComposerAttachMenu: (options: ChatComposerAttachMenuOptions) => void;
  reAddChatImageToComposer: (message: Message, imageIndex: number) => Promise<void>;
  savePromptToLibrary: (prompt: string) => Promise<void>;
  listPromptLibrary: (params: PromptLibraryListParams) => Promise<PromptLibraryListResponse>;
  incrementPromptLibraryUsage: (promptId: string) => Promise<void>;
  removePromptFromLibrary: (promptId: string) => Promise<void>;
}

/** One saved entry of the prompt library, as served by /api/prompt-library. */
export interface SavedPromptEntry {
  id: string;
  prompt: string;
  modelId?: string;
  params?: Record<string, unknown>;
  paramsCanonical?: string;
  organisationId?: string | null;
  count?: number;
  userId?: string;
  savedBy?: {
    id: string;
    name: string;
    email?: string | null;
  };
  createdAt?: string;
}

/** A prompt library owner offered as a filter facet alongside the saved prompts. */
export interface PromptLibraryMember {
  id: string;
  name: string;
  imageUrl: string | null;
  promptCount?: number;
  creditsSpent?: number;
  isFaterOrg?: boolean;
}

/** Query parameters of a prompt library page read. */
export interface PromptLibraryListParams {
  limit: number;
  offset: number;
  ownerId?: string | null;
  search?: string;
}

/** Payload of a prompt library page read. Every field stays optional: the caller guards them. */
export interface PromptLibraryListResponse {
  prompts?: SavedPromptEntry[];
  pagination?: { total?: number; hasMore?: boolean };
  members?: PromptLibraryMember[];
  currentUserId?: string | null;
}

/** Which composer sends the turn: a Studio project chat, the leasing Portal, or the Master hero. */
export interface ChatComposerSubmitOptions {
  /** 'project' talks to the Studio agent and never anchors a Property. Defaults to 'leasing'. */
  chatContext?: 'project' | 'leasing';
  /** Set by the landing composer: every send opens a fresh Master session. */
  hero?: { collaborationMode: CollaborationMode };
  /**
   * Send THIS text instead of the composer's current prompt: the structural resend behind the
   * hover arrow on a user message (SRC-031). Everything else behaves exactly like pressing send,
   * including staged composer images riding along; the user's typed draft is left untouched.
   */
  promptOverride?: string;
  /** Image URLs from the original message when using structural resend. */
  imageUrlOverrides?: string[];
}

export interface ChatComposerQueueOptions {
  chatContext?: 'project' | 'leasing';
}

export interface ChatComposerAttachMenuOptions {
  /** The "+" button: its rect gives x, its enclosing [data-composer-card] gives y. */
  anchorElement: HTMLElement | null;
  importLabel: string;
  /** Opens the composer's own file picker, which lives on the component's input ref. */
  onImportFiles: () => void;
  importLogEvent: string;
  menuLogEvent?: string;
}


// ============================== Leasing Module Types ==============================

export interface InventoryRefreshOptions {
  force?: boolean;
  ttl?: number;
  throwOnError?: boolean;
}

export type AutosaveStatus =
  | 'idle'
  | 'queued'
  | 'saving'
  | 'saved'
  | 'error';

export interface AutosaveState {
  dirty: boolean;
  saveState: AutosaveStatus;
  lastDraftSignature: string | null;
  lastPersistedSignature: string | null;
  retryCount: number;
  lastError: string | null;
  updatedAt: number;
}

export interface QueueAutosaveOptions<TPayload = unknown> {
  entityType: KernelEntityType;
  entityId: string;
  scope?: string;
  reason: string;
  debounceMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  buildPayload: () => TPayload | null | undefined;
  serializePayload?: (payload: TPayload) => string;
  /** For delta payloads: folds a still-unsent payload into the next one instead of replacing it. */
  mergePending?: (pending: TPayload, next: TPayload) => TPayload;
  /** Payloads are field deltas of one entity: one equal to the last persisted is still sent, and with mergePending a failed one is folded into the next write. */
  fieldDeltas?: boolean;
  persist: (
    payload: TPayload,
    context: {
      entityType: KernelEntityType;
      entityId: string;
      scope: string;
      reason: string;
      signature: string;
      attempt: number;
    }
  ) => Promise<void | boolean | { sent: unknown }>;
}

export interface AutoSaveAdapter<TServer, TDraft, TPayload = Partial<TServer>> {
  mapToDraft: (server: TServer) => TDraft;
  mapToPayload: (draft: TDraft) => TPayload;
  serialize?: (draft: TDraft) => string;
}

export interface UseEntityDraftAutosaveOptions<TServer, TDraft, TPayload = Partial<TServer>> {
  kernel: React.RefObject<KernelHandle | null>;
  entityType: KernelEntityType;
  entityId: string;
  scope: string;
  serverData: TServer | null | undefined;
  draft: TDraft;
  setDraft: (draft: TDraft) => void;
  adapter: AutoSaveAdapter<TServer, TDraft, TPayload>;
  reason: string;
  debounceMs?: number;
  validate?: (draft: TDraft) => boolean;
  isDraftInitialized?: boolean;
  initializeDraft?: (draft: TDraft) => void;
  persist: (
    payload: TPayload,
    context: {
      entityType: KernelEntityType;
      entityId: string;
      scope: string;
      reason: string;
      signature: string;
      attempt: number;
    }
  ) => Promise<void | boolean>;
  onSuccess?: (
    payload: TPayload,
    context: {
      entityType: KernelEntityType;
      entityId: string;
      scope: string;
      reason: string;
      signature: string;
      attempt: number;
    }
  ) => Promise<void> | void;
}

export interface UseEntityDraftAutosaveResult {
  autosaveState: AutosaveState;
  draftSignature: string;
  serverSignature: string | null;
}

export interface useInventoryReturnType {
  readPropertyTrafficEvidence: (propertyId: string, auditId: string, dataset: TrafficPlanEvidenceDataset, page?: TrafficPlanEvidencePageSelector) => Promise<TrafficPlanEvidenceReadResponse | TrafficPlanEvidencePageResponse>;
  syncEntity: <K extends InventoryEntityType>(type: K, id: string, options?: { force?: boolean; requireFresh?: boolean }) => Promise<InventoryEntityState<K> | null>;
  ensureFactoryPreset: (workspaceId: string) => Promise<StudioWorkspaceDTO | null>;
  removeFactoryPreset: (workspaceId: string) => Promise<StudioWorkspaceDTO | null>;
  geocodePropertyAsset: (
    propertyId: string,
    payload?: {
      address?: string;
      city?: string;
      country?: string;
      forceAddressUpdate?: boolean;
    }
  ) => Promise<LeasingPropertyDTO | null>;
  uploadBrandLogo: (brandId: string, file: File) => Promise<LeasingBrandDTO | null>;
  uploadFloorPlan: (floorId: string, file: File) => Promise<void>;
  ensureInventoryIds: <K extends InventoryEntityType>(
    entity: K,
    ids: string[],
    options?: { batchSize?: number; force?: boolean }
  ) => Promise<{
    requested: number;
    missing: number;
    hydrated: number;
    unresolvedIds: string[];
    errors: string[];
  }>;

  initializeInventory: () => Promise<void>;
  reconnectInventory: () => Promise<void>;
  clearInventory: () => void;
  refreshInventory: <K extends InventoryEntityType>(
    entity: K,
    filters?: Partial<InventoryRegistry[K]['filters']>,
    limit?: number,
    options?: InventoryRefreshOptions
  ) => Promise<void>;
  queryInventory: <K extends InventoryEntityType>(
    entity: K,
    options?: {
      filters?: Partial<InventoryRegistry[K]['filters']>;
      limit?: number;
      offset?: number;
      force?: boolean;
      ttl?: number;
      skipRows?: ReadonlySet<string>;
    }
  ) => Promise<{
    ids: string[];
      pagination: Pagination<K> | null;
      error: string | null;
    }>;
  loadOlderTimelineEvents: (args: {
    sessionId: string;
    scope: 'session' | 'subject';
    subject?: { entityType: string; entityId: string } | null;
  }) => Promise<void>;
  fetchInventoryCount: (
    entity: InventoryCountableEntityType,
    options?: {
      force?: boolean;
      ttlMs?: number;
      signal?: AbortSignal;
      organisationId?: string | null;
    }
  ) => Promise<void>;
  fetchInventoryStats: (
    entity: InventoryStatsEntityType,
    options?: {
      force?: boolean;
      ttlMs?: number;
      signal?: AbortSignal;
      organisationId?: string | null;
      scopeKey?: string;
      queryParams?: Record<string, string | number | boolean | null | undefined>;
      endpointOverride?: string;
      includeOrganisationIdParam?: boolean;
    }
  ) => Promise<void>;
  invalidateInventoryStats: (
    entity: InventoryStatsEntityType,
    options?: {
      scopeKey?: string;
      organisationId?: string | null;
    }
  ) => void;
  mutateInventory: (operations: InventoryMutationOperation[], reason: string, signal?: AbortSignal) => Promise<InventoryMutationSuccess[]>;
  queueStudioWrite: (operations: InventoryMutationOperation[], options?: { batch?: boolean }) => Promise<InventoryMutationSuccess[]>;
  /** A Studio save preparing its payload before queueStudioWrite (a layer's pixels uploading): the page cannot leave meanwhile. */
  prepareStudioWrite: <T>(preparation: Promise<T>) => Promise<T>;
  /** Sends the Studio writes a fault left held, without waiting for the next periodic try. */
  resumeStudioWrites: () => void;
  /** For a page about to go: sends every unsent Studio write now, in a request that outlives the page. True when some of them cannot leave that way. */
  sendStudioWritesAtLeave: () => boolean;
  /**
   * Runs `perform`, a request that changes the server-side identity of a live page. First sends the Studio writes of the outgoing
   * identity and waits for the server, STUDIO_WRITE_DRAIN_LIMIT_MS at most, then suspends the Studio write queue. When `perform`
   * throws, or `failed` says its outcome changed nothing, sending resumes; otherwise the queue stays suspended until clearInventory.
   */
  runIdentitySwitch: <T>(perform: () => Promise<T>, failed?: (outcome: T) => boolean) => Promise<T>;
  requestFactoryChange: (request: FactoryManagementRequest) => Promise<{ revision?: number; persona?: FactoryPersonaDto; personas?: FactoryPersonaDto[]; object?: FactoryObjectDto; objects?: FactoryObjectDto[]; archetype?: FactoryArchetypeDto; archetypes?: FactoryArchetypeDto[]; referenceFolders?: import('#lib/list/factoryProfile').FactoryFolder[]; factoryProfileRevision?: number; canEdit?: boolean; derivedFromReferences?: boolean }>;
  requestDaimonConversation: (request: DaimonConversationRequest) => Promise<Response>;
  requestDaimonTool: (request: { name: string; args: Record<string, unknown>; sessionId?: string | null }, signal?: AbortSignal) => Promise<Response>;
  requestModelist: (request: ModelistRequest) => Promise<Response>;
  /** The Video tool's films and their runner (app/api/video). */
  requestVideo: (request: VideoRequest) => Promise<Response>;
  /** Files: shared documents, their links and visits (app/api/share). */
  requestShare: (request: ShareRequest) => Promise<Response>;
  loadFater3DSites: () => Promise<void>;
  renameFater3DSite: (sessionId: string, name: string) => Promise<void>;
  deleteFater3DSite: (sessionId: string) => Promise<void>;
  applyInventoryEvents: (events: RealtimeEvent[]) => void;
  watchInventoryEchoes: () => { echoed: ReadonlySet<string>; stop: () => void };
  queueAutosave: <TPayload = unknown>(options: QueueAutosaveOptions<TPayload>) => void;
  /** Queues changed fields of a workspace whose row the server has shown, on its one save entry. */
  queueStudioWorkspaceSave: (workspaceId: string, projectId: string, changes: Partial<import('#lib/studio-runtime-dto').StudioWorkspaceAutosavePayload>) => void;
  /** Fields the server was already given with these values: a reconciliation does not send them again. */
  markStudioWorkspaceFieldsSent: (workspaceId: string, fields: Partial<import('#lib/studio-runtime-dto').StudioWorkspaceAutosavePayload>) => void;
  flushAutosave: (
    entityType: KernelEntityType,
    entityId: string,
    scope?: string
  ) => Promise<boolean>;
  /** Settles when the save of this entity and scope that is in flight does, at once when none is. */
  awaitAutosaveInFlight: (
    entityType: KernelEntityType,
    entityId: string,
    scope?: string
  ) => Promise<void>;
  cancelAutosave: (
    entityType: KernelEntityType,
    entityId: string,
    scope?: string
  ) => void;
  settleAutosave: (
    entityType: KernelEntityType,
    entityId: string,
    options?: {
      scope?: string;
      signature?: string;
      saveState?: Extract<AutosaveStatus, 'idle' | 'saved'>;
    }
  ) => void;
  getAutosaveState: (
    entityType: KernelEntityType,
    entityId: string,
    scope?: string
  ) => AutosaveState;
  getUnobservedAutosaveValues: (
    entityType: KernelEntityType,
    entityId: string
  ) => Array<Record<string, unknown>>;
  setAutosaveBaseline: (
    entityType: KernelEntityType,
    entityId: string,
    signature: string,
    options?: {
      scope?: string;
      saveState?: AutosaveStatus;
    }
  ) => void;

  createEntity: <K extends InventoryEntityType>(type: K, payload: Partial<InventoryRegistry[K]['dto']>) => Promise<InventoryMutationSuccess>;
  updateEntity: <K extends InventoryEntityType>(type: K, id: string, payload: Partial<InventoryRegistry[K]['dto']>) => Promise<InventoryMutationSuccess>;
  /** Fater admins only: stamp or unstamp a brand by hand. */
  setBrandFaterStamp: (brandId: string, stamped: boolean) => Promise<void>;
  /** Fater admins only: ask the worker to evaluate every brand again now. */
  requestBrandEvaluationRefresh: () => Promise<void>;
  deleteEntity: <K extends InventoryEntityType>(type: K, id: string) => Promise<InventoryMutationSuccess>;
}

export interface useScoringCampaignsReturnType {
  sourceBudgetProposal: (campaignId: string, request: { action: 'propose' | 'accept' | 'decline' | 'read' | 'latest'; chatSessionId: string; proposalId?: string; additionalCredits?: number; goal?: string; targetScore?: number; brief?: SourceBriefChange['to'] }) => Promise<SourceBudgetProposal | null>;
  /** Open a new sourcing campaign draft in a NEW conversation; resolves to its session id. */
  beginSourcingCampaignDraft: (portfolioId?: string, propertyId?: string, unitId?: string) => Promise<string | null>;
  /**
   * The dashboard's Launch bar: the user's words, the assets and a credit ceiling. Opens the draft
   * conversation, records the mandate and hands the brief to the agent, which sets up and launches
   * the campaign on its own (lib/leasing/sourcingMandate.ts).
   */
  launchSourcingFromBrief: (brief: { prompt: string; propertyIds: string[]; unitId?: string | null; creditCeiling: number | null; targetScore: number; params?: SourcingPresetParams | null; onLaunched?: (campaignId: string) => void }) => Promise<void>;
  /** Open a campaign WITH its own conversation (the forward half of the campaign<->chat binding). */
  openSourceCampaign: (campaignId: string) => Promise<void>;
  /** Close the open campaign and hand the chat back to the portfolio's dashboard conversation (the reverse half). */
  closeSourceCampaign: () => void;
  /**
   * Start an asset sourcing run. One campaign covers however many assets are handed to it: a list
   * is the multi-asset shape the Launch button sends, a scalar the single-asset shape the agent's
   * commands send, and each reaches the route in the shape it arrived in.
   */
  launchSourcingCampaign: (
    property: string | string[],
    options?: LaunchSourcingCampaignOptions
  ) => Promise<SourcingCampaignDTO>;
  stopSourcingCampaign: (campaignId: string) => Promise<void>;
  /**
   * Store the country restriction the portfolio-wide sourcing runs under. Normalizes the codes,
   * skips an unchanged value, never throws and owns its localized error toast: resolves true when
   * the value is stored (a no-op included), false when the caller must roll its optimistic state back.
   */
  savePortfolioFitCountryCodes: (
    portfolioId: string,
    nextCodes: string[]
  ) => Promise<boolean>;
  /** Same contract for the minimum network size a brand must reach to enter the candidate pool. */
  savePortfolioFitMinimumNetworkSize: (
    portfolioId: string,
    value: number
  ) => Promise<boolean>;
  /**
   * Launch a Source campaign from one complete setup brief: persist the brief onto the entities the
   * engine reads it from, start the run, write the campaign's doctrine, and report the outcome.
   * The scope is derived from the brief's asset selection. It never throws and owns its own toasts,
   * so a caller only has its own busy flag left to manage.
   */
  launchSourceCampaignFromBrief: (brief: SourceCampaignBrief) => Promise<void>;
  destroySourcingCampaignWithLeads: (campaignId: string) => Promise<{ success: true; deletedLeadCount: number }>;
  /** Rename a campaign of any granularity. Applies locally first, then reconciles. Never throws. */
  renameCampaign: (campaignId: string, name: string) => Promise<void>;
  /** Same as above for the portfolio-fit family, but silent: it reports nothing and rethrows, the caller owns the localized toasts. */
  destroyPortfolioFitCampaignWithFits: (
    campaignId: string
  ) => Promise<{ success: true; deletedPortfolioFitCount: number }>;
  /** Rename a sourcing campaign, then reconcile the detail row and the campaign cards. Throws the API message on failure, the caller owns the toast. */
  renameSourcingCampaign: (campaignId: string, name: string) => Promise<void>;
  /** Research mode: marks the held campaign, sends the agent /fill-data on its first 10 brands, then the run's one scoring. False when nothing was started. */
  /** Drop terminal campaigns with zero results for a portfolio, before listing them. Never throws: tidying a list is not fatal. */
  flushEmptyCampaigns: (portfolioId: string) => Promise<void>;
  /**
   * Destroy a Source-center campaign of ANY granularity and clear the view state pointing at it.
   * Throws on failure (already reported).
   *
   * The first argument was typed to the single literal `'SourcingCampaign'` and ignored by the
   * body, so a portfolio campaign was sent to the asset route and answered 404: 23 of 125 campaigns
   * were undeletable. It takes the campaign's real `targetKind` now, and the legacy literal stays
   * accepted so an older caller keeps meaning "asset".
   */
  destroySourceCampaign: (
    entityType: CampaignTargetKind | 'SourcingCampaign',
    campaignId: string
  ) => Promise<void>;
  reapplySourcingCampaignRubric: (
    campaignId: string
  ) => Promise<ReapplySourcingRubricResult>;
  /**
   * Re-run the full sourcing analysis (research + judge) for one brand of a shortlist, or for a
   * whole row selection. Billed per brand (one credit each), queued to the worker: resolves as soon
   * as the pass is accepted, not when it finishes.
   */
  rerunSourcingCampaignBrand: (
    campaignId: string,
    brands: string | string[]
  ) => Promise<void>;
  handleCampaignEvent: (event: RealtimeEvent) => void;
  flagLeadInteresting: (leadId: string) => Promise<void>;
  flagLeadDismissed: (leadId: string) => Promise<void>;
  flagPortfolioFitInteresting: (portfolioFitId: string) => Promise<void>;
  flagPortfolioFitDismissed: (portfolioFitId: string) => Promise<void>;
}

export interface useTeamsReturnType {
  /** Single authorisation predicate for every organisation-team surface. Mirrors the server guard. */
  canManageTeams: (organisationId: string | null | undefined) => boolean;
  /** Teams of an organisation as the UI sees them: inventory registry merged with the organisation snapshot, sorted by creation. */
  getOrganisationTeams: (organisationId: string) => OrganisationTeamDTO[];
  /** Members of an organisation, for identity lookups inside callbacks (unordered). */
  getOrganisationMembers: (organisationId: string) => LeasingUserDTO[];
  getOrganisationTeamPlans: (orgId: string) => Promise<OrganisationTeamPlanOptionDTO[]>;
  getOrganisationTeamDetails: (teamId: string) => Promise<OrganisationTeamDetailsDTO>;
  createOrganisationTeam: (input: CreateOrganisationTeamInput) => Promise<OrganisationTeamDTO | null>;
  addOrganisationTeamMembers: (teamId: string, memberIds: string[]) => Promise<Pick<OrganisationTeamDTO, 'id' | 'memberIds'> | null>;
  removeOrganisationTeamMember: (teamId: string, memberId: string) => Promise<Pick<OrganisationTeamDTO, 'id' | 'memberIds'> | null>;
  updateOrganisationTeamPlan: (teamId: string, input: UpdateOrganisationTeamPlanInput) => Promise<OrganisationTeamPlanDTO | null>;
  moveOrganisationTeamMembers: (input: MoveOrganisationTeamMembersInput) => Promise<{ memberIds: string[]; targetTeamId: string | null } | null>;
  transferOrganisationTeamCredits: (input: TransferOrganisationTeamCreditsInput) => Promise<{ fromTeam: OrganisationTeamDTO; toTeam: OrganisationTeamDTO; dryRun: boolean } | null>;
  deleteOrganisationTeam: (teamId: string) => Promise<void>;
  /** Opens an impersonation session on a member, then drops the Kernel state bound to the previous identity. */
  impersonateOrganisationMember: (userId: string) => Promise<void>;
  /** Resets a member password and returns the generated one, for the caller to display once. */
  resetOrganisationMemberPassword: (userId: string) => Promise<{ newPassword: string }>;
  setOrganisationMemberAdmin: (organisationId: string, userId: string, isOrganisationAdmin: boolean) => Promise<void>;
  /** Normalises and validates the requested email, then persists it and returns it. Throws the validation reason. */
  changeOrganisationMemberEmail: (organisationId: string, userId: string, currentEmail: string, nextEmail: string) => Promise<string>;
  deleteOrganisationMember: (organisationId: string, userId: string, options: UserDeletionOptions) => Promise<void>;
  deleteOrganisationMemberAvatar: (userId: string) => Promise<void>;
  /** Validates then uploads the member avatar image, and resyncs the User entity. Throws the server refusal reason. */
  uploadOrganisationMemberAvatar: (userId: string, file: File) => Promise<void>;
  /** Validates the optional usage target, then persists the manager's subordinate list. Throws the validation reason. */
  setOrganisationMemberSubordinates: (input: SetOrganisationMemberSubordinatesInput) => Promise<void>;
  setOrganisationMemberPermissions: (userId: string, permissions: UserPermissions) => Promise<void>;
}

/** Envelope fields any contact-discovery schema can carry, used for debug telemetry only. */
interface ContactDiscoveryEnvelopeBase {
  providersScanned?: unknown;
  providersSkipped?: unknown;
  attempts?: unknown;
  cost?: { stopReason?: string; settledCredits?: number } | null;
}

/** Current contact-discovery response: counters nested under `counts`, budget under `cost`. */
export interface ContactDiscoveryEnvelopeV2 extends ContactDiscoveryEnvelopeBase {
  schemaVersion: 'CONTACT_DISCOVERY_V2';
  counts?: {
    searchedRaw?: number;
    accepted?: number;
    dropped?: number;
    persisted?: { created?: number; updated?: number } | null;
  } | null;
}

/** Historical contact-discovery response: flat counters, no cost envelope. */
export interface ContactDiscoveryEnvelopeLegacy extends ContactDiscoveryEnvelopeBase {
  schemaVersion?: undefined;
  searched?: number;
  preValidation?: { accepted?: number; dropped?: number } | null;
  created?: number;
  updated?: number;
}

export type ContactDiscoveryEnvelope = ContactDiscoveryEnvelopeV2 | ContactDiscoveryEnvelopeLegacy;

/** Both discovery schemas collapsed to the same counters, plus the one-line status the UI shows. */
export interface ContactDiscoverySummary {
  searched: number;
  accepted: number;
  dropped: number;
  created: number;
  updated: number;
  /** 'COMPLETED' unless the V2 cost envelope reports why the run stopped early. */
  stopReason: string;
  /** Credits settled by the run; null on the legacy schema, which does not report them. */
  settledCredits: number | null;
  /** Display string rendered as-is by the caller, so both schemas read the same way. */
  statusText: string;
}

/** Raw contact-discovery response, augmented by the Kernel with its normalised summary. */
export interface ContactDiscoveryResult {
  contacts?: unknown[];
  discovery?: ContactDiscoveryEnvelope | null;
  message?: string;
  summary: ContactDiscoverySummary;
  [key: string]: unknown;
}

/**
 * What `navigateBackFromSubject` did with the header's up-arrow. `source-home` is the one case the
 * Kernel cannot finish on its own: only the empty dashboard was resolvable, so the caller must run
 * its canonical "go home to Source" landing (PortalComponent.handleHomeSource).
 */
export type SourceBackNavigationOutcome = 'source-dashboard' | 'source-home' | 'table' | 'none';

export interface useLeasingWorkflowsReturnType {
  /**
   * Runs contact discovery for a brand and lands its result in the UI: POST, brand resync, then a
   * forced refresh of the brand's contacts (the pre-discovery empty result is cached under this
   * brand's query signature and would otherwise be re-served over the fresh rows).
   */
  discoverBrandContacts: (
    brandId: string,
    payload: {
      maxResults?: number;
      page?: number;
      enrich?: boolean;
      revealPersonalEmails?: boolean;
      revealPhoneNumber?: boolean;
      domain?: string;
      country?: string;
      countries?: string[];
      titles?: string[];
      seniorities?: string[];
      debug?: boolean;
      allocatedBudgetCredits?: number;
    }
  ) => Promise<ContactDiscoveryResult>;
  enrichBrand: (brandId: string, dimension?: string, sourceLogoUrl?: string, cik?: string, siren?: string) => Promise<any>;
  /**
   * Enrich a list of brands in one bounded-concurrency run, pausable from the composer's Stop
   * button (the remainder is stashed in `sourceEnrichPausedBrandIds` for a later resume).
   */
  bulkEnrichBrands: (
    brandIds: string[],
    options: { lang: string; onProgress?: (done: number, total: number) => void },
  ) => Promise<void>;
  checkBrandData: (brandId: string, lang: string) => Promise<BrandDataCheck | null>;
  loadBrandExpansionSignal: (brandId: string) => Promise<BrandExpansionSignal | null>;
  loadBrandNews: (brandId: string) => Promise<BrandNews | null>;
  loadBrandGoogleSignals: (brandId: string) => Promise<BrandGoogleSignals | null>;
  clearBrandContacts: (brandId: string) => Promise<any>;
  removeBrandContact: (brandId: string, contactId: string) => Promise<any>;
  enrichContact: (contactId: string, brandId?: string) => Promise<any>;
  syncDealKernelStateFocus: (dealId: string, focusActionType?: LeasingActionType) => void;
  resolveAudit: (auditId: string, action: AuditResolutionAction, manualOverride?: Record<string, any>) => Promise<void>;
  promoteToDeal: (params: { unitId: string; missionId: string; lead: LeasingLeadDTO }) => Promise<{ success: boolean; dealId: string }>;
  generateDocument: (dealId: string, templateId?: string, documentType?: GeneratedDocumentType) => Promise<any>;
  downloadGeneratedDocument: (docId: string) => Promise<void>;
  focusEntityUI: (entityType: EntityType, entityId: string, focusActionType?: LeasingActionType) => Promise<void>;
  loadSourceAssets: () => Promise<void>;
  loadSourceRentRoll: (propertyId: string) => Promise<void>;
  saveSourceRentRollRecord: (propertyId: string, entityType: 'Unit' | 'Deal', entityId: string, fieldKey: string) => Promise<void>;
  focusTableUI: (view: SessionTableFocusView, options?: { isStillWanted?: () => boolean }) => Promise<void>;
  /**
   * Applies the `?fx=` focus target parsed off the portal URL: clears the active session, then
   * focuses the entity (minting a dedicated session first when `forceNewSession` is set) and
   * lands its tab. Fire-and-forget: the focus work is detached so it cannot block bootstrap.
   */
  applyPortalFocusTarget: (focusTarget: NonNullable<PortalUrlState['focusTarget']>) => void;
  /**
   * Non-null when the active subject belongs to the Source wing: the header's up-arrow then reads
   * "Back to dashboard" and this is its last-resort landing (org default, else empty dashboard).
   */
  resolveSourceDashboardFallbackPortfolioId: () => string | null;
  /**
   * "Back" from the active subject: the Source dashboard when `fallbackPortfolioId` says the
   * subject is in the Source wing, the entity type's table view otherwise.
   */
  navigateBackFromSubject: (fallbackPortfolioId: string | null) => SourceBackNavigationOutcome;
  fetchLeasingActions: (orgId?: string) => Promise<void>;
  triggerActionUI: (action: { type: LeasingActionType; entityType?: EntityType; entityId?: string }) => Promise<void>;
  setSourcingSelection: (
    targetType: 'Portfolio' | 'Property' | 'Unit',
    targetId: string,
    selection: SourcingSelectionTriple,
  ) => void;
  resolveStoredSourcingTarget: () => SourcingTargetSelection | null;
}

export interface useEntityKernelStateReturnType {
  makeEntityKernelStateKey: (entityType: KernelEntityType, entityId: string) => string;
  readEntityKernelState: (entityType: KernelEntityType, entityId: string) => Record<string, unknown> | null;
  readEntityKernelMergedState: (entityType: KernelEntityType, entityId: string) => Record<string, unknown> | null;
  readEntityRuntimeState: <K extends KernelEntityType>(
    entityType: K,
    entityId: string
  ) => Partial<KernelEntityRuntimeState<K>>;
  readProjectKernelMergedState: (projectId: string) => ProjectKernelMergedState | null;
  readProjectRuntimeState: (projectId: string) => Partial<ProjectRuntimeState>;
  writeEntityKernelState: (
    entityType: KernelEntityType,
    entityId: string,
    state: Record<string, unknown>
  ) => void;
  mergeEntityKernelState: (
    entityType: KernelEntityType,
    entityId: string,
    patch: Record<string, unknown>
  ) => void;
  patchEntityRuntimeState: <K extends KernelEntityType>(
    entityType: K,
    entityId: string,
    patch: Partial<KernelEntityRuntimeState<K>>
  ) => void;
  patchProjectRuntimeState: (projectId: string, patch: Partial<ProjectRuntimeState>) => void;
  removeEntityKernelState: (entityType: KernelEntityType, entityId: string) => void;
  reconcileEntityKernelState: (
    entityType: KernelEntityType,
    entityId: string,
    options?: {
      eventType?: LeasingEventType;
    }
  ) => void;
}

// The main benefit of kernel modules is the Single Source of Truth principle.
// Each module covers a central subject, it's key to maintain code coherence since AI agents process files as units.

export const ENTITY_TYPES = [
  'Property',
  'Floor',
  'Unit',
  'UnitPosition',
  'Portfolio',
  'Brand',
  'BrandLocation',
  'LegalEntityFinancialRecord',
  'LegalEntity',
  'MarketSignal',
  'Campaign',
  'SourcingCampaign',
  'PortfolioFitCampaign',
  'PortfolioFit',
  'Deal',
  'ChatSession',
  'GeneratedDocument',
  'Lead',
  'Project',
  'Organisation',
  'User',
  'Notification',
  'Contact',
  'Audit',
  'SlideCorrection',
  'UniversalFact',
  'StudioWorkspace',
  'StudioLayer',
  'StudioWorkspaceVersion',
] as const;

export const INVENTORY_ENDPOINTS: Partial<Record<InventoryEntityType, string>> = {
  Property: '/api/inventory/properties',
  Floor: '/api/inventory/floors',
  Unit: '/api/inventory/units',
  Brand: '/api/inventory/brands',
  Portfolio: '/api/inventory/portfolios',
  LegalEntity: '/api/inventory/legal-entities',
  MarketSignal: '/api/inventory/market-signals',
  Deal: '/api/inventory/deals',
  ChatSession: '/api/inventory/sessions',
  TimelineEvent: '/api/inventory/timeline-events',
  Audit: '/api/inventory/audits',
  Campaign: '/api/inventory/campaigns',
  SourcingCampaign: '/api/inventory/sourcing-campaigns',
  PortfolioFitCampaign: '/api/inventory/portfolio-fit-campaigns',
  PortfolioFit: '/api/inventory/portfolio-fits',
  Lead: '/api/inventory/leads',
  Project: '/api/inventory/projects',
  Organisation: '/api/inventory/organisations',
  Team: '/api/inventory/teams',
  User: '/api/inventory/users',
  Notification: '/api/inventory/notifications',
  Contact: '/api/inventory/contacts',
  UniversalFact: '/api/inventory/facts',
  StudioWorkspace: '/api/inventory/workspaces',
  StudioLayer: '/api/inventory/layers',
  StudioWorkspaceVersion: '/api/inventory/workspace-versions',
  CreditGrant: '/api/inventory/credit-grants',
  SlideCorrection: '/api/inventory/slide-corrections',
};

export const INVENTORY_COUNTABLE_ENTITY_TYPES = [
  'Property',
  'Unit',
  'Brand',
  'Contact',
  'Deal',
] as const;

export type InventoryCountableEntityType = (typeof INVENTORY_COUNTABLE_ENTITY_TYPES)[number];
export const INVENTORY_COUNT_GLOBAL_SCOPE_KEY = '__global__';

export interface InventoryCountEntry {
  count: number;
  isLoading: boolean;
  error: string | null;
  timestamp: number;
}

export type InventoryCountState = {
  [K in InventoryCountableEntityType]: Record<string, InventoryCountEntry>;
};

type TableStatsCardTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger';

interface TableStatsCard {
  id: string;
  label: string;
  value: number | string;
  unit?: string | null;
  tone?: TableStatsCardTone;
  hint?: string | null;
}

interface TableStatsSectionRow {
  key: string;
  label: string;
  value: number | string;
  unit?: string | null;
}

interface TableStatsSection {
  id: string;
  label: string;
  rows: TableStatsSectionRow[];
}

export interface BrandRevenueCoverage {
  brands: number;
  sourced: number;
  /** Filed accounts only; a subset of sourced, and the same figure as the "Official revenue" card. */
  official: number;
  verified: number;
}

export interface TableStatsResponseV1 {
  originBuckets?: BrandOriginBucket[];
  version: 'v1';
  entityType: string;
  scopeKey: string;
  computedAt: string;
  executionMs: number;
  isPartial: boolean;
  cards: TableStatsCard[];
  sections?: TableStatsSection[];
}

export const INVENTORY_STATS_SUPPORTED_ENTITY_TYPES = [
  'Audit',
  'Brand',
  'PortfolioFit',
] as const;

export type InventoryStatsEntityType = (typeof INVENTORY_STATS_SUPPORTED_ENTITY_TYPES)[number];
export const INVENTORY_STATS_GLOBAL_SCOPE_KEY = '__global__';

export interface InventoryStatsEntry {
  data: TableStatsResponseV1 | null;
  isLoading: boolean;
  error: string | null;
  timestamp: number;
  stale: boolean;
}

export type InventoryStatsState = {
  [K in InventoryStatsEntityType]: Record<string, InventoryStatsEntry>;
};

export type EntityType = (typeof ENTITY_TYPES)[number];
export type KernelEntityType = EntityType;

export const MUTATION_ENTITY_TYPES = [
  'Property',
  'Floor',
  'UnitPosition',
  'Unit',
  'Brand',
  'Portfolio',
  'Deal',
  'ChatSession',
  'PortfolioFit',
  'Lead',
  'GeneratedDocument',
  'LegalEntity',
  'MarketSignal',
  'StudioWorkspace',
  'StudioLayer',
  'StudioWorkspaceVersion',
  'Project',
  'TimelineEvent',
] as const;
export type MutationEntityType = (typeof MUTATION_ENTITY_TYPES)[number];

interface KernelEntityRuntimeStateMap {
  Campaign: { footfallCampaignStatus?: 'loading' | 'failed' };
  Notification: { toastShownForUserId?: string; clearError?: string };
  Property: PropertyRuntimeState;
  Floor: FloorRuntimeState;
  Unit: UnitRuntimeState;
  Portfolio: PortfolioRuntimeState;
  Brand: BrandRuntimeState;
  LegalEntity: LegalEntityRuntimeState;
  MarketSignal: MarketSignalRuntimeState;
  Deal: DealRuntimeState;
  PortfolioFit: PortfolioFitRuntimeState;
  ChatSession: SessionRuntimeState;
  Lead: LeadRuntimeState;
  Project: ProjectRuntimeState;
  Organisation: OrganisationRuntimeState;
  StudioWorkspace: StudioWorkspaceRuntimeState;
  StudioLayer: StudioLayerRuntimeState;
}

interface KernelSharedRuntimeState {
  inventoryDetailCoverage?: 'full' | null;
}

export type KernelEntityRuntimeState<K extends KernelEntityType> =
  (
    K extends keyof KernelEntityRuntimeStateMap
      ? KernelEntityRuntimeStateMap[K]
      : Record<string, never>
  ) &
  KernelSharedRuntimeState;

type LeasingReference = string | { $ref: string };

interface LeasingBaseOperation<A extends LeasingEventType
 = LeasingEventType
, E extends MutationEntityType = MutationEntityType> {
  action: A;
  entity: E;
  tempId?: string;
}

interface LeasingCreatePropertyOperation extends LeasingBaseOperation<'CREATE', 'Property'> {
  payload: Partial<LeasingPropertyDTO>;
}

interface LeasingCreateUnitOperation extends LeasingBaseOperation<'CREATE', 'Unit'> {
  payload: Partial<LeasingUnitDTO> & {
    unitIdentifier: string;
    propertyId: LeasingReference;
    floorId?: LeasingReference;
  };
}

interface LeasingCreateDealOperation extends LeasingBaseOperation<'CREATE', 'Deal'> {
  payload: Partial<Omit<LeasingDealDTO, 'unitId' | 'brandId' | 'tenantId' | 'incentiveTemplateId'>> & {
    unitId?: LeasingReference;
    brandId?: LeasingReference;
    tenantId?: LeasingReference | null;
    incentiveTemplateId?: LeasingReference | null;
    activateOnSign?: boolean;
    setActiveDeal?: boolean | null;
    keyDates?: Record<string, unknown>;
  };
}

interface LeasingCreateBrandOperation extends LeasingBaseOperation<'CREATE', 'Brand'> {
  payload: Partial<LeasingBrandDTO>;
}

interface LeasingCreatePortfolioOperation extends LeasingBaseOperation<'CREATE', 'Portfolio'> {
  payload: Partial<LeasingPortfolioDTO>;
}

interface LeasingUpdateBrandOperation extends LeasingBaseOperation<'UPDATE', 'Brand'> {
  id: LeasingReference;
  payload: Partial<LeasingBrandDTO>;
}

interface LeasingUpdatePortfolioOperation extends LeasingBaseOperation<'UPDATE', 'Portfolio'> {
  id: LeasingReference;
  payload: Partial<LeasingPortfolioDTO>;
}

interface LeasingUpdateUnitOperation extends LeasingBaseOperation<'UPDATE', 'Unit'> {
  id: LeasingReference;
  payload: Partial<LeasingUnitDTO> & { expectedUpdatedAt?: string };
}

interface LeasingUpdateDealOperation extends LeasingBaseOperation<'UPDATE', 'Deal'> {
  id: LeasingReference;
  payload: Partial<Omit<LeasingDealDTO, 'unitId' | 'brandId' | 'tenantId' | 'incentiveTemplateId'>> & {
    expectedUpdatedAt?: string;
    unitId?: LeasingReference;
    brandId?: LeasingReference;
    tenantId?: LeasingReference | null;
    incentiveTemplateId?: LeasingReference | null;
    activateOnSign?: boolean;
    setActiveDeal?: boolean | null;
    keyDates?: Record<string, unknown>;
  };
}

interface LeasingDeleteUnitOperation extends LeasingBaseOperation<'DELETE', 'Unit'> {
  id: LeasingReference;
}

interface LeasingDeletePropertyOperation extends LeasingBaseOperation<'DELETE', 'Property'> {
  id: LeasingReference;
}

interface LeasingDeleteDealOperation extends LeasingBaseOperation<'DELETE', 'Deal'> {
  id: LeasingReference;
}

interface LeasingDeleteBrandOperation extends LeasingBaseOperation<'DELETE', 'Brand'> {
  id: LeasingReference;
}

interface LeasingDeletePortfolioOperation extends LeasingBaseOperation<'DELETE', 'Portfolio'> {
  id: LeasingReference;
}

interface LeasingCreateFloorOperation extends LeasingBaseOperation<'CREATE', 'Floor'> {
  payload: Partial<Omit<LeasingFloorDTO, 'propertyId'>> & {
    propertyId: LeasingReference;
  };
}

interface LeasingUpdateFloorOperation extends LeasingBaseOperation<'UPDATE', 'Floor'> {
  id: LeasingReference;
  payload: Partial<LeasingFloorDTO>;
}

interface LeasingDeleteFloorOperation extends LeasingBaseOperation<'DELETE', 'Floor'> {
  id: LeasingReference;
}

interface LeasingCreateProjectOperation extends LeasingBaseOperation<'CREATE', 'Project'> {
  payload: Partial<LeasingProjectDTO>;
}

interface LeasingUpdateProjectOperation extends LeasingBaseOperation<'UPDATE', 'Project'> {
  id: LeasingReference;
  payload: Partial<LeasingProjectDTO>;
}

interface LeasingDeleteProjectOperation extends LeasingBaseOperation<'DELETE', 'Project'> {
  id: LeasingReference;
}

interface ChatSessionCreateOperation extends LeasingBaseOperation<'CREATE', 'ChatSession'> {
  payload: Partial<ChatSessionInventoryEntityState>;
}

interface ChatSessionUpdateOperation extends LeasingBaseOperation<'UPDATE', 'ChatSession'> {
  id: LeasingReference;
  payload: Partial<ChatSessionInventoryEntityState> & { listCreativeContextChange?: ListCreativeContextChange };
}

interface ChatSessionDeleteOperation extends LeasingBaseOperation<'DELETE', 'ChatSession'> {
  id: LeasingReference;
}

interface LeasingDeleteGeneratedDocumentOperation extends LeasingBaseOperation<'DELETE', 'GeneratedDocument'> {
  id: LeasingReference;
}

interface LeasingDeleteLegalEntityOperation extends LeasingBaseOperation<'DELETE', 'LegalEntity'> {
  id: LeasingReference;
}

interface LeasingDeleteMarketSignalOperation extends LeasingBaseOperation<'DELETE', 'MarketSignal'> {
  id: LeasingReference;
}

interface LeasingDeleteLeadOperation extends LeasingBaseOperation<'DELETE', 'Lead'> {
  id: LeasingReference;
}

interface LeasingUpdatePropertyOperation extends LeasingBaseOperation<'UPDATE', 'Property'> {
  id: LeasingReference;
  payload: Omit<Partial<LeasingPropertyDTO>, 'analystWorkbook' | 'analystAssumptions' | 'merchScenarios'> & {
    analystInitialize?: true;
    analystWorkbook?: Pick<TableDoc, 'id' | 'propertyId' | 'sheets'>;
    /** The revision read; the server stamps the author and time. */
    analystAssumptions?: Omit<AnalystAssumptions, 'updatedAt' | 'updatedBy'>;
    /** The full published list with the revision read; owners only, a stale revision is refused. */
    merchScenarios?: AssetScenarios;
  };
}

interface LeasingUpdateLeadOperation extends LeasingBaseOperation<'UPDATE', 'Lead'> {
  id: LeasingReference;
  payload: Partial<LeasingLeadDTO>;
}

interface LeasingUpdatePortfolioFitOperation extends LeasingBaseOperation<'UPDATE', 'PortfolioFit'> {
  id: LeasingReference;
  payload: Partial<PortfolioFitDTO>;
}

interface StudioCreateWorkspaceOperation extends LeasingBaseOperation<'CREATE', 'StudioWorkspace'> {
  payload: Partial<StudioWorkspaceDTO>;
}

interface StudioUpdateWorkspaceOperation extends LeasingBaseOperation<'UPDATE', 'StudioWorkspace'> {
  id: LeasingReference;
  payload: Partial<StudioWorkspaceDTO>;
}

interface StudioDeleteWorkspaceOperation extends LeasingBaseOperation<'DELETE', 'StudioWorkspace'> {
  id: LeasingReference;
}

interface StudioCreateLayerOperation extends LeasingBaseOperation<'CREATE', 'StudioLayer'> {
  payload: Partial<StudioLayerDTO>;
}

interface StudioUpdateLayerOperation extends LeasingBaseOperation<'UPDATE', 'StudioLayer'> {
  id: LeasingReference;
  payload: Partial<StudioLayerDTO> & { listPersonaToggle?: { id: string; enabled: boolean }; listObjectToggle?: { id: string; enabled: boolean }; listArchetypeToggle?: { id: string; enabled: boolean } };
}

interface StudioDeleteLayerOperation extends LeasingBaseOperation<'DELETE', 'StudioLayer'> {
  id: LeasingReference;
}

interface StudioCreateWorkspaceVersionOperation extends LeasingBaseOperation<'CREATE', 'StudioWorkspaceVersion'> {
  payload: Partial<StudioWorkspaceVersionDTO>;
}

interface StudioUpdateWorkspaceVersionOperation extends LeasingBaseOperation<'UPDATE', 'StudioWorkspaceVersion'> {
  id: LeasingReference;
  payload: Partial<StudioWorkspaceVersionDTO>;
}

interface StudioDeleteWorkspaceVersionOperation extends LeasingBaseOperation<'DELETE', 'StudioWorkspaceVersion'> {
  id: LeasingReference;
}

interface TimelineEventCreateOperation extends LeasingBaseOperation<'CREATE', 'TimelineEvent'> {
  payload: Partial<TimelineEventDTO> & {
    category: TimelineEventCategory;
    eventType: TimelineEventType;
    payload?: Record<string, unknown>;
  };
}

interface TimelineEventUpdateOperation extends LeasingBaseOperation<'UPDATE', 'TimelineEvent'> {
  id: LeasingReference;
  payload: Partial<TimelineEventDTO>;
}

interface TimelineEventDeleteOperation extends LeasingBaseOperation<'DELETE', 'TimelineEvent'> {
  id: LeasingReference;
}

type LeasingCreateOperation =
  | LeasingCreatePropertyOperation
  | LeasingCreateFloorOperation
  | LeasingCreateUnitOperation
  | LeasingCreateBrandOperation
  | LeasingCreatePortfolioOperation
  | LeasingCreateDealOperation
  | ChatSessionCreateOperation
  | LeasingCreateProjectOperation
  | StudioCreateWorkspaceOperation
  | StudioCreateLayerOperation
  | StudioCreateWorkspaceVersionOperation
  | TimelineEventCreateOperation;

type LeasingUpdateOperation =
  | LeasingUpdateFloorOperation
  | LeasingUpdateUnitOperation
  | LeasingUpdatePropertyOperation
  | LeasingUpdateLeadOperation
  | LeasingUpdatePortfolioFitOperation
  | LeasingUpdateDealOperation
  | ChatSessionUpdateOperation
  | LeasingUpdateBrandOperation
  | LeasingUpdatePortfolioOperation
  | LeasingUpdateProjectOperation
  | StudioUpdateWorkspaceOperation
  | StudioUpdateLayerOperation
  | StudioUpdateWorkspaceVersionOperation
  | TimelineEventUpdateOperation;

type LeasingDeleteOperation =
  | LeasingDeleteFloorOperation
  | LeasingDeleteUnitOperation
  | LeasingDeletePropertyOperation
  | LeasingDeleteDealOperation
  | ChatSessionDeleteOperation
  | LeasingDeleteBrandOperation
  | LeasingDeleteLeadOperation
  | LeasingDeletePortfolioOperation
  | LeasingDeleteProjectOperation
  | LeasingDeleteGeneratedDocumentOperation
  | LeasingDeleteLegalEntityOperation
  | LeasingDeleteMarketSignalOperation
  | StudioDeleteWorkspaceOperation
  | StudioDeleteLayerOperation
  | StudioDeleteWorkspaceVersionOperation
  | TimelineEventDeleteOperation;

interface LeasingUpdateUnitPositionOperation extends LeasingBaseOperation<'UPDATE', 'UnitPosition'> {
  payload: Partial<LeasingUnitPositionDTO>;
}

export type InventoryMutationOperation = 
  | LeasingCreateOperation 
  | LeasingUpdateOperation 
  | LeasingDeleteOperation
  | LeasingUpdateUnitPositionOperation;

export interface InventoryMutationSuccess {
  updatedAt?: string;
  status: 'SUCCESS';
  index: number;
  entity: MutationEntityType;
  action: LeasingEventType
;
  id: string;
  tempId?: string;
}

export interface InventoryMutationFailure {
  status: 'ERROR';
  index: number;
  entity: MutationEntityType;
  action: LeasingEventType
;
  tempId?: string;
  message: string;
  // The operation at `index` was refused on purpose and will be refused again as sent; absent on a fault.
  refused?: boolean;
  // The fault was the database's or the transaction's, not the operation's at `index`: the same request can succeed later.
  transient?: boolean;
}

// The reason every batched Studio autosave request starts with: the mutate route recognises its own client by it.
export const STUDIO_AUTOSAVE_BATCH_REASON_PREFIX = 'Autosave Studio DTO batched';

// The waits before a faulted Studio write request is sent again; past the last one the queue is held
// and tries again every STUDIO_WRITE_HELD_RETRY_MS.
export const STUDIO_WRITE_RETRY_DELAYS_MS = [1_000, 3_000, 10_000];
export const STUDIO_WRITE_HELD_RETRY_MS = 30_000;
// How long the browser waits for an answer before sending the same operations again. Not a bound on
// the server, which may still be working on the first send: applied twice, these writes leave the
// rows as applied once, though the audit event, the socket events and Project.savedAt repeat.
export const STUDIO_WRITE_REQUEST_LIMIT_MS = 90_000;
// The browser caps at 64 KiB the bodies of all the keepalive requests a page has in flight, telemetry
// beacons included: the operations a leaving page sends stay under this.
export const STUDIO_WRITE_KEEPALIVE_BODY_LIMIT_BYTES = 48 * 1024;
export const STUDIO_WRITE_DRAIN_LIMIT_MS = 3_000;
// Past this, a queue an identity switch still suspends tells the user its writes are not leaving.
export const STUDIO_WRITE_SUSPENSION_LIMIT_MS = 15_000;

interface InventoryMutationResponseSuccess {
  success: true;
  results: InventoryMutationSuccess[];
}

interface InventoryMutationResponseFailure {
  success: false;
  error: InventoryMutationFailure;
}

type InventoryMutationResponse = InventoryMutationResponseSuccess | InventoryMutationResponseFailure;

export interface LeasingFileDTO {
  id: string;
  filePath: string;
  mimeType: string | null;
}

interface BaseLeasingEntity {
  id: string;
  facts?: UniversalFactDTO[];
}

export type UnitType = 'RETAIL' | 'KIOSK' | 'STORAGE' | 'OFFICE' | 'RESTAURANT' | 'OTHER';
export const UnitType = {
  RETAIL: 'RETAIL',
  KIOSK: 'KIOSK',
  STORAGE: 'STORAGE',
  OFFICE: 'OFFICE',
  RESTAURANT: 'RESTAURANT',
  OTHER: 'OTHER',
} as const;

export type UnitStatus = 'AVAILABLE' | 'UNDER_OFFER' | 'LEASED' | 'EXPIRING_SOON' | 'REPLACEMENT_UNDER_OFFER';
export const UnitStatus = {
  AVAILABLE: 'AVAILABLE',
  UNDER_OFFER: 'UNDER_OFFER',
  LEASED: 'LEASED',
  EXPIRING_SOON: 'EXPIRING_SOON',
  REPLACEMENT_UNDER_OFFER: 'REPLACEMENT_UNDER_OFFER',
} as const;

export interface LeasingFloorDTO {
  id: string;
  propertyId: string;
  name: string;
  level: number;
  surface: number | null;
  calibrationFactor: number | null;
  
  // NOUVEAU STANDARD : IDs uniquement
  planFileId: string | null; 
  unitIds?: string[]; // Liste des unités présentes sur cet étage
  
  // Editable declared/theoretical count for foundation metadata.
  unitCount: number;
  // Actual UnitPosition association count for list/table display.
  associatedUnitCount: number;
}

export interface LeasingUnitDTO extends BaseLeasingEntity {
  updatedAt?: string;
  unitIdentifier: string;
  name: string | null;
  type: UnitType;
  status: UnitStatus | null;
  areaSqft: number | null;
  areaSqm: number | null;
  frontage: number | null;
  askingRent: number | null;
  askingRentCurrency: string | null;
  description: string | null;
  
  // Clés étrangères (Foreign Keys)
  propertyId: string;
  activeDealId: string | null;
  thumbnailFileId: string | null;
  thumbnailFile?: LeasingFileDTO | null;
  tenantName?: string | null;
  tenantCategory?: BrandCategory | null;
  brandLogoUrl?: string | null;

  // --- SKELETON ARRAYS ---
  // Ces champs sont `undefined` dans les listes, et peuplés uniquement dans les vues détails
  floorIds?: string[]; 
  positions?: { floorId: string; geoJson: SpatialManifest | null }[];
  dealIds?: string[]; // Historique des deals
  dealCount?: number; // Stat detail-only, computed by the Unit full-details endpoint
}

type FloorMapperInteractionMode = 'IDLE' | 'DRAWING_NEW' | 'EDITING_POINT' | 'CALIBRATING' | 'MEASURE_FRONTAGE' | 'PANNING';

export interface FloorMapperVolatileState {
  viewport: { x: number; y: number; zoom: number };
  interactionMode: FloorMapperInteractionMode;
  activeUnitId: string | null;
  draftPoints: NormalizedPoint[];
  calibrationPoints: [NormalizedPoint, NormalizedPoint] | null;
  frontagePoints: NormalizedPoint[];
}

/**
 * Runtime-only floor overlay stored in entityKernelState.
 * Never persisted in LeasingFloor DTO storage.
 */
export interface FloorRuntimeState {
  activeTab?: 'FOUNDATION' | 'UNITS' | 'THREE_D' | 'MAPPER';
  mapperState?: FloorMapperVolatileState;
}

export interface SourcingTargetRuntimeState {
  portfolioId?: string | null;
  propertyId?: string | null;
  unitId?: string | null;
  updatedAt?: number;
}

// Trimmed, always-present form of a sourcing target selection ('' when absent),
// shared by the Source Center view and the kernel's setSourcingSelection.
export interface SourcingSelectionTriple {
  portfolioId: string;
  propertyId: string;
  unitId: string;
}

// The single sourcing entity a session is (or was) working on, as resolved by
// resolveStoredSourcingTarget.
export interface SourcingTargetSelection {
  entityType: 'Portfolio' | 'Property' | 'Unit';
  entityId: string;
}

/**
 * Runtime-only unit overlay stored in entityKernelState.
 * Never persisted in LeasingUnit DTO storage.
 */
export interface UnitRuntimeState {
  sourceRentRollEdit?: SourceRentRollEdit;
  sourceRentRollDealId?: string | null;
  activeTab?: 'OVERVIEW' | 'LEADS' | 'DEALS' | 'CRITICAL_QUESTIONS';
  lastFocusedActionType?: LeasingActionType;
  entityActions?: Array<{ type: LeasingActionType; entityType?: string; entityId?: string }>;
  sourcingSelection?: SourcingTargetRuntimeState;
}

export interface LeasingLocation {
  lat: number;
  lng: number;
  resolvedAddress?: string | null;
  city?: string | null;
  country?: string | null;
  countryCode?: string | null;
}

export interface AnalystCellStyle {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  background?: string;
  align?: 'left' | 'center' | 'right';
  format?: string;
  numFmt?: string;
  border?: { top?: string; right?: string; bottom?: string; left?: string };
}

export interface TableSheet {
  id: string;
  name: string;
  /** Sparse, zero-indexed row,col values; '=' prefixes a live formula. */
  cells: Record<string, string>;
  styles?: Record<string, AnalystCellStyle>;
  paper?: boolean;
}

export interface TableDoc {
  id: string;
  propertyId?: string;
  /** Session-only permission derived from trusted inventory assignments; never persisted. */
  sharedAccess?: 'owner' | 'reader';
  name: string;
  createdAt: number;
  updatedAt: number;
  favorite?: boolean;
  thumbnail?: string;
  locked?: boolean;
  compact?: boolean;
  fullWidth?: boolean;
  paperView?: boolean;
  sheets: TableSheet[];
}

export type FootfallDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export interface PulseTrafficView { mode: 'activity' | 'routes'; day: FootfallDay; hour: number; /** The day plays hour by hour on the map. */ playing?: boolean }
export interface PulseMapActivity { placeId: string | null; business: string | null; percent: number | null }
export interface PropertyAnnualVisitorFact { id: string; content: string; sourceUrl: string | null; verifiedAt: string | null }
export interface PropertyShopActivity {
  days: Array<{ day: FootfallDay; hours: Array<{ hour: number; percent: number }> }>;
}
export type TrafficMetric = 'station_passenger_movements' | 'station_validation_entries' | 'station_validation_share' | 'relative_shop_activity';
export interface TrafficObservation {
  id: string;
  sourceId: string;
  subject: { kind: 'station' | 'business'; id: string; name: string };
  metric: TrafficMetric;
  unit: 'passengers' | 'validations' | 'percent';
  basis: 'provider_estimate' | 'recorded_events' | 'relative_index';
  value: number | null;
  bounds: { lower: number; upper: number } | null;
  rawValue: number | null;
  period: { from: string; to: string; timezone: 'Europe/Paris'; operatingDayStartsAt?: 4 } | null;
  dimensions: Record<string, string | number>;
}
export interface TrafficSourceReceipt {
  id: string;
  provider: string;
  datasetId: string;
  url: string;
  documentationUrl: string | null;
  licence: string | null;
  retrievedAt: string;
  dataUpdatedAt: string | null;
  rawHashes: Array<{ file: string; sha256: string }>;
  records: number;
  releaseQuarter: string | null;
  limitations: string[];
}
export interface AcquiredTrafficSource { receipt: TrafficSourceReceipt; metadata: unknown; records: unknown[] }
/** Resident stock is contextual demand, never a count of visits or shopping trips. */
export interface TrafficDemandObservation {
  id: string; sourceId: string;
  subject: { kind: 'iris' | 'commune'; id: string; code: string; name: string | null; geographyDate: string };
  metric: 'resident_population' | 'resident_age_population' | 'municipal_population' | 'separately_counted_population' | 'total_legal_population';
  unit: 'people'; basis: 'census_estimate'; referenceYear: number; value: number | null; rawValue: string | number | null;
  dimensions: { variable: string; populationDefinition: string; ageBand?: string };
}
export interface TrafficDemandPartition {
  known: number; missing: number; total: number | null; residual: number | null;
}
export interface TrafficDemandPack {
  schema: 'fater-traffic-demand-v1'; propertyId: string; organisationId: string; generatedAt: string;
  trafficPackSha256: string; scope: { kind: 'administrative'; communeCodes: string[]; name: string };
  observations: TrafficDemandObservation[];
  iris: Array<{ id: string; communeCode: string; type: string; quality: string; raw: Record<string, string>; agePartition: TrafficDemandPartition }>;
  municipalRecords: Record<string, unknown>[];
  communeTotals: Array<{ subjectId: string; communeCode: string; sourceId: string; referenceYear: 2022; method: 'sum_disjoint_iris'; members: string[]; known: number; missing: number; value: number | null; agePartition: TrafficDemandPartition }>;
  sources: TrafficSourceReceipt[];
  coverage: { communes: number; iris: number; observations: number; sourceCsvRows: number; measuredVisitorObservations: 0 };
  limitations: string[];
}
/** Census workplace/resident stocks; these are never daily arrivals or centre visitors. */
export type TrafficWorkdaySource = 'employment' | 'commuting';
export interface TrafficWorkdayObservation {
  id: string; sourceId: string;
  subject: { kind: 'commune'; id: string; code: string; geographyDate: '2025-01-01' };
  metric: 'workplace_jobs' | 'employed_residents' | 'commuting_location_population' | 'commuting_mode_population';
  unit: 'jobs' | 'people'; basis: 'primary_census_estimate'; referenceYear: 2022;
  value: number | null; rawValue: string;
  dimensions: { variable: string; definition: string; category: string | null };
}
export interface TrafficWorkdayShare {
  sourceId: string; numeratorVariable: string; denominatorVariable: 'P22_ACTOCC15P'; unit: 'fraction';
  value: number | null; status: 'known' | 'missing' | 'zero_denominator'; exceedsDenominator: boolean | null;
}
export interface TrafficWorkdayPack {
  schema: 'fater-traffic-workday-v1'; propertyId: string; organisationId: string; generatedAt: string;
  trafficPackSha256: string; demandPackSha256: string;
  scope: { kind: 'administrative'; communeCodes: string[]; name: string; geographyDate: '2025-01-01' };
  observations: TrafficWorkdayObservation[];
  communes: Array<{ subjectId: string; communeCode: string; sourceId: string; referenceYear: 2022;
    modeShares: TrafficWorkdayShare[]; locationShares: TrafficWorkdayShare[];
    modePartition: TrafficDemandPartition; locationPartition: TrafficDemandPartition }>;
  sourceRows: Array<{ sourceId: string; rows: Record<string, string>[]; sourceCsvRows: number }>;
  sources: Array<TrafficSourceReceipt & { referenceYear: 2022; geographyDate: '2025-01-01'; publicationDateAsDisplayed: string;
    originalReleaseDate: null; variables: Array<{ variable: string; definition: Record<string, string> }> }>;
  coverage: { communes: number; observations: number; sourceCsvRows: Record<string, number>; measuredVisitorObservations: 0 };
  limitations: string[];
}
export interface TrafficDataPack {
  schema: 'fater-traffic-data-v1';
  propertyId: string;
  organisationId: string;
  generatedAt: string;
  inputSha256: string;
  places: Array<{ census: Record<string, unknown>; activityProfile: Record<string, unknown> | null }>;
  observations: TrafficObservation[];
  dailyValidationTotals: Array<{ operatingDate: string; subjectId: string; value: number | null; bounds: { lower: number; upper: number } | null; rows: number; derivedFrom: string[] }>;
  hourlyCoverage: Array<{ subjectId: string; dayType: string; hours: number[]; missingHours: number[]; sumPercent: number; measurementPeriod: null }>;
  annualVisitorStatements: unknown[];
  sources: TrafficSourceReceipt[];
  coverage: { places: number; profiles: number; activityHours: number; publicObservations: number; measuredMallVisitorObservations: 0; measuredShopVisitorObservations: 0 };
  limitations: string[];
}
/** An operator publication is an attendance reference, not an independently measured series. */
export interface TrafficAttendanceReferencePack {
  schema: 'fater-attendance-references-v1'; propertyId: string; organisationId: string; generatedAt: string; inputSha256: string;
  asset: { name: 'Espace Saint Quentin'; communeCode: '78423'; commune: 'Montigny-le-Bretonneux' };
  sources: Array<{ id: string; publisher: 'Altarea'; file: string; url: string; sha256: string; retrievedAt: string; status: 200; bytes: number; contentType: string; licence: null }>;
  references: Array<{
    id: string; sourceId: string; claimGroupId: string; statementKind: 'reported_annual_claim'; value: 12000000; periodUnit: 'year'; reportedUnit: 'visitors';
    publication: { displayedText: string; normalized: string; precision: 'day' | 'month'; financialReportingYear: 2021 | null };
    witnesses: { amount: string; identity: string; scope: string[]; management: string | null };
    locator: { kind: 'html_article' | 'pdf_page'; selector: string | null; physicalPage: 14 | null; displayedSlide: 14 | null; textSha256: string; coverTextSha256: string | null };
    scope: { kind: 'centre_description'; components: Array<'pedestrian_street' | 'covered_gallery'> | null; countingPolygon: null };
    measurement: { start: null; end: null; year: null; method: null; countDefinition: null; countingPerimeter: null };
    calibrationEligible: false; ineligibilityReasons: Array<'measurement_period_unknown' | 'counting_method_unknown' | 'count_definition_unknown' | 'counting_perimeter_unknown'>;
  }>;
  claimGroups: Array<{ id: string; publisher: 'Altarea'; value: 12000000; periodUnit: 'year'; reportedUnit: 'visitors'; referenceIds: string[]; relation: 'same_publisher_corroboration'; independentMeasurements: 0 }>;
  coverage: { publications: 2; publishers: 1; claimGroups: 1; independentlyVerifiedMeasuredVisitorObservations: 0; eligibleCalibrationReferences: 0 };
  limitations: string[];
}

export type TrafficDirectionalBucket = 'sameCommune' | 'otherLocal' | 'outbound' | 'inbound';
export type TrafficDirectionalNativeRow = Record<'CODGEO' | 'LIBGEO' | 'DCLT' | 'L_DCLT', string> & Record<string, string>;
export interface TrafficDirectionalSource {
  year: 2022 | 2023; id: string; provider: 'INSEE'; licence: string;
  spec: { year: 2022 | 2023; id: string; variable: string; geographyDate: string; pubDate: string; page: string; zip: string; file: string; sha256: string; compressedBytes: number; expandedBytes: number };
  metadata: { referenceYear: 2022 | 2023; geographyDate: string; publicationDateAsDisplayed: string; witnesses: Array<{ selector: string; text: string; sha256: string }>; sourceWarnings: string[] };
  archiveReceipt: TrafficDirectionalSource['documentationReceipt'];
  reuseReceipt: TrafficDirectionalSource['documentationReceipt'];
  documentationReceipt: { file: string; url: string; retrievedAt: string; status: 200; bytes: number; sha256: string; contentType: string; acquisition?: 'adopted_original_capture'; originalCapture?: Record<string, unknown> };
}
export interface TrafficDirectionalCommutingPack {
  schema: 'fater-traffic-directional-commuting-v1'; propertyId: string; organisationId: string; generatedAt: string;
  trafficPackSha256: string; workdayPackSha256: string; scope: { communeCodes: string[]; selection: 'origin_or_destination' };
  vintages: Array<{
    referenceYear: 2022 | 2023; geographyDate: string; sourceId: string; source: TrafficDirectionalSource;
    observations: Array<{ id: string; sourceId: string; sourceOrdinal: number; native: TrafficDirectionalNativeRow;
      origin: { code: string; label: string }; destination: { code: string; label: string }; value: number; rawValue: string;
      referenceYear: 2022 | 2023; geographyDate: string; metric: 'employed_residence_work_relationship'; unit: 'people'; basis: 'complementary_census_estimate'; bucket: TrafficDirectionalBucket; approximateBelow200: boolean; sourceWarnings: string[] }>;
    sourceExceptions: Array<{ ordinal: number; native: TrafficDirectionalNativeRow; value: number; reason: 'missing_origin_code_and_label'; sourceId: string; origin: null; destination: { code: string; label: string }; referenceYear: 2023; geographyDate: string }>;
    endpoints: Array<{ code: string; label: string; local: boolean; roles: Array<'residence' | 'workplace'>; sourceId: string; geographyDate: string }>;
    aggregates: { territorial: Record<TrafficDirectionalBucket, { rows: number; value: number }>;
      perCommune: Array<Record<TrafficDirectionalBucket, { rows: number; value: number }> & { communeCode: string }>;
      total: { rows: number; value: number }; definition: string };
    coverage: { sourceCsvRows: number; validSourceCsvRows: number; sourceQualityExceptions: number; exceptionDestinationTouchesScope: false;
      selectedRows: number; endpoints: number; affectedRows: number; zeroRows: number; approximateBelow200Rows: number; measuredVisitorObservations: 0 };
  }>;
  coverage: { vintages: 2; selectedRows: number; validSourceCsvRows: number; sourceQualityExceptions: number; measuredMallVisitorObservations: 0; measuredShopVisitorObservations: 0 };
  limitations: string[];
}

export type TrafficIndoorSourceRole = 'venue' | 'overlay' | 'census' | 'activity';
export interface TrafficIndoorSourceSnapshot {
  schema: 'fater-traffic-indoor-snapshot-v1'; propertyId: string; organisationId: string; capturedAt: string;
  sources: Array<{
    role: TrafficIndoorSourceRole;
    audit: { id: string; entityType: 'Property'; entityId: string; organisationId: string; procedureName: string; createdAt: string; updatedAt: string; payload: unknown; marker: unknown };
    representation: 'database_decoded_json' | 'json_text'; payloadSha256: string; markerSha256: string; textSha256: string | null;
  }>;
}
export interface TrafficIndoorCoveragePack {
  schema: 'fater-traffic-indoor-coverage-v1'; propertyId: string; organisationId: string; generatedAt: string; snapshotSha256: string;
  sources: Array<{ role: TrafficIndoorSourceRole; auditId: string; procedureName: string; createdAt: string; updatedAt: string; representation: TrafficIndoorSourceSnapshot['sources'][number]['representation']; payloadSha256: string; markerSha256: string; textSha256: string | null }>;
  originalUnits: Array<{ id: string; native: Record<string, unknown>; source: TrafficIndoorSourceRole }>;
  effectiveUnits: Array<{ id: string; originalUnitId: string; interpretation: Omit<PulseVenueFeature, 'areaSqm'> & { areaSqm: number | null }; originalSource: 'venue'; overlaySource: 'overlay'; acceptedPlaceIds: string[]; candidatePlaceIds: string[]; legacySelectedPlaceId: string | null; snapshotNameActivityPlaceId: string | null;
    identityCoverage: 'unnamed' | 'accepted_source_declaration' | 'ambiguous_candidates' | 'candidate_only' | 'unmatched'; activityCoverage: 'accepted_relative_profile' | 'unavailable'; measuredVisitors: null }>;
  reclassifications: Array<{ id: string; from: 'unit'; to: 'amenity'; source: 'overlay' }>;
  places: Array<{ placeId: string; native: Record<string, unknown>; source: 'census'; profilePlaceId: string | null; acceptedUnitIds: string[]; candidateUnitIds: string[]; indoorFloor: null; measuredVisitors: null;
    identityCoverage: 'accepted_source_declaration' | 'ambiguous_candidates' | 'candidate_only' | 'unbound'; activityCoverage: 'relative_profile_available' | 'unavailable' }>;
  profiles: Array<{ placeId: string; native: Record<string, unknown>; normalizedActivity: PropertyShopActivity | null; source: 'activity'; acceptedUnitIds: string[]; measuredVisitors: null;
    identityCoverage: 'accepted_source_declaration' | 'unbound'; activityCoverage: 'relative_profile_available' | 'unavailable' }>;
  declarations: Array<{ unitId: string; placeId: string; tenantName: string | null; native: Record<string, unknown>; source: 'activity'; accepted: boolean; reason: 'accepted_source_declaration' | 'unit_absent' | 'reclassified_amenity' | 'duplicate_unit_declaration' | 'tenant_or_profile_mismatch' }>;
  nameActivityCandidates: Array<{ unitId: string; placeId: string; method: 'snapshot_name_only_activity'; accepted: false; source: 'activity' }>;
  methods: { explicitBindings: 'unique_source_declaration_and_retained_tenant_consistency'; nameCandidates: 'legacy_normalised_whole_word_name'; legacySelection: 'highest_recorded_review_count'; nameActivity: 'snapshot_exact_normalised_name_only'; surveyedSpatialIdentity: false };
  coverage: { originalUnits: number; effectiveUnits: number; reclassifiedUnits: number; originalNamed: number; effectiveNamed: number; effectiveUnnamed: number;
    levels: Array<{ id: string | null; original: number; effective: number; named: number; unnamed: number }>;
    places: number; locatedPlaces: number; profiles: number; activityHours: number; acceptedBindings: number; acceptedBusinesses: number; rejectedDeclarations: number;
    candidateUnits: number; candidatePlaces: number; ambiguousCandidateUnits: number; legacySelectedUnits: number; legacySelectedPlaces: number; nameActivityCandidates: number;
    independentlyVerifiedSpatialBindings: 0; measuredMallVisitorObservations: 0; measuredShopVisitorObservations: 0 };
  limitations: string[];
}

export type TrafficSpatialGeometry = { type: 'Polygon'; coordinates: Array<Array<[number, number]>> } | { type: 'MultiPolygon'; coordinates: Array<Array<Array<[number, number]>>> };
export interface TrafficSpatialMetadata {
  datasetId: 'iris0'; edition: 2024; creator: 'IGN & INSEE'; licence: string; description: string;
  dataProcessed: string; nationalRecords: number; extent: [number, number, number, number]; bbox: unknown; fields: unknown[];
}
export interface TrafficSpatialOriginPack {
  schema: 'fater-traffic-spatial-origins-v1'; propertyId: string; organisationId: string; generatedAt: string;
  trafficPackSha256: string; demandPackSha256: string;
  scope: TrafficDemandPack['scope'];
  geography: { statisticalReferenceDate: '2024-01-01'; identityReferenceDate: '2024-01-01'; geometryEdition: 2024;
    geometryReferenceDate: null; coordinateSystem: 'OGC:CRS84'; coordinateSystemBasis: string; providerCrs: null };
  geometryMetadata: TrafficSpatialMetadata;
  origins: Array<{ id: string; irisCode: string; communeCode: string; geometrySourceId: string;
    regionalName: string | null; regionalCommuneName: string | null; regionalType: string | null;
    geometry: TrafficSpatialGeometry; representativePoint: { kind: 'provider_point'; sourceField: 'geo_point_2d'; coordinates: [number, number] };
    reference: Record<string, string | number | null>;
    residents: { observationId: string; variable: 'P22_POP'; value: number | null; rawValue: string };
    ages: Array<{ observationId: string; variable: string; ageBand: string; value: number | null; rawValue: string }>;
    agePartition: TrafficDemandPartition }>;
  population: { sourceId: 'insee-iris-population-2022'; referenceYear: 2022; unit: 'people'; basis: 'census_estimate' };
  sources: Array<TrafficSourceReceipt & { edition: number | null; geographyDate: string | null; publicationDateAsDisplayed: string | null }>;
  coverage: { origins: number; communes: number; populationObservations: number; referenceNationalRows: number;
    regionalNationalRows: number; vertices: number; rings: number; measuredVisitorObservations: 0 };
  limitations: string[];
}
export interface TrafficSpatialAuditMarker {
  schema: 'fater-traffic-spatial-storage-v1'; storage: 'json-text-v1'; revision: string; packSha256: string; payloadSha256: string;
  parent: { auditId: string; revision: string; payloadSha256: string };
}
export interface PropertyTrafficSpatialSummary {
  schema: 'fater-traffic-spatial-summary-v1'; auditId: string; revision: string; generatedAt: string;
  parent: TrafficSpatialAuditMarker['parent']; parentCurrent: boolean | null; parentVerified: boolean;
  packSha256: string; trafficPackSha256: string; demandPackSha256: string;
  geography: TrafficSpatialOriginPack['geography']; coverage: TrafficSpatialOriginPack['coverage'];
  sources: Array<Pick<TrafficSpatialOriginPack['sources'][number], 'id' | 'provider' | 'url' | 'licence' | 'retrievedAt' | 'edition' | 'geographyDate' | 'publicationDateAsDisplayed'>>;
  dataUrl: string;
}
export interface TrafficSpatialDataResponse {
  schema: 'fater-traffic-spatial-response-v1'; descriptor: PropertyTrafficSpatialSummary; pack: TrafficSpatialOriginPack;
}
export interface TrafficEvidenceAuditRow {
  id: string; entityType: string; entityId: string; organisationId: string; procedureName: string;
  suggestedFix: unknown; flaggedIssues?: unknown;
}
export type TrafficPlanEvidenceDataset = 'indoor' | 'directional' | 'attendance';
export interface TrafficPlanEvidenceParentBinding { auditId: string; revision: string; payloadSha256: string }
export interface TrafficPlanEvidenceTextIdentity { originalTextSha256: string; originalTextBytes: number }
export interface TrafficPlanEvidenceManifest {
  schema: 'fater-plan-traffic-evidence-manifest-v1'; propertyId: string; organisationId: string; generatedAt: string;
  parents: { core: TrafficPlanEvidenceParentBinding; spatial: TrafficPlanEvidenceParentBinding };
  datasets: Record<TrafficPlanEvidenceDataset, TrafficPlanEvidenceTextIdentity>;
  indoorSnapshot: TrafficPlanEvidenceTextIdentity;
}
export interface TrafficPlanEvidenceStorage {
  schema: 'fater-plan-traffic-evidence-storage-v1'; storage: 'json-text-v1'; manifest: TrafficPlanEvidenceManifest;
  texts: Record<TrafficPlanEvidenceDataset, string>; indoorSnapshotText: string;
}
export interface TrafficPlanEvidenceAuditMarker {
  schema: 'fater-plan-traffic-evidence-marker-v1'; storage: 'json-text-v1'; revision: string; payloadSha256: string;
  manifest: TrafficPlanEvidenceManifest;
}
export interface PropertyTrafficPlanEvidenceSummary {
  schema: 'fater-plan-traffic-evidence-summary-v1'; propertyId: string; auditId: string; revision: string; generatedAt: string;
  parents: TrafficPlanEvidenceManifest['parents']; parentsCurrent: { core: boolean | null; spatial: boolean | null }; parentsVerified: true;
  datasets: Record<TrafficPlanEvidenceDataset, TrafficPlanEvidenceTextIdentity & { schema: string; generatedAt: string; coverage: Record<string, number>; dataUrl: string;
    vintages: Array<{ referenceYear: number | null; geographyDate: string | null; publicationDate: string | null; measurementYear: number | null }> }>;
  indoorLegacySourcesOverlapCore: boolean; measuredVisitorObservations: 0; limitations: string[];
}
export interface TrafficPlanEvidenceReadResponse {
  schema: 'fater-plan-traffic-evidence-response-v1'; propertyId: string; auditId: string; revision: string; dataset: TrafficPlanEvidenceDataset;
  originalTextSha256: string; originalTextBytes: number; descriptor: PropertyTrafficPlanEvidenceSummary;
  pack: TrafficIndoorCoveragePack | TrafficDirectionalCommutingPack | TrafficAttendanceReferencePack;
}
export interface TrafficPlanEvidencePageSelector { collection: string; offset: number; limit: number }
export interface TrafficPlanEvidencePageResponse extends Omit<TrafficPlanEvidenceReadResponse, 'schema' | 'pack'> {
  schema: 'fater-plan-traffic-evidence-page-v1'; organisationId: string; packSchema: string;
  collection: string; coverage: unknown; limitations: string[]; total: number; offset: number; requestedLimit: number;
  returned: number; nextOffset: number | null; byteLimited: boolean; records: unknown[];
  vintages?: Array<{ referenceYear: number; geographyDate: string; sourceId: string }>;
}
/** Immutable public observations; the private collection archives retain the original source bytes. */
export interface TrafficEngineDataset {
  schema: 'fater-traffic-engine-v1'; storage: 'json-text-v1'; revision: string; propertyId: string; organisationId: string;
  generatedAt: string; packHashes: { traffic: string; demand: string; workday?: string };
  legacySources: Array<{ id: string; procedureName: string; payloadSha256: string }>;
  transport: Pick<TrafficDataPack, 'observations' | 'dailyValidationTotals' | 'hourlyCoverage'>;
  demand: Pick<TrafficDemandPack, 'observations' | 'communeTotals' | 'scope'> & {
    iris: Array<Omit<TrafficDemandPack['iris'][number], 'raw'>>; sourceCsvRows: number;
  };
  sources: TrafficSourceReceipt[];
  workday?: Pick<TrafficWorkdayPack, 'observations' | 'communes' | 'scope' | 'sources' | 'limitations'> & { sourceCsvRows: Record<string, number> };
  coverage: TrafficDataPack['coverage'] & { communes: number; iris: number; demandObservations: number; workdayObservations?: number };
  limitations: string[];
}
export interface PropertyTrafficDataSummary {
  schema: 'fater-traffic-engine-summary-v1'; revision: string; auditId: string; generatedAt: string;
  /** null on a historical audit projection; false when the asset's legacy sources have since advanced. */
  legacySourcesCurrent: boolean | null;
  legacySources: TrafficEngineDataset['legacySources'];
  coverage: TrafficEngineDataset['coverage'];
  annualStation: TrafficObservation[];
  dailyStation: { from: string; to: string; days: number; exactDays: number; subjectId: string; unit: 'validations'; bounds: { lower: number; upper: number } | null } | null;
  hourlyStation: TrafficDataPack['hourlyCoverage'];
  residents: { referenceYear: 2022; geographyDate: '2024-01-01'; scope: TrafficDemandPack['scope']; value: number | null;
    communes: Array<Omit<TrafficDemandPack['communeTotals'][number], 'members'>>;
    ages: Array<{ variable: string; ageBand: string; value: number | null; known: number; missing: number }>;
    ageResidual: number | null;
  };
  sources: TrafficSourceReceipt[]; limitations: string[];
  workday?: {
    referenceYear: 2022; geographyDate: '2025-01-01'; workplaceVariable: 'P22_EMPLT'; residentVariable: 'P22_ACTOCC15P';
    modeVariables: string[]; locationVariables: string[];
    communes: Array<{ communeCode: string; workplaceJobs: number | null; employedResidents: number | null;
      modes: Array<number | null>; locations: Array<number | null>; modeMissing: number; locationMissing: number; modeResidual: number | null; locationResidual: number | null }>;
    sources: Array<Pick<TrafficWorkdayPack['sources'][number], 'id' | 'provider' | 'datasetId' | 'url' | 'licence' | 'retrievedAt' | 'publicationDateAsDisplayed' | 'originalReleaseDate'> & { archiveSha256: string }>;
  };
}
export interface PropertyFootfallSummary {
  shopsInside: number | null;
  centreReviews: number | null;
  topShops: Array<{ name: string; reviews: number }>;
  /** Every named shop the census found, with its public review count and rating where recorded. */
  shops: Array<{ name: string; reviews: number | null; rating: number | null; placeId?: string | null; location?: { lng: number; lat: number } | null; floorLabel?: string | null; category?: string | null; businessStatus?: string | null; activity?: PropertyShopActivity | null; activityUnits?: Array<{ id: string; tenantName: string | null }> }>;
  /** Stored popular times, normalized to each business's own peak; never visitor counts. */
  activity?: { source: string | null; sourceUrl: string; capturedAt: string | null; recordedAt: string | null; profileCount: number } | null;
  annualStations: Array<{ name: string; year: number; passengers: number; source: string | null }>;
  station: {
    name: string;
    perDay: Array<{ day: string; entries: number | null }>;
    period: { from: string; to: string } | null;
    source: string | null;
    recordedAt: string | null;
  } | null;
  baseRecordedAt: string | null;
}

/** A unit's current lease as the financial model reads it, computed on the server, never typed. A null rent is unknown, never 0. */
/** A fact a lease lacks, with the value the centre's survey gives when it has one (lib/analyst/leaseGaps.ts). */
export interface LeaseGap {
  unitId: string; unit: string; dealId: string | null; field: 'tenant' | 'areaSqm' | 'rent' | 'leaseStart' | 'leaseEnd';
  /** The survey's value: a brand for a tenant, square metres for an area. Never a guess: absent when the survey has none. */
  proposal: { value: number | null; brandId: string | null; label: string } | null;
}
export interface AnalystRentRow { unitId: string; unit: string; status: UnitStatus; areaSqm: number | null; tenant: string | null; brandId: string | null; dealId: string | null; annualRent: number | null; leaseStart: string | null; leaseEnd: string | null; /** Rent or dates filled with mock-ups (lib/analyst/rentRoll.ts withMockups). */ mockup?: boolean }

/** The asset's financial assumptions (lib/analyst/assumptions.ts). A null figure is unset; `mockup` puts the pill on every figure built on them. */
export interface AnalystAssumptions {
  yieldPct: number | null; yieldBasis: string; referenceRent: number | null; referenceRentBasis: string;
  worksPerSqm: number | null; rentFreeMonths: number | null; indexationPct: number | null; mockup: boolean;
  revision: number; updatedAt: string | null; updatedBy: string | null;
}

/** Merch scenarios published to the asset (lib/analyst/assetScenarios.ts): shared by its managers, read by Analyst. */
export interface AssetScenarios {
  revision: number;
  items: Array<{
    id: string; planName: string; scenarioName: string; authorId: string; authorName: string; publishedAt: string;
    totals: { valueCreated: number | null; uplift: number | null; works: number | null; occupancy: string };
    mockup: boolean; plan: PulsePlan;
  }>;
}

export interface LeasingPropertyDTO extends BaseLeasingEntity {
  /** Detail-only attributed annual visitor statements; verification dates are not measurement years. */
  annualVisitorFacts?: PropertyAnnualVisitorFact[];
  /** Detail-only summary of the caller organisation's saved activity signals. */
  footfall?: PropertyFootfallSummary | null;
  /** Detail-only, scoped public traffic and resident-demand evidence. */
  trafficData?: PropertyTrafficDataSummary | null;
  /** Detail-only pinned access to verified original residential geography. */
  trafficSpatialData?: PropertyTrafficSpatialSummary | null;
  trafficEvidenceData?: PropertyTrafficPlanEvidenceSummary | null;
  /** Detail-only indoor map of the centre, when one has been imported. */
  venue?: PropertyVenue | null;
  analystWorkbook?: TableDoc | null;
  analystRevision?: number;
  /** One row per unit from the leases (lib/analyst/rentRoll.ts), on the detail only; the Rent Roll sheet is a view of it. */
  rentRoll?: AnalystRentRow[];
  /** What the recorded leases lack, on the detail only; filled on the lease (Unit, Deal), never in a sheet. */
  leaseGaps?: LeaseGap[];
  analystAssumptions?: AnalystAssumptions;
  merchScenarios?: AssetScenarios;
  name: string;
  assetShortCode?: string | null;
  address: string | null;
  city?: string | null;
  country?: string | null;
  location: LeasingLocation | null;
  urls?: string[];
  /** The asset's own logo, archived from its website. */
  logoUrl?: string | null;
  scrapedData?: Record<string, unknown> | null;
  type: string | null;
  notes: string | null;
  declaredFloorCount?: number;
  declaredUnitCount?: number;
  portfolioId: string;
  portfolioName: string | null;
  leasingManagerIds?: string[];
  leasingManagers?: MemberData[];
  portfolioOrganisationId?: string;
  annualVisitors: number | null;
  catchmentPopulation: number | null;
  /** The centre's positioning, one sentence (lib/leasing/positioningNote.ts); '' = unknown to the model. */
  positioningNote?: string | null;
  parkingSpaces?: number | null;
  totalUnits: number;
  sourcingExclusionTenants: boolean;
  sourcingExclusionRecentLeads: boolean;
  sourcingCategoryFilters?: PropertySourcingCategoryFilters | null;
  thumbnailFileId: string | null;
  thumbnailFile: LeasingFileDTO | null;
  unitIds?: string[];
  floorIds?: string[];
  leadIds?: string[];
  factIds?: string[];
  factCount?: number;
  lastFactVerifiedAt?: string | null;
  sourcingTaxonomy: SourcingTaxonomy;
  scoringRubric?: ScoringCriterion[];
  occupancyRate?: number;
  totalAreaSqm?: number;
  vacantAreaSqm?: number;
}

/** A free-form, agent/user-authored sheet of the asset business-plan model, rendered as an extra tab. */
export interface AssetModelCustomSheet {
  id: string;
  name: string;
  /** sparse cell map keyed "row,col" -> string value */
  cells: Record<string, string>;
  /** Excel number-format code per cell, keyed "row,col" (from import), used to render values
   *  exactly as in Excel. Absent for agent/user-authored sheets. */
  cellFormats?: Record<string, string>;
  /** Excel visual style per cell, keyed "row,col" (from import), bold/colours/fills/borders. */
  cellStyles?: Record<string, CellStyle>;
}

/** The asset repositioning business-plan model of one property (client-only what-if state). */
export interface AssetModelRuntimeState {
  /** The editable assumptions. Undefined until the model is first seeded. */
  params?: ScenarioParams;
  /** Agent/user-authored extra tabs. */
  customSheets?: AssetModelCustomSheet[];
  /** Lease-level rent-roll edits, keyed by unitId (or "add:*" for new tenancies). */
  rentRollEdits?: Record<string, RentRollEdit>;
}

/**
 * Runtime-only property overlay stored in entityKernelState.
 * Never persisted in LeasingProperty DTO storage.
 */
/**
 * What survives of a Property's local UI state now that there is no asset page.
 *
 * The tab, the last focused action, the per-entity action list, the thumbnail upload flag, the
 * selected floor and the expanded units were all state of that page and went with it. Both fields
 * left belong to SOURCE, which is the only surface an asset still has.
 */
export interface PropertyRuntimeState {
  footfallStatus?: 'loading' | 'loaded' | 'failed';
  rentRoll?: {
    status: 'loading' | 'ready' | 'error';
    unitIds: string[];
    dealIds: string[];
    error?: string;
  };
  /**
   * The repositioning business-plan model, moved into this overlay on preprod so the in-app
   * spreadsheet and the agent share one source of truth (components/asset-management/
   * assetModelStore.ts). ANALYST state, not asset-page state, so it survives the page's removal -
   * unlike the tab, the focused action, the entity actions and the thumbnail flag, which belonged
   * to that page and went with it.
   */
  assetModel?: AssetModelRuntimeState;
  sourcingBudgetCreditsDraft?: number;
  sourcingSelection?: SourcingTargetRuntimeState;
}

export interface LeasingPortfolioDTO extends BaseLeasingEntity {
  id: string;
  name: string;
  type: PortfolioType;
  category: PortfolioCategory | null;
  status: PortfolioStatus | null;
  logoUrl: string | null;
  websiteUrls: string[];
  city: string | null;
  country: string | null;
  portfolioFitCountryCodes?: string[];
  portfolioFitMinimumNetworkSize?: number;
  intentScore: number | null;
  propertyCount: number;
  realPropertyCount?: number | null;
  updatedAt: string;
  createdAt: string;

  // Optional Detail fields (Skeleton)
  description?: string | null;
  linkedinUrl?: string | null;
  twitterUrl?: string | null;
  facebookUrl?: string | null;
  foundedYear?: number | null;
  address?: string | null;
  state?: string | null;
  postalCode?: string | null;
  employeeCount?: number | null;
  annualRevenue?: string | null;
  totalFunding?: string | null;
  industry?: string | null;
  technologies?: string[];
  intentTopic?: string | null;
  scoringRubric?: ScoringCriterion[];
  propertyIds?: string[];
  portfolioFitIds?: string[];
  portfolioFitCampaignIds?: string[];
}

/**
 * Runtime-only portfolio overlay stored in entityKernelState.
 * Never persisted in LeasingPortfolio DTO storage.
 */
export interface PortfolioRuntimeState {
  activeTab?: 'FOUNDATION' | 'PROPERTIES' | 'PORTFOLIO_FIT_CAMPAIGNS' | 'PORTFOLIO_FITS';
  propertiesViewMode?: 'table' | 'grid';
  portfolioFitsViewMode?: 'table' | 'grid';
  sourcingViewMode?: 'table' | 'grid';
  portfolioFitBudgetCreditsDraft?: number;
  portfolioFitExcludeRecentScoresDraft?: boolean;
  portfolioFitRequireKnownWebsiteDraft?: boolean;
  portfolioFitMinimumNetworkSizeDraft?: number;
  sourcingSelection?: SourcingTargetRuntimeState;
}

export interface LeasingMarketSignalDTO {
  id: string;
  propertyName: string;
  propertyAddress: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  location: Record<string, unknown> | null;
  latitude: number | null;
  longitude: number | null;
  summary: string | null;
  sourceName: string | null;
  unitIdentifier: string | null;
  unitType: string | null;
  floorLabel: string | null;
  status: string | null;
  areaSqm: number | null;
  areaSqft: number | null;
  rentAmount: number | null;
  rentCurrency: string | null;
  rentCadence: string | null;
  chargesIncluded: boolean | null;
  availabilityDate: string | null;
  listingReference: string | null;
  listingHeadline: string | null;
  listingDescription: string | null;
  amenities: unknown;
  keyFacts: Record<string, unknown> | null;
  listingUrl: string;
  rawPriceText: string | null;
  properties: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Runtime-only market signal overlay stored in entityKernelState.
 * Never persisted in LeasingMarketSignal DTO storage.
 */
export interface MarketSignalRuntimeState {
  activeTab?: 'FOUNDATION' | 'LISTING' | 'INTEGRITY';
}

export interface NumericSummaryStats {
  average: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
}

export interface MarketSignalBenchmarkExample {
  id: string;
  unitIdentifier: string | null;
  propertyName: string;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  areaSqm: number | null;
  rentAmount: number | null;
  rentCurrency: string | null;
  rentCadence: string | null;
  chargesIncluded: boolean | null;
  listingUrl: string;
  listingReference: string | null;
  distanceKm: number;
  rentPerSqm: number | null;
  ingestionDate: string;
  score: number;
  summary: string | null;
}

export interface MarketSignalBenchmarkResponse {
  center: {
    latitude: number;
    longitude: number;
    resolvedAddress: string | null;
    source: 'provided' | 'geocoded';
  };
  filters: {
    radiusKm: number;
    maxExamples: number;
    targetAreaSqm: number | null;
    rentCurrency: string | null;
  };
  comparableCount: number;
  totalScopedSignals: number;
  stats: {
    rentPerSqm: NumericSummaryStats;
    rentAmount: NumericSummaryStats;
    areaSqm: NumericSummaryStats;
  };
  examples: MarketSignalBenchmarkExample[];
  metadata: {
    notes: string[];
    currencyMatchedCount: number;
    sampleSizeBeforeFilters: number;
  };
}

export interface LeasingBrandLocationDTO {
  id: string;
  brandId?: string;
  name: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  surfaceArea: number | null;
  openedAt: string | null;
  isClosed: boolean;
  closedAt: string | null;
  /** SIRET (14-digit) when the store came from the government registry, used to link the official
   *  closure record (proof) for closed stores. */
  externalId?: string | null;
  /** No `review:` marker on the row's source yet: written (e.g. by /fill-stores) but not passed
   *  by the store reviewer. The map draws these green (SRC-035). */
  pendingReview?: boolean;
  /** Effective store review; negative verdicts must not appear as active map pins. */
  reviewVerdict?: 'GO' | 'CLOSED' | 'NOGO' | null;
}

type BrandContact = {
  name?: string | null;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  [key: string]: unknown;
};

export interface ContactDTO {
  id: string;
  parentId: string;
  parentType: string;
  sourcePersonId: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  title: string | null;
  photoUrl: string | null;
  email: string | null;
  primaryPhone: string | null;
  directPhone: string | null;
  linkedinUrl: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  organizationName: string | null;
  seniority: string | null;
  departments: string[];
  lastSourcedAt: string | null;
  lastEnrichedAt: string | null;
  createdAt: string;
}
export type FinancialStatus = 'STRONG' | 'STABLE' | 'WATCHLIST' | 'DISTRESSED' | 'UNKNOWN';

export type LegalRole = 'HEAD_OFFICE' | 'FRANCHISEE' | 'SUBSIDIARY' | 'HOLDING' | 'UNKNOWN';

export interface LeasingLegalEntityDTO {
  id: string;
  registrationNumber: string;
  country: string;
  legalName: string;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  status: string | null;
  registrationDate: string | null;
  financialScore: number | null;
  financialStatus: FinancialStatus | null;
  revenueGrowth: number | null;
  lastFinancialYear: number | null;
  shareCapital: number | null;
  workforceRangeMin: number | null;
  /** BODACC court distress, ungated: a fact about the company, citable, never a revenue claim. */
  distressStatus?: string | null;
  distressLatestDate?: string | null;
  distressLatestNature?: string | null;
  financials?: LeasingBrandFinancialRecordDTO[];
  linkedBrands?: { id: string; name: string; role: string; logoUrl?: string | null }[];
}

/**
 * Runtime-only legal entity overlay stored in entityKernelState.
 * Never persisted in LeasingLegalEntity DTO storage.
 */
export interface LegalEntityRuntimeState {
  activeTab?: 'FOUNDATION' | 'FINANCIALS' | 'BRANDS';
}

interface LeasingBrandLegalStatusDTO {
  role: LegalRole;
  isPrimary: boolean;
  legalEntity: LeasingLegalEntityDTO;
}

export interface LeasingBrandDTO extends BaseLeasingEntity {
  name: string;
  qualification?: BrandQualityAssessment;
  /** Public reviewed facts; null explicitly retires stale or unapproved cached proof. */
  campaignDossier?: BrandCampaignDossierProjection | null;
  origin?: string | null;
  lifecycleStatus?: BrandLifecycleStatus;
  liquidatedAt?: string | null;
  category: BrandCategory;
  cnccSector?: CNCCSector | null;
  cnccSubFamily?: string | null;
  cnccActivity?: string | null;
  marketPositioning?: BrandMarketPositioning | null;
  /** How the brand positions itself, one sentence (scripts/brands/positioning-note.ts); '' = unknown to the model. */
  positioningNote?: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  description: string | null;
  summary: string | null;
  geographicFootprint: string | null;
  brandNarrative: string | null;
  expansionTeamContacts?: BrandContact[] | null;
  keyContacts?: BrandContact[] | null;

  // --- SKELETON ARRAYS ---
  legalEntityIds?: string[];
  primaryLegalEntityId?: string | null;
  legalEntityCount?: number;
  dealIds?: string[];
  leadIds?: string[];
  factIds?: string[];
  factCount?: number;
  lastFactVerifiedAt?: string | null;

  // Physical Specs
  surfaceMin?: number | null;
  surfaceMax?: number | null;
  surfaceStandard?: number | null;
  locationTypes?: string[];
  conceptFormats?: string[];

  // Network Health
  declaredNetworkSize?: number | null;
  foundedYear?: number | null;

  annd?: number | null;
  anndMin?: number | null;
  anndP10?: number | null;
  anndP50?: number | null;
  anndStdDev?: number | null;
  locationCount?: number | null;
  operatingCountryCodes?: string[];
  spatialCalculatedAt?: string | null;
  sortableFinancialScore?: number | null;
  sortableTotalRevenue?: number | null;
  sortableRevenueGrowth?: number | null;
  revenueConfidence?: number | null;
  revenueIsEstimate?: boolean | null;
  /**
   * WHOSE accounts the financial figures on this brand are.
   *
   * Our financials come from a national company registry, so a brand's revenue is the revenue of
   * ONE legal entity in ONE country. Pull & Bear reads EUR 170.46M because that is PULL & BEAR
   * FRANCE; the worldwide brand and Inditex are different numbers entirely. Taking it for a global
   * figure is a real commercial error - it was made in this app - and nothing on screen said
   * otherwise. These two carry the entity name and its country so every surface can.
   */
  revenueEntityName?: string | null;
  revenueEntityCountry?: string | null;
  /** The period the source attributes the figure to. An amount with no year reads as "now". */
  revenuePeriod?: string | null;
  /**
   * The court's word on the brand's company, from BODACC announcements. A LIQUIDATION here with a
   * verified revenue is not a contradiction - it is the whole picture, and both are shown.
   */
  distressSignal?: { status: string; date: string | null; nature: string | null } | null;
  /**
   * The MODELLED revenue, in its own field so nothing can mistake it for a measured one.
   *
   * It is store count x a typical surface x a sector benchmark. No source states it. It is kept
   * because for the ~45% of French companies that file their accounts confidentially it is the
   * only signal there is, and it is fenced because a modelled number rendered beside a filed one,
   * in the same column and the same format, is how a leasing manager comes to trust both equally.
   * Never evidence to a scoring judge, never a point on a chart, never sorted against a verified
   * figure, and always shown with the inputs it was built from. See lib/leasing/factGate.ts.
   */
  estimatedRevenue?: {
    revenue: number | null;
    currency: string;
    year: number | null;
    method: string | null;
    inputs: Record<string, unknown> | null;
  } | null;
  /**
   * The filed years, and only the filed years. This is what the revenue chart plots.
   *
   * It exists because the chart used to draw four points from ONE number by back-projecting it at
   * an assumed growth rate: a picture of a history we did not have. A series has to come from a
   * series. Each entry carries its own citation so a reader can open the page it came from.
   */
  verifiedFinancials?: Array<{
    year: number;
    revenue: number | null;
    currency: string;
    /** Net profit from the same filing as `revenue`; null when that filing is not ours to prove. */
    netIncome?: number | null;
    entityName: string | null;
    sourceUrl: string | null;
  }>;
  /** The brand's group or worldwide revenue, verified, kept apart from the French series. */
  groupFinancials?: Array<{ year: number; revenue: number; currency: string; entityName: string | null; sourceUrl: string | null }>;
  /**
   * Figures we HOLD but have not proved: web research and registry reads whose verdict is still
   * NULL or UNPROVEN. They render in their own labelled block, never on the verified chart, never
   * in a sort - the owner's call of 2026-09-19: show what we have, ranked by how proved it is,
   * instead of a blank panel beside an agent quoting the very numbers the panel hides.
   * CONTRADICTED rows are excluded: a figure proven wrong is not information, it is a retraction.
   */
  declaredFinancials?: Array<{
    year: number;
    revenue: number;
    currency: string;
    source: string | null;
    sourceUrl: string | null;
  }>;
  /**
   * Why the brand has no recent accounts, from the official gazette and directory
   * (scripts/brands/register-filings.ts, lib/leasing/registerAccounts.ts): accounts withheld by law,
   * a company too young to have filed twice, or a company that does not file. Null when the register
   * has nothing to explain.
   */
  registerAccounts?: {
    kind: 'confidential' | 'young' | 'not_filing' | 'partial';
    lastFiled: number | null;
    founded: string | null;
    withheld: number[];
    lastTwo: Array<{ year: number; fact: 'read' | 'withheld' | 'not_filed' }>;
    companies: Array<{ siren: string; name: string; parent: boolean; created: string | null; state: string | null; closedOn: string | null; openSites: number | null;
      filings: Array<{ year: number; confidential: boolean; url: string | null }> }>;
    finding: { en: string; fr: string; sources: Array<{ label: string; url: string }> } | null;
    checkedAt: string | null;
  } | null;

  // Enriched fields (Optional)
  activeUnitCount?: number;
  activePropertyCount?: number;
  knownLocationsCount?: number;
  portfolioFootprint?: LeasingBrandFootprintEntryDTO[];
  contactIds?: string[];
  /** Fater's one recommended person to contact (scripts/brands/recommend-contacts.ts). */
  recommendedContact?: { contactId: string; reason: string; summary: string } | null;
  /** The best photos of the brand's own shops, from Google, copied into our storage (scripts/brands/store-gallery.ts). */
  storeGallery?: Array<{ url: string; city: string | null; address: string | null; author: string | null }>;
  /** The brand's French prices on its trade's reference products, each with its page and picture (scripts/brands/brand-prices.ts). */
  productPrices?: {
    activity: string | null; entryPriceEur: number | null; typicalPriceEur: number | null;
    /** 1 = the trade's median brand, 2 = twice as dear. */
    priceIndex: number | null; checkedAt: string | null;
    items: Array<{ product: string; priceEur: number; url: string; image: string | null }>;
  } | null;
  locations?: LeasingBrandLocationDTO[];
  scanHistory?: LeasingBrandScanHistoryEntry[];
  lastContactDiscoveryAt?: string | null;
  /** When each researched field was last checked, `{ website: iso, ... }`. */
  enrichmentCheckedAt?: Record<string, string> | null;
  contactDiscoveryStatus?: string | null;
}

/**
 * How much weight a data-check finding carries.
 *  'wrong'   the stored value is contradicted by something we can point at
 *  'suspect' it looks off but nothing proves it
 *  'gap'     it is absent, and the absence actually blocks a leasing decision
 */
export type BrandDataFindingSeverity = 'wrong' | 'suspect' | 'gap';

/** One thing the data check judged to be worth telling the user about. */
export interface BrandDataFinding {
  /** Two or three words naming the subject, in the user's language. */
  label: string;
  /** The verdict itself, four words at most: what the row says without hovering. */
  verdict: string;
  /** One sentence explaining it, in the user's language. The row carries it as its title. */
  detail: string;
  severity: BrandDataFindingSeverity;
}

/**
 * The result of one data-check pass over a brand. An empty `findings` with a `checkedAt`
 * means the pass ran and found nothing, which is itself an answer worth showing.
 */
export interface BrandDataCheck {
  checkedAt: string;
  /** The language the findings were written in, so a language switch can invalidate them. */
  lang: string;
  /**
   * What the same click PROVED, before it commented on anything.
   *
   * The check used to be pure opinion about a stored record. Now it first tries to earn the record
   * back: which company this brand actually is (from its own store footprint, never from a name
   * match) and what that company filed. Reported to the user because "we could not prove it, and
   * here is why" is the most useful thing this button can say when it comes back empty — and
   * `confidential` in particular is permanent, not a gap worth clicking again.
   */
  verification?: {
    anchor: { method: string | null; siren: string | null; entityName: string | null; reason: string | null };
    revenue: {
      outcome: 'VERIFIED' | 'UNPROVEN' | 'CONTRADICTED';
      value: number | null;
      year: number | null;
      reason: string | null;
      sourceUrl: string | null;
    };
  };
  findings: BrandDataFinding[];
}

/**
 * The France openings/closures signal served by GET /api/inventory/brands/[brandId]/openings.
 * A derived sub-resource of the brand, not a column of the Brand DTO: it is loaded on demand by
 * the expansion chart rather than paid for on every brand read.
 */
export type BrandCampaignDossierProjection = {
  milestones?: BrandExpansionMilestone[];
  overview?: CampaignOverviewProjection;
  registrySeries?: CampaignRegistrySeries;
  plannedPremises?: CampaignPlannedPremise[];
  sources: Array<Pick<BrandQualificationSource, 'id' | 'url' | 'effectiveAt' | 'observedAt' | 'quote'>>;
};

export interface BrandExpansionSignal {
  registrySeries?: (CampaignRegistrySeries & { sources: Array<Pick<BrandQualificationSource, 'url' | 'effectiveAt' | 'observedAt' | 'quote'>> }) | null;
  plannedPremises?: Array<CampaignPlannedPremise & { sources: Array<Pick<BrandQualificationSource, 'url' | 'effectiveAt' | 'observedAt' | 'quote'>> }>;
  /** Independently approved physical milestones; these do not certify complete annual history. */
  milestones?: Array<BrandExpansionMilestone & { sources: Array<Pick<BrandQualificationSource, 'url' | 'effectiveAt' | 'quote'>> }>;
  authoritative?: boolean;
  anchored?: boolean;
  hasFrenchPresence?: boolean;
  source?: string | null;
  /** The SIRENs whose establishment history the series was read from; the registry link per SIREN. */
  sirens?: string[];
  /** The same companies with their register names, for the chart's caption. */
  companies?: Array<{ siren: string; name: string | null }>;
  /** Open French stores we hold, and the counted establishments still open today (null when the detail is partial). */
  frenchStores?: number | null;
  openEstablishments?: number | null;
  /** For a web-researched series: the pages it was read from. */
  sources?: string[];
  calculatedAt?: string | null;
  status?: string | null;
  confidence?: number | null;
  lastOpeningDate?: string | null;
  openingsByYear?: Record<string, number>;
  closuresByYear?: Record<string, number>;
  openings3y?: number | null;
  closures3y?: number | null;
  netOpenings3y?: number | null;
  evidence?: BrandOpeningsEvidence | null;
}

/** The official establishments behind a brand's openings/closures counts. */
export interface BrandOpeningsEvidence {
  source: string;
  method: 'SIRENE_HISTORIQUE' | 'RECHERCHE_ENTREPRISES';
  release: string | null;
  datasetUrl: string;
  sirens: string[];
  total: number;
  establishments: Array<{ siret: string; opened: string | null; closed: string | null; isClosed?: boolean }>;
}

/**
 * The three issues of that load. The chart tells "still loading" apart from "the signal is
 * momentarily unavailable", so the failure has to be representable, not just an absent payload.
 */
/** The brand's recent news, kept by lib/brands/brandNews.ts: a summary and the articles behind it. */
export interface BrandNews {
  at: string;
  summary: { en: string; fr: string };
  articles: Array<{ url: string; title: string; source: string; publishedAt: string | null; image: string | null; idea: { en: string; fr: string } }>;
}

/** A brand's shops on Google Maps, taken together (lib/brands/brandGoogleSignals.ts). */
export interface BrandGoogleSignals {
  at: string;
  shopsMatched: number;
  openShops: number;
  averageRating: number | null;
  medianReviews: number | null;
  totalReviews: number;
  operationalShare: number;
  medianWeeklyHours: number | null;
  ratingBands: Array<{ from: number; to: number; shops: number }>;
  shops: Array<{ name: string; city: string | null; reviews: number; rating: number }>;
}

export type BrandGoogleSignalsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'loaded'; signals: BrandGoogleSignals | null };

export type BrandNewsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'loaded'; news: BrandNews | null };

export type BrandExpansionSignalState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'loaded'; signal: BrandExpansionSignal };

export type BrandExpansionPeriod = '3y' | '6y' | '10y' | 'all' | number;

/**
 * Runtime-only brand overlay stored in entityKernelState.
 * Never persisted in LeasingBrand DTO storage.
 */
export interface BrandRuntimeState {
  activeTab?: 'OVERVIEW' | 'LEGAL_ENTITIES' | 'CONTACTS' | 'LEADS' | 'FACTS';
  /**
   * The reader's own assumptions for the revenue estimate, as typed into the hypothesis chart.
   *
   * An overlay and NOT a Brand column, deliberately: "what if they really have 300 stores" is a
   * question this user is asking right now, not a fact about the brand. Writing it to the record
   * would publish one person's what-if to everyone and, worse, feed it back into the estimator as
   * though it were a signal we had sourced.
   */
  revenueHypothesis?: { storeCount?: number | null; surfaceSqm?: number | null };
  leadsViewMode?: 'table' | 'grid';
  viewMode?: 'map' | 'stats';
  focusedLocationId?: string | null;
  selectedLegalEntityId?: string | null;
  isGeoSelectionMode?: boolean;
  /**
   * Last data check for this brand. It lives here, not on the Brand row, because it is a
   * per-user on-demand reading of the data rather than data itself, and because caching it
   * in the kernel is what keeps re-selecting a brand from re-billing the pass.
   */
  dataCheck?: BrandDataCheck;
  /**
   * Last expansion signal read for this brand, with its load outcome. Same reasoning as
   * `dataCheck`: a per-user on-demand reading of a derived sub-resource, cached here so that
   * clicking away and back does not re-query it.
   */
  expansionSignal?: BrandExpansionSignalState;
  expansionPeriod?: BrandExpansionPeriod;
  /** The brand's news, read when its News tab opens (lib/brands/brandNews.ts). */
  news?: BrandNewsState;
  /** Its shops on Google Maps, read when the Overview opens. */
  googleSignals?: BrandGoogleSignalsState;
}

export type DealStatus =
  | 'DRAFT'
  | 'REVIEW'
  | 'APPROVED'
  | 'OFFER_SENT'
  | 'SIGNED'
  | 'DEAD'

export type GeneratedDocumentType = 'INCENTIVE' | 'LOI';

interface GeneratedDocumentStaleness {
  schemaVersion: 'DOC_STALENESS_V1';
  isStale: boolean;
  profile: 'INCENTIVE_TERMS_V1' | 'LOI_TERMS_V1';
  baselineHash: string;
  currentHash: string;
  changedKeys: string[];
  baselineCapturedAt: string;
  staleSince: string | null;
  checkedAt: string;
}

interface GeneratedDocumentMetadata {
  missing_critical_data?: Array<{ field: string; reason: string }>;
  suggestions?: string[];
  confidence?: number;
  professional_readiness_score?: number;
  professional_review?: {
    summary?: string;
    issues?: Array<{
      code: string;
      severity: 'LOW' | 'MEDIUM' | 'HIGH';
      fieldPath: string;
      currentValue?: string | null;
      reason: string;
      suggestedFix: string;
      blocksProfessionalUse?: boolean;
    }>;
  };
  staleness?: GeneratedDocumentStaleness;
  [key: string]: unknown;
}

interface LeasingBrandFootprintEntryDTO {
  dealId: string;
  dealStatus: DealStatus;
  tenantLegalName: string | null;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  unitId: string;
  unitIdentifier: string | null;
  propertyId: string;
  propertyName: string | null;
  isActiveOccupant: boolean;
}

export interface LeasingBrandScanHistoryEntry {
  countryCode: string;
  lastScannedAt: string;
  locationsFound: number;
}

export type BrandSortField = 'RELEVANCE' | 'NAME' | 'LOCATION_COUNT' | 'SURFACE' | 'FINANCIAL_SCORE' | 'REVENUE' | 'GROWTH' | 'INTERNAL_PRIORITY';

interface BaseInventoryFilters {
  search?: string;
  ids?: string[];
}

/** Admin > Brands: one import source's brand count and data-quality counts. */
export interface BrandOriginBucket {
  origin: string;
  brands: number;
  stores: number;
  withStores: number;
  registryMatched: number;
  withWebsite: number;
  categorised: number;
  usedByClients: number;
  liquidated: number;
  /** Revenue from the company registry (not an estimate). */
  officialRevenue: number;
  estimatedRevenue: number;
  /** Store-opening trend known and backed by a recorded source. */
  expansionSourced: number;
  expanding: number;
  /** Has every "Data completeness" card: stores, registry, website, category, official revenue, expansion. */
  complete: number;
}

export interface LeasingBrandFilters extends BaseInventoryFilters {
  includeQualification?: boolean;
  /** 'recommended': only brands carrying the Fater stamp (Admin > Data's recommendation list). */
  stamp?: 'recommended' | 'privileged' | 'ready' | 'enriching' | 'tier2' | 'tier3' | 'out';
  qualificationStatus?: BrandQualityAssessment['eligibility'] | BrandQualityAssessment['eligibility'][];
  category?: BrandCategory | null;
  /** Import wave code(s) from lib/brands/brandOrigin.ts, or 'UNKNOWN'. */
  origin?: string | string[];
  /** Admin > Brands: 'COMPLETE' or 'INCOMPLETE' against the Data completeness cards. */
  completeness?: 'COMPLETE' | 'INCOMPLETE';
  includeLiquidated?: boolean;
  hasLegalEntity?: boolean;
  minWorkforce?: number;
  minFinancialScore?: number;
  minShareCapital?: number;
  minLocations?: number;
  conceptFormats?: BrandConceptFormat[];
  minSurface?: number;
  maxSurface?: number;
  /** Classification filters. hasFrenchPresence: 'true' | 'false'. openingsData: 'complete' (has
   *  authoritative SIRENE data) | 'missing'. Sent as query params, enforced server-side. */
  hasFrenchPresence?: string;
  openingsData?: string;
  /** ISO-2 country code, brands with an open store in that country (tenants folder browser). */
  country?: string;
  sortBy?: BrandSortField;
  sortDir?: 'asc' | 'desc';
}

export type PortfolioSortField = 'NAME' | 'INTENT' | 'ASSETS' | 'STATUS' | 'UPDATED' | 'CREATED';

export interface LeasingPortfolioFilters extends BaseInventoryFilters {
  type?: PortfolioType[];
  category?: PortfolioCategory[];
  status?: string[];
  country?: string;
  minAssets?: number;
  minIntentScore?: number;
  maxIntentScore?: number;
  minEmployees?: number;
  maxEmployees?: number;
  sortBy?: PortfolioSortField;
  sortDir?: 'asc' | 'desc';
}

export type PropertySortField = 'NAME' | 'CITY' | 'UNIT_COUNT' | 'OWNER' | 'OCCUPANCY';

export interface LeasingPropertyFilters extends BaseInventoryFilters {
  country?: string;
  owner?: string; // Landlord
  managerId?: string;
  minUnits?: number;
  minSurface?: number; // Min surface of any unit in property
  maxSurface?: number;
  sortBy?: PropertySortField;
  sortDir?: 'asc' | 'desc';
  organisationId?: string;
}

export interface UniversalFactFilters extends BaseInventoryFilters {
  entityType?: string;
  entityId?: string;
  category?: UniversalFactCategory;
}

interface LeasingBrandListItemDTO extends LeasingBrandDTO {
  activeUnitCount: number;
  activePropertyCount: number;
  knownLocationsCount: number;
}

interface LeasingCoTenancyEntry {
  unitId: string;
  unitIdentifier: string;
  status: string | null;
  areaSqft: number | null;
  brandName: string | null;
  brandCategory: string | null;
  dealId: string | null;
  tenantLegalName?: string | null;
  leaseEndDate?: string | null;
  isSubjectUnit?: boolean;
}

type MarketDemandJobStatus = 'PENDING' | 'RUNNING' | 'UPLOADED' | 'ANALYZED' | 'FAILED';

interface MarketDemandJobParams {
  subcategory: string;
  searchQuery: string;
  assetClass?: string | null;
  maxIterations?: number | null;
}

interface MarketDemandMissionJob {
  jobId: string;
  status: MarketDemandJobStatus;
  params: MarketDemandJobParams;
  queuedAt: string;
  completedAt?: string | null;
  blobUrl?: string | null;
  lastError?: string | null;
  unitIds: string[];
  metrics?: {
    totalDemands: number;
    qualifiedLeads: number;
    lastUpdatedAt: string;
  };
}

interface MarketDemandMissionUnit {
  id: string;
  propertyId: string;
  propertyName: string | null;
  unitIdentifier: string;
  assetType: string | null;
  areaSqm: number | null;
  areaSqft: number | null;
  targetRent: number | null;
  profile: string | null;
  locationLabel: string | null;
}

export interface MarketDemandArticleData {
  url: string;
  title: string | null;
  description: string | null;
  minimalSurface: number | null;
  maximalSurface: number | null;
  maximalRent: number | null;
}

interface MarketDemandMatchResult {
  unitId: string;
  demandUrl: string;
  demandTitle: string | null;
  demandDescription: string | null;
  matchScore: number;
  reason: string | null;
  budgetEstimate?: number | null;
  surfaceRange?: {
    min?: number | null;
    max?: number | null;
  };
}

interface MarketDemandMissionAnalysis {
  totalDemandsScanned: number;
  qualifiedLeadsFound: number;
  matches: MarketDemandMatchResult[];
  lastUpdatedAt: string;
}

interface MarketDemandMissionPayload {
  missionType: 'MARKET_DEMAND_ANALYSIS';
  units: MarketDemandMissionUnit[];
  jobs: MarketDemandMissionJob[];
  analysis: MarketDemandMissionAnalysis | null;
}

// --- Market Sourcing Types ---

interface SourcingMatrixItem {
  brandId: string;
  name: string;
  instinctScore?: number; // 0-100 (Weighted Average), undefined = not scored yet
  criteriaScores?: Record<string, number>; // id -> score (0-100)
  reasoning?: string;
  selected?: boolean; // UI state
}

/** What the user authorised the Source agent to drive on its own: an asset set, a goal, a credit ceiling. */
export interface SourcingMandate {
  goal: string;
  propertyIds: string[];
  creditCeiling: number;
  /** Sum of the budgets of the launches and extensions already started under this mandate. */
  committedCredits: number;
  createdAt: string;
  expiresAt: string;
  /** 'launch-bar': the user pressed Launch on the dashboard bar; the agent launches without asking. */
  origin?: 'chat' | 'launch-bar';
  /** A unit fit: the one unit of the asset the mandate is for. */
  unitId?: string | null;
  /** The champion's score the agent works towards (lib/leasing/sourcingMandate.ts, default 90). */
  targetScore?: number;
}

export interface SourceBudgetProposal {
  id: string;
  campaignId: string;
  campaignName: string;
  chatSessionId: string;
  additionalCredits: number;
  previousCeiling: number;
  creditCeiling: number;
  committedCredits: number;
  goal: string;
  targetScore: number;
  expiresAt: string;
  status: 'pending' | 'approving' | 'accepted' | 'declined' | 'failed';
  message?: string;
  /** A widened brief the approval also applies, and the new candidates it opens, measured when proposed. */
  brief?: SourceBriefChange | null;
  newCandidates?: number;
}
/** A change to a campaign's pool: surface range and minimum store count, from what the run had to what it proposes. */
export interface SourceBriefChange {
  from: { surfaceMinSqm: number | null; surfaceMaxSqm: number | null; minimumNetworkSize: number | null };
  to: { surfaceMinSqm?: number | null; surfaceMaxSqm?: number | null; minimumNetworkSize?: number | null };
}

/**
 * A mandate as the SERVER holds it, on the config of the campaign it drives (config.mandate). The
 * worker's driver (lib/leasing/mandateDriver.ts) takes each landed pass from here, browser closed.
 */
export type ServerSourcingMandate = {
  goal: string;
  targetScore: number;
  creditCeiling: number;
  committedCredits: number;
  expiresAt: string;
  chatSessionId: string;
  /** The user the mandate spends for; set by the server, never read from a request. */
  userId: string;
  unitId: string | null;
  /** A pass launched under the mandate has not been driven yet. Cleared by the driver's claim. */
  pendingPass: boolean;
  /** Human-approved campaign allowance, retained for server-side continuation reservations. */
  budgetProposalId?: string;
  passes: number;
  endedAt?: string | null;
  outcome?: 'champion' | 'ceiling' | 'stopped' | 'failed' | 'expired' | 'blocked' | null;
};

export interface ScoringCriterion {
  id: string;
  label: string;
  weight: number;
  minimumScore?: number; // Optional eliminatory threshold on criterion points scale (0..weight)
}

// THE SCORING GRID: one, for every asset and every run (owner, 2026-10-07: "there should be just
// one and it should be the most complete and coherent"). Assets used to carry their own editable
// rubric, templates loaded others, and each brief appended criteria, so one centre's shortlist held
// rows scored on sixteen different grids. The criteria are fixed here; only a run's weights move
// (lib/leasing/runWeights.ts) and the brief only tells two of them what was asked
// (lib/leasing/briefCriteria.ts scoringGrid). Weights sum to 100, so the fit is a 0-100 index.
// NO eliminatory floors: a weak criterion lowers the score; only "would the brand come here"
// (measured, lib/leasing/catchmentFit.ts) sets a brand aside, and it scales the whole score.
export const DEFAULT_PROPERTY_SCORING_RUBRIC: ScoringCriterion[] = [
  {
    // Replaces the old proximity_cannibalization_risk (20) + operational_logistics_capacity (10),
    // which measured the SAME distance in OPPOSITE directions: one punished being close, the other
    // punished being far. 30 points of the grid partly cancelled itself and the netting was
    // invisible in the final score. One signed criterion with an explicit sweet spot instead.
    id: 'network_fit',
    label:
      "Adequation au Reseau Existant : distance aux points de vente VERIFIES de l'enseigne DANS LE PAYS CIBLE. Il y a un optimum : trop pres = cannibalisation du magasin existant (tres mauvais) ; trop loin de tout cluster = pas de management, pas de logistique, pas de notoriete locale (mauvais). Chercher : adresses des magasins verifies dans le pays, distance au plus proche en km, taille du cluster regional, entrepot ou plateforme logistique la plus proche. Une enseigne SANS presence verifiee dans le pays ne gagne PAS de points ici : score neutre au mieux.",
    weight: 10,
  },
  {
    // NEW. The single most attackable gap in the old grid: nothing asked whether the brand can
    // actually pay this asset's rent. financial_health_risk is solvency, which is a different
    // question from unit economics.
    id: 'rent_paying_capacity',
    label:
      "Capacite Locative : l'enseigne peut-elle payer le loyer de cet actif ? Chercher : chiffre d'affaires au m2 (densite de vente) publie ou estime pour l'enseigne, loyer moyen ou taux d'effort (loyer/CA) declare par le groupe, marge brute de la categorie, loyers pratiques par des enseignes comparables dans des centres equivalents. Une enseigne dont l'economie ne supporte pas l'ERV de l'actif est mal notee meme si tout le reste correspond.",
    weight: 10,
  },
  {
    // NEW at asset level. Surface existed only as a FILTER, which excludes but never ranks: a
    // brand at the very edge of the range scored identically to a perfect fit. The /refine critic
    // invented this criterion unprompted, which is the strongest evidence the gap was real.
    id: 'surface_format_fit',
    label:
      "Compatibilite Surface & Format : le format type de l'enseigne rentre-t-il dans les surfaces reellement disponibles dans l'actif ? Chercher : surface moyenne et fourchette min/max des magasins de l'enseigne (m2 de vente), format de concept (standard, compact, flagship, corner, kiosque), existence d'un format reduit adapte aux centres commerciaux, contraintes de lineaire de vitrine. Un ecart de surface est penalisant meme quand la marque est parfaite par ailleurs.",
    weight: 15,
  },
  {
    // NEW, and it absorbs the old price_positioning_retail_mix_fit (10). That criterion only asked
    // "do they match what is already there" and never "do we NEED them", a centre with six shoe
    // retailers does not need a seventh that fits perfectly.
    id: 'merchandising_fit_and_gap',
    label:
      "Complementarite & Manque a Combler : l'enseigne complete-t-elle le merchandising de l'actif, ou double-t-elle une offre deja presente ? Chercher : categorie CNCC precise de l'enseigne, enseignes concurrentes directes deja presentes dans l'actif, categories sous-representees ou absentes du centre, positionnement prix compare aux locomotives en place. Combler un manque vaut plus que s'ajouter a une categorie saturee.",
    weight: 10,
  },
  {
    // Measured, never judged: the model's guess is replaced by catchmentFit.ts. It absorbs the old
    // socio_demographic_affinity (a model reading INSEE figures it was not given) and the brief's
    // yes/no positioning check, which gave Patagonia, Jules and Action the same 10/10.
    id: 'catchment_fit',
    label:
      "L'enseigne viendrait-elle ici : mesuré, jamais jugé. (1) Quartiers de ses propres magasins (INSEE Filosofi 2021, niveau de vie à 1 km) comparés à celui de l'actif ; (2) son positionnement et ses prix comparés aux enseignes déjà signées dans le centre. Ne pas noter : le score est calculé.",
    weight: 25,
  },
  {
    // Cut from 25 to 10. It was the heaviest criterion in the grid and barely discriminated ,
    // nearly every brand scored full marks on it, so a quarter of the score was a constant.
    id: 'development_intent',
    label:
      "Dynamique d'Expansion Recente : preuves VERIFIEES d'ouvertures, de signatures et d'investissements DANS LE PAYS CIBLE sur les 24 derniers mois. Chercher : communiques d'ouverture, articles de presse locale, annonces de plan de developpement avec un nombre d'ouvertures cible, recrutements de directeurs de magasin dans le pays. Un reseau declare a l'etranger ne compte pas ; sans preuve verifiee dans le pays, la note est tres basse.",
    weight: 10,
  },
  {
    // Cut to 5: whether the brand would come to a centre like this one is now measured (catchment_fit).
    id: 'portfolio_center_type_experience',
    label:
      "Experience des Formats de Centres : l'enseigne sait-elle exploiter ce type d'emplacement ? Chercher : repartition de son parc entre centre commercial, retail park, rue commercante, gare et outlet, et si elle exploite deja des centres comparables en taille et en zone de chalandise.",
    weight: 5,
  },
  {
    // NEW. Asset managers price a locomotive and a passenger very differently, and nothing in the
    // old grid saw the difference.
    id: 'traffic_contribution',
    label:
      "Contribution au Trafic : l'enseigne apporte-t-elle du flux ou en consomme-t-elle ? Chercher : notoriete de l'enseigne dans le pays, taille de sa communaute et de son programme de fidelite, statut de locomotive ou d'enseigne d'accompagnement dans les centres ou elle est deja presente, frequentation revendiquee.",
    weight: 5,
  },
  {
    // NEW. A top-ranked brand you cannot contract with is a wasted slot: nothing captured WHO
    // signs, or whether the brand is taking sites in a window that matches the unit.
    id: 'deal_readiness',
    label:
      "Faisabilite du Deal : peut-on reellement signer, et avec qui ? Chercher : mode de developpement (succursales, franchise, master-franchise), existence d'un franchise ou master-franchise sur le territoire, interlocuteur developpement identifiable, calendrier d'ouvertures annonce, et toute contrainte technique connue du concept (extraction pour la restauration, hauteur sous plafond, acces livraison, autorisation CDAC pour les grandes surfaces).",
    weight: 5,
  },
  {
    id: 'financial_health_risk',
    label:
      "Sante Financiere & Risque : solidite financiere et risque de defaillance. Chercher : dernier chiffre d'affaires et resultat publies, evolution sur trois ans, capitaux propres, procedure collective ou plan de sauvegarde, fermetures de magasins recentes.",
    weight: 5,
  },
];

// UNIT FIT: does THIS unit suit THIS brand.
//
// Rebuilt from first principles, because the previous four criteria (surface, tenant mix, brand
// preference, footfall) were too coarse to separate two candidates: they scored the centre more
// than the unit, and three of the four things that actually kill a deal (frontage, technical
// feasibility, affordable rent) had nowhere to be expressed at all.
//
// The dimensions below are the ones a leasing manager and a retailer's expansion director really
// argue about when they walk a specific lot. Each label carries its own definition, because the
// label IS the instruction the judge receives: an unexplained criterion is scored on the model's
// assumptions rather than on ours.
//
// Weights sum to 100, so the unit-fit score stays a true 0-100 index, like the asset rubric.
// Eliminatory thresholds (minimumScore) are set only where a failure is genuinely disqualifying
// rather than merely bad, because an eliminatory criterion silently removes a candidate.
export const DEFAULT_PORTFOLIO_SCORING_RUBRIC: ScoringCriterion[] = [
  {
    id: 'financial_health_risk',
    label: 'Sante Financiere & Risque : Solidite financiere, resilience et risque de defaillance de la marque.',
    weight: 10,
  },
  {
    id: 'development_intent',
    label: "Dynamique d'Expansion Recente : Preuves d'ouvertures, de signatures et d'investissements recents sur le territoire cible.",
    weight: 50,
  },
  {
    id: 'portfolio_center_type_experience',
    label: "Experience Formats Centres : Niveau d'experience de la marque sur les types de centres predominants du portfolio (mall, retail park, high street, etc.).",
    weight: 40,
  },
];

interface SourcingSource {
  title: string;
  url: string;
  snippet: string;
}

interface SourcingExecutionTurn {
  turnNumber: number;
  thought: string;
  action: 'SEARCH' | 'CONCLUDE';
  searchQueries?: string[];
  newFindings?: SourcingSource[];
}

export interface AtomicFact {
  claim: string;       // The extracted information (e.g. "Revenue 2023 is 50M€")
  /**
   * The verbatim text from the source, and NULL when there is no source.
   *
   * These were once required strings, which sounds strict and did the opposite: an internal
   * database reading had to invent something to put here, so it carried `exactQuote: "Financial
   * Oracle"` and `sourceUrl: "Fater Intelligence DB"` - the shape of a citation with nothing
   * behind it, weighed by the judge exactly like a real one. Null is the honest value, and the
   * prompt builder omits the Quote/Source lines entirely when it sees one.
   */
  exactQuote: string | null;
  sourceUrl: string | null;
  sourceTitle?: string;
  publishedDate?: string;
  isInternal?: boolean;
  /**
   * The researcher's own sentence about what this batch of facts means, written in the same call
   * that produced them. The live row ticker speaks this instead of echoing `claim`.
   */
  narration?: string;
  evidenceCheck?: {
    status: 'supported' | 'contradicted' | 'unresolved';
    checkedAt: string;
    basis: 'retrieved_text' | 'unavailable';
    reused?: boolean;
    reuseCoverage?: Array<{ question: string; factIds: string[] }>;
  };
}

export interface DiscoveredLocation {
  city: string;
  address: string;
  postalCode?: string | null;
  sourceUrl: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface SourcingCriterionResult {
  criterionId: string;
  label: string;
  score: number;
  maxScore: number;
  reasoning: string;
  citations: string[]; // Kept for backward compatibility, but derived from facts
  facts: AtomicFact[]; // The new source of truth
}

export interface SearchIntent {
  criterionId: string;
  question: string;
}

// ── The scored analysis, as the shared shell renders it ───────────────────────────────
// Built by lib/leasing/analysisGroups.ts, rendered by components/entities/ScoredAnalysis.tsx for
// every shortlist row (PortfolioFit, Lead).

/** Simple = the verdict table. Detailed = the verdict plus the evidence behind it. */
export type AnalysisViewMode = 'simple' | 'detailed';

/** One scored criterion, with the questions asked for it and the facts found. */
export interface CriterionGroup {
  id: string;
  /** The criterion's short name only, see criterionTitle. */
  label: string;
  /** The authored label, name and definition, kept for search and the cell tooltip. */
  labelFull: string;
  score?: number;
  maxScore?: number;
  reasoning?: string;
  /** Empty on rows scored before the evidence columns existed. */
  facts: AtomicFact[];
  /** Empty on rows scored before the evidence columns existed. */
  inquiries: SearchIntent[];
}

/** One question and the facts positionally attributed to it. */
export interface CriterionEvidenceBucket {
  inquiry?: SearchIntent;
  facts: AtomicFact[];
}

export interface RenderableCriterionGroup extends CriterionGroup {
  buckets: CriterionEvidenceBucket[];
}

export type AnalysisColumnId = 'score' | 'criterion' | 'inquiries' | 'facts' | 'links';

// ── A shortlist's own doctrine context ────────────────────────────────────────────────
// Served read-only by app/api/inventory/sourcing-campaigns/[campaignId]/doctrine-context:
// the rubric that actually governs an open shortlist, and where its points are really
// earned. It answers "what is this shortlist judging on, and how is it scoring?" in one call.

/** Where a criterion in the effective rubric comes from. */
export type SourcingCriterionOrigin =
  | 'RUBRIC' // stored on Property.scoringRubric
  | 'BRIEF' // appended at launch from the campaign brief (lib/leasing/briefCriteria.ts)
  | 'OBSERVED'; // only seen in the rows, i.e. scored by a run whose rubric has since changed

/** One criterion, aggregated across the shortlist's rows. */
export interface SourcingCriterionBreakdownDTO {
  criterionId: string;
  label: string;
  /** Points this criterion can award at most under the rubric AS IT STANDS NOW, i.e. its weight. */
  maxPossible: number;
  /** Mean points earned across the rows that carry a result for it; null when none do. */
  averageEarned: number | null;
  /**
   * What the rows were actually scored out of (the largest maxScore stored on the sampled
   * evidence); null when no row carries a result. It differs from `maxPossible` when the weight
   * was edited after the run, and `averageEarned` belongs to THIS scale, not to the current weight.
   */
  observedMaxScore: number | null;
  /** How many rows carry a result for this criterion. */
  scoredRows: number;
  origin: SourcingCriterionOrigin;
}

export interface SourcingCampaignDoctrineContextDTO {
  campaignId: string;
  propertyId: string;
  /** Whether the run is still filling the shortlist, so the aggregates below are partial. */
  campaignStatus: string;
  /**
   * The rubric governing this campaign, derived the way that run's own worker derives it: for an
   * ASSET run the asset rubric plus the brief criteria the worker appends, for a UNIT run the
   * asset's unit-fit rubric (or the standard grid unit-fit execution falls back to).
   */
  effectiveRubric: ScoringCriterion[];
  /**
   * Where `effectiveRubric` comes from. `DECLARED` is the normal case (the asset's stored rubric,
   * plus the brief). `OBSERVED` means nothing is declared any more and the criteria were rebuilt
   * from what the rows were actually scored on, so the panel can label them for what they are
   * instead of presenting reconstructed criteria as the asset's doctrine.
   */
  rubricOrigin: 'DECLARED' | 'OBSERVED';
  /** Why the run carries its weights, and who set them (lib/leasing/runWeights.ts); null when it uses the asset's own. */
  runWeights?: { source: 'ai' | 'user'; reason: string | null } | null;
  scoringBreakdown: SourcingCriterionBreakdownDTO[];
  counts: {
    rowsTotal: number;
    rowsScored: number;
    /** How many scored rows the aggregation actually read (capped for very large shortlists). */
    rowsSampled: number;
  };
}

/**
 * Longest instruction document a shortlist's doctrine accepts. It lives here rather than in the
 * editor because both writers must agree on it: the doctrine workspace and the chat command that
 * lets the agent rewrite the same document.
 */
export const SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH = 40000;

/**
 * Manual per-row prospection tracking, user-written, per shortlist row (per-local).
 * Shared by the 3 fit DTOs so the same brand can carry a distinct outreach state
 * per asset / unit / portfolio (which is what feeds per-local prospection stats).
 */
interface ProspectionTrackingFields {
  prospectionContacted?: boolean | null;
  prospectionChannel?: string | null;
  prospectionResponse?: string | null;
  prospectionComment?: string | null;
}

export interface LeasingLeadDTO extends ProspectionTrackingFields {
  id: string;
  propertyId: string;
  /** The unit a unit fit scored this brand for; null for an asset-level row. */
  unitId: string | null;
  brandId: string;
  // Brand preview, populated by the list query so the row/card can show the
  // brand without a separate fetch (skeleton rule: undefined when not selected).
  brandName?: string | null;
  brandLogoUrl?: string | null;
  portfolioFitId?: string | null;
  status: string;
  /** null until the run has actually scored this lead. */
  fitScore: number | null;
  rationale: string;
  
  // Heavy/Detail fields (optional for skeleton)
  criteriaResults?: SourcingCriterionResult[];
  nearestStoreDistance?: number | null;
  nearestStoreName?: string | null;
  nearestStoreLat?: number | null;
  nearestStoreLng?: number | null;
  strategy?: SearchIntent[];
  rawFacts?: AtomicFact[];
  sources?: any[];
  
  // Relations (IDs only)
  /** The run that owns the CURRENT score. Use it to name a campaign, never to test membership. */
  lastCampaignId?: string | null;
  /**
   * Every campaign this brand was analysed by, so a finished run keeps the shortlist it was paid
   * for. Read it through `leadBelongsToCampaign` (lib/leasing/campaignMembership.ts).
   */
  campaignIds?: string[];
  promotedDealId?: string | null;
  promotedDealUnitId?: string | null;
  promotedDealAssignedUserId?: string | null;
  
  createdAt: string;
  updatedAt: string;
}

/**
 * Runtime-only lead overlay stored in entityKernelState.
 * Never persisted in LeasingLead DTO storage.
 */
export interface LeadRuntimeState {
  analysisViewMode?: AnalysisViewMode;
}

export type UniversalFactCategory =
  | 'FINANCIAL_PERFORMANCE'
  | 'STRATEGIC_DIRECTION'
  | 'CONTACT_INFO'
  | 'LEGAL_STATUS'
  | 'PHYSICAL_SPECIFICATION'
  | 'MARKET_POSITION'
  | 'OPERATIONAL_METRIC'
  | 'OTHER';

export const UniversalFactCategory = {
  FINANCIAL_PERFORMANCE: 'FINANCIAL_PERFORMANCE',
  STRATEGIC_DIRECTION: 'STRATEGIC_DIRECTION',
  CONTACT_INFO: 'CONTACT_INFO',
  LEGAL_STATUS: 'LEGAL_STATUS',
  PHYSICAL_SPECIFICATION: 'PHYSICAL_SPECIFICATION',
  MARKET_POSITION: 'MARKET_POSITION',
  OPERATIONAL_METRIC: 'OPERATIONAL_METRIC',
  OTHER: 'OTHER',
} as const satisfies Record<string, UniversalFactCategory>;

export interface UniversalFactDTO {
  id: string;
  entityType: string;
  entityId: string;
  category: UniversalFactCategory;
  content: string;
  sourceUrl: string | null;
  verifiedAt: string;
}

export interface LeasingCostMetrics {
  totalCostUsd: number;
  totalCacheSavingsUsd?: number;
  callCount: number;
  webSearchCount: number;
  modelUsage: Record<string, {
    inputTokens: number;
    cachedInputTokens?: number;
    uncachedInputTokens?: number;
    outputTokens: number;
    reasoningTokens?: number;
    promptAudioTokens?: number;
    completionAudioTokens?: number;
    calls: number;
    cost: number;
    cacheSavingsUsd?: number;
  }>;
}

export interface SourcingExecutionPayload {
  userId: string;
  targetBrandIds: string[];
  propertyId: string;
  targetUnitIds?: string[];
  /** A unit fit: the one unit this pass scores for, and the hypothesis it carries. */
  unitId?: string | null;
  unitHypothesis?: string | null;
  initialScores?: Record<string, number>;
  protocol: string[];
  rubric: ScoringCriterion[];
  /**
   * The layered SOURCE doctrine this shortlist runs under (org + tool layers, then the campaign's
   * own layer last), resolved at launch by lib/prompt/contextStack.resolveCampaignDoctrine.
   * It governs HOW the evidence is weighed; the rubric above stays the scoring contract.
   * Undefined when nobody wrote any doctrine, which keeps the prompt unchanged.
   */
  doctrine?: string;
  config: {
    batchSize: number;
    maxBudgetCredits?: number;
    justJudge?: boolean;
    useLegacyData?: boolean;
    /** Set aside a scored brand that clearly misses the brief (campaign config excludeOffScope, default on). */
    excludeOffScope?: boolean;
    /** The store formats the brief asks for (campaign config acceptedFormats); caps surface & format. */
    acceptedFormats?: string[];
    /**
     * THIS PASS IS NOT THE END OF THE RUN, so it must not leave the campaign row on a terminal
     * status when it finishes cleanly.
     *
     * A campaign covering several assets is executed as one pass per asset, and the row IS the
     * progress surface: PENDING / WAITING / RUNNING mean "work is happening". Letting an
     * intermediate pass write COMPLETE opens a window in which the run looks finished while it is
     * not, and everything that reads the status acts on it: the stop button becomes a silent no-op
     * (the stop route treats a terminal campaign as already settled), the agent narrates a third of
     * a shortlist, the progressive-reveal gate drops and the table shows the next asset's unscored
     * seed rows, and the launch guardrail would admit a second campaign on an asset still queued.
     *
     * Only a CLEAN completion is deferred. An abort or a failure is the end of the run whichever
     * pass it happens on, so those statuses are written exactly as they always were.
     */
    deferCompletion?: boolean;
  };
  notifyOnComplete?: boolean;
  recallCommanderOnComplete?: boolean;

  progress: {
    total: number;
    completed: number;
    failed: number;
    status: 'IDLE' | 'RUNNING' | 'COMPLETED';
  };

  results: any[];
  metrics?: LeasingCostMetrics;
}

interface SourcingCampaignConfig {
  /** What this run is called. Stamped from the brief at launch, editable from the card's
   *  right-click menu. Declared here because config is rebuilt from this shape in places, and an
   *  undeclared field is one a future rebuild drops with no error. */
  name?: string;
  /**
   * EVERY ASSET THIS RUN COVERS, when it covers more than one.
   *
   * A campaign row has a single `propertyId` column and that stays the PRIMARY asset, the one its
   * tenancy checks, its queue message and its conversation are keyed on, and the one the
   * `Lead.lastCampaignId` foreign key needs. The rest of the coverage lives here, because picking
   * three assets must produce ONE campaign and not three. Absent on every single-asset run, so a
   * reader that predates it sees exactly what it always saw.
   *
   * Resolved through `resolveCampaignPropertyIds` (lib/leasing/sourcingCampaignLifecycle), never by
   * reading this field raw.
   */
  propertyIds?: string[];
  excludePropertyBrands: boolean;
  excludeRecentLeads: boolean;
  countryFilter?: string;
  maxBudgetCredits?: number;
  justJudge?: boolean;
  notifyOnComplete?: boolean;
  recallCommanderOnComplete?: boolean;
  /**
   * Set while this campaign is lending its lifecycle to a SINGLE-BRAND re-analysis (the right
   * bar's "Run analysis again"). It lives in config rather than metrics because the orchestrator
   * replaces metrics wholesale on every progress broadcast; a launch always creates a new campaign
   * row, so it cannot outlive the campaign it describes. Readers use it to tell "this shortlist is
   * being built" from "one of its rows is being re-analysed".
   */
  brandRerun?: {
    brandId: string;
    queuedAt: string;
    requestedByUserId: string;
  };
}

export type SourcingCampaignStatus =
  | 'PENDING'
  | 'WAITING'
  | 'RUNNING'
  | 'COMPLETE'
  | 'FAILED'
  | 'ABORTED';

export interface LaunchSourcingCampaignPayload {
  useLegacyData?: boolean;
  /** A unit fit: one unit of the (single) asset, and the one-line hypothesis for it. */
  unitId?: string;
  unitHypothesis?: string;
  /** How many brands to analyse; the budget is what they cost. Outranks maxBudgetCredits. */
  brandCount?: number;
  /** Hands the mandate to the server, which drives every pass after this one (mandateDriver.ts). */
  mandate?: Pick<ServerSourcingMandate, 'goal' | 'targetScore' | 'creditCeiling' | 'committedCredits' | 'expiresAt' | 'chatSessionId'> & Partial<Pick<ServerSourcingMandate, 'passes'>>;
  /** The user's own words on what they are looking for; read by the selection stage. */
  reason?: string;
  /**
   * Extend an EXISTING shortlist instead of starting a new one.
   *
   * "Analyse 10 more brands" means ten more rows on the list already on screen, judged by the same
   * doctrine and the same rubric so the scores are comparable. Launching a second campaign gives
   * the user a second list scored on its own terms, which is not what they asked for and cannot be
   * reconciled with the first. When this is set the run reuses that campaign's row, its config and
   * its id: the new Leads carry the same lastCampaignId, so they land in the same shortlist.
   */
  extendCampaignId?: string;
  notifyOnComplete?: boolean;
  recallCommanderOnComplete?: boolean;
  maxBudgetCredits?: number;
  justJudge?: boolean;
  countryFilter?: string;
  /**
   * Narrow THIS run's candidate pool to these CNCC sectors, on top of the asset's saved filters.
   *
   * Extending deliberately reuses the campaign's rubric, brief and filters, so the new rows stay
   * comparable with the old ones - and that left no way to say "another one, F&B this time". The
   * request was silently dropped and a credit was spent on a brand from the wrong sector
   * (2026-08-16). The RUBRIC is what must not change for comparability; which brands are
   * CONSIDERED is a different question, and narrowing it is the whole point of the ask.
   *
   * Same shape and same treatment as `countryFilter`: an override for this run only, never written
   * back onto the asset or the campaign.
   */
  cnccSectorFilter?: CNCCSector[];
  /**
   * Narrow THIS run to these CNCC secteurs - the fine category, not the famille.
   *
   * "Add only fitness brands" was answered with a hypermarket and a discounter because a sector was
   * the only thing that could be expressed and fitness is not a sector. Validated against the
   * referential at the boundary, and it overrides the asset's own activity list for this run only.
   */
  cnccActivityFilter?: string[];
  /**
   * A widened brief for this campaign's pool, from an accepted proposal (owner, 2026-10-14: "the agent should itself
   * propose to enlarge the surface range or else ... everything it can to attain the goal"). Unlike the CNCC overrides it
   * is written into the campaign's filters, so the passes after it keep it. The scoring brief (top-level config range) is
   * left as launched: a wider pool does not make a 350 m2 brand fit a 211 m2 unit.
   */
  briefOverride?: SourceBriefChange['to'];
  /**
   * ANALYSE EXACTLY THESE BRANDS, named by the user, instead of picking from the candidate pool.
   *
   * "/extend add LCL" spent its credit on Clean City. Measured 2026-08-17 on Espace Saint Quentin:
   * the pool is the portfolio macro pass narrowed by the asset's policy and then stripped of every
   * recently evaluated brand, which left 18 candidates ordered by internal record id, and LCL was
   * not among them because it had been evaluated before. The command had no way to name a brand
   * either, so the run took the first id and the agent reported having added the brand it was asked
   * for. Naming a brand is now the constraint, not a hint.
   *
   * Deliberately bypasses the standing policy for the named brands only, on the same reasoning as
   * `cnccSectorFilter`: an explicit request is a departure from the policy, and intersecting would
   * reproduce the silent failure this field exists to end. What it does NOT bypass is the shortlist
   * itself, since re-scoring a row that is already there cannot make the list grow. Ids are checked
   * against the Brand table at the boundary, and a name that resolves to nothing is reported rather
   * than replaced by a substitute.
   */
  targetBrandIds?: string[];
}

export interface LaunchSourcingCampaignOptions {
  payload?: LaunchSourcingCampaignPayload;
}

/**
 * Everything the Source setup form holds at the moment its Launch button is pressed, i.e. one
 * complete sourcing brief. The engine is DERIVED from it and never stated: assets picked means
 * asset sourcing, an empty selection means the portfolio-wide pass.
 */
export interface SourceCampaignBrief {
  portfolioId: string;
  /** The setup panel's data mode. Research all data: the agent rebuilds the top brands before its verdict. */
  /** Legacy data OFF with research ON: stored values are ignored (never erased) and the run is capped at 10 brands. */
  useLegacyData?: boolean;
  /** The assets the run covers. Empty = the portfolio-wide pass. */
  assetIds: string[];
  /** Brands to analyse per asset; the route prices them. */
  brandCount: number;
  /** What those brands cost, derived from the count; kept for presets and drafts. */
  budgetCredits: number;
  countryCodes: string[];
  minimumNetworkSize: number;
  minimumFitScore: number;
  cnccActivities: string[];
  surfaceMinSqm: number;
  surfaceMaxSqm: number;
  /** PropertySourcingCategoryFilters.acceptedFormats. */
  formats?: string[];
  excludeOnsiteTenants: boolean;
  excludeRecentlyScored: boolean;
  requireKnownWebsite: boolean;
  /** Unlist off-scope brands (PropertySourcingCategoryFilters.excludeOffScope). */
  excludeOffScope: boolean;
  /** The campaign's own doctrine document, written once the campaign has an id. */
  instructions: string;
  lang: string;
}

export interface LaunchPortfolioFitCampaignPayload {
  maxBudgetCredits?: number;
  modelName?: string;
  excludeRecentPortfolioFits?: boolean;
  requireKnownWebsite?: boolean;
}

interface LaunchPortfolioFitCampaignOptions {
  payload?: LaunchPortfolioFitCampaignPayload;
}

/**
 * What the free re-apply path reports back. It rescales the points each lead already earned
 * against the current rubric, so the counts below are the only proof the shortlist actually
 * moved. `rubricMismatch` is the anomalous minority the server re-applied around rather than
 * swallowing; a systemic mismatch is refused with a 409 instead of landing here.
 */
export interface ReapplySourcingRubricResult {
  summary: {
    totalLeads: number;
    updatedLeads: number;
    dismissedAfterReapply: number;
    resurrectedFromDiscard: number;
    skippedNoCriteria: number;
    skippedRubricMismatch: number;
  } | null;
  rubricMismatch: {
    skippedLeads: number;
    evaluableLeads: number;
    criteriaWithoutEvidence: string[];
    storedCriteriaOutsideRubric: string[];
  } | null;
}

export interface SourcingCampaignDTO {
  id: string;
  propertyId: string;
  organisationId: string;
  status: SourcingCampaignStatus;
  config: SourcingCampaignConfig;
  targetBrandIds: string[];
  leadIds?: string[];
  metrics?: unknown;
  createdAt: string;
  updatedAt: string;
  propertyName?: string;
  leadsCount?: number;
  nonDiscardedLeadsCount?: number;
}

/** PORTFOLIO | ASSET | UNIT. What a campaign is about, as data rather than as three tables. */
export type CampaignTargetKind = 'PORTFOLIO' | 'ASSET';

/**
 * One campaign, whatever its granularity.
 *
 * Replaces the three near-identical DTOs below it. The target is data: `propertyIds` holds zero
 * assets for a portfolio-wide run, one for a single asset, and MANY for a multi-asset run, which
 * none of the three legacy tables can express.
 *
 * `resultCount` is the granularity's own result family already made comparable: leads for an
 * asset run, portfolio fits for a portfolio run, unit fits for a unit run, each excluding rows a
 * human took off the list. The dashboard card renders one number and does not care which table it
 * came from.
 */
export interface CampaignDTO {
  id: string;
  targetKind: CampaignTargetKind;
  organisationId: string;
  portfolioId: string;
  propertyIds: string[];
  /** The one unit a unit fit targets; empty for an asset run. */
  unitIds: string[];
  status: SourcingCampaignStatus;
  config: SourcingCampaignConfig;
  /**
   * How many brands the run targets, not which ones. Measured on the preview 2026-08-15, the two
   * id arrays were 862KB of a 973KB campaign list response (targetBrandIds alone 805KB, 77%) for
   * one portfolio, and that list is fetched on every entry into Source. Nothing reading a
   * CampaignDTO ever wanted the ids: both consumers took `.length` and threw the array away.
   * The runs that do need the ids read them from SourcingCampaignDTO / PortfolioFitCampaignDTO,
   * which are separate DTOs off separate routes and still carry them.
   */
  targetBrandCount: number;
  targetLeadCount: number;
  metrics?: unknown;
  createdAt: string;
  updatedAt: string;
  createdByUserId?: string | null;
  /** Display label: the asset's name for ASSET and UNIT runs, the portfolio's for PORTFOLIO. */
  targetName?: string;
  /** The (primary) asset's logo, shown on the card and the campaign header. Undefined when not resolved. */
  targetLogoUrl?: string | null;
  /** Results of this run, excluding rows a human removed. */
  /**
   * DETAIL ONLY. The list route reduces these to `targetBrandCount` / `resultCount` on purpose - a
   * dashboard of fifty runs must not ship fifty id arrays. The detail route adds them back, because
   * a campaign PAGE needs the rows themselves, and their absence is what kept those screens tied to
   * the older route's payload.
   */
  targetBrandIds?: string[];
  leadIds?: string[];
  resultCount?: number;
  /** Results before that exclusion, so a card can show "12 of 200" without a second query. */
  totalResultCount?: number;
}

export type PortfolioFitCampaignStatus =
  | 'PENDING'
  | 'WAITING'
  | 'RUNNING'
  | 'COMPLETE'
  | 'FAILED'
  | 'ABORTED';

interface PortfolioFitCampaignConfig {
  maxBudgetCredits?: number;
  modelName?: string;
  excludeRecentPortfolioFits?: boolean;
  requireKnownWebsite?: boolean;
  portfolioFitCountryCodes?: string[];
  portfolioFitMinimumNetworkSize?: number;
}

export interface PortfolioFitCampaignDTO {
  id: string;
  portfolioId: string;
  organisationId: string;
  /** Who launched the run. Stored since the model was written; surfaced as the shortlist's owner. */
  createdByUserId?: string | null;
  status: PortfolioFitCampaignStatus;
  config: PortfolioFitCampaignConfig;
  targetBrandIds: string[];
  portfolioFitIds?: string[];
  metrics?: unknown;
  createdAt: string;
  updatedAt: string;
  portfolioName?: string;
  portfolioFitsCount?: number;
  nonDismissedPortfolioFitsCount?: number;
}

export type PortfolioFitStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCESS'
  | 'FAILED'
  | 'ABORTED'
  | 'INTERESTING'
  | 'DISMISSED';

export interface PortfolioFitDTO extends ProspectionTrackingFields {
  id: string;
  portfolioId: string;
  brandId: string;
  campaignId: string | null;
  leadIds?: string[];
  score: number;
  rationale: string;
  status: PortfolioFitStatus;
  criteriaResults?: SourcingCriterionResult[];
  rawFacts?: AtomicFact[];
  createdAt: string;
  updatedAt: string;
  brandName?: string | null;
  brandLogoUrl?: string | null;
  portfolioName?: string | null;
  // Assets of THIS portfolio where the brand is already present (an active, non-dead
  // deal). Lets the reference shortlist show "already in 3 of our centres" at a glance.
  presentInProperties?: { id: string; name: string }[];
}

/**
 * Runtime-only portfolio fit overlay stored in entityKernelState.
 * Never persisted in PortfolioFit DTO storage.
 */
export interface PortfolioFitRuntimeState {
  analysisViewMode?: AnalysisViewMode;
}

/**
 * Inventory/portal project row DTO (list/discovery context).
 * Not the Studio scene skeleton (`ProjectDTO`).
 */
export interface LeasingProjectDTO {
  id: string;
  name: string;
  imageUrl: string | null;
  /** Topmost layer image; the card falls back to it when imageUrl is a blank poster. */
  fallbackImageUrl?: string | null;
  workspaceIds?: string[];
  projectType?: ProjectType;
  userId: string;
  userName?: string | null;
  userAvatarUrl?: string | null;
  /** The agent that made the project for its owner (lib/toolset), when one did: shown beside the owner on the card. */
  madeByAgent?: { agent: string; module: string };
  organisationId: string | null;
  organisationName?: string | null;
  totalCreditsSpent: number;
  createdAt: string;
  updatedAt: string;
  favorite: boolean;
  toDelete?: boolean;
}

export interface LeasingOrganisationDTO {
  id: string;
  name: string;
  logoUrl: string | null;
  contactEmail: string | null;
  createdAt: string;
  updatedAt: string;
  projectCount?: number;
  userCount?: number;
  allowOverage?: boolean;
  overageDisplayText?: string | null;
  agentPlaybook?: string | null;
  defaultCollaborationMode?: CollaborationMode;
  favoriteLanguage?: 'ENGLISH' | 'FRENCH' | null;
  plans?: OrganisationPlanDTO[];
  teams?: OrganisationTeamDTO[];
  stats?: OrganisationStats | null;
  totalCreditsSpent?: number;
  remainingCredits?: number;
  urlLogo?: string | null;
  factory?: FactoryOrganisationProjection;
}

/**
 * Runtime-only organisation overlay stored in entityKernelState.
 * Never persisted in LeasingOrganisation DTO storage.
 */
export interface OrganisationChartRuntimeState {
  viewport?: KernelViewportRuntimeState;
  layoutMode?: 'FAMILY_TREE' | 'COMPACT';
  collapsedUserIds?: string[];
  selectedUserId?: string | null;
  hasAutoFitOnce?: boolean;
  /** Member list search box. Runtime overlay, never a DTO column (AGENTS.md 4.2). */
  memberSearch?: string;
  /** Member list sort field, defaults to 'hierarchy'. */
  memberSortField?: string;
  /** Member list sort direction, defaults to 'asc'. */
  memberSortDir?: 'asc' | 'desc';
}

export interface OrganisationTeamsCanvasRuntimeState {
  viewport?: KernelViewportRuntimeState;
  selectedTeamId?: string | null;
  selectedMemberId?: string | null;
  selectedSourcePlanId?: string | null;
  memberMoveTargetTeamId?: string | null;
  transferTargetTeamId?: string | null;
  transferCreditsInput?: string;
  budgetCreditsInput?: string;
  teamLifecycleMode?: 'CREATE_TEAM' | 'DISSOLVE_TEAM' | null;
  createTeamNameInput?: string;
  createTeamDescriptionInput?: string;
  createTeamSourcePlanId?: string;
  createTeamCreditsInput?: string;
  budgetDraftMode?: 'ALLOCATE_FROM_ORG' | 'RELEASE_TO_ORG' | 'TRANSFER_TEAM_TO_TEAM' | null;
  pendingAction?: 'MOVE_MEMBER' | 'TRANSFER_CREDITS' | 'UPDATE_TEAM_PLAN' | 'CREATE_TEAM' | 'DELETE_TEAM' | null;
  layoutMode?: 'POOLS' | 'COMPACT' | 'USAGE';
  hasAutoFitOnce?: boolean;
}

export interface OrganisationRuntimeState {
  /** 'MANAGEMENT' is the pre-2026-09-29 single settings view; it now opens General. */
  activeTab?: 'GENERAL' | 'MEMBERS' | 'USAGE' | 'BILLING' | 'MANAGEMENT' | 'TEAMS' | 'CHART' | 'TEAMS_JNN';
  /** What the settings bar's search box filters the sections by. */
  settingsSearch?: string;
  chart?: OrganisationChartRuntimeState;
  teamsCanvas?: OrganisationTeamsCanvasRuntimeState;
  factoryLoad?: { context: string; status: 'loading' | 'ready' | 'error'; error?: string };
  factoryNavigation?: { folderId: string; imageId?: string; sessionId: string; userId: string; requestId: string; freshBank?: boolean };
  factoryObjectDraft?: FactoryObjectDraft;
  factoryObjectPanelRequests?: Record<string, { ownerId: string; contentRevision: number; attempt: FactoryObjectPanelRequest; startedAt: number }>;
}

export interface LeasingUserDTO {
  id: string;
  organisationId: string | null;
  teamId?: string | null;
  managerId?: string | null;
  subordinateIds?: string[];
  reportCcUserIds?: string[];
  groupUsageTargets?: {
    addedAt: string;
    value: number;
    groupName?: string;
  }[];
  firstName: string | null;
  lastName: string | null;
  email: string;
  imageUrl: string | null;
  isOrganisationAdmin: boolean;
  lastActive: string | null;
  projectCount: number;
  createdAt: string;
  authId?: string | null;
  permissions?: UserPermissions;
  generationCount: number;
  phoneNumber?: string | null;
}

export type NotificationType = 'SYSTEM' | (string & {});

export type NotificationSeverity = 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';

export interface NotificationDTO {
  id: string;
  userId: string;
  organisationId: string;
  type: NotificationType;
  severity: NotificationSeverity;
  title: string;
  body: string;
  readAt: string | null;
  archivedAt: string | null;
  dedupeKey: string | null;
  entityType: EntityType | null;
  entityId: string | null;
  actionUrl: string | null;
  data: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}


export interface GeneratedDocument {
  id: string;
  dealId: string;
  templateId: string;
  templateName: string;
  type: GeneratedDocumentType;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'COMPLETE';
  url: string | null;
  createdAt: string;
  metadata?: GeneratedDocumentMetadata | null;
}

interface DealIncentiveStepDTO {
  id: string;
  dealId: string;
  stepOrder: number;
  startMonth: number | null;
  endMonth: number | null;
  rentPercent: number;
  createdAt: string;
  updatedAt: string;
}

export interface LeasingDealDTO extends BaseLeasingEntity {
  // --- REQUIRED (List/Row Fields) ---
  id: string;
  unitId: string;
  brandId: string;
  status: 'DRAFT' | 'REVIEW' | 'APPROVED' | 'OFFER_SENT' | 'SIGNED' | 'DEAD';
  rentAmount: number | null;
  rentCurrency: string | null;
  updatedAt: string;
  createdAt: string;

  // --- OPTIONAL (Detail/Skeleton Fields) ---
  assignedUserId?: string | null;
  tenantId?: string | null;
  tenantLegalName?: string | null;
  leaseStartDate?: string | null;
  leaseEndDate?: string | null;
  
  incentiveTemplateId?: string | null;
  incentiveTemplateName?: string | null;
  incentiveRentFreeMonths?: number | null;
  incentiveFitOutAmount?: number | null;
  incentiveFitOutAmountPerSqm?: number | null;
  incentiveHasStepRents?: boolean;
  estimatedTenantFitoutCost?: number | null;
  estimatedTenantFitoutCostPerSqm?: number | null;
  
  permittedUse?: string | null;
  rentBasis?: string | null;
  rentPaymentFrequency?: string | null;
  rentPaymentTiming?: string | null;
  rentIndexationType?: string | null;
  rentVariablePercent?: number | null;
  opexCamAmount?: number | null;
  opexTaxesAmount?: number | null;
  opexMarketingFeeAmount?: number | null;
  opexBasis?: string | null;
  securityDepositAmount?: number | null;
  securityDepositMonths?: number | null;
  securityGapGuaranteeMonths?: number | null;
  leaseDurationYears?: number | null;
  leaseFirmPeriodYears?: number | null;
  leaseBreakOptions?: string | null;
  possessionDate?: string | null;
  openingDate?: string | null;
  rentCommencementDate?: string | null;
  proposalValidityDate?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactCompany?: string | null;
  contactNotes?: string | null;

  // --- HEAVY JSON / RELATIONS (Skeleton Arrays) ---
  financials?: any;
  terms?: any;
  contactInfo?: Record<string, unknown> | null;
  marketSignalBenchmark?: MarketSignalBenchmarkResponse | null;
  projectIds?: string[];
  incentiveSteps?: DealIncentiveStepDTO[];
  generatedDocs?: GeneratedDocument[];
}

export interface SourceRentRollEdit {
  values: Record<string, string>;
  expectedUpdatedAt: string;
  saving?: boolean;
  error?: string;
  refreshRequired?: boolean;
}

export interface SourceRentRollField {
  entity: 'Unit' | 'Deal';
  key: keyof LeasingUnitDTO | keyof LeasingDealDTO;
  label: string;
  group: string;
  kind: 'text' | 'number' | 'date' | 'boolean' | 'readonly' | 'unitType';
}

/**
 * Runtime-only deal overlay stored in entityKernelState.
 * Never persisted in LeasingDeal DTO storage.
 */
export interface DealRuntimeState {
  sourceRentRollEdit?: SourceRentRollEdit;
  rentRollDetail?: { status: 'loading' | 'ready' | 'error'; error?: string };
  activeTab?: 'ECONOMICS' | 'CONTACT' | 'DRAFTING' | 'DESIGNS' | 'UNIT';
  activeEconomicsTab?: DealEconomicsTab;
  lastFocusedActionType?: LeasingActionType;

  draftingPreviewMode?: 'TEMPLATE' | 'DOCUMENT';
  draftingSelectedDocumentId?: string | null;
  draftingSelectedDocumentType?: GeneratedDocumentType;
  draftingIsGenerating?: boolean;
  draftingPendingDocumentId?: string | null;
  draftingFocusRequestAt?: number;

  entityActions?: Array<{ type: LeasingActionType; entityType?: string; entityId?: string }>;

  designsIsQuickstarting?: boolean;
  selectedBrandContactId?: string | null;
  isDiscoveringContacts?: boolean;
  contactDiscoveryMessage?: string | null;

  // Contact draft (UI/runtime-only; never persisted directly)
  contactDraftInitializedDealId?: string;
  contactNameDraft?: string;
  contactEmailDraft?: string;
  contactPhoneDraft?: string;
  contactRoleDraft?: string;

  // Economics draft (UI/runtime-only; never persisted directly)
  economicsDraftInitializedDealId?: string;
  economicsRentAmountDraft?: number;
  economicsLeaseFirmPeriodYearsDraft?: number;
  economicsLeaseDurationYearsDraft?: number;
  economicsIncentiveRentFreeMonthsDraft?: number;
  economicsIncentiveFitOutAmountDraft?: number;
  economicsEstimatedTenantFitoutCostDraft?: number | null;
  economicsIncentiveHasStepRentsDraft?: boolean;
  economicsIncentiveStepsDraft?: Array<{
    stepOrder?: number;
    startMonth: number;
    endMonth: number;
    rentPercent: number;
  }>;
}

interface SpecificAggregations {
  SlideCorrection: {
    pending: number;
    approved: number;
    heldOut: number;
    rejected: number;
  };
  Brand: {
    unfilteredTotalBrands: number;
    totalLocations: number;
    totalWebsites: number;
    totalLogos: number;
    totalSirens: number;
    totalFinancials: number;
    /** Fater stamp counts, present when the listing asked for the qualification. */
    stampScored?: number;
    stampRecommended?: number;
    stampInMarket?: number;
    stampFinancialEvidence?: number;
    stampFullyDocumented?: number;
    stampVerifiedCompany?: number;
    stampAuraRead?: number;
    stampHotOrRising?: number;
    stampWithLogo?: number;
    stampPrivileged?: number;
    stampReady?: number;
    /** What the gap counts are measured over: the privileged pool on Data, else the market. */
    stampBase?: number;
    /** When the stored evaluation behind the qualification was computed; null while the first one is computed. */
    evaluationComputedAt?: string | null;
  };
  LegalEntity: {
    total: number;
    withFinancials: number;
    strongHealth: number;
    distressed: number;
  };
  Project: {
    totalCreditsSpent: number;
  };
}

type LeasingAggregations<K extends InventoryEntityType> = K extends keyof SpecificAggregations ? SpecificAggregations[K] : Record<string, never>;

export interface Pagination<K extends InventoryEntityType = any> {
  mode: 'replace' | 'append';
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
  aggregations?: LeasingAggregations<K>;
}
export type UnitSortField = 'IDENTIFIER' | 'PROPERTY' | 'FLOOR' | 'AREA' | 'STATUS' | 'TYPE' | 'RENT';

export interface LeasingMarketSignalFilters extends BaseInventoryFilters {
  minSurface?: number;
  maxSurface?: number;
  countries?: string[];
  postalCodes?: string[];
  cities?: string[];
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export type LegalEntitySortField = 'NAME' | 'SCORE' | 'GROWTH' | 'CAPITAL' | 'WORKFORCE';

export interface LeasingLegalEntityFilters extends BaseInventoryFilters {
  focusIds: string[];
  country?: string;
  status?: string[];
  minFinancialScore?: number;
  maxFinancialScore?: number;
  minShareCapital?: number;
  minWorkforce?: number;
  financialStatus?: FinancialStatus[];
  sortBy?: LegalEntitySortField;
  sortDir?: 'asc' | 'desc';
}

export interface QueryCacheEntry {
  timestamp: number;
  ids: string[];      // The specific IDs returned by this query
  total: number;      // The total count (for pagination)
}

interface EntitySlice<K extends InventoryEntityType> {
  isLoading: boolean;
  error: string | null;
  lastActivity: number;
  // Bumped only when a CREATE expired this slice's query cache, so a mounted query-mode list can
  // re-query without reacting to every UPDATE.
  lastCreateActivity?: number;
  registry: Record<string, InventoryEntityStateMap[K]>;
  queryCache: Record<string, QueryCacheEntry>;
  queryErrors?: Record<string, string>;
}

/**
 * Maps Leasing Inventory Entities to their respective DTO and Filter types.
 * This registry drives the generic store and logic engines.
 */
export interface InventoryRegistry {
  Property: { dto: LeasingPropertyDTO; filters: LeasingPropertyFilters };
  Floor: { dto: LeasingFloorDTO; filters: { propertyId?: string } & BaseInventoryFilters };
  Unit: { dto: LeasingUnitDTO; filters: LeasingUnitFilters };
  Brand: { dto: LeasingBrandDTO; filters: LeasingBrandFilters };
  Portfolio: { dto: LeasingPortfolioDTO; filters: LeasingPortfolioFilters };
  LegalEntity: { dto: LeasingLegalEntityDTO; filters: LeasingLegalEntityFilters };
  Deal: { dto: LeasingDealDTO; filters: LeasingDealFilters };
  ChatSession: { dto: ChatSession; filters: LeasingChatSessionFilters };
  TimelineEvent: { dto: TimelineEventDTO; filters: LeasingTimelineEventFilters };
  GeneratedDocument: { dto: GeneratedDocument; filters: { dealId?: string } };
  BrandLocation: { dto: LeasingBrandLocationDTO; filters: { brandId?: string } };
  LegalEntityFinancialRecord: { dto: LeasingBrandFinancialRecordDTO; filters: { legalEntityId?: string } };
  MarketSignal: { dto: LeasingMarketSignalDTO; filters: LeasingMarketSignalFilters };
  Audit: { dto: DataAuditDTO; filters: LeasingAuditFilters };
  SlideCorrection: { dto: SlideCorrectionDTO; filters: SlideCorrectionFilters };
  Campaign: { dto: CampaignDTO; filters: CampaignFilters };
  SourcingCampaign: { dto: SourcingCampaignDTO; filters: SourcingCampaignFilters };
  PortfolioFitCampaign: { dto: PortfolioFitCampaignDTO; filters: PortfolioFitCampaignFilters };
  PortfolioFit: { dto: PortfolioFitDTO; filters: PortfolioFitFilters };
  Lead: { dto: LeasingLeadDTO; filters: LeasingLeadFilters };
  Project: { dto: LeasingProjectDTO; filters: LeasingProjectFilters };
  Organisation: { dto: LeasingOrganisationDTO; filters: LeasingOrganisationFilters };
  Team: { dto: OrganisationTeamDTO; filters: OrganisationTeamFilters };
  User: { dto: LeasingUserDTO; filters: LeasingUserFilters };
  Notification: { dto: NotificationDTO; filters: NotificationFilters };
  Contact: { dto: ContactDTO; filters: LeasingContactFilters };
  UniversalFact: { dto: UniversalFactDTO; filters: UniversalFactFilters };
  StudioWorkspace: { dto: StudioWorkspaceDTO; filters: StudioWorkspaceFilters };
  StudioLayer: { dto: StudioLayerDTO; filters: StudioLayerFilters };
  StudioWorkspaceVersion: { dto: StudioWorkspaceVersionDTO; filters: StudioWorkspaceVersionFilters };
  CreditGrant: { dto: CreditGrantDTO; filters: CreditGrantFilters };
}

export type InventoryEntityType = keyof InventoryRegistry;
export const INVENTORY_ENTITY_TYPES = [
  'Property',
  'Floor',
  'Unit',
  'Brand',
  'Portfolio',
  'LegalEntity',
  'Deal',
  'ChatSession',
  'TimelineEvent',
  'GeneratedDocument',
  'BrandLocation',
  'LegalEntityFinancialRecord',
  'MarketSignal',
  'Audit',
  'SlideCorrection',
  'Campaign',
  'SourcingCampaign',
  'PortfolioFitCampaign',
  'PortfolioFit',
  'Lead',
  'Project',
  'Organisation',
  'Team',
  'User',
  'Notification',
  'Contact',
  'UniversalFact',
  'StudioWorkspace',
  'StudioLayer',
  'StudioWorkspaceVersion',
  'CreditGrant',
] as const satisfies readonly InventoryEntityType[];

type _InventoryEntityTypesMissingFromConstant = Exclude<InventoryEntityType, (typeof INVENTORY_ENTITY_TYPES)[number]>;
type _InventoryEntityTypesExtraInConstant = Exclude<(typeof INVENTORY_ENTITY_TYPES)[number], InventoryEntityType>;
const _assertInventoryEntityTypesConstantCoversRegistry: _InventoryEntityTypesMissingFromConstant extends never ? true : never = true;
const _assertInventoryEntityTypesConstantHasNoExtras: _InventoryEntityTypesExtraInConstant extends never ? true : never = true;

type ProjectInventoryEntityState =
  LeasingProjectDTO &
  Partial<ProjectRuntimeState> & {
    runtime?: Partial<ProjectRuntimeState>;
  };

type PropertyInventoryEntityState =
  LeasingPropertyDTO &
  Partial<PropertyRuntimeState> & {
    runtime?: Partial<PropertyRuntimeState>;
  };

export type FloorInventoryEntityState =
  LeasingFloorDTO &
  Partial<FloorRuntimeState> & {
    runtime?: Partial<FloorRuntimeState>;
  };

export type UnitInventoryEntityState =
  LeasingUnitDTO &
  Partial<UnitRuntimeState> & {
    runtime?: Partial<UnitRuntimeState>;
  };

export type BrandInventoryEntityState =
  LeasingBrandDTO &
  Partial<BrandRuntimeState> & {
    runtime?: Partial<BrandRuntimeState>;
  };

export type PortfolioInventoryEntityState =
  LeasingPortfolioDTO &
  Partial<PortfolioRuntimeState> & {
    runtime?: Partial<PortfolioRuntimeState>;
  };

export type LegalEntityInventoryEntityState =
  LeasingLegalEntityDTO &
  Partial<LegalEntityRuntimeState> & {
    runtime?: Partial<LegalEntityRuntimeState>;
  };

export type DealInventoryEntityState =
  LeasingDealDTO &
  Partial<DealRuntimeState> & {
    runtime?: Partial<DealRuntimeState>;
  };

export type MarketSignalInventoryEntityState =
  LeasingMarketSignalDTO &
  Partial<MarketSignalRuntimeState> & {
    runtime?: Partial<MarketSignalRuntimeState>;
  };

export type PortfolioFitInventoryEntityState =
  PortfolioFitDTO &
  Partial<PortfolioFitRuntimeState> & {
    runtime?: Partial<PortfolioFitRuntimeState>;
  };

export type LeadInventoryEntityState =
  LeasingLeadDTO &
  Partial<LeadRuntimeState> & {
    runtime?: Partial<LeadRuntimeState>;
  };

export type OrganisationInventoryEntityState =
  LeasingOrganisationDTO &
  Partial<OrganisationRuntimeState> & {
    runtime?: Partial<OrganisationRuntimeState>;
  };

export type ChatSessionInventoryEntityState =
  ChatSession &
  Partial<SessionRuntimeState> & {
    runtime?: Partial<SessionRuntimeState>;
  };

type StudioWorkspaceInventoryEntityState =
  StudioWorkspace & {
    runtime?: Partial<StudioWorkspaceRuntimeState>;
  };

export type StudioLayerInventoryEntityState =
  StudioLayer & {
    runtime?: Partial<StudioLayerRuntimeState>;
  };

/**
 * Frontend canonical inventory entity states.
 * - API/socket payloads still use `InventoryRegistry[*].dto`.
 * - Registry storage uses this map (DTO + runtime-capable state where applicable).
 */
interface InventoryEntityStateMap {
  Property: PropertyInventoryEntityState;
  Floor: FloorInventoryEntityState;
  Unit: UnitInventoryEntityState;
  Brand: BrandInventoryEntityState;
  Portfolio: PortfolioInventoryEntityState;
  LegalEntity: LegalEntityInventoryEntityState;
  Deal: DealInventoryEntityState;
  ChatSession: ChatSessionInventoryEntityState;
  TimelineEvent: TimelineEventDTO;
  GeneratedDocument: GeneratedDocument;
  BrandLocation: LeasingBrandLocationDTO;
  LegalEntityFinancialRecord: LeasingBrandFinancialRecordDTO;
  MarketSignal: MarketSignalInventoryEntityState;
  Audit: DataAuditDTO;
  SlideCorrection: SlideCorrectionDTO;
  Campaign: CampaignDTO;
  SourcingCampaign: SourcingCampaignDTO;
  PortfolioFitCampaign: PortfolioFitCampaignDTO;
  PortfolioFit: PortfolioFitInventoryEntityState;
  Lead: LeadInventoryEntityState;
  Project: ProjectInventoryEntityState;
  Organisation: OrganisationInventoryEntityState;
  Team: OrganisationTeamDTO;
  User: LeasingUserDTO;
  Notification: NotificationDTO;
  Contact: ContactDTO;
  UniversalFact: UniversalFactDTO;
  StudioWorkspace: StudioWorkspaceInventoryEntityState;
  StudioLayer: StudioLayerInventoryEntityState;
  StudioWorkspaceVersion: StudioWorkspaceVersionDTO;
  CreditGrant: CreditGrantDTO;
}

export type InventoryEntityState<K extends InventoryEntityType> = InventoryEntityStateMap[K];

export type LeasingInventoryState = {
  [K in InventoryEntityType]: EntitySlice<K>;
};

interface StudioInventoryState {}

export type KernelInventoryState = LeasingInventoryState & StudioInventoryState;

export interface LeasingUnitFilters extends BaseInventoryFilters {
  propertyId?: string;
  floorId?: string;
  type?: UnitType | null;
  status?: string | null;
  minArea?: number;
  maxArea?: number;
  minRent?: number;
  maxRent?: number;
  sortBy?: UnitSortField;
  sortDir?: 'asc' | 'desc';
  organisationId?: string;
  managerId?: string;
}

export interface LeasingDealFilters extends BaseInventoryFilters {
  propertyId?: string;
  unitId?: string;
  brandId?: string;
  assignedUserId?: string;
  status?: string;
  minRent?: number;
  maxRent?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  organisationId?: string;
}

interface LeasingAuditFilters extends BaseInventoryFilters {
}


export interface MarketSignalStats {
  totalSignals: number;
  uniqueCities: number;
  avgSurfaceSqm: number | null;
  surfaceMinSqm: number | null;
  surfaceMaxSqm: number | null;
}

interface LeasingChatSessionFilters extends BaseInventoryFilters {
  userId?: string;
  // Creation-scope filters ("agents live in projects"), served off the
  // indexed immutable scope columns.
  creationModule?: SessionCreationModule | 'master';
  creationEntityType?: string;
  creationEntityId?: string;
}

interface LeasingTimelineEventFilters extends BaseInventoryFilters {
  chatSessionId?: string;
  category?: TimelineEventCategory | TimelineEventCategory[];
  eventType?: string;
  targetEntityType?: string;
  targetEntityId?: string;
  sortBy?: 'createdAt';
  sortDir?: 'asc' | 'desc';
}

export interface CampaignFilters extends BaseInventoryFilters {
  /** Every campaign of the portfolio, whatever its granularity. The dashboard's default. */
  portfolioId?: string;
  /** Narrows to one granularity. Absent means all of them, which is what the dashboard opens on. */
  targetKind?: CampaignTargetKind;
  /** Campaigns touching this asset, matched against the propertyIds array. */
  propertyId?: string;
  unitId?: string;
  status?: SourcingCampaignStatus;
  sortBy?: 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

export interface SourcingCampaignFilters extends BaseInventoryFilters {
  propertyId?: string;
  /** All campaigns of the portfolio's properties (route filters on `property.portfolioId`). */
  portfolioId?: string;
  status?: SourcingCampaignStatus;
  sortBy?: 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

export interface PortfolioFitCampaignFilters extends BaseInventoryFilters {
  portfolioId?: string;
  status?: PortfolioFitCampaignStatus;
  sortBy?: 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

export interface PortfolioFitFilters extends BaseInventoryFilters {
  portfolioId?: string;
  brandId?: string;
  campaignId?: string;
  reviewMode?: boolean;
  status?: PortfolioFitStatus;
  minScore?: number;
  maxScore?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'score';
  sortDir?: 'asc' | 'desc';
}

export interface LeasingProjectFilters extends BaseInventoryFilters {
  projectKind?: StudioProjectKind;
  userId?: string;
  organisationId?: string;
  organisationName?: string;
  userName?: string;
  toDelete?: boolean;
  minDate?: string;
  maxDate?: string;
  sortBy?: 'NAME' | 'CREATED' | 'UPDATED' | 'CREDITS';
  sortDir?: 'asc' | 'desc';
}

export interface LeasingOrganisationFilters extends BaseInventoryFilters {
}

interface LeasingUserFilters extends BaseInventoryFilters {
  organisationId?: string;
}

interface NotificationFilters extends BaseInventoryFilters {
  unreadOnly?: boolean;
  includeArchived?: boolean;
  severity?: NotificationSeverity;
  type?: NotificationType;
  sortBy?: 'createdAt';
  sortDir?: 'asc' | 'desc';
}

export interface LeasingContactFilters extends BaseInventoryFilters {
  parentId?: string;
  parentType?: string;
}

interface StudioWorkspaceFilters extends BaseInventoryFilters {
  projectId?: string;
  sortBy?: 'name' | 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

interface StudioLayerFilters extends BaseInventoryFilters {
  projectId?: string;
  workspaceId?: string;
  type?: string;
  visible?: boolean;
  sortBy?: 'renderOrder' | 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

interface StudioWorkspaceVersionFilters extends BaseInventoryFilters {
  projectId?: string;
  workspaceId?: string;
  status?: StudioWorkspaceVersionStatus;
  parentVersionId?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'timestamp';
  sortDir?: 'asc' | 'desc';
}

export interface LeasingLeadFilters extends BaseInventoryFilters {
  propertyId?: string;
  lastCampaignId?: string;
  brandId?: string;
  portfolioFitId?: string;
  reviewMode?: boolean;
  status?: string;
  minFitScore?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

interface MarketSignalInventoryResponse {
  properties: LeasingPropertyDTO[];
  signals: LeasingMarketSignalDTO[];
  stats: MarketSignalStats;
  pagination?: Pagination | null;
}

export interface LeasingDealerSessionMetadata { // TODO : rename this, it's now our standard chat session metadata
  id: string;
  name: string;
  type?: string;
  messageCount: number;
  contextSummary?: ChatSessionContextAnchor[];
  activeSubject?: ActiveEntityFocus | null;
  subjectHistory?: ChatSubjectFocusEntry[];
  lastMessageSnippet?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type LeasingEventType = 'CREATE' | 'UPDATE' | 'DELETE';

export interface LeasingBrandFinancialRecordDTO {
  id: string;
  year: number;
  closingDate: string | null;
  revenue: number | null;
  netIncome: number | null;
  ebitda: number | null;
  equity: number | null;
  cashPosition: number | null;
  source: string | null;
  createdAt: string;
}

interface LeasingUnitPositionDTO {
  id: string;
  unitId: string;
  floorId: string;
  geoJson: SpatialManifest | null;
}

type EntityPayloadMap = {
  Property: LeasingPropertyDTO;
  Floor: LeasingFloorDTO;
  Unit: LeasingUnitDTO;
  UnitPosition: LeasingUnitPositionDTO;
  Brand: LeasingBrandDTO;
  Portfolio: LeasingPortfolioDTO;
  BrandLocation: LeasingBrandLocationDTO;
  LegalEntityFinancialRecord: LeasingBrandFinancialRecordDTO;
  LegalEntity: LeasingLegalEntityDTO;
  MarketSignal: LeasingMarketSignalDTO;
  Campaign: CampaignDTO;
  SourcingCampaign: SourcingCampaignDTO;
  PortfolioFitCampaign: PortfolioFitCampaignDTO;
  PortfolioFit: PortfolioFitDTO;
  Deal: LeasingDealDTO;
  ChatSession: ChatSession;
  TimelineEvent: TimelineEventDTO;
  GeneratedDocument: GeneratedDocument;
  Lead: LeasingLeadDTO;
  Project: LeasingProjectDTO;
  Organisation: LeasingOrganisationDTO;
  User: LeasingUserDTO;
  Notification: NotificationDTO;
  Contact: ContactDTO;
  Audit: DataAuditDTO;
  SlideCorrection: SlideCorrectionDTO;
  UniversalFact: UniversalFactDTO;
  StudioWorkspace: StudioWorkspaceDTO;
  StudioLayer: StudioLayerDTO;
  StudioWorkspaceVersion: StudioWorkspaceVersionDTO;
};

export type SocketStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';

interface RealtimeEventBase<E extends EntityType = EntityType> {
  entityType: E;
  entityId: string;
  createdAt?: string;
  userId?: string;
  organisationId?: string;
}

type RealtimeCreateEvent<E extends EntityType = EntityType> =
  RealtimeEventBase<E> & {
    eventType: 'CREATE';
    payload: EntityPayloadMap[E];
  };

type RealtimeUpdateEvent<E extends EntityType = EntityType> =
  RealtimeEventBase<E> & {
    eventType: 'UPDATE';
    payload: Partial<EntityPayloadMap[E]>;
  };

type RealtimeDeleteEvent<E extends EntityType = EntityType> =
  RealtimeEventBase<E> & {
    eventType: 'DELETE';
    payload: Partial<EntityPayloadMap[E]>;
  };

export type RealtimeEvent<E extends EntityType = EntityType> =
  | RealtimeCreateEvent<E>
  | RealtimeUpdateEvent<E>
  | RealtimeDeleteEvent<E>;

export type LeasingAgentRoleId = 'WEB_SCRAPER' | 'DATA_ANALYST';

export type PortalView = 'dashboard' | 'properties' | 'units' | 'marketSignals' | 'brands' | 'contacts' | 'portfolios' | 'deals' | 'audits' | 'legalEntities' | 'projects' | 'organisations' | 'globalActivity' | 'data' | 'actionCenter' | 'tables' | 'doctrine' | 'slides' | 'finance' | 'brandBase' | 'footfall' | 'fater3d' | 'video';

// Doctrine (admin): read-only repository of the instruction/context files each
// agent receives. Metadata travels in the manifest; file content is fetched
// per-file from the admin-gated API so prompt text never ships in the bundle.
export type DoctrineAgentId = 'source' | 'analyst' | 'studio' | 'slides';
type DoctrineFileFormat = 'markdown' | 'text';

export interface DoctrineFileMeta {
  id: string;
  filename: string;
  title: string;
  description?: string;
  format: DoctrineFileFormat;
  section?: string;
}

export interface DoctrineAgentManifest {
  id: DoctrineAgentId;
  name: string;
  description: string;
  files: DoctrineFileMeta[];
}

export interface DoctrineManifestResponse {
  agents: DoctrineAgentManifest[];
}

interface DoctrineFileContentResponse {
  agentId: DoctrineAgentId;
  fileId: string;
  content: string;
}

// ---------------------------------------------------------------------------
// Multi-layer context system
// ---------------------------------------------------------------------------
// The layered, role-scoped context stack that feeds every agent brain and is
// browsable/editable in the Doctrine tab. System doctrine (the hardcoded
// knowledge bases) is intentionally NOT part of this surface, it lives in the
// codebase and is never displayed in the product.

export const DOCTRINE_AGENT_KEYS = ['GLOBAL', 'SOURCE', 'ANALYST', 'STUDIO', 'MASTER', 'SLIDES'] as const;
export type DoctrineAgentKey = (typeof DOCTRINE_AGENT_KEYS)[number];

// Tool agents (exclude GLOBAL), the ones a tool-doctrine admin can be scoped to.
export const DOCTRINE_TOOL_KEYS = ['SOURCE', 'ANALYST', 'STUDIO', 'MASTER', 'SLIDES'] as const;
export type DoctrineToolKey = (typeof DOCTRINE_TOOL_KEYS)[number];

// Editable doctrine layer families (DoctrineDocument.scope in the DB).
//
// CAMPAIGN is the odd one out and deliberately so: every other layer is a standing rule that
// outlives any single piece of work, while a CAMPAIGN doctrine is the scoring instructions of ONE
// shortlist (its rubric is the other half). It is the last layer applied, so it wins over the org and tool
// doctrine unless those are `mandatory`. It is not browsable in the Doctrine tab, it is reached
// from the shortlist it belongs to.
export type DoctrineLayerKind = 'ORG' | 'TOOL' | 'TEAM' | 'USER' | 'CAMPAIGN';
export type MemoryOrigin = 'USER_DIRECTED' | 'AGENT_VOLUNTEERED';

// A single editable doctrine document (org / tool / team / user instructions).
export interface DoctrineDocumentDTO {
  id: string | null; // null when the layer has no row yet (never edited)
  kind: DoctrineLayerKind;
  agent: DoctrineAgentKey;
  scopeId: string; // organisationId | teamId | userId depending on kind
  title: string | null;
  content: string;
  format: DoctrineFileFormat;
  mandatory: boolean;
  version: number;
  updatedAt: string | null;
  editable: boolean; // resolved for the requesting user
}

// A single durable memory entry (the user's memory file is the ranked set of these).
export interface DoctrineMemoryEntryDTO {
  id: string;
  agent: DoctrineAgentKey;
  content: string;
  origin: MemoryOrigin;
  importance: number;
  createdAt: string;
}

// One browsable node in the Doctrine tab tree, scoped to the viewer's rights.
export interface DoctrineSectionMeta {
  key: string; // stable client id, e.g. "org:GLOBAL", "tool:SOURCE", "user:instructions", "user:memory"
  group: 'ORGANISATION' | 'MY_DOCTRINE';
  kind: DoctrineLayerKind | 'MEMORY';
  agent: DoctrineAgentKey;
  scopeId: string;
  title: string;
  subtitle?: string;
  editable: boolean;
  contentType: 'document' | 'memory';
  parentKey?: string | null; // tree parent; null/undefined = a root node
  container?: boolean; // a non-editable parent page (org root, "My doctrine")
  icon?: string | null; // stored per-page emoji override (persisted on the doc row)
  documentId?: string | null; // the backing DoctrineDocument id, when one exists
  isSubpage?: boolean; // a user-created nested page (a DoctrineDocument with parentId set)
  contributesToContext?: boolean; // subpage: whether its content is injected into agents
  systemKey?: string | null; // a page the system keeps (e.g. 'slides:reference-style'); never deletable, never a parent
  followsDefault?: boolean; // system page no person has written: it tracks the default text
}

/** One state of a doctrine page in its history, newest first; `current` is the live text. */
export interface DoctrineVersionDTO {
  id: string;
  version: number;
  title: string | null;
  content: string;
  /** Who wrote this text; null for text the system wrote (a default). */
  authorName: string | null;
  createdAt: string;
  current: boolean;
}

export interface DoctrineWorkspaceManifest {
  sections: DoctrineSectionMeta[];
}

// Content payload for one section (document layers), memory sections use DoctrineMemoryEntryDTO[].
export interface DoctrineSectionContentResponse {
  key: string;
  document?: DoctrineDocumentDTO;
  memory?: DoctrineMemoryEntryDTO[];
}

export const SESSION_TABLE_FOCUS_VIEWS = [
  'properties',
  'units',
  'marketSignals',
  'brands',
  'contacts',
  'portfolios',
  'portfolioFits',
  'deals',
  'audits',
  'legalEntities',
  'projects',
  'organisations',
] as const;

export type SessionTableFocusView = (typeof SESSION_TABLE_FOCUS_VIEWS)[number];

export interface DataAuditDTO {
  id: string;
  entityType: string;
  entityId: string;
  organisationId: string;
  plausibilityScore: number;
  procedureName: string;
  flaggedIssues: string[];
  suggestedFix: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  entityName?: string;
  entitySummary?: string;
  currentValue?: any;
  entityImage?: string | null;
}

/** A Slides Reviewer chapter an organisation may rewrite as a doctrine page (systemKey 'slides:<key>'). */
export type SlidesChapterKey = 'guideline-inline-unit' | 'reference-style';
/** An organisation's edited chapters; a missing key means Fater's default text. */
export type SlidesChapterOverrides = Partial<Record<SlidesChapterKey, string>>;

/**
 * One human correction of a Slides review, as the Training table shows it: what the
 * Reviewer wrote, what a person changed it to, and where the decision stands.
 */
export interface SlideCorrectionDTO {
  id: string;
  organisationId: string;
  projectId: string;
  /** The review the change was made to and the changed field's ledger id: what a decision points at. */
  runId: string;
  changeId: string;
  dossier: string;
  centre: string;
  tenant: string;
  page: number;
  point: string;
  before: string;
  after: string;
  /** Hash of the ledger value behind `after`; a pre-judge verdict is valid for this hash only. */
  afterHash: string;
  change: 'judgement' | 'wording' | 'layout' | 'added' | 'removed';
  byName: string;
  createdAt: string;
  /** Null until the pre-judge has looked at the row. */
  suggested: 'correction' | 'preference' | 'one-off' | 'noise' | null;
  suggestedReason: string | null;
  decision: 'pending' | 'approved' | 'rejected' | 'held-out';
  /** Whether the caller is an assigned review expert for this row's organisation (or a QA actor on the QA deck). */
  canDecide: boolean;
  pageId: string;
  elementId: string | null;
  /** The page image and the point on it the correction is about, so a row can show the drawing. */
  context: { snapshotUrl: string; pageWidth: number; pageHeight: number; x: number; y: number } | null;
  /** The element as the Reviewer left it and as the person left it, in page points, so the queue
   *  draws the change on the page instead of describing it. Null on a stamp or synthesis edit. */
  shapes?: { before: SlideCorrectionShape | null; after: SlideCorrectionShape | null } | null;
}

export interface SlideCorrectionShape {
  type: SlideElement['type'];
  frame: SlideElementFrame;
  text?: string;
  anchor?: { x: number; y: number };
  rotation?: number;
  color?: string;
  severity?: 'info' | 'warn' | 'fail';
}

/** One reviewed deck in the Training queue, for the dossier picker. */
export interface SlideCorrectionDossier { projectId: string; tenant: string; centre: string; edits: number; waiting: number; last: string }

export interface SlideCorrectionFilters {
  /** One dossier's edits only, read in the order they were made. */
  projectId?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  organisationId?: string;
  decision?: SlideCorrectionDTO['decision'];
}

export type AuditResolutionAction =
  | 'FIX'
  | 'VERIFY'
  | 'CLEAR_DATA'
  | 'TERMINATE_ENTITY'
  | 'MANUAL_FIX'
  | 'CREATE_BRAND_AND_UNIT'
  | 'NOT_REAL_BRAND';

export interface ChameleonAsset {
  url: string;
  description: string;
  mimeType?: string | null;
}

export interface ChameleonContext {
  template: {
    name: string;
    description: string | null;
    rawHtml: string;
    requiredSchema: any;
    mappingInstructions?: string | null;
  };
  deal: {
    id: string;
    status: string;
    financials: any;
    terms: any;
  };
  assets: ChameleonAsset[];
  author: {
    name: string;
    email: string;
    phone?: string | null;
    role: string | null;
  };
}

export const DEFAULT_GLOBAL_SESSION_NAME = 'Global Session';



export type ActionPriority = 'CRITICAL' | 'WARNING' | 'OPPORTUNITY' | 'INFO';

export const LEASING_ACTION_TYPES = [
  'RENT_ROLL',
  'DECLARE_PORTFOLIO_SIZE',
  'DATA_GAP_NO_UNITS',
  'DATA_GAP_MISSING_PROPERTIES',
  'DATA_GAP_NO_LEASING_MANAGER',
  'DATA_GAP_NO_FLOOR_PLAN',
  'DATA_GAP_MISSING_UNITS',
  'DATA_GAP_MISSING_FLOORS',
  'DATA_GAP_NO_FLOOR_CALIBRATION',
  'DATA_GAP_NO_UNIT_SURFACE',
  'DATA_GAP_NO_UNIT_POSITION',
  'DATA_GAP_NO_MAIN_PHOTO',
  'DATA_GAP_UNMAPPED_LOCATIONS',
  'RISK_ALERT_TENANT',
  'VALIDATE_UNIT_STATUS',
  'DATA_GAP_NO_LEASE_END_DATE',
  'STALE_DEAL',
  'IDENTIFY_ASSET_CANDIDATES',
  'ORCHESTRATE_SOURCING',
  'REVIEW_CANDIDATES',
  'SELECT_INCENTIVE_TEMPLATE',
  'SELECT_LOI_TEMPLATE',
  'DECIDE_BASE_RENT',
  'DECIDE_LEASE_DURATION',
  'DECIDE_RENT_FREE_PERIOD',
  'DECIDE_FITOUT_CONTRIBUTION',
  'DECIDE_STEP_RENTS',
  'VALIDATE_INCENTIVE_COVENANT',
  'ESTIMATE_TENANT_FITOUT_COST',
  'REDACT_INCENTIVE_LETTER',
  'REDACT_LOI',
  'SELECT_DEAL_CONTACT',
] as const;

export type LeasingActionType = typeof LEASING_ACTION_TYPES[number];

/**
 * Official support matrix for action/entity coupling used by the action UX.
 * Source of truth comes from action engine emissions + action specs.
 */
export const LEASING_ACTION_UI_SUPPORTED_COUPLES = [
  { actionType: 'RENT_ROLL', entityType: 'Property' },
  { actionType: 'DECLARE_PORTFOLIO_SIZE', entityType: 'Portfolio' },
  { actionType: 'DATA_GAP_NO_UNITS', entityType: 'Property' },
  { actionType: 'DATA_GAP_MISSING_PROPERTIES', entityType: 'Portfolio' },
  { actionType: 'DATA_GAP_NO_LEASING_MANAGER', entityType: 'Property' },
  { actionType: 'DATA_GAP_NO_FLOOR_PLAN', entityType: 'Property' },
  { actionType: 'DATA_GAP_MISSING_UNITS', entityType: 'Property' },
  { actionType: 'DATA_GAP_MISSING_FLOORS', entityType: 'Property' },
  { actionType: 'DATA_GAP_NO_FLOOR_CALIBRATION', entityType: 'Property' },
  { actionType: 'DATA_GAP_NO_UNIT_SURFACE', entityType: 'Unit' },
  { actionType: 'DATA_GAP_NO_UNIT_POSITION', entityType: 'Unit' },
  { actionType: 'DATA_GAP_NO_MAIN_PHOTO', entityType: 'Property' },
  { actionType: 'DATA_GAP_NO_MAIN_PHOTO', entityType: 'Unit' },
  { actionType: 'RISK_ALERT_TENANT', entityType: 'Deal' },
  { actionType: 'VALIDATE_UNIT_STATUS', entityType: 'Unit' },
  { actionType: 'VALIDATE_UNIT_STATUS', entityType: 'Deal' },
  { actionType: 'DATA_GAP_NO_LEASE_END_DATE', entityType: 'Deal' },
  { actionType: 'STALE_DEAL', entityType: 'Deal' },
  { actionType: 'IDENTIFY_ASSET_CANDIDATES', entityType: 'Property' },
  { actionType: 'ORCHESTRATE_SOURCING', entityType: 'Portfolio' },
  { actionType: 'ORCHESTRATE_SOURCING', entityType: 'Property' },
  { actionType: 'ORCHESTRATE_SOURCING', entityType: 'Unit' },
  { actionType: 'REVIEW_CANDIDATES', entityType: 'Property' },
  { actionType: 'REVIEW_CANDIDATES', entityType: 'SourcingCampaign' },
  { actionType: 'SELECT_INCENTIVE_TEMPLATE', entityType: 'Deal' },
  { actionType: 'SELECT_LOI_TEMPLATE', entityType: 'Deal' },
  { actionType: 'DECIDE_BASE_RENT', entityType: 'Deal' },
  { actionType: 'DECIDE_LEASE_DURATION', entityType: 'Deal' },
  { actionType: 'DECIDE_RENT_FREE_PERIOD', entityType: 'Deal' },
  { actionType: 'DECIDE_FITOUT_CONTRIBUTION', entityType: 'Deal' },
  { actionType: 'DECIDE_STEP_RENTS', entityType: 'Deal' },
  { actionType: 'VALIDATE_INCENTIVE_COVENANT', entityType: 'Deal' },
  { actionType: 'ESTIMATE_TENANT_FITOUT_COST', entityType: 'Deal' },
  { actionType: 'REDACT_INCENTIVE_LETTER', entityType: 'Deal' },
  { actionType: 'REDACT_LOI', entityType: 'Deal' },
  { actionType: 'SELECT_DEAL_CONTACT', entityType: 'Deal' },
  { actionType: 'DATA_GAP_UNMAPPED_LOCATIONS', entityType: 'Brand' },
] as const satisfies ReadonlyArray<{
  actionType: LeasingActionType;
  entityType: EntityType;
}>;

export type LeasingActionUiSupportedCouple = (typeof LEASING_ACTION_UI_SUPPORTED_COUPLES)[number];

export interface LeasingActionItem {
  id: string; // Unique hash of the action (e.g., "IDENTIFY_ASSET_CANDIDATES_unit123")
  type: LeasingActionType;
  priority: ActionPriority;
  title: string;
  description: string;
  ctaLabel: string;
  entityType?: EntityType;
  entityId?: string;
  metadata?: Record<string, any>;
}

export interface LeasingActionEngineResponse {
  actions: LeasingActionItem[];
  textReport: string; // The LLM-friendly text version for the Dealer
}

// ---------------------------------------------------------------------------
// Slides module (AI-native slide/PDF review editor)
// ---------------------------------------------------------------------------
// The deck is our JSON document: an ordered list of pages, each an immutable
// vector base (imported PDF page, rendered client-side) plus an overlay
// element tree. All coordinates are PAGE POINTS (PDF pts, origin top-left) so
// overlays survive any render scale and map 1:1 at pdf-lib export.
// Decision record: docs/plans/slides-module-architecture.md.

export type SlidesGuidelineKey = 'inline_unit' | 'kiosk' | 'food_unit';

export type SlideReviewVerdict = 'pass' | 'warn' | 'fail' | 'missing' | 'na';

export interface SlideElementFrame {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface SlideElementBase {
  id: string;
  frame: SlideElementFrame;
  rotation?: number;
  color?: string; // hex; review annotations default to red #FF3B30
  opacity?: number;
  /** Guideline point this element substantiates (e.g. 'p1'..'p10'), if any. */
  guidelinePointId?: string;
  /** Who authored the element ('agent' = Reviewer command, 'user' = manual tool).
   *  Provenance for the phase-2 learning loop: user edits/removals of agent
   *  elements are the training signal. Absent on legacy elements. */
  origin?: 'agent' | 'user';
  /** The person whose words a callout's text is: stamped when someone writes or edits it by hand,
   *  cleared when the Reviewer rewrites it. The name is kept as it was when they wrote. */
  writtenBy?: { userId: string; name: string };
  /** Metered credits the anchor locates for this element settled at, summed
   *  across re-locates. App-set from the locate-anchor response, never accepted
   *  from the agent. Absent on manual elements and on decks reviewed before
   *  metering (display falls back to SLIDES_NOMINAL_ANNOTATION_CREDITS). */
  credits?: number;
}

interface SlideNoteElement extends SlideElementBase {
  type: 'note'; // localized review comment: text bubble + leader line to a point
  text: string;
  severity?: 'info' | 'warn' | 'fail';
  /** Optional target point (page points, top-left origin) the note's leader line
   *  points to, the exact spot on the drawing the comment concerns. */
  anchor?: { x: number; y: number };
}

interface SlideArrowElement extends SlideElementBase {
  type: 'arrow'; // from frame origin to frame origin+w/h (vector as frame)
  text?: string;
  strokeWidth?: number;
}

interface SlideRectElement extends SlideElementBase {
  type: 'rect';
  text?: string;
  strokeWidth?: number;
  fill?: boolean;
}

interface SlideEllipseElement extends SlideElementBase {
  type: 'ellipse';
  text?: string;
  strokeWidth?: number;
  fill?: boolean;
}

interface SlideTextElement extends SlideElementBase {
  type: 'text'; // free text box (authoring + review synthesis)
  text: string;
  /** Point size (default 16). Scaled by canvas zoom for display; used verbatim in the PDF. */
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  italic?: boolean;
  align?: 'left' | 'center' | 'right';
  /** Line-step as a multiple of fontSize (default 1.25). */
  lineHeight?: number;
  fontFamily?: 'sans' | 'serif' | 'mono';
  // color / opacity / frame come from SlideElementBase. Text WRAPS to frame.w.
}

interface SlideStampElement extends SlideElementBase {
  type: 'stamp'; // review stamp chip (e.g. 'P3 FAIL', 'MISSING PIECE')
  text: string;
  verdict?: SlideReviewVerdict;
}

interface SlideImageElement extends SlideElementBase {
  type: 'image';
  url: string;
}

/** A stamp from the RDM library (lib/slides/blockArtwork.ts). */
export type SlideBlockKind = 'cartouche' | 'betterPlaces' | 'tenantSurvey' | 'ministamp'
  | 'materialBoard' | 'signage' | 'exposedCeilings' | 'lightingCeiling' | 'flooring'
  | 'siteSpecific' | 'limitedTime' | 'conditionOfApproval' | 'rollerShutter' | 'digitalContent' | 'fbKiosk' | 'kioskValidation'
  | 'noteLed' | 'noteLogoHoles' | 'noteCarpets' | 'seductiveDisclaimer';

interface SlideBlockElement extends SlideElementBase {
  type: 'block'; // one movable, resizable box; each of its cells is an editable text
  block: SlideBlockKind;
  fields: Record<string, string>;
}

interface SlidePatchElement extends SlideElementBase {
  type: 'patch'; // covers base content with bg color + optional new text
  text?: string;
  background: string;
  fontSize?: number;
}

export type SlideElement =
  | SlideNoteElement
  | SlideArrowElement
  | SlideRectElement
  | SlideEllipseElement
  | SlideTextElement
  | SlideStampElement
  | SlideImageElement
  | SlidePatchElement
  | SlideBlockElement;

export interface SlidePageDoc {
  id: string;
  index: number;
  kind: 'pdf' | 'blank';
  /** Page index inside the source PDF (kind 'pdf' only). */
  pdfPageIndex?: number;
  /** Page size in PDF points. Blank pages default to 16:9 (960x540). */
  width: number;
  height: number;
  /** Raster snapshot (blob URL) used for LLM vision + filmstrip fallback. */
  snapshotUrl?: string;
  /** Snapshot with a page-point coordinate grid overlaid, shown ONLY to the agent's
   *  vision so it anchors callouts by reading {x,y} off the grid (not by eyeballing).
   *  The editor, filmstrip and exported PDF always use the clean `snapshotUrl`. */
  gridSnapshotUrl?: string;
  /** Positioned text labels extracted from the PDF page (page points, top-left
   *  origin, x/y = label centre). The DETERMINISTIC anchor source: the agent
   *  anchors a callout by NAMING a label (anchorText) and the app resolves the
   *  exact coordinates from here, no vision coordinate regression. */
  textMap?: Array<{ t: string; x: number; y: number }>;
  /** Extracted text content of the base PDF page (LLM context). */
  textContent?: string;
  /** Agent/user page label ('Facade elevation', 'Floor plan'…). */
  label?: string;
  /** Page background colour (hex). Defaults to white in every renderer. */
  background?: string;
  /** The review's cover page, added in front of the tenant's deck (never part of the proposal). */
  cover?: boolean;
  elements: SlideElement[];
}

export interface DeckReviewPointState {
  verdict: SlideReviewVerdict;
  comment?: string;
}

export interface DeckReviewState {
  guidelineKey: SlidesGuidelineKey;
  round: number;
  /** Keyed 'p1'..'p10' per the RDM 10-point validation system. */
  points: Record<string, DeckReviewPointState>;
  synthesis?: string;
  missingPieces?: string[];
  /** Validation-card header the reviewer fills from the proposal (RDM initials,
   *  retailer "cellule", and mall "centre"). Rendered on the exported card. */
  header?: { rdm?: string; cellule?: string; centre?: string; date?: string; issue?: string };
  /** Overall design verdict on the card: 'A' validé / 'B' sous réserves / 'C' non validé. */
  status?: 'A' | 'B' | 'C';
  /** "Dépôt du DA" note, e.g. "DA non déposable." (planning filing, off-grid). No longer printed. */
  daNote?: string;
  /** The reviewer's own wording of the Dépôt du DA / Statut lines, replacing the fixed sentence
   *  until the line's colour changes. Typed by hand only; the agent never writes them. */
  authorityText?: string;
  statusText?: string;
  /** Whether the dossier can be filed with the authorities. INDEPENDENT of
   *  `status`: a scheme can be filable while still carrying design
   *  reservations. Unset falls back to the old derivation from `status`. */
  authorityStatus?: 'pass' | 'fail';
  /** false prints the card without its authorities line: URW's "Retail - 10P" version, for centres
   *  where no planning filing applies (stamp sheet, 2026-10-13). Absent = shown. */
  authorityRow?: false;
  /** Language of the stamp's fixed captions. The card must be entirely one
   *  language or the other so it can be handed to a tenant as it is. */
  language?: 'en' | 'fr';
  /** Placement + size of the validation stamp on page 1, as a frame in PAGE
   *  POINTS (same coordinate space as slide elements): x/y top-left, w×h box.
   *  The reviewer moves it (drag the header) and resizes it in all dimensions
   *  (8 handles) in the viewer; the exporter reads the same frame. `fontScale`
   *  (default 1) multiplies the text size independently of the box size. */
  stamp?: { x: number; y: number; w: number; h: number; fontScale?: number };
}

/** Compact archive of a CLOSED review round, kept on the deck when a revised
 *  proposal is imported (non-destructive: nothing from a past round is lost).
 *  Deliberately light, verdicts, labels and the callout texts, so the doc
 *  doesn't balloon round over round; it is exactly what the Reviewer needs to
 *  diff V(n) against V(n-1): resolved findings, persisting ones, and pieces
 *  that DISAPPEARED from the resubmission. */
export interface SlideDeckRoundArchive {
  round: number;
  closedAt: string; // ISO
  sourceFileName?: string | null;
  /** Page inventory of that round (index + reviewer label). */
  pageLabels: Array<{ index: number; label?: string }>;
  review?: DeckReviewState;
  /** The round's localized callouts: page index + note text (+ severity). */
  findings: Array<{ pageIndex: number; text: string; severity?: string; guidelinePointId?: string }>;
}

export interface SlideBrandReference {
  assetUrl: string;
  sourceUrl: string;
  imageUrl: string;
  title: string;
  description: string;
  publishedDate?: string;
  market?: string;
}

export interface ProposalCriticContext {
  guidelines: string;
  brandBrief?: string;
  referenceDescriptions?: string[];
}

export type ProposalSheetKind = 'croquis' | 'proposal';

export interface ProposalCartoucheMetadata {
  projectName: string;
  reviewRound: number;
  createdAt: string;
}

export interface BrandReferenceCandidate {
  imageUrl?: string;
  sourceUrl: string;
  title: string;
  publishedDate?: string;
}

export interface SlideBrandResearch {
  brand: { name: string; websiteUrl?: string };
  researchedAt: string;
  status: 'complete' | 'partial' | 'invalidated';
  brief: string;
  sources: Array<{ url: string; title: string; publishedDate?: string; kind: 'web' | 'internal' }>;
  references: SlideBrandReference[];
}

export interface SlideBrandResearchRun {
  id: string;
  projectId: string;
  sessionId: string;
  controller: AbortController;
  phase: 'identify' | 'web' | 'internal' | 'ready' | 'complete';
  brand?: { name: string; websiteUrl?: string };
  searches: number;
  webFailed: boolean;
  internalFailed: boolean;
  referencesFailed?: boolean;
  sources: Array<{ url: string; title: string; publishedDate?: string; kind: 'web' | 'internal'; image?: string }>;
  references: SlideBrandReference[];
  /** Set when Review was clicked on a deck with no research yet: the review starts once the brief is saved. */
  thenReview?: 'start' | 'restart';
}

export interface SlideReviewRun {
  id: string;
  projectId: string;
  sessionId: string;
  round: number;
  status: 'running' | 'completed' | 'incomplete' | 'stopped' | 'failed';
  startedAt: string;
  /** Client-observed provenance, never human approval or attested model authorship. */
  evidenceVersion?: 1;
  brief?: string;
  userEdited?: boolean;
  agentMutationCount?: number;
}

export interface SlideProposalRun extends Pick<SlideReviewRun, 'id' | 'projectId' | 'sessionId' | 'round' | 'status' | 'startedAt'> {
  kind: 'proposal';
  sheets?: Array<{ pageId: string; imageId: string; imageUrl: string; cartoucheId: string; cartoucheUrl: string }>;
  needsInspection?: boolean;
}

export type SlideAgentRun = SlideReviewRun | SlideProposalRun;

export interface SlideProposalProgress {
  phase: 'preparing' | 'generating' | 'checking' | 'assembling' | 'finishing';
  sheet?: 1 | 2;
  attempt?: number;
}

export interface SlideReviewEvidence {
  id: string;
  schemaVersion: 1;
  kind: 'input' | 'result';
  provenance: 'client_observed';
  eligibility: 'input_only' | 'pending_expert_review' | 'interleaved' | 'unattributed' | 'source_changed' | 'incomplete';
  capturedAt: string;
  capturedByUserId: string;
  run: SlideReviewRun;
  snapshotHash: string;
  snapshot: {
    deckId: string;
    projectId: string;
    organisationId: string;
    version: number;
    sourceFileUrl: string | null;
    sourceFileName: string | null;
    sourcePageCount: number;
    guidelineKey: string;
    reviewRound: number;
    doc: SlideDeckDoc;
  };
  context?: { guideline: string; doctrine: string };
}

export interface SlideReviewChange {
  id: string;
  path: string[];
  category: 'added' | 'removed' | 'wording' | 'judgement' | 'anchor' | 'presentation' | 'source';
  before: unknown;
  after: unknown;
}

export interface SlideLearningActor {
  dbUserId: string;
  userOrgId: string | null;
  isFaterAdmin: boolean;
}

export interface SlideCorrectionRequest {
  action: 'checkpoint';
  requestId: string;
  projectId: string;
  baselineId: string;
  baselineHash: string;
  expectedVersion: number;
  annotations: Array<{ changeId: string; classification: 'correction' | 'preference' | 'presentation' | 'other'; reason: string }>;
}

export interface SlideDecisionRequest {
  action: 'decide';
  requestId: string;
  projectId: string;
  checkpointId: string;
  checkpointHash: string;
  predecessorId: string | null;
  decision: 'approve' | 'reject' | 'revoke';
  confirmed: true;
  reason: string;
  applicability?: string;
  family?: string;
  partition?: 'reference' | 'evaluation';
}

export interface SlideCorrectionCheckpoint {
  id: string;
  schemaVersion: 1;
  kind: 'correction';
  projectId: string;
  organisationId: string;
  runId: string;
  actorId: string;
  createdAt: string;
  requestHash: string;
  baselineId: string;
  baselineHash: string;
  snapshot: SlideReviewEvidence['snapshot'];
  snapshotHash: string;
  guidelineHash: string;
  changes: SlideReviewChange[];
  annotations: SlideCorrectionRequest['annotations'];
  qa: boolean;
}

export interface SlideExpertDecision {
  id: string;
  schemaVersion: 1;
  kind: 'decision';
  projectId: string;
  organisationId: string;
  runId: string;
  actorId: string;
  createdAt: string;
  requestHash: string;
  checkpointId: string;
  checkpointHash: string;
  predecessorId: string | null;
  decision: 'approve' | 'reject' | 'revoke';
  reason: string;
  applicability?: string;
  family?: string;
  partition?: 'reference' | 'evaluation';
  sourceIdentity: string;
  qa: boolean;
}

/** One autosave of a finished review that changed what the Reviewer wrote: the Training table's raw material. */
export interface SlideEditCapture {
  id: string;
  schemaVersion: 1;
  kind: 'edit';
  projectId: string;
  organisationId: string;
  runId: string;
  actorId: string;
  createdAt: string;
  version: number;
  changes: SlideReviewChange[];
}

/** The pre-judge's suggested class for one captured change, valid for the exact text it judged. */
export interface SlideTrainingJudgement {
  id: string;
  schemaVersion: 1;
  kind: 'judgement';
  projectId: string;
  organisationId: string;
  runId: string;
  actorId: string;
  createdAt: string;
  changeId: string;
  afterHash: string;
  suggested: 'correction' | 'preference' | 'one-off' | 'noise';
  reason: string;
  model: string;
}

/** An expert's decision on one Training row; the latest one for a row is its decision. */
export interface SlideTrainingCuration {
  id: string;
  schemaVersion: 1;
  kind: 'curation';
  projectId: string;
  organisationId: string;
  runId: string;
  actorId: string;
  createdAt: string;
  changeId: string;
  decision: 'pending' | 'approved' | 'rejected' | 'held-out';
}

export interface SlideReferenceUsage {
  id: string;
  schemaVersion: 1;
  kind: 'usage';
  projectId: string;
  organisationId: string;
  runId: string;
  actorId: string;
  createdAt: string;
  attemptId: string;
  phase: 'prepared' | 'dispatch_authorized' | 'provider_invoked' | 'response_received' | 'failed';
  references: Array<{ checkpointId: string; snapshotHash: string; decisionId: string }>;
  contextHash: string;
  redactedReferences?: number;
  /** Training rows (SlideCorrection ids) whose approved correction was in the block. */
  curatedRows?: string[];
}

export interface SlideReferenceAttempt {
  projectId: string;
  organisationId: string;
  runId: string;
  actorId: string;
  attemptId: string;
  block: string;
  references: SlideReferenceUsage['references'];
  curatedRows?: string[];
}

export type SlideLearningEvent = SlideCorrectionCheckpoint | SlideExpertDecision | SlideReferenceUsage;

export interface SlideLearningView {
  enabled: boolean;
  canDecide: boolean;
  counts: Record<'input' | 'result' | 'correction' | 'decision' | 'usage', number>;
  events: SlideLearningEvent[];
  nextCursor: string | null;
  improvement: 'Not measured';
  baseline: { id: string; hash: string; eligibility: SlideReviewEvidence['eligibility'] } | null;
  checkpointDecisions: SlideExpertDecision[];
}

export interface SlideReviewComparison {
  baselineId: string;
  baselineHash: string;
  version: number;
  snapshotHash: string;
  changes: SlideReviewChange[];
}

export interface SlideLearningRuntime {
  userId: string;
  loading: boolean;
  error?: string;
  view?: SlideLearningView;
  historyCursor?: string;
  comparison?: SlideReviewComparison;
  annotations: SlideCorrectionRequest['annotations'];
  selectedCheckpointId?: string;
  orphanAnnotations?: Array<{ annotation: SlideCorrectionRequest['annotations'][number]; change?: SlideReviewChange }>;
  decisionReason: string;
  applicability: string;
  family: string;
  partition: 'reference' | 'evaluation';
  confirmed: boolean;
  pending?: SlideCorrectionRequest | SlideDecisionRequest;
  acknowledgedId?: string;
  lessons: Record<string, string>;
}

/** Where a presentation deck came from: the Plan scenario it was sent from, for the agent and the deck's own labels. */
export interface SlideDeckOrigin { module: 'plan'; planId: string; planName: string; scenarioName: string; propertyId: string; propertyName: string; sentAt: string }
export interface SlideDeckDoc {
  /** A review of a tenant's proposal (the default), or a presentation the deck's own team is building for decision makers. */
  kind?: 'review' | 'presentation';
  origin?: SlideDeckOrigin;
  brandResearch?: SlideBrandResearch;
  reviewRun?: SlideReviewRun;
  proposalRun?: SlideProposalRun;
  pages: SlidePageDoc[];
  review?: DeckReviewState;
  /** Closed rounds, oldest first (see SlideDeckRoundArchive). */
  rounds?: SlideDeckRoundArchive[];
  /**
   * The companion STUDIO project holding this deck's counter-proposal drafts.
   *
   * A proposal is generated on the Studio rails, so every draft - including the
   * ones the critic rejected - exists as a real workspace version. They belong
   * in a canvas project, not in the deck: a deck is a document, and hanging
   * Studio workspaces off its own project would point two different editors at
   * one project. Remembered here so the second proposal of a deck lands beside
   * the first instead of scattering one project per attempt.
   */
  proposalProjectId?: string | null;
}

export interface SlideDeckDTO {
  id: string;
  projectId: string;
  /** Project name used when the validation stamp has no explicit unit header. */
  name?: string;
  organisationId?: string | null;
  sourceFileUrl?: string | null;
  sourceFileName?: string | null;
  sourcePageCount: number;
  guidelineKey: SlidesGuidelineKey;
  reviewRound: number;
  status: 'DRAFT' | 'REVIEWING' | 'REVIEWED';
  doc: SlideDeckDoc;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** Lightweight deck summary for the Slides file browser (GET /api/slides/decks). */
export interface SlideDeckSummaryDTO {
  id: string;
  projectId: string;
  name: string;
  guidelineKey: SlidesGuidelineKey;
  status: 'DRAFT' | 'REVIEWING' | 'REVIEWED';
  reviewRound: number;
  pageCount: number;
  coverUrl?: string | null;
  kind?: 'review' | 'presentation';
  updatedAt: string;
  /** What this review costs: its annotation count at the base per-annotation
   *  price, plus the LLM inference its agent turns consumed. The annotation part
   *  is priced from the deck rather than the ledger, so a deck reviewed before
   *  per-annotation pricing shipped still shows today's price. */
  creditsUsed: number;
}

export type SlidesTool = 'select' | 'note' | 'arrow' | 'rect' | 'ellipse' | 'text' | 'stamp' | 'image';

export interface SlideDeckRuntimeState {
  learning?: SlideLearningRuntime;
  reviewRunId?: string | null;
  proposalRunId?: string | null;
  proposalProgress?: SlideProposalProgress;
  activePageId?: string;
  /** Figma-Slides view mode: single slide stage (default) or the slide grid. */
  viewMode?: 'single' | 'grid';
  selectedElementIds?: string[];
  activeTool?: SlidesTool;
  zoomLevel?: number;
  panOffset?: { x: number; y: number };
  rightBarTab?: 'params' | 'pages';
  /** The review language picked for this deck (switch above the chat, else the user's saved language at launch). */
  reviewLanguage?: 'fr' | 'en';
}

// --- Admin Activity feed (cross-module) -------------------------------------------------
// Shared between lib/admin-activity-feed.ts (server) and components/GlobalActivityMonitor.tsx.

export type ActivityModule = 'studio' | 'source' | 'slides' | 'analyst' | 'daimon';

/** One thing that happened, normalised across all four modules. */
export interface ActivityItem {
  id: string;
  /** ISO timestamp the stream is ordered by. */
  at: string;
  module: ActivityModule;
  /** Plain-language description of what the user did, e.g. "validated a version". */
  verb: string;
  subject: string;
  subjectId: string | null;
  /** EntityType for kernel focus navigation, when the subject is a focusable entity. */
  subjectEntityType: string | null;
  userId: string | null;
  userName?: string | null;
  organisationId: string | null;
  organisationName?: string | null;
  /** Credits this row consumed. null when the spend is not attributable to the row, which is
   *  different from 0: an Analyst session has no cost link at all, a free action costs nothing. */
  credits: number | null;
  outcome: 'ok' | 'running' | 'failed';
  imageUrl: string | null;
  /** Secondary line, e.g. "Round 2 · 14 pages". */
  detail: string | null;
}

/**
 * Credits spent in the window, split by the module that spent them.
 *
 * `analyst` is always 0 and `assist` holds every chat turn that touched no project and no
 * property, GenerationLog has no chatSessionId, so Master/Dictator/Analyst/project-less chat
 * spend is genuinely indistinguishable. Reported separately rather than misattributed.
 */
export interface ActivityModuleTotals {
  studio: number;
  source: number;
  slides: number;
  analyst: number;
  /** The Daimon's voice answers (her realtime models); her web research bills on a shared model, in assist. */
  daimon: number;
  assist: number;
}

export interface ActivityPresenceEntry {
  userId: string;
  userName: string;
  organisationId: string | null;
  organisationName: string | null;
  lastActive: string | null;
}

export interface ActivityFeedResult {
  sourceUsage?: SourceUsageReport;
  items: ActivityItem[];
  credits: ActivityModuleTotals;
  /** Credits spent in the window keyed by organisation id. */
  orgCredits: Record<string, number>;
  presence: ActivityPresenceEntry[];
  windowStart: string;
  windowEnd: string;
  /** Null when every row in the window is present. Otherwise the instant from which the stream is
   *  complete: at least one source hit its row cap, so anything older was dropped deliberately. */
  completeSince: string | null;
  generatedAt: string;
}

export type SourceCampaignReport = {
  runId: string; campaignId: string; kind: SourceCampaignRun['kind']; acceptedAt: string;
  outcome: SourceCampaignOutcome; contradictory: boolean;
  terminals: Array<Extract<SourceCampaignEventDetail, { phase: 'terminal' }> & { occurredAt: string }>;
  stopRequested: boolean; queueMs: number | null; executionMs: number | null;
  settledCredits: number | null; reservedCredits: number | null;
  linkedLedgers: number; unavailableLedgers: number;
};
export type SourceUsageDay = {
  key: string; date: string; userId: string; userName: string; organisationId: string; organisationName: string;
  visits: number; firstObserved: string; lastObserved: string;
  visibleMs: number | null; activeMs: number | null; gapMs: number; droppedRecords: number;
  actions: Array<{ action: SourceActionName; phase: SourceActionPhase; origin: SourceActionInput['origin']; count: number }>;
  runs: SourceCampaignReport[];
};
export type SourceUsageReport = {
  environment: SourceCampaignRun['environment']; windowStart: string; windowEnd: string; generatedAt: string;
  rows: SourceUsageDay[]; invalidRecords: number;
  truncated: { client: boolean; accepted: boolean; lifecycle: boolean; associations: boolean };
};

export type FactoryPersonaSheetModel = 'nano-banana-2' | 'mai-2.5-flash' | 'flux-2-klein-4b' | 'flux-2-pro';
export type ListFactoryImageSelection = { folderId: string; imageId: string; usage: 'style' | 'pose' | 'setting' | 'composition' | 'content' };
export type ListFactoryInspection = { revision: number; selectedFolderIds: string[]; selectedImages?: { folderId: string; imageId: string }[]; images: { folderId: string; imageId: string }[] };
export type FactoryPersonaSheetAttempt = { operationId: string; expectedRevision: number; model: FactoryPersonaSheetModel };
export type FactoryPersonaPanelAttempt = { userId: string; personaId: string; identityRevision: number; turnKey: string; attempt: FactoryPersonaSheetAttempt; settled: boolean };
export type FactoryManagementScope = { organisationId: string; workspaceId?: never } | { workspaceId: string; organisationId?: never };
export type FactoryManagementRequest = FactoryManagementScope & { userId: string } & ({
  resource: 'personas' | 'objects' | 'archetypes'; method: 'GET'; payload?: never; signal?: AbortSignal;
} | {
  resource: 'profile' | 'personas' | 'archetypes' | 'personas/sheet' | 'objects' | 'objects/sheet';
  method: 'POST' | 'PATCH' | 'DELETE';
  payload: Record<string, unknown>;
});
export type FactoryArchetypeDto = {
  cleanupPending?: boolean;
  id: string; handle: string; name: string; sourceDescription: string;
  revision: number; promptOverride?: string;
  attachedPrompt?: string;
  abstractDirection: import('#lib/list/factoryArchetypeDirection').FactoryArchetypeAbstractDirection;
  referenceImages: { id: string; imageUrl: string; thumbnailUrl: string }[];
  imageUrl?: string; thumbnailUrl?: string;
};
export type FactoryOrganisationProjection = {
  status: 'ready'; organisationId: string; revision: number; canEdit: boolean;
  referenceFolders: import('#lib/list/factoryProfile').FactoryFolder[];
  masterPrompt: string; userInstructions: string;
  lastDerivedDirection?: import('#lib/list/factoryProfile').FactoryDerivedDirection;
  archetypes: FactoryArchetypeDto[]; personas: FactoryPersonaDto[];
  objects?: FactoryObjectDto[]; objectsRevision?: number;
  feedbackLessons?: string;
} | { status: 'unavailable'; message: string };
export type FactoryPersonaAsset = { path: string; contentType: 'image/jpeg' | 'image/png' | 'image/webp'; bytes: number };
export type FactoryPersonaReference = { id: string; imageAsset: FactoryPersonaAsset; thumbnailAsset: FactoryPersonaAsset };
export type FactoryPersona = {
  id: string; handle: string; name: string; notes: string; revision: number; identityRevision: number;
  status: 'draft' | 'generating' | 'ready' | 'failed'; error?: string;
  referenceImages: FactoryPersonaReference[];
  sheet?: { imageAsset: FactoryPersonaAsset; thumbnailAsset: FactoryPersonaAsset; identityRevision: number; model?: FactoryPersonaSheetModel };
  operation?: { id: string; identityRevision: number; startedAt: number; consumedAt?: number; model?: FactoryPersonaSheetModel; imageSize?: '4K' };
  sheetOperationIds?: string[];
  createdAt: number; updatedAt: number; createdByUserId: string; deletedAt: number | null;
};
export type FactoryPersonaDto = Pick<FactoryPersona, 'id' | 'handle' | 'name' | 'notes' | 'revision' | 'identityRevision' | 'status' | 'error'> & {
  referenceImages: { id: string; url: string; thumbnailUrl: string }[];
  sheet?: { url: string; thumbnailUrl: string; identityRevision: number; model?: FactoryPersonaSheetModel };
  operationModel?: FactoryPersonaSheetModel;
  operationImageSize?: '4K';
  operationId?: string;
};

export type ListPersonaSelection = { id: string; identityRevision: number };
export type ListOutputPersonaAssociation = ListPersonaSelection & { handle?: string; enabled: boolean };
export type ListObjectSelection = { id: string; contentRevision: number };
export type ListOutputObjectAssociation = ListObjectSelection & { handle?: string; enabled: boolean };
export type ListOutputArchetypeAssociation = { id: string; revision: number; handle?: string; enabled: boolean };
export type FactoryObjectGenerationReference = { id: string; url: string; role: 'object-original'; objectId: string; contentRevision: number; referenceId: string };
export type FactoryPersonaGenerationReference = { id: string; url: string; role: 'persona-original' | 'persona-sheet'; personaId: string; identityRevision: number; referenceId: string };

export type FactoryObjectDimensions = {
  width?: number; height?: number; depth?: number;
  unit: 'mm' | 'cm' | 'm' | 'in'; provenance: 'user-supplied' | 'estimated'; note: string;
};
export type FactoryObject = Omit<FactoryPersona, 'identityRevision' | 'sheet' | 'operation'> & {
  contentRevision: number; dimensions: FactoryObjectDimensions;
  sheet?: { imageAsset: FactoryPersonaAsset; thumbnailAsset: FactoryPersonaAsset; contentRevision: number; revision?: number; model: 'nano-banana-2' };
  sheetHistory?: (NonNullable<FactoryObject['sheet']> & { revision: number })[];
  operation?: { id: string; contentRevision: number; startedAt: number; consumedAt?: number; imageSize: '4K'; model: 'nano-banana-2' };
};
export type FactoryObjectDto = Pick<FactoryObject, 'id' | 'handle' | 'name' | 'notes' | 'revision' | 'contentRevision' | 'status' | 'error' | 'dimensions'> & {
  referenceImages: FactoryPersonaDto['referenceImages'];
  sheet?: { url: string; thumbnailUrl: string; contentRevision: number; model: 'nano-banana-2' };
  operationId?: string;
};
export type FactoryObjectDraft = {
  ownerId: string; objectId: string; expectedRevision: number;
  name: string; handle: string; notes: string; dimensions: FactoryObjectDimensions;
  references: (FactoryPersonaDto['referenceImages'][number] & { file?: File; uploadedPath?: string })[];
  saving?: boolean; error?: string; dirty?: boolean;
  panelAttempt?: FactoryObjectPanelRequest; panelBusy?: boolean; panelStartedAt?: number;
};
export type FactoryObjectPanelRequest = { operationId: string; expectedRevision: number; model: 'nano-banana-2' };
export type FactoryObjectPanelAttempt = { userId: string; objectId: string; contentRevision: number; turnKey: string; attempt: FactoryObjectPanelRequest; settled: boolean };
export interface BrandPriorityEvidenceInput {
  id: string;
  name: string;
  lifecycleStatus?: string | null;
  liquidatedAt?: string | null;
  outOfScopeReason?: string | null;
  hasFrenchPresence?: boolean | null;
  operatingCountryCodes?: readonly string[];
  openingsDefensibility?: string | null;
  openingsEscalate?: boolean | null;
  openingsCalculatedAt?: string | null;
  openingsLastDate?: string | null;
  openingsEvidence?: unknown;
  openingsByYearJson?: string | null;
  closuresByYearJson?: string | null;
  locations: readonly {
    id: string;
    country?: string | null;
    openedAt?: string | null;
    closedAt?: string | null;
    verifiedAt?: string | null;
    source?: string | null;
  }[];
  legalIdentities: readonly {
    id: string;
    role?: string | null;
    anchorMethod?: string | null;
    anchoredAt?: string | null;
    anchorEvidence?: unknown;
    legalEntity: {
      id: string;
      registrationNumber: string;
      legalName: string;
      country?: string | null;
      financialStatus?: string | null;
      lastFinancialYear?: number | null;
      distressStatus?: string | null;
      distressCheckedAt?: string | null;
    };
  }[];
}

export interface BrandPriorityEvidenceAssessment {
  brandId: string;
  name: string;
  retained: boolean;
  recordedFlags: string[];
  france: {
    status: 'unassessed' | 'stored_records' | 'conflicting_records';
    physicalOperationVerified: false;
    flagged: boolean;
    tagged: boolean;
    openLocationRecords: number;
    anchoredCompanyRecords: number;
    conflictingLocationIds: string[];
    references: { recordId: string; source: string | null; observedAt: string | null }[];
  };
  headquarters: { status: 'unassessed'; frenchHeadOfficeLinks: number; frenchSubsidiaryLinks: number };
  expansion: {
    status: 'unassessed' | 'registry_proxy' | 'conflicting_records';
    shopOpeningsVerified: false;
    attribution: string | null;
    source: string | null;
    observedAt: string | null;
    recordedLastDate: string | null;
    latestRegistryEvent: string | null;
    inspectedEstablishments: number;
    uniqueEstablishments: number;
    duplicateEstablishments: number;
    validRegistryOpenings: number;
    invalidRecords: number;
    invalidCalculationDate: boolean;
    declaredTotal: number | null;
    complete: boolean;
    annualSeriesPresent: boolean;
  };
  health: {
    status: 'unassessed';
    storedEntitySignals: { entityId: string; legalName: string; financialStatus: string | null; lastFinancialYear: number | null; distressStatus: string | null; distressCheckedAt: string | null; linkId: string }[];
  };
  attractiveness: { status: 'unassessed' };
  missingEvidence: string[];
}


/** Source-bound complete campaign evidence; runtime guards remain stricter than this shape. */
export type CampaignDossier = {
  version: "campaign-dossier-v2";
  overview: {
    fingerprint: string;
    fieldEvidence: Array<{
      field: "name" | "logo" | "business_summary" | "website" | "founded" | "network" | "positioning" | "category" | "surface" | "footprint" | "revenue" | "legal";
      basis: "primary_fact" | "analyst_judgment";
      reason: string;
      sourceIds: Array<string>;
    }>;
    network: {
      scope: "global";
      format: "consumer_retail";
      count: number;
      countries: Array<string>;
      locations: Array<{
        locationId: string;
        operatorId: string;
        sourceIds: Array<string>;
      }>;
      sourceIds: Array<string>;
      unresolvedIssues: Array<string>;
    };
    surface: {
      scope: string;
      profiles: Array<{
        format: string;
        basis: "current_format_requirement" | "documented_examples";
        reason: string;
        minSqm: number;
        maxSqm: number;
        standardSqm: number | null;
        examples: Array<{
          address: string;
          country: string;
          areaSqm: number;
          at: string | null;
          sourceIds: Array<string>;
        }>;
        sourceIds: Array<string>;
      }>;
    };
    operators: Array<{
      legalEntityId: string;
      country: string;
      registrationNumber: string;
      sourceIds: Array<string>;
    }>;
    revenue: {
      legalEntityId: string;
      country: string;
      currency: "EUR";
      geographicScope: "legal_entity_accounts";
      annual: true;
      comparisonReason: string;
      records: Array<{
        year: number;
        revenue: number;
        netIncome: number | null;
        periodStart: string | null;
        periodEnd: string;
        periodLabel: string;
        sourceIds: Array<string>;
      }>;
      sourceIds: Array<string>;
    };
  };
  expansion: {
    country: "FR";
    semantics: "matched_retail_registration";
    coverage: "complete_available_operator_history";
    startAt: string;
    endAt: string;
    physicalHistoryCoverage: "unknown" | "partial";
    unresolvedIssues: Array<string>;
    operators: Array<{
      legalEntityId: string;
      siren: string;
      availableCount: number;
      returnedCount: number;
      release: string;
      pages: Array<{
        page: number;
        recordCount: number;
        sourceIds: Array<string>;
      }>;
      sourceIds: Array<string>;
      records: Array<{
        siret: string;
        registeredAt: string | null;
        ceasedAt: string | null;
        administrativeStatus: "A" | "F";
        role: "retail" | "headquarters" | "warehouse" | "production" | "duplicate" | "transfer" | "planned" | "unresolved";
        address: string;
        postcode: string;
        communeCode: string;
        premiseId: string | null;
        duplicateOfSiret: string | null;
        sourceIds: Array<string>;
        brandSiteSourceIds: Array<string>;
        reason: string;
        physicalMatch: {
          address: string;
          postcode: string;
          communeCode: string;
          sourceIds: Array<string>;
          reason: string;
        } | null;
      }>;
    }>;
  };
};
export type CampaignOverviewSnapshot = {
  name: string; logoUrl: string | null; summary: string | null; description: string | null; websiteUrl: string | null; foundedYear: number | null;
  declaredNetworkSize: number | null; marketPositioning: string | null; category: string | null; cnccSector: string | null; cnccSubFamily: string | null; cnccActivity: string | null;
  surfaceMin: number | null; surfaceMax: number | null; surfaceStandard: number | null; geographicFootprint: string | null; operatingCountryCodes: string[];
};
export type CampaignCurrentState = {
  overview: CampaignOverviewSnapshot;
  locations: Array<{ id: string; country: string | null; address: string | null; postalCode: string | null; latitude: number | null; longitude: number | null }>;
  operators: Array<{ id: string; country: string; registrationNumber: string; anchorMethod: string | null;
    distressStatus: string | null; distressLatestDate: string | null; financialStatus: string | null;
    financials: Array<{ year: number; revenue: number | null; netIncome: number | null; currency: string | null; verificationVerdict: string | null }> }>;
};
export type CampaignRegistrySeries = { semantics: 'matched_retail_registration'; country: 'FR'; startAt: string; endAt: string; sirens: string[];
  registeredByYear: Record<string, number>; ceasedByYear: Record<string, number>; sourceIds: string[]; completeAvailable: true; physicalHistoryCoverage: 'unknown' | 'partial' };
export type CampaignOverviewProjection = Omit<CampaignDossier['overview'], 'network'> & { snapshot: CampaignOverviewSnapshot; network: CampaignDossier['overview']['network'] & { countryCounts: Record<string, number> } };
export type CampaignChecks = { overviewVerified: boolean; networkVerified: boolean; expansionVerified: boolean; blockers: string[];
  overview?: CampaignOverviewProjection; registrySeries?: CampaignRegistrySeries; plannedPremises?: CampaignPlannedPremise[] };

export type CampaignPlannedPremise = { address: string; postcode: string; communeCode: string; operatorId: string; siret: string; targetAt: null; sourceIds: string[] };

export type BrandQualificationBinding = {
  id: string; name: string; websiteUrl: string | null; parentBrandId: string | null;
};
export type BrandQualificationDependency = { kind: 'location' | 'operator'; id: string; fingerprint: string };
export type BrandQualificationState = BrandQualificationBinding & {
  lifecycleStatus: string; liquidatedAt: string | null; outOfScopeReason: string | null;
  dependencies: BrandQualificationDependency[];
  campaignState?: CampaignCurrentState;
};
export type BrandQualificationClaim = {
  verdict: 'supported' | 'unknown' | 'contradicted'; reason: string; sourceIds: string[];
};
export type BrandQualificationSource = {
  id: string; url: string; quote: string;
  kind: 'live_primary' | 'dated_primary' | 'independent' | 'secondary';
  scope: 'brand' | 'brand_france' | 'current_operator' | 'other'; subjectId: string;
  effectiveAt: string; observedAt: string;
  /** Retained source byte hash, required by complete campaign dossiers. */
  contentSha256?: string;
};
export type BrandQualificationPremise = {
  id: string; address: string; postcode: string; communeCode: string; active: boolean; sourceIds: string[];
};
export type BrandQualificationEvent = {
  premiseId: string; type: 'opening' | 'closure'; status: 'actual' | 'planned' | 'registry_proxy'; at: string; sourceIds: string[];
};
/** An admitted, independently reviewed physical French opening or named planned opening. */
export type BrandExpansionMilestone = Omit<BrandQualificationEvent, 'type' | 'status'> & {
  type: 'opening'; status: 'actual' | 'planned'; address: string; postcode: string; communeCode: string;
};
export type BrandQualificationPayload = {
  version: 'source-brand-quality-v1'; brand: BrandQualificationBinding;
  dependencies: BrandQualificationDependency[]; assessor: string; assessedAt: string;
  sources: BrandQualificationSource[];
  campaignDossier?: CampaignDossier;
  /** Source-attributed logo checked as displayable; optional for qualification, required for campaign readiness. */
  logo?: { url: string; sourceIds: string[]; checkedAt: string; displayable: true };
  requirements: Record<'identity' | 'relevance' | 'france' | 'vitality', BrandQualificationClaim>;
  premises: BrandQualificationPremise[]; events: BrandQualificationEvent[];
  credibility: { route: 'network' | 'reported_network' | 'destination' | 'emerging' | 'unknown'; reason: string; sourceIds: string[] };
  distress: { status: 'clear' | 'current' | 'unresolved' | 'historical_resolved'; reason: string; sourceIds: string[] };
  attractiveness: { tier: 'limited' | 'established' | 'strong' | 'destination'; basis: 'independent_recognition' | 'documented_demand' | 'positioning_only'; reason: string; sourceIds: string[] } | null;
  resilience: { tier: 'stable' | 'strong'; basis: 'profitability_and_solvency' | 'current_financing' | 'revenue_only' | 'no_recorded_distress'; reason: string; sourceIds: string[] } | null;
  headquarters: { countryCode: string; basis: 'brand_headquarters' | 'local_registration'; reason: string; sourceIds: string[] } | null;
  networkWindow: { startAt: string; endAt: string; startingCount: number; completeOpenings: boolean; completeClosures: boolean; sourceIds: string[] } | null;
};
export type BrandQualificationReceipt = {
  payload: BrandQualificationPayload;
  review: { reviewer: string; reviewedAt: string; payloadSha256: string };
};
export type BrandPriorityComponent = { points: number; maximum: number; known: boolean; reason: string; sourceIds: string[] };
export type BrandQualificationResult = {
  policyVersion: 'source-brand-quality-v1'; brandId: string;
  eligibility: 'qualified' | 'hold'; priority: number | null; evidenceCoverage: number;
  components: Record<'openingRecency' | 'netGrowth' | 'attractiveness' | 'resilience' | 'frenchHeadquarters', BrandPriorityComponent>;
  reasons: string[];
  /** Empty unless the receipt is qualified and its expansion evidence is admissible now. */
  expansionMilestones?: BrandExpansionMilestone[];
  physicalNetworkChange?: { net: number; startingCount: number; startAt: string; endAt: string };
  campaignChecks?: CampaignChecks;
  campaignOverview?: CampaignOverviewProjection;
  registrySeries?: CampaignRegistrySeries;
  plannedPremises?: CampaignPlannedPremise[];
};
/** The Fater stamp: every brand scored from official data already on file (lib/leasing/faterStamp.ts). */
export type FaterStampComponent = { points: number; maximum: number; known: boolean; reason: string; source: string };
export type FaterStamp = {
  /** Still in the market: scored and sourceable. */
  eligible: boolean;
  /** The Fater stamp: Fater recommends this tenant. */
  recommended: boolean;
  /** In the privileged pool: retail, no red flag, and wanted by URW or Altarea. */
  privileged: boolean;
  /** 1 privileged pool, 2 in market with 3+ own French shops and not ruled out by clients, 3 the rest of the market and retail brands not yet in France; null out of the market (lib/leasing/faterStamp.ts). */
  tier: 1 | 2 | 3 | null;
  /** Privileged with zero unresolved required campaign checks; unsupported optional facts stay unknown. */
  dataReady: boolean;
  /** Current active French shop count used by the reviewed campaign readiness check; never global. */
  frenchStores?: number;
  /** Missing verified identity, criteria, financials or stores; contacts are optional enrichment. */
  dataGaps: string[];
  /** URW and Altarea's read of the brand. */
  client: { fit: string; reason: string } | null;
  score: number;
  coverage: number;
  /** Red flags, including the ones that keep a brand out of the market. */
  reasons: string[];
  components: Record<'client' | 'financial' | 'aura' | 'covenant' | 'recency' | 'growth' | 'reach' | 'draw' | 'frenchHeadquarters', FaterStampComponent>;
};

export type BrandQualityAssessment = Omit<BrandQualificationResult, 'eligibility'> & {
  eligibility: 'qualified' | 'hold' | 'unassessed';
  evaluatedAt: string;
  reviewedAt: string | null;
  sources: BrandQualificationSource[];
  stamp?: FaterStamp;
};

export type BrandQualificationBulkDocument = {
  id: string; url: string; finalUrl: string; title: string; text: string;
  observedAt: string; publishedAt: string | null; dateEvidence: string | null;
  contentSha256: string; truncated: boolean;
  originalContentSha256?: string;
};
export type BrandQualificationBulkPremise = {
  id: string; address: string; postcode: string; city: string;
  communeCode: string | null; communeEvidenceUrl: string | null; sourceUrls: string[];
  sourceEvidence?: Array<{ documentId: string; quote: string; context: string[] }>;
  geographyEvidence?: { url: string; text: string; observedAt: string; contentSha256: string };
};
export type BrandQualificationBulkPacket = {
  brand: BrandQualificationState;
  expectedLatestReceiptId: string | null;
  candidatePremises: BrandQualificationBulkPremise[];
  documents: BrandQualificationBulkDocument[];
  retrievalFailures: { url: string; reason: string; evidence?: BrandQualificationBulkPremise['geographyEvidence'] }[];
  collectedAt: string;
};

// Daimon browser execution contract shared with the phone transport.
export type HostedDaimonBinding = {
  callId: string;
  /** The identity the page must show: the caller's, or the user a Fater admin caller impersonates. */
  dbUserId: string;
  userOrgId: string;
  conversationId: string;
  impersonating?: boolean;
};

export type HostedDaimonContext = {
  dbUserId: string | null;
  userOrgId: string | null;
  isImpersonating: boolean;
  conversationId: string | null;
};

export type HostedDaimonTool = {
  callId: string;
  toolCallId: string;
  name: string;
  args: Record<string, unknown>;
};

export type HostedDaimonResult = {
  summary: string;
  cards?: DaimonConversationMessage['cards'];
  image?: string;
  conversationId: string | null;
};

export interface HostedDaimonBridge {
  context(): HostedDaimonContext;
  bind(binding: HostedDaimonBinding): Promise<HostedDaimonContext>;
  interrupt(callId: string, itemId?: string): Promise<void>;
  userTurn(turn: { callId: string; itemId: string; text: string }): Promise<HostedDaimonContext>;
  execute(tool: HostedDaimonTool): Promise<HostedDaimonResult>;
  end(callId: string): Promise<void>;
}

export type TrafficVisitorCountGeometry = Extract<TrafficSpatialGeometry, { type: 'MultiPolygon' }> & {
  srid: 4326; vertices: number; bytes: number;
};
export type TrafficVisitorCountNative = Record<'CODE' | 'NAME' | 'VALIDATED_AT' | 'KIND' | 'CITY' | 'POSTCODE' | 'GEOG' | 'MARCH_FOOTFALL', string>;
export type TrafficVisitorCountPeriod = {
  start: '2024-03-01'; endExclusive: '2024-04-01'; timeZone: 'Europe/Paris'; precision: 'month'; sourceBasis: 'catalogue_sample_and_column_definition';
};
export interface TrafficVisitorCountPack {
  schema: 'fater-traffic-visitor-counts-v1'; propertyId: string; organisationId: string; snapshotSha256: string; generatedAt: string;
  sources: Record<'csv' | 'catalogue' | 'client', { file: string; url: string; sha256: string; bytes: number; status: 200; contentType: string; retrievedAt: string; discoveryReceiptSha256: string }>;
  catalogue: { period: TrafficVisitorCountPeriod; sourceMetadataDateMeaning: 'Creation date'; method: string; sampleStatus: 'indicative'; witnesses: Array<{ locator: string; text: string | null; sha256: string }> };
  observation: {
    id: string; sourceOrdinal: 32; sourceNative: TrafficVisitorCountNative; value: 781650; rawValue: '781650'; metric: 'supplier_reported_pedestrian_footfall'; unit: 'pedestrian_count'; basis: 'supplier_estimate'; sampleStatus: 'indicative';
    period: TrafficVisitorCountPeriod; geometry: TrafficVisitorCountGeometry; independentMeasurementVerified: false; calibrationEligible: false;
    unknowns: Record<'eventDefinition' | 'uniquePersonDefinition' | 'repeatVisitTreatment' | 'sampleExpansion' | 'accuracy' | 'dwellThreshold' | 'visitorOrPasserEligibility' | 'centreEntryEquivalence' | 'sampleAuthenticity', null>;
  };
  geography: {
    method: string; reference: [number, number]; earthRadiusMetres: number; supplierBounds: [number, number, number, number]; nativeNamedUnitBounds: [number, number, number, number];
    namedUnits: number; exactlyInside: number; within75m: number; centreDistanceMetres: number; maximumDistanceMetres: number; associationAccepted: boolean; perimeterEquivalent: false;
    thresholds: { minimumFractionWithinDistance: number; distanceMetres: number; maximumCentreDistanceMetres: number };
    distances: Array<{ unitId: string; representativeMetres: [number, number]; inside: boolean; distanceMetres: number }>; nativeVenueAuditId: string; nativeSnapshotSha256: string;
  };
  excludedCandidates: Array<{ sourceOrdinal: 185; sourceNative: TrafficVisitorCountNative; value: 231812; geometry: TrafficVisitorCountGeometry; indoorUnitIds: []; reason: string; additiveToCentreCount: false }>;
  coverage: { sourceRows: 427; sourceGeometryExceptions: Array<{ ordinal: number; code: string; sourceNative: TrafficVisitorCountNative; field: 'GEOG'; reason: 'original_geometry_missing' }>; decodedVertices: number; historicalMonthlySupplierCountSamples: 1; independentlyMeasuredVisitorObservations: 0; currentVisitorObservations: 0; hourlyVisitorObservations: 0; shopVisitorObservations: 0 };
  readiness: { historicalMonthlySupplierAggregateAvailable: true; productionPlanReady: false; current: false; daily: false; hourly: false; shops: false; corridors: false; unresolved: string[] };
  usage: { scope: 'private_source_evaluation'; commercialLicenceVerified: false; automatedPlanImportAllowed: false; termsUrl: string; relevantSections: string[] };
}
